// v1.0.1 - Tick Confirmation Update
import { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Bot, Mail, MessageSquare, Phone, CheckCircle2, FileText, Send, Users, Loader2, Mic, Plus, UserPlus, PhoneOff, Globe, Edit3, Pencil, X, Check, MessageCircle, Trash2, ArrowLeft, ArrowRight, Calendar, AlertCircle, AlertTriangle, Play, Pause, Volume2, ChevronDown, ChevronUp, RotateCcw, RefreshCw, Clock, TrendingUp, Activity, Star, QrCode, ArrowUpRight, CheckSquare, User, UserCheck, ListChecks, Search, XCircle, MapPin, Eye, EyeOff, HeartPulse, Sparkles, Table, LayoutList, Kanban, Copy, ArrowUp, ArrowDown, ArrowUpDown, Lock } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useConversation } from '@elevenlabs/react';
import { MOCK_WORKERS } from '../data/mockWorkers';
import { assignWorkerToClient, releaseWorkerByClientId } from '../services/assignmentService';
import { SendQuotationModal } from './components/SendQuotationModal';
import { CARE_SERVICES } from '../constants/services';
import PayslipGenerator from '../components/hr/PayslipGenerator';
import { recordServiceInvoice, endService } from '../services/serviceLifecycle';
import ClientDetailsModal from './components/ClientDetailsModal';
import { useAuth } from '../contexts/AuthContext';
import RequestDeletionModal from '../components/common/RequestDeletionModal';
import { useLocation, useNavigate } from 'react-router-dom';
import { normalizePhoneDigits, phoneLast10, phonesMatch } from '../utils/phone';
import { maskPhoneNumber } from '../utils/phoneMask';
import { buildVoiceCallIntakePrefill, buildLeadIntakePrefill } from '../utils/voiceCallIntake';
import { buildConsentFlowActionData, buildBabyCareFlowActionData, buildPatientCareFlowActionData, resolveLeadDisplayName } from '../utils/consentFlow';
import {
    findClientMasterByPhone,
    isLegacyPipelineStage,
    isManualInvoiceLead,
    isPipelineVisibleLead,
    NEW_LEAD_PIPELINE_STAGE,
    sanitizePipelineStages,
    extractCity,
    isLeadInMonth,
    getLeadMonthKey,
} from '../utils/crm';
import {
    formatLeadValueDisplay,
    getLeadServiceDays,
    getLeadValueLabel,
    isShortTermService,
} from '../utils/quotationEstimate';
import { calculateClientAttendanceSummary, calculateClientServiceDaysFromAttendance, type ClientAttendanceSummary } from '../utils/billingRate';

const ELEVENLABS_AGENT_ID = import.meta.env.VITE_ELEVENLABS_AGENT_ID || '';

const RupeeIcon = ({ className }: { className?: string }) => (
    <span className={`font-bold leading-none flex items-center justify-center ${className || ''}`} style={{ fontFamily: 'system-ui, sans-serif' }}>₹</span>
);

export const getLatestByCreatedAt = (items?: any[]) => {
    if (!items || items.length === 0) return null;
    return [...items].sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime())[0];
};

// --- CUSTOM AUDIO PLAYER COMPONENT ---
const VoicePlayer = ({ src }: { src: string }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const audioRef = useRef<HTMLAudioElement | null>(null);

    const togglePlay = () => {
        if (!audioRef.current) return;
        if (isPlaying) {
            audioRef.current.pause();
        } else {
            audioRef.current.play();
        }
        setIsPlaying(!isPlaying);
    };

    const handleTimeUpdate = () => {
        if (audioRef.current) {
            const currentProgress = (audioRef.current.currentTime / audioRef.current.duration) * 100;
            setProgress(currentProgress || 0);
        }
    };

    const handleEnded = () => {
        setIsPlaying(false);
        setProgress(0);
    };

    const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (audioRef.current) {
            const seekTo = (parseFloat(e.target.value) / 100) * audioRef.current.duration;
            audioRef.current.currentTime = seekTo;
            setProgress(parseFloat(e.target.value));
        }
    };

    return (
        <div className="flex items-center gap-3 bg-slate-50/50 border border-slate-200/60 p-2 rounded-xl w-full group transition-all hover:bg-white hover:shadow-sm">
            <audio
                ref={audioRef}
                src={src}
                onTimeUpdate={handleTimeUpdate}
                onEnded={handleEnded}
                preload="none"
            />
            <button
                onClick={togglePlay}
                className="w-10 h-10 flex items-center justify-center rounded-full bg-emerald-600 text-white shadow-md hover:bg-emerald-700 transition-all transform active:scale-95 shrink-0"
            >
                {isPlaying ? <Pause className="w-5 h-5 fill-white" /> : <Play className="w-5 h-5 fill-white ml-0.5" />}
            </button>
            <div className="flex-1 space-y-1.5 flex flex-col justify-center pr-2">
                <input
                    type="range"
                    min="0"
                    max="100"
                    value={progress}
                    onChange={handleSeek}
                    className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-emerald-600"
                />
                <div className="flex items-center justify-between text-[10px] uppercase font-bold text-slate-400 tracking-wider">
                    <span className="flex items-center gap-1"><Volume2 className="w-3 h-3 text-slate-300" /> Audio Track</span>
                    <span className="text-emerald-500">{isPlaying ? 'Playing' : 'Ready'}</span>
                </div>
            </div>
        </div>
    );
};

const ATTENTION_EVENT_TYPES = new Set([
    'lead_created',
    'form_filled',
    'quote_question',
    'quote_call_requested',
    'needs_attention',
    'call_received',
    'automation_error',
    'whatsapp_deposit_invoice_alert_failed',
]);

const buildAttentionNotification = (activity: any) => {
    const metadata = activity?.metadata || {};
    const leadName = metadata.lead_name || metadata.name || 'Lead';
    const service = metadata.service || metadata.detected_service || metadata.service_category;
    const source = metadata.source || 'CRM';
    const messagePreview = metadata.message_preview ? ` Message: "${String(metadata.message_preview).slice(0, 80)}"` : '';

    switch (activity?.event_type) {
        case 'lead_created':
            return {
                title: source === 'AI Phone Call' ? 'New Voice Lead' : 'New Lead Needs Review',
                body: `${leadName} from ${source}${service ? ` for ${service}` : ''}.`,
            };
        case 'form_filled':
            return {
                title: 'New Intake Form Submitted',
                body: `${leadName}${service ? ` requested ${service}` : ' submitted service details'}.`,
            };
        case 'quote_question':
            return {
                title: 'Quote Question Needs Reply',
                body: `${leadName} tapped Ask a question.${messagePreview}`,
            };
        case 'quote_call_requested':
            return {
                title: 'Call Requested From Quote',
                body: `${leadName} tapped Schedule a call.${messagePreview}`,
            };
        case 'call_received':
            return {
                title: 'Voice Call Needs Review',
                body: `${leadName} completed an AI call${service ? ` about ${service}` : ''}.`,
            };
        case 'automation_error':
        case 'whatsapp_deposit_invoice_alert_failed':
            return {
                title: 'Automation Needs Attention',
                body: activity.description || metadata.reason || 'An automated workflow failed.',
            };
        case 'needs_attention':
            return {
                title: 'Lead Needs Attention',
                body: `${leadName}: ${metadata.reason || activity.description || 'Manual review required.'}`,
            };
        default:
            return null;
    }
};

const buildWhatsAppFailureNotification = (log: any) => {
    const payload = log?.payload || {};
    const template = payload.templateName || payload.type || 'WhatsApp message';
    const recipient = payload.original_recipient || payload.phone || 'client';
    return {
        title: 'WhatsApp Send Failed',
        body: `${template} failed for ${recipient}. ${log.error_message || 'Check delivery logs.'}`,
    };
};

const formatNotificationTime = (createdAt: string) => {
    const date = new Date(createdAt);
    const now = new Date();
    const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return date.toDateString() === now.toDateString() ? time : `${date.toLocaleDateString()}, ${time}`;
};

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const isRetryableGreetingError = (error?: string | null) => {
    if (!error) return false;
    return (
        error === 'GREETING_PENDING_LEAD_LINK' ||
        error === 'GREETING_ERROR_NO_LEAD' ||
        error.includes('lead-less intake greeting dispatch') ||
        error.includes('not linked to a lead')
    );
};

export default function CRM() {
    const [activeTab, setActiveTab] = useState<'pipeline' | 'clients' | 'automations' | 'voice' | 'trash'>(() => {
        return (localStorage.getItem('crmActiveTab') as any) || 'pipeline';
    });

    useEffect(() => {
        localStorage.setItem('crmActiveTab', activeTab);
    }, [activeTab]);
    const [leads, setLeads] = useState<any[]>([]);
    const [trashedLeads, setTrashedLeads] = useState<any[]>([]);
    const [isLoadingTrash, setIsLoadingTrash] = useState(false);
    const { user, canViewPhoneNumbers } = useAuth();
    const [requestDeleteLead, setRequestDeleteLead] = useState<{ id: string; name: string; defaultActionType?: 'move_to_trash' | 'permanent_delete' } | null>(null);
    // Delete choice modal state
    const [deleteChoiceModal, setDeleteChoiceModal] = useState<{ id: string; name: string } | null>(null);
    // Reviews state
    const [reviews, setReviews] = useState<any[]>([]);
    const [isLoadingReviews, setIsLoadingReviews] = useState(false);
    // Returning client modal state
    const [returningClientModal, setReturningClientModal] = useState<{ phone: string; name: string; existingClient: any; pendingLeadData: any } | null>(null);

    // Month and City Filtering for Pipeline & Clients tabs
    const [crmSelectedMonth, setCrmSelectedMonth] = useState<string>(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });
    const [crmViewAllMonths, setCrmViewAllMonths] = useState<boolean>(false);
    const [crmSelectedCity, setCrmSelectedCity] = useState<string>('all');
    const [isCityDropdownOpen, setIsCityDropdownOpen] = useState<boolean>(false);

    const crmMonthLabel = (ym: string) => {
        if (!ym) return '';
        const [y, m] = ym.split('-');
        const date = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
        return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    };

    const crmPrevMonth = () => {
        const [y, m] = crmSelectedMonth.split('-');
        const prev = new Date(parseInt(y, 10), parseInt(m, 10) - 2, 1);
        setCrmSelectedMonth(`${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`);
    };

    const crmNextMonth = () => {
        const [y, m] = crmSelectedMonth.split('-');
        const next = new Date(parseInt(y, 10), parseInt(m, 10), 1);
        setCrmSelectedMonth(`${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}`);
    };

    const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSettingsLoaded, setIsSettingsLoaded] = useState(false);
    const [isSimulatingInquiry, setIsSimulatingInquiry] = useState(false);

    const [deliveryLogs, setDeliveryLogs] = useState<any[]>([]);
    const [attentionEvents, setAttentionEvents] = useState<any[]>([]);
    const notifiedAttentionIds = useRef<Set<string>>(new Set());

    const fetchDeliveryLogs = async () => {
        try {
            const { data } = await supabase
                .from('whatsapp_logs')
                .select('id, status, error_message, payload, created_at')
                .order('created_at', { ascending: false })
                .limit(20);
            if (data) setDeliveryLogs(data);
        } catch (err) { }
    };

    const fetchAttentionEvents = async () => {
        try {
            const { data } = await supabase
                .from('crm_lead_activity')
                .select('id, lead_id, event_type, description, metadata, created_at')
                .in('event_type', Array.from(ATTENTION_EVENT_TYPES))
                .order('created_at', { ascending: false })
                .limit(20);
            if (data) setAttentionEvents(data);
        } catch (err) {
            console.warn('Failed to fetch attention events:', err);
        }
    };

    const notifyAttention = (key: string, title: string, body: string) => {
        if (!key || notifiedAttentionIds.current.has(key)) return;
        notifiedAttentionIds.current.add(key);

        toast.success(title, { description: body, duration: 7000 });

        if ("Notification" in window && Notification.permission === "granted") {
            new Notification(title, { body, icon: '/favicon.ico' });
        }
    };


    useEffect(() => {
        const fetchAutomationSettings = async () => {
            const { data, error } = await supabase
                .from('automation_settings')
                .select('*')
                .eq('id', 'global')
                .maybeSingle();

            console.log("[fetchAutomationSettings] data:", data, "error:", error);
            if (data && !error) {
                setWorkflows({
                    greeting: data.greeting_enabled
                });

                // Cloud Sync: If database has columns for stages/templates, use them
                if (data.client_stages) {
                    setClientStages(data.client_stages);
                }
                if (data.pipeline_stages) {
                    // Defensively strip out any client stages that may have leaked in
                    const knownClientStages = new Set(data.client_stages ?? ['Active Client', 'Monthly Billing', 'Closed Won']);
                    const cleanPipeline = sanitizePipelineStages(
                        data.pipeline_stages.filter((s: string) => !knownClientStages.has(s))
                    );
                    setPipelineStages(cleanPipeline);
                }
                if (data.whatsapp_templates) {
                    let updatedTemplates = { ...data.whatsapp_templates };
                    let needsMigration = false;

                    // Check if 'inquiry' template is still using old hardcoded text
                    const oldEnglish = "Hi {{name}}, welcome to 99 Care! We've received your inquiry.";
                    if (updatedTemplates.inquiry?.English?.includes(oldEnglish)) {
                        updatedTemplates.inquiry = {
                            Hinglish: "Hi {{name}}! 🌟 99 Care me aapka swagat hai. Kripya niche diye gaye button par click karke apni zaroorat batayein taaki hum aapke liye best staff find kar sakein.",
                            Hindi: "Namaste {{name}} ji, 99 Care mein aapka swagat hai! Kripya niche diye gaye button par click karein aur apni requirements form me bharein.",
                            English: "Hi {{name}}, welcome to 99 Care! Please tap the button below to fill out our intake form so we can understand your requirements and assign the best healthcare staff for you."
                        };
                        needsMigration = true;
                    }

                    setWhatsappTemplates(updatedTemplates);

                    if (needsMigration) {
                        supabase.from('automation_settings').update({ whatsapp_templates: updatedTemplates }).eq('id', 'global').then(() => console.log("Migrated WhatsApp templates to cloud."));
                    }
                }
            } else if (!data && !error) {
                console.log("[fetchAutomationSettings] row missing, initializing...");
                await supabase.from('automation_settings').upsert({ id: 'global', greeting_enabled: true }, { onConflict: 'id' });
            }
            setIsSettingsLoaded(true);
        };

        fetchLeads();
        fetchDeliveryLogs();
        fetchAttentionEvents();
        fetchAutomationSettings();

        const interval = setInterval(() => {
            fetchLeads();
            fetchDeliveryLogs();
            fetchAttentionEvents();
            fetchVoiceData?.();
        }, 1000 * 30); // 30s auto-refresh

        // --- REALTIME SUBSCRIPTIONS ---
        const callSub = supabase.channel('realtime_calls_v2')
            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'call_transcripts' }, (payload) => {
                const call = payload.new;
                toast.success(`Voice Call Ended: ${call.phone_number}`);
                window.setTimeout(() => fetchVoiceData?.(), 1200);
            })
            .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'call_transcripts' }, (payload) => {
                const call = payload.new;
                if (call?.lead_id || call?.automation_error === 'GREETING_PENDING_LEAD_LINK') {
                    fetchVoiceData?.();
                }
            })
            .subscribe();

        const leadsSub = supabase.channel('realtime_leads_v2')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'crm_leads' }, () => {
                fetchLeads();
            })
            .subscribe();

        const consentSub = supabase.channel('realtime_client_consents_v2')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'client_consents' }, () => {
                fetchLeads();
            })
            .subscribe();

        const activitySub = supabase.channel('realtime_activity_v2')
            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'crm_lead_activity' }, (payload) => {
                const activity = payload.new;
                if (ATTENTION_EVENT_TYPES.has(activity.event_type)) {
                    setAttentionEvents(prev => [activity, ...prev.filter(item => item.id !== activity.id)].slice(0, 20));
                    const notification = buildAttentionNotification(activity);
                    if (notification) {
                        notifyAttention(`activity:${activity.id}`, notification.title, notification.body);
                    }
                    fetchLeads();
                }
            })
            .subscribe();

        const whatsappFailureSub = supabase.channel('realtime_whatsapp_failures_v2')
            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'whatsapp_logs' }, (payload) => {
                const log = payload.new;
                if (log?.status === 'failed' || log?.status === 'error' || log?.error_message) {
                    setDeliveryLogs(prev => [log, ...prev.filter(item => item.id !== log.id)].slice(0, 20));
                    const notification = buildWhatsAppFailureNotification(log);
                    notifyAttention(`whatsapp:${log.id}`, notification.title, notification.body);
                }
            })
            .subscribe();



        // Request browser notification permission
        if ("Notification" in window && Notification.permission === "default") {
            Notification.requestPermission();
        }

        return () => {
            clearInterval(interval);
            supabase.removeChannel(callSub);
            supabase.removeChannel(leadsSub);
            supabase.removeChannel(consentSub);
            supabase.removeChannel(activitySub);
            supabase.removeChannel(whatsappFailureSub);
        };
    }, []);

    // AI Automation State (Real-time from whatsapp_logs)
    const automationLogs = useMemo(() => {
        return deliveryLogs.map((log: any) => {
            const payload = log.payload || {};
            const createdDate = new Date(log.created_at);
            const timeStr = createdDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            const dateStr = createdDate.toLocaleDateString();
            const now = new Date();
            const isToday = createdDate.toDateString() === now.toDateString();
            const displayTime = isToday ? timeStr : `${dateStr}, ${timeStr}`;

            let title = 'Action Executed';
            let desc = 'AI performed an automated action.';
            let icon = Bot;

            if (payload.pipelineStageUpdate) {
                title = 'Stage Advanced';
                desc = `Moved lead to "${payload.pipelineStageUpdate}".`;
                icon = CheckCircle2;
            } else if (payload.templateName === 'post_call_intake') {
                title = 'Auto-Greeting Sent';
                desc = `Sent welcome menu to customer at ${payload.original_recipient}.`;
                icon = MessageSquare;
            } else if (payload.templateName?.startsWith('drip')) {
                title = 'Drip Sequence Triggered';
                desc = `Sent follow-up step ${payload.templateName.split('_')[2]} to ${payload.original_recipient}.`;
                icon = Send;
            } else if (payload.templateName) {
                title = 'Template Dispatched';
                desc = `Sent ${payload.templateName} to ${payload.original_recipient}.`;
                icon = FileText;
            } else if (payload.message || payload.type === 'ai_response') {
                title = 'AI Response Sent';
                desc = payload.message
                    ? `Replied: "${payload.message.substring(0, 40)}${payload.message.length > 40 ? '...' : ''}"`
                    : 'Sent automated response to customer.';
                icon = MessageCircle;
            } else if (payload.type === 'incoming_message') {
                title = 'Message Received';
                desc = payload.raw_text
                    ? `From customer: "${payload.raw_text.substring(0, 40)}${payload.raw_text.length > 40 ? '...' : ''}"`
                    : 'New incoming message detected.';
                icon = MessageSquare;
            }

            return {
                id: log.id,
                type: payload.templateName || 'custom',
                icon,
                title,
                desc,
                time: displayTime,
                status: log.status === 'error' ? 'error' : 'success'
            };
        });
    }, [deliveryLogs]);

    const attentionNotifications = useMemo(() => {
        const activityItems = attentionEvents
            .map((activity: any) => {
                const notification = buildAttentionNotification(activity);
                if (!notification) return null;
                return {
                    id: `activity-${activity.id}`,
                    title: notification.title,
                    desc: notification.body,
                    time: formatNotificationTime(activity.created_at),
                    created_at: activity.created_at,
                    icon: activity.event_type === 'call_received' ? Phone : activity.event_type === 'form_filled' ? FileText : AlertTriangle,
                    status: 'attention',
                };
            })
            .filter((item): item is NonNullable<typeof item> => Boolean(item));

        const failureItems = deliveryLogs
            .filter((log: any) => log?.status === 'failed' || log?.status === 'error' || log?.error_message)
            .map((log: any) => {
                const notification = buildWhatsAppFailureNotification(log);
                return {
                    id: `whatsapp-${log.id}`,
                    title: notification.title,
                    desc: notification.body,
                    time: formatNotificationTime(log.created_at),
                    created_at: log.created_at,
                    icon: AlertTriangle,
                    status: 'error',
                };
            });

        return [...activityItems, ...failureItems]
            .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
            .slice(0, 20);
    }, [attentionEvents, deliveryLogs]);

    // AI WhatsApp Agent State
    const [isAgentModalOpen, setIsAgentModalOpen] = useState(false);
    const [isQuotationModalOpen, setIsQuotationModalOpen] = useState(false);
    const [quotationTargetLead, setQuotationTargetLead] = useState<any>(null);
    const [agentTargetLead, setAgentTargetLead] = useState<any>(null);
    const [agentTargetAction, setAgentTargetAction] = useState<'inquiry' | 'quotation' | 'consent' | 'staff' | 'deposit' | 'billing' | 'custom'>('inquiry');
    // Per-call greeting dispatch status (keyed by call.id)
    const [callGreetingStatus, setCallGreetingStatus] = useState<Record<string, 'sending' | 'sent' | 'error'>>({});
    const processingCalls = useRef<Set<string>>(new Set());
    const autoGreetAttempted = useRef<Set<string>>(new Set());
    const [assignmentResult, setAssignmentResult] = useState<any>(null);

    // Add Lead Modal State
    const [isAddLeadModalOpen, setIsAddLeadModalOpen] = useState(false);
    const [addLeadName, setAddLeadName] = useState('');
    const [addLeadPhone, setAddLeadPhone] = useState('');
    const [addLeadDuplicateWarning, setAddLeadDuplicateWarning] = useState<any>(null);
    const [addLeadCheckingDuplicate, setAddLeadCheckingDuplicate] = useState(false);

    // Add Client Modal State (Comprehensive for Ongoing / Existing Clients)
    const [isAddClientModalOpen, setIsAddClientModalOpen] = useState(false);
    const [addClientName, setAddClientName] = useState('');
    const [addClientPhone, setAddClientPhone] = useState('');
    const [addClientAddress, setAddClientAddress] = useState('');
    const [addClientPriority, setAddClientPriority] = useState('medium');
    const [addClientServiceName, setAddClientServiceName] = useState('Old Age Care');
    const [addClientShiftHours, setAddClientShiftHours] = useState(10);
    const [addClientShiftType, setAddClientShiftType] = useState('10-Hour Day Shift');
    const [addClientStartDate, setAddClientStartDate] = useState(() => new Date().toISOString().split('T')[0]);
    const [addClientIsOpenEnded, setAddClientIsOpenEnded] = useState(true);
    const [addClientEndDate, setAddClientEndDate] = useState('');
    const [addClientCompleteMonthRate, setAddClientCompleteMonthRate] = useState<number | ''>(600);
    const [addClientIncompleteMonthRate, setAddClientIncompleteMonthRate] = useState<number | ''>(1000);
    const [addClientDepositAmount, setAddClientDepositAmount] = useState<number | ''>(5000);
    const [addClientDepositMethod, setAddClientDepositMethod] = useState('UPI');
    const [addClientDepositDate, setAddClientDepositDate] = useState(() => new Date().toISOString().split('T')[0]);
    const [addClientDepositNotes, setAddClientDepositNotes] = useState('');
    const [addClientSelectedWorkers, setAddClientSelectedWorkers] = useState<{ workerId: string; workerName: string; startDate: string }[]>([]);
    const [addClientAutoMarkAttendance, setAddClientAutoMarkAttendance] = useState(true);
    const [addClientAbsentDays, setAddClientAbsentDays] = useState<number | ''>(0);
    const [isSubmittingAddClient, setIsSubmittingAddClient] = useState(false);
    const [addClientWorkerSearch, setAddClientWorkerSearch] = useState('');

    // Patient Care & Work Form Scope state for Add Client Modal
    const [addClientPatientName, setAddClientPatientName] = useState('');
    const [addClientAge, setAddClientAge] = useState('');
    const [addClientAgeUnit, setAddClientAgeUnit] = useState<'Days' | 'Months' | 'Years'>('Years');
    const [addClientWeight, setAddClientWeight] = useState('');
    const [addClientRelativeName, setAddClientRelativeName] = useState('');
    const [addClientAltPhone, setAddClientAltPhone] = useState('');
    const [addClientMedicalCondition, setAddClientMedicalCondition] = useState('');
    const [addClientFormType, setAddClientFormType] = useState<'baby_care_form' | 'patient_care_form'>('patient_care_form');
    const [addClientDuties, setAddClientDuties] = useState<string[]>([]);
    const [addClientCareInstructions, setAddClientCareInstructions] = useState('');
    const [isAddClientCareExpanded, setIsAddClientCareExpanded] = useState(true);

    // Client Invoice Generator State (CRM Pipeline)
    const [isClientInvoiceOpen, setIsClientInvoiceOpen] = useState(false);
    const [clientInvoiceLead, setClientInvoiceLead] = useState<any>(null);
    const [ciDays, setCiDays] = useState<number>(1);
    const [ciRate, setCiRate] = useState<number>(0);
    const [ciDeposit, setCiDeposit] = useState<number>(0);
    const [ciStartDate, setCiStartDate] = useState('');
    const [ciEndDate, setCiEndDate] = useState('');
    const [ciAttendanceVerified, setCiAttendanceVerified] = useState(true);
    const [ciAttendanceSummary, setCiAttendanceSummary] = useState<ClientAttendanceSummary | null>(null);
    const [isCiLoadingAttendance, setIsCiLoadingAttendance] = useState(false);
    const [ciLeadId, setCiLeadId] = useState('');
    const [addLeadConfirmDuplicate, setAddLeadConfirmDuplicate] = useState(false);
    const [showFullTimeline, setShowFullTimeline] = useState(false);

    // Staff Picker State
    const [isStaffPickerOpen, setIsStaffPickerOpen] = useState(false);
    const [staffSearchQuery, setStaffSearchQuery] = useState('');
    const [isDepositModalOpen, setIsDepositModalOpen] = useState(false);
    const [depositMethod, setDepositMethod] = useState('Online Transfer');
    const [depositDate, setDepositDate] = useState<string>(() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    });
    const [depositLeadTarget, setDepositLeadTarget] = useState<any>(null);
    const [staffPickerTargetLead, setStaffPickerTargetLead] = useState<any>(null);
    const [availableWorkers, setAvailableWorkers] = useState<any[]>([]);
    const [allWorkers, setAllWorkers] = useState<any[]>([]); // All employees for Kanban badge
    const [selectedWorker, setSelectedWorker] = useState<any>(null);
    const [isLoadingWorkers, setIsLoadingWorkers] = useState(false);
    const [agentDraftLang, setAgentDraftLang] = useState<'English' | 'Hindi' | 'Hinglish'>('English');
    const [agentDraftText, setAgentDraftText] = useState('');
    const [staffModalActiveTab, setStaffModalActiveTab] = useState<'client' | 'worker'>('client');
    const [workerDraftText, setWorkerDraftText] = useState('');
    const [isSendingToWorker, setIsSendingToWorker] = useState(false);
    const [workerSentSuccess, setWorkerSentSuccess] = useState(false);
    const [isEditingTemplate, setIsEditingTemplate] = useState(false);
    const [templateDraftText, setTemplateDraftText] = useState('');
    const [quotationVars, setQuotationVars] = useState({ v1: '', v2: '', v3: '', v4: '' });
    const [invoiceDepositAmount, setInvoiceDepositAmount] = useState('');
    const [invoiceStartDate, setInvoiceStartDate] = useState('');
    const [invoiceEndDate, setInvoiceEndDate] = useState('');
    const [isInvoiceOngoing, setIsInvoiceOngoing] = useState(false);
    const [invoiceDueDate, setInvoiceDueDate] = useState('');

    // Service Period Modal State
    const [isServicePeriodOpen, setIsServicePeriodOpen] = useState(false);
    const [serviceType, setServiceType] = useState<'one_day' | 'date_range'>('one_day');
    const [serviceStartDate, setServiceStartDate] = useState('');
    const [serviceEndDate, setServiceEndDate] = useState('');
    const [serviceHours, setServiceHours] = useState(1);
    const [calculatedBill, setCalculatedBill] = useState(0);

    // Transcript Modal State
    const [isTranscriptModalOpen, setIsTranscriptModalOpen] = useState(false);
    const [selectedCall, setSelectedCall] = useState<any>(null);

    // WhatsApp Chat Modal State
    const [isWhatsappChatOpen, setIsWhatsappChatOpen] = useState(false);
    const [selectedWhatsappLead, setSelectedWhatsappLead] = useState<any>(null);
    const [whatsappChat, setWhatsappChat] = useState<any[]>([]);
    const [isFetchingChat, setIsFetchingChat] = useState(false);
    const chatScrollRef = useRef<HTMLDivElement | null>(null);

    const fetchWhatsappChat = async (lead: any) => {
        if (!lead) return;
        setSelectedWhatsappLead(lead);
        setIsWhatsappChatOpen(true);
        setIsFetchingChat(true);
        setWhatsappChat([]);

        try {
            // 1. Collect all phone number representations for this lead
            const rawPhones = [lead.whatsapp_number, lead.phone].filter(Boolean);
            
            // Also collect related lead phones if duplicate/linked
            if (lead.duplicate_of_lead_id || lead.id) {
                const linkedLeadIds = new Set([lead.id, lead.duplicate_of_lead_id].filter(Boolean));
                leads.forEach((l: any) => {
                    if (linkedLeadIds.has(l.id) || linkedLeadIds.has(l.duplicate_of_lead_id)) {
                        if (l.whatsapp_number) rawPhones.push(l.whatsapp_number);
                        if (l.phone) rawPhones.push(l.phone);
                    }
                });
            }

            const digitsList = rawPhones
                .map((p: string) => String(p).replace(/\D/g, ''))
                .filter(Boolean);

            const phoneSuffixes = [...new Set(
                digitsList
                    .map((d: string) => d.length >= 10 ? d.slice(-10) : d)
                    .filter((d: string) => d.length >= 7)
            )];

            let messagesList: any[] = [];

            // 2. Fetch all messages from whatsapp_messages (no date restriction to ensure full history)
            if (phoneSuffixes.length > 0) {
                const orQuery = phoneSuffixes.map(suffix => `phone.ilike.%${suffix}%`).join(',');
                const { data: dbMsgs, error: msgError } = await supabase
                    .from("whatsapp_messages")
                    .select("id, role, content, created_at")
                    .or(orQuery)
                    .order("created_at", { ascending: true });

                if (!msgError && dbMsgs) {
                    messagesList = dbMsgs.map((m: any) => ({
                        id: m.id,
                        role: m.role || 'user',
                        content: m.content || '',
                        created_at: m.created_at,
                        source: 'message'
                    }));
                }
            }

            // 3. Fetch from whatsapp_logs to include template dispatches, AI greetings & quotes not in whatsapp_messages
            try {
                const { data: logsData } = await supabase
                    .from('whatsapp_logs')
                    .select('id, created_at, status, payload')
                    .order('created_at', { ascending: true })
                    .limit(300);

                if (logsData) {
                    const matchedLogs = logsData.filter((log: any) => {
                        const p = log.payload;
                        if (!p) return false;
                        if (lead.id && (p.lead_id === lead.id || p.leadId === lead.id)) return true;
                        if (lead.duplicate_of_lead_id && (p.lead_id === lead.duplicate_of_lead_id || p.leadId === lead.duplicate_of_lead_id)) return true;
                        const recipient = String(p.original_recipient || p.contacts?.[0]?.wa_id || p.to || '');
                        return phoneSuffixes.some(suffix => recipient.includes(suffix));
                    });

                    for (const log of matchedLogs) {
                        const p = log.payload || {};
                        let text = p.message;
                        if (!text && p.templateName) {
                            if (p.templateName === 'post_call_intake') {
                                const name = p.flowData?.name || (p.templateParams && p.templateParams[0]) || lead.name || 'there';
                                const service = p.flowData?.service || (p.templateParams && p.templateParams[1]) || 'healthcare';
                                text = `Hi ${name}, welcome to 99 Care! Please tap the button below to fill out our intake form so we can understand your requirements for ${service}.`;
                            } else if (p.templateName === 'quote_client_v2') {
                                const name = (p.templateParams && p.templateParams[0]) || lead.name || 'Customer';
                                text = `Hello ${name}, please find the customized quotation details for your care request.`;
                            } else if (p.templateName === 'client_review_request') {
                                text = `Namaste, we hope you had a great experience with 99 Care! Please share your feedback and review.`;
                            } else if (p.templateName.includes('consent')) {
                                text = `Consent form & service agreement sent.`;
                            } else if (p.templateName.includes('staff')) {
                                text = `Staff allocation & verified ID card link sent.`;
                            } else {
                                text = `[WhatsApp Template Sent: ${p.templateName}]`;
                            }
                        }

                        if (text && String(text).trim()) {
                            const logTime = new Date(log.created_at).getTime();
                            // Check if this log is already represented in messagesList within 30 seconds
                            const isDuplicate = messagesList.some((m: any) => {
                                const mTime = new Date(m.created_at).getTime();
                                return Math.abs(logTime - mTime) < 30000 && (
                                    m.content.trim() === String(text).trim() ||
                                    (p.templateName && m.content.includes(p.templateName))
                                );
                            });

                            if (!isDuplicate && log.status !== 'failed') {
                                messagesList.push({
                                    id: log.id,
                                    role: 'assistant',
                                    content: String(text).trim(),
                                    created_at: log.created_at,
                                    status: log.status,
                                    source: 'log'
                                });
                            }
                        }
                    }
                }
            } catch (logErr) {
                console.warn('Could not merge whatsapp_logs into chat history:', logErr);
            }

            // 4. Sort unified history chronologically
            messagesList.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

            setWhatsappChat(messagesList);
        } catch (err: any) {
            console.error('Failed to fetch chat logs:', err);
            toast.error(`Unable to load chat: ${err.message}`);
        } finally {
            setIsFetchingChat(false);
        }
    };

    // Auto-scroll chat to bottom when messages update
    useEffect(() => {
        if (isWhatsappChatOpen && chatScrollRef.current) {
            chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
        }
    }, [whatsappChat, isWhatsappChatOpen]);

    // Realtime subscription for incoming WhatsApp messages while chat modal is open
    useEffect(() => {
        if (!isWhatsappChatOpen || !selectedWhatsappLead) return;

        const targetPhones = [selectedWhatsappLead.whatsapp_number, selectedWhatsappLead.phone].filter(Boolean);
        const suffixes = [...new Set(
            targetPhones
                .map((p: string) => String(p).replace(/\D/g, ''))
                .filter(Boolean)
                .map((d: string) => d.length >= 10 ? d.slice(-10) : d)
                .filter((d: string) => d.length >= 7)
        )];

        if (suffixes.length === 0) return;

        const channel = supabase
            .channel(`whatsapp_chat_${selectedWhatsappLead.id || 'realtime'}`)
            .on(
                'postgres_changes',
                { event: 'INSERT', schema: 'public', table: 'whatsapp_messages' },
                (payload) => {
                    const newMsg = payload.new as any;
                    const msgPhone = String(newMsg.phone || '').replace(/\D/g, '');
                    const isForLead = suffixes.some(s => msgPhone.includes(s));
                    if (isForLead) {
                        setWhatsappChat((prev) => {
                            if (prev.some((m) => m.id === newMsg.id || (m.content === newMsg.content && Math.abs(new Date(m.created_at).getTime() - new Date(newMsg.created_at).getTime()) < 5000))) {
                                return prev;
                            }
                            return [...prev, {
                                id: newMsg.id,
                                role: newMsg.role || 'user',
                                content: newMsg.content || '',
                                created_at: newMsg.created_at || new Date().toISOString(),
                                source: 'message'
                            }];
                        });
                    }
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, [isWhatsappChatOpen, selectedWhatsappLead]);

    const [whatsappTemplates, setWhatsappTemplates] = useState<Record<string, Record<string, string>>>({
        inquiry: {
            Hinglish: "Hi {{name}}! 🌟 99 Care me aapka swagat hai. Kripya niche diye gaye button par click karke apni zaroorat batayein taaki hum aapke liye best staff find kar sakein.",
            Hindi: "Namaste {{name}} ji, 99 Care mein aapka swagat hai! Kripya niche diye gaye button par click karein aur apni requirements form me bharein.",
            English: "Hi {{name}}, welcome to 99 Care! Please tap the button below to fill out our intake form so we can understand your requirements and assign the best healthcare staff for you."
        },
        quotation: {
            Hinglish: "Namaste {{name}} ji! 🙏 Aapke liye humne ek customized quotation taiyar ki hai.\n\n📋 Service: {{v1}}\n⏰ Hours: {{v2}}\n💰 Full Month: {{v3}}\n💵 Half Month: {{v4}}\n\nKoi bhi changes chahiye toh batayein — hum adjust kar sakte hain!",
            Hindi: "Namaste {{name}} ji, aapki quotation taiyar hai.\n\n📋 सेवा: {{v1}}\n⏰ घंटे: {{v2}}\n💰 पूरा महीना: {{v3}}\n💵 आधा महीना: {{v4}}\n\nकोई प्रश्न हो तो हमसे संपर्क करें।",
            English: "Hi {{name}}, your customized quotation from 99 Care is ready!\n\n📋 Service: {{v1}}\n⏰ Hours: {{v2}}\n💰 Full Month: {{v3}}\n💵 Half Month: {{v4}}\n\nFeel free to reach out if you'd like any adjustments."
        },
        consent: {
            Hinglish: "Hi {{name}} ji! Ek aakhri step baki hai — apna consent form yahan fill karein aur hum service start kar denge! ✅\nhttps://docs.google.com/forms/d/e/1FAIpQLScENgj7ltdWY9O3leGhXVl1U4wExTX1zlHItApNhRkqui3dIg/viewform",
            Hindi: "Namaste {{name}} ji, aage badhne ke liye kripya yeh consent form bharein:\nhttps://docs.google.com/forms/d/e/1FAIpQLScENgj7ltdWY9O3leGhXVl1U4wExTX1zlHItApNhRkqui3dIg/viewform",
            English: "Hi {{name}}, just one final step — please complete the consent form below and we'll get your service started:\nhttps://docs.google.com/forms/d/e/1FAIpQLScENgj7ltdWY9O3leGhXVl1U4wExTX1zlHItApNhRkqui3dIg/viewform"
        },
        staff: {
            Hinglish: "Good news {{name}} ji! 🎉 Aapke liye {{workerName}} ({{workerRole}}) ko assign kiya gaya hai!\n\n👤 Unki ID Card aur profile verify karne ke liye yahan dekhen:\n{{link}}\n\nYe link 30 din tak valid hai. Koi concern ho toh hume batayein!",
            Hindi: "Namaste {{name}} ji, aapke liye {{workerName}} ({{workerRole}}) ko assign kiya gaya hai.\n\nID Card dekhne ke liye:\n{{link}}\n\nYe link 30 din tak valid hai.",
            English: "Great news {{name}}! We have assigned {{workerName}} ({{workerRole}}) to you.\n\n🔗 View their verified ID Card: {{link}}\n\nPlease verify their identity upon arrival. This link is valid for 30 days."
        },
        deposit: {
            Hinglish: "Namaste {{name}} ji! 🙏 Aapka deposit invoice ready hai.\n\n💰 Amount aur payment details yahan dekhein:\nhttps://99care.in/invoice/{{link}}\n\nPayment complete karne ke baad service start ho jayegi!",
            Hindi: "Namaste {{name}} ji, aapka deposit invoice taiyar hai. Kripya yahan dekhen:\nhttps://99care.in/invoice/{{link}}",
            English: "Hi {{name}}, your deposit invoice is ready. Please review and complete the payment here:\nhttps://99care.in/invoice/{{link}}\n\nService will begin once payment is confirmed!"
        },
        billing: {
            Hinglish: "Namaste {{name}} ji! 📄 Is mahine ka bill taiyar ho gaya hai.\n\nBill amount aur details yahan dekhein:\nhttps://99care.in/bill/{{link}}\n\nPayment complete karne par confirmation milegi. Shukriya! 🙏",
            Hindi: "Namaste {{name}} ji, is mahine ka bill taiyar hai. Kripya yahan dekhen:\nhttps://99care.in/bill/{{link}}",
            English: "Hi {{name}}, your monthly bill for this period is ready. Please review and pay here:\nhttps://99care.in/bill/{{link}}\n\nThank you for choosing 99 Care!"
        }
    });

    useEffect(() => {
        // Sync templates to DB only — do not write to localStorage
        // localStorage is vulnerable to browser extensions and XSS
        const syncToCloud = async () => {
            try {
                await supabase.from('automation_settings').upsert({
                    id: 'global',
                    whatsapp_templates: whatsappTemplates
                }, { onConflict: 'id' });
            } catch (e) { console.warn('Template cloud sync failed:', e); }
        };
        syncToCloud();
    }, [whatsappTemplates]);

    const PROTECTED_STAGES = ['New Inquiry', 'Active Client', 'Closed Won', 'Lost'];

    // Initialize pipelineStages from localStorage or defaults
    // Initialize pipelineStages - prioritize clean split defaults
    const [pipelineStages, setPipelineStages] = useState<string[]>(['New Inquiry', 'In Discussion', 'Quotation Sent', 'Form Submitted', 'Staff Assigned', 'Deposit Pending']);

    const [clientStages, setClientStages] = useState<string[]>(['Active Client', 'Monthly Billing', 'Closed Won']);

    // Helper to sync to cloud only when manually changed
    const syncStagesToCloud = async (pStages: string[], cStages: string[]) => {
        if (!isSettingsLoaded) {
            console.log("[syncStagesToCloud] blocked: settings not yet loaded from cloud.");
            return;
        }
        try {
            await supabase.from('automation_settings').upsert({
                id: 'global',
                pipeline_stages: pStages,
                client_stages: cStages
            }, { onConflict: 'id' });
        } catch (e) { }
    };

    // Sync pipelineStages to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('crmPipelineStages', JSON.stringify(pipelineStages));
    }, [pipelineStages]);

    useEffect(() => {
        localStorage.setItem('crmClientStages', JSON.stringify(clientStages));
    }, [clientStages]);

    const [isAddingStage, setIsAddingStage] = useState(false);
    const [newStageName, setNewStageName] = useState('');
    const [editingStageIdx, setEditingStageIdx] = useState<number | null>(null);
    const [editingStageName, setEditingStageName] = useState('');

    const [editingLeadValueId, setEditingLeadValueId] = useState<string | null>(null);
    const [editingLeadValueAmount, setEditingLeadValueAmount] = useState<string>('');

    const [editingLeadDetailsId, setEditingLeadDetailsId] = useState<string | null>(null);
    const [editingLeadName, setEditingLeadName] = useState<string>('');
    const [editingLeadPhone, setEditingLeadPhone] = useState<string>('');
    const [selectedInspectorLead, setSelectedInspectorLead] = useState<any | null>(null);
    const [releasedAssignment, setReleasedAssignment] = useState<any | null>(null);
    const [showWorkFormModal, setShowWorkFormModal] = useState<any | null>(null);
    const [manualWorkFormModal, setManualWorkFormModal] = useState<{
        isOpen: boolean;
        lead: any;
        formType: 'baby_care_form' | 'patient_care_form';
        patientName: string;
        duties: string[];
        otherWork: string;
        isSaving: boolean;
    } | null>(null);
    const [isDispatchingWorkForm, setIsDispatchingWorkForm] = useState(false);
    const [detailsModalClient, setDetailsModalClient] = useState<any | null>(null);
    const [isEndingLeadService, setIsEndingLeadService] = useState(false);

    // ── CRM View Mode & Table Sorting (Rows & Columns View) ───────────────────
    const [crmViewMode, setCrmViewMode] = useState<'table' | 'pipeline'>(() => {
        try {
            return (localStorage.getItem('crmViewMode') as 'table' | 'pipeline') || 'table';
        } catch {
            return 'table';
        }
    });
    const handleSetCrmViewMode = (mode: 'table' | 'pipeline') => {
        setCrmViewMode(mode);
        try {
            localStorage.setItem('crmViewMode', mode);
        } catch {}
    };

    const [crmSearchQuery, setCrmSearchQuery] = useState('');
    const [crmSortField, setCrmSortField] = useState<'id' | 'name' | 'city' | 'phone' | 'priority' | 'stage' | 'rate' | 'created_at'>('created_at');
    const [crmSortAsc, setCrmSortAsc] = useState<boolean>(false);

    const [copiedLeadIdBadge, setCopiedLeadIdBadge] = useState<string | null>(null);
    const [copiedLeadPhoneBadge, setCopiedLeadPhoneBadge] = useState<string | null>(null);

    const handleCopyLeadId = (idStr: string, e: React.MouseEvent) => {
        e.stopPropagation();
        navigator.clipboard.writeText(idStr);
        setCopiedLeadIdBadge(idStr);
        toast.success(`Copied ID ${idStr}`);
        setTimeout(() => setCopiedLeadIdBadge(null), 2000);
    };

    const handleCopyLeadPhone = (phoneStr: string, e: React.MouseEvent) => {
        e.stopPropagation();
        navigator.clipboard.writeText(phoneStr);
        setCopiedLeadPhoneBadge(phoneStr);
        toast.success(`Copied phone number`);
        setTimeout(() => setCopiedLeadPhoneBadge(null), 2000);
    };

    const handleCrmSort = (field: 'id' | 'name' | 'city' | 'phone' | 'priority' | 'stage' | 'rate' | 'created_at') => {
        if (crmSortField === field) {
            setCrmSortAsc(!crmSortAsc);
        } else {
            setCrmSortField(field);
            setCrmSortAsc(true);
        }
    };

    const handleEndLeadService = async (lead: any) => {
        const clientServices = (lead.services || []) as any[];
        const activeService = clientServices.find((s: any) => s.status === 'active');
        if (!activeService?.id) {
            toast.error('No active service found for this client.');
            return;
        }
        if (!window.confirm(`End service and settle deposit for "${lead.name}"?\n\nThis will calculate verified attendance days, settle the deposit on the final invoice, release assigned caregivers, and move the client to "Closed Won".`)) return;

        setIsEndingLeadService(true);
        const toastId = toast.loading('Ending service & settling deposit...');
        try {
            const res = await endService(activeService.id);
            if (!res.success) throw new Error(res.error || 'Failed to end service');
            toast.success(`Service ended & deposit settled for ${lead.name}! Moved to Closed Won.`, { id: toastId });
            setSelectedInspectorLead(null);
            await fetchLeads();
        } catch (err: any) {
            console.error('Failed to end service:', err);
            toast.error(`Failed to end service: ${err.message || 'Unknown error'}`, { id: toastId });
        } finally {
            setIsEndingLeadService(false);
        }
    };

    function parseAgeAndWeight(
        rawAge?: string | number | null,
        rawUnit?: string | null,
        rawWeight?: string | number | null,
        isBabyDefault: boolean = false
    ): { age: string; ageUnit: 'Days' | 'Months' | 'Years'; weight: string } {
        let ageStr = rawAge != null ? String(rawAge).trim() : '';
        let weightStr = rawWeight != null ? String(rawWeight).trim() : '';
        let unit: 'Days' | 'Months' | 'Years' = isBabyDefault ? 'Days' : 'Years';

        const unitLower = String(rawUnit || '').trim().toLowerCase();
        if (unitLower.includes('day') || unitLower === 'd') {
            unit = 'Days';
        } else if (unitLower.includes('month') || unitLower === 'mo') {
            unit = 'Months';
        } else if (unitLower.includes('year') || unitLower === 'yr') {
            unit = 'Years';
        }

        if (ageStr) {
            const weightMatch = ageStr.match(/(\d+(?:\.\d+)?)\s*(?:kgs?|kilos?|kg\b)/i);
            if (weightMatch) {
                if (!weightStr) {
                    weightStr = weightMatch[1];
                }
                ageStr = ageStr.replace(/(?:&|\band\b|,|\/|\+)?\s*\d+(?:\.\d+)?\s*(?:kgs?|kilos?|kg\b)/gi, '').trim();
            }

            if (/\b(?:days?|d)\b/i.test(ageStr)) {
                unit = 'Days';
            } else if (/\b(?:months?|m|mo)\b/i.test(ageStr)) {
                unit = 'Months';
            } else if (/\b(?:years?|yrs?|y)\b/i.test(ageStr)) {
                unit = 'Years';
            }

            const cleanAgeNum = ageStr.replace(/\b(?:years?|yrs?|months?|mo|days?|d)\b/gi, '').trim();
            if (cleanAgeNum) {
                ageStr = cleanAgeNum;
            }
        }

        if (weightStr) {
            weightStr = weightStr.replace(/\s*(?:kgs?|kg|kilos?)\b/gi, '').trim();
        }

        return { age: ageStr, ageUnit: unit, weight: weightStr };
    }

    const PATIENT_CARE_DUTY_LABELS: Record<string, string> = {
        pateint_bath: 'Patient Bath',
        massage: 'Massage',
        diaper_change: 'Diaper Change',
        feeding: 'Feeding',
        medication: 'Medication',
        exercise: 'Exercise',
        clean_room: 'Clean Room',
        cut_nails: 'Grooming (Hair/Nails)',
        changing_cloths: 'Change/Wash Clothes',
        change_bedshit: 'Change Bedsheet',
        take_into_hospital: 'Take to Hospital',
        take_in_washroom: 'Take to Washroom',
        urine_bag: 'Urine Bag Care',
        dressing: 'Wound Dressing',
        feed_rt_tube: 'RT Tube Feeding',
        sponge_bath: 'Sponge Bath',
    };

    const BABY_CARE_DUTY_LABELS: Record<string, string> = {
        baby_massage: 'Baby Massage',
        baby_bath: 'Baby Bath',
        baby_feeding: 'Baby Feeding',
        sponge_bath: 'Sponge Bath',
        massage: 'Massage',
        diaper_change: 'Diaper Change',
        feeding: 'Feeding',
        medication: 'Medication',
        exercise: 'Exercise',
        clean_room: 'Clean Room',
        cut_nails: 'Grooming (Hair/Nails)',
        changing_cloths: 'Change/Wash Clothes',
        change_bedshit: 'Change Bedsheet',
        pateint_bath: 'Baby Bath',
        take_in_washroom: 'Take to Washroom',
        urine_bag: 'Urine Bag Care',
        dressing: 'Wound Dressing',
        feed_rt_tube: 'RT Tube Feeding',
    };

    const getDutyLabel = (dutyId: string, formType: string): string => {
        const map = formType === 'baby_care_form' ? BABY_CARE_DUTY_LABELS : PATIENT_CARE_DUTY_LABELS;
        return map[dutyId] || dutyId.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    };

    // Inspector editing state for new fields
    const [inspectorActivity, setInspectorActivity] = useState<any[]>([]);
    const [isLoadingActivity, setIsLoadingActivity] = useState(false);
    const [editingInspectorEmail, setEditingInspectorEmail] = useState(false);
    const [inspectorEmailDraft, setInspectorEmailDraft] = useState('');
    const [editingInspectorService, setEditingInspectorService] = useState(false);
    const [inspectorServiceDraft, setInspectorServiceDraft] = useState('');
    const [inspectorNameDraft, setInspectorNameDraft] = useState('');
    const [inspectorPhoneDraft, setInspectorPhoneDraft] = useState('');
    const [editingInspectorName, setEditingInspectorName] = useState(false);
    const [editingInspectorPhone, setEditingInspectorPhone] = useState(false);
    const [inspectorNoteDraft, setInspectorNoteDraft] = useState('');
    const [isSavingNote, setIsSavingNote] = useState(false);

    // ── Direct Inline Editing & Database Confirmation States ────────
    const [pendingDbConfirmation, setPendingDbConfirmation] = useState<{
        isOpen: boolean;
        title: string;
        description?: string;
        fieldName: string;
        oldValue: string | number | null;
        newValue: string | number | null;
        onConfirm: () => Promise<void>;
        loading?: boolean;
    } | null>(null);

    const [quickEditLead, setQuickEditLead] = useState<any | null>(null);
    const [quickEditForm, setQuickEditForm] = useState<{
        serviceCategory: string;
        shiftType: string;
        completeMonthDailyRate: string;
        incompleteMonthDailyRate: string;
        startDate: string;
        address: string;
        patientName: string;
        relativeName: string;
        patientPhone: string;
    }>({
        serviceCategory: '',
        shiftType: '',
        completeMonthDailyRate: '',
        incompleteMonthDailyRate: '',
        startDate: '',
        address: '',
        patientName: '',
        relativeName: '',
        patientPhone: '',
    });

    const [editingInspectorConsentField, setEditingInspectorConsentField] = useState<string | null>(null);
    const [inspectorConsentDraft, setInspectorConsentDraft] = useState<string>('');
    const [editingInspectorRateType, setEditingInspectorRateType] = useState<'complete' | 'incomplete' | null>(null);
    const [inspectorRateDraft, setInspectorRateDraft] = useState<string>('');

    const openQuickEdit = (lead: any) => {
        const consent = getLatestByCreatedAt(lead?.client_consents);
        const quote = getLatestByCreatedAt(lead?.crm_quotations);
        const activeSvc = (lead?.services || []).find((s: any) => s.status === 'active') || lead?.services?.[0];

        const completeRate = lead.complete_month_daily_rate || quote?.complete_month_rate || activeSvc?.complete_month_daily_rate || '';
        const incompleteRate = lead.incomplete_month_daily_rate || quote?.incomplete_month_rate || activeSvc?.incomplete_month_daily_rate || '';
        const serviceCat = consent?.service_category || lead.service_interest || quote?.service_category || '';
        const shift = consent?.offered_time || lead.shift_type || quote?.shift_type || '';
        const startDate = consent?.service_start_date || lead.plannedStart || quote?.start_date || activeSvc?.start_date || '';
        const addr = consent?.address || lead.location || '';
        const pName = consent?.patient_name || lead.name || '';
        const rName = consent?.relative_name || lead.name || '';
        const pPhone = consent?.contact_number || lead.whatsapp_number || lead.phone || '';

        setQuickEditForm({
            serviceCategory: serviceCat,
            shiftType: shift,
            completeMonthDailyRate: completeRate ? String(completeRate) : '',
            incompleteMonthDailyRate: incompleteRate ? String(incompleteRate) : '',
            startDate: startDate ? String(startDate).slice(0, 10) : '',
            address: addr,
            patientName: pName,
            relativeName: rName,
            patientPhone: pPhone,
        });
        setQuickEditLead(lead);
    };

    const handleSaveQuickEdit = (targetLead?: any) => {
        const lead = targetLead || quickEditLead;
        if (!lead) return;
        const consent = getLatestByCreatedAt(lead?.client_consents);
        const quote = getLatestByCreatedAt(lead?.crm_quotations);
        const activeSvc = (lead?.services || []).find((s: any) => s.status === 'active') || lead?.services?.[0];

        const oldComplete = lead.complete_month_daily_rate || quote?.complete_month_rate || activeSvc?.complete_month_daily_rate || 0;
        const oldIncomplete = lead.incomplete_month_daily_rate || quote?.incomplete_month_rate || activeSvc?.incomplete_month_daily_rate || 0;
        const oldService = consent?.service_category || lead.service_interest || 'Not set';

        const newComplete = parseFloat(quickEditForm.completeMonthDailyRate) || 0;
        const newIncomplete = parseFloat(quickEditForm.incompleteMonthDailyRate) || 0;

        setPendingDbConfirmation({
            isOpen: true,
            title: `Save Lead Info: ${lead.name}`,
            description: 'This will make changes from database as well.',
            fieldName: 'Lead Rates & Care Details',
            oldValue: `Full: ₹${oldComplete}/d, Part: ₹${oldIncomplete}/d, Service: ${oldService}`,
            newValue: `Full: ₹${newComplete}/d, Part: ₹${newIncomplete}/d, Service: ${quickEditForm.serviceCategory || 'Not set'}`,
            onConfirm: async () => {
                const leadId = lead.id;

                // 1. Update crm_leads
                const crmUpdates: any = {};
                if (newComplete > 0) {
                    crmUpdates.complete_month_daily_rate = newComplete;
                    crmUpdates.estimated_value_monthly = newComplete * 30;
                }
                if (newIncomplete > 0) {
                    crmUpdates.incomplete_month_daily_rate = newIncomplete;
                }
                if (quickEditForm.serviceCategory) {
                    crmUpdates.service_interest = quickEditForm.serviceCategory;
                }
                if (quickEditForm.shiftType) {
                    crmUpdates.shift_type = quickEditForm.shiftType;
                }
                if (quickEditForm.patientName || quickEditForm.relativeName) {
                    crmUpdates.name = quickEditForm.patientName || quickEditForm.relativeName;
                }
                if (quickEditForm.patientPhone) {
                    crmUpdates.phone = quickEditForm.patientPhone;
                    crmUpdates.whatsapp_number = quickEditForm.patientPhone;
                }

                if (Object.keys(crmUpdates).length > 0) {
                    const { error: crmErr } = await supabase.from('crm_leads').update(crmUpdates).eq('id', leadId);
                    if (crmErr) throw crmErr;
                }

                // 2. Upsert client_consents
                let updatedConsent: any = null;
                if (consent?.id) {
                    const { data: cData, error: cErr } = await supabase
                        .from('client_consents')
                        .update({
                            service_category: quickEditForm.serviceCategory || consent.service_category || null,
                            offered_time: quickEditForm.shiftType || consent.offered_time || null,
                            service_start_date: quickEditForm.startDate || consent.service_start_date || null,
                            address: quickEditForm.address || consent.address || null,
                            patient_name: quickEditForm.patientName || consent.patient_name || null,
                            relative_name: quickEditForm.relativeName || consent.relative_name || null,
                            contact_number: quickEditForm.patientPhone || consent.contact_number || null,
                        })
                        .eq('id', consent.id)
                        .select()
                        .single();
                    if (!cErr && cData) updatedConsent = cData;
                } else {
                    const { data: cData, error: cErr } = await supabase
                        .from('client_consents')
                        .insert([{
                            lead_id: leadId,
                            phone: quickEditForm.patientPhone || lead.whatsapp_number || lead.phone || '',
                            service_category: quickEditForm.serviceCategory || null,
                            offered_time: quickEditForm.shiftType || null,
                            service_start_date: quickEditForm.startDate || null,
                            address: quickEditForm.address || null,
                            patient_name: quickEditForm.patientName || lead.name || null,
                            relative_name: quickEditForm.relativeName || lead.name || null,
                            contact_number: quickEditForm.patientPhone || null,
                            terms_accepted: true,
                        }])
                        .select()
                        .single();
                    if (!cErr && cData) updatedConsent = cData;
                }

                // 3. Update active service in services table if any
                try {
                    const svcUpdates: any = {};
                    if (newComplete > 0) svcUpdates.complete_month_daily_rate = newComplete;
                    if (newIncomplete > 0) svcUpdates.incomplete_month_daily_rate = newIncomplete;
                    if (quickEditForm.startDate) svcUpdates.start_date = quickEditForm.startDate;

                    if (Object.keys(svcUpdates).length > 0) {
                        await supabase
                            .from('services')
                            .update(svcUpdates)
                            .or(`client_id.eq.${leadId},lead_id.eq.${leadId}`)
                            .eq('status', 'active');
                    }
                } catch (svcErr) {
                    console.warn('Could not sync services table:', svcErr);
                }

                // 4. Also keep clients table in sync
                try {
                    const clientUpdates: any = {};
                    if (quickEditForm.patientName || quickEditForm.relativeName) {
                        clientUpdates.client_name = quickEditForm.patientName || quickEditForm.relativeName;
                    }
                    if (quickEditForm.patientPhone) {
                        clientUpdates.phone_number = quickEditForm.patientPhone;
                    }
                    if (Object.keys(clientUpdates).length > 0) {
                        await supabase.from('clients').update(clientUpdates).eq('id', leadId);
                    }
                } catch (clErr) {
                    console.warn('Clients sync:', clErr);
                }

                // 5. Update local state
                setLeads(prev => prev.map(l => {
                    if (l.id !== leadId) return l;
                    const prevConsents = updatedConsent ? (l.client_consents || []).filter((c: any) => c.id !== updatedConsent.id) : (l.client_consents || []);
                    return {
                        ...l,
                        ...crmUpdates,
                        client_consents: updatedConsent ? [updatedConsent, ...prevConsents] : l.client_consents,
                    };
                }));

                setSelectedInspectorLead((prev: any) => {
                    if (!prev || prev.id !== leadId) return prev;
                    const prevConsents = updatedConsent ? (prev.client_consents || []).filter((c: any) => c.id !== updatedConsent.id) : (prev.client_consents || []);
                    return {
                        ...prev,
                        ...crmUpdates,
                        client_consents: updatedConsent ? [updatedConsent, ...prevConsents] : prev.client_consents,
                        ...(newComplete > 0 ? {
                            valueAmount: newComplete * 30,
                            value: `₹${(newComplete * 30).toLocaleString('en-IN')}/mo`
                        } : {})
                    };
                });

                setQuickEditLead(null);
                toast.success('Lead details & rates saved to database!');
            }
        });
    };

    const handleSaveLeadRate = (leadId: string, rateType: 'complete' | 'incomplete', newRateStr: string) => {
        const lead = leads.find(l => l.id === leadId) || selectedInspectorLead;
        const newRate = parseFloat(newRateStr.replace(/[^0-9.]/g, ''));
        if (isNaN(newRate) || newRate < 0) {
            toast.error('Please enter a valid rate amount');
            return;
        }

        const isComplete = rateType === 'complete';
        const fieldName = isComplete ? 'Full Month Rate' : 'Partial Month Rate';
        const dbField = isComplete ? 'complete_month_daily_rate' : 'incomplete_month_daily_rate';
        const currentRate = (lead as any)?.[dbField] ?? (isComplete ? lead?.crm_quotations?.[0]?.complete_month_rate : lead?.crm_quotations?.[0]?.incomplete_month_rate) ?? 0;

        setPendingDbConfirmation({
            isOpen: true,
            title: `Update ${fieldName}`,
            description: 'This will make changes from database as well.',
            fieldName,
            oldValue: `₹${Number(currentRate).toLocaleString('en-IN')}/day`,
            newValue: `₹${Number(newRate).toLocaleString('en-IN')}/day`,
            onConfirm: async () => {
                const updates: any = { [dbField]: newRate };
                if (isComplete) {
                    updates.estimated_value_monthly = newRate * 30;
                }

                const { error } = await supabase
                    .from('crm_leads')
                    .update(updates)
                    .eq('id', leadId);
                if (error) throw error;

                // Sync active services if any
                try {
                    await supabase
                        .from('services')
                        .update({ [dbField]: newRate })
                        .or(`client_id.eq.${leadId},lead_id.eq.${leadId}`)
                        .eq('status', 'active');
                } catch (sErr) {
                    console.warn(sErr);
                }

                // Sync state
                setSelectedInspectorLead((prev: any) => {
                    if (!prev || prev.id !== leadId) return prev;
                    return {
                        ...prev,
                        [dbField]: newRate,
                        ...(isComplete ? {
                            estimated_value_monthly: newRate * 30,
                            valueAmount: newRate * 30,
                            value: `₹${(newRate * 30).toLocaleString('en-IN')}/mo`
                        } : {})
                    };
                });

                setLeads(prev => prev.map(l => {
                    if (l.id !== leadId) return l;
                    return {
                        ...l,
                        [dbField]: newRate,
                        ...(isComplete ? { estimated_value_monthly: newRate * 30 } : {})
                    };
                }));

                setEditingInspectorRateType(null);
                setInspectorRateDraft('');
                toast.success(`${fieldName} updated in database!`);
            }
        });
    };

    const handleSaveConsentField = (
        leadId: string,
        field: string,
        newValue: any,
        fieldLabel: string,
        currentDisplayVal: string,
        newDisplayVal: string
    ) => {
        setPendingDbConfirmation({
            isOpen: true,
            title: `Update ${fieldLabel}`,
            description: 'This will make changes from database as well.',
            fieldName: fieldLabel,
            oldValue: currentDisplayVal || 'None / Not set',
            newValue: newDisplayVal || 'None / Not set',
            onConfirm: async () => {
                const lead = leads.find(l => l.id === leadId) || selectedInspectorLead;
                const existingConsent = getLatestByCreatedAt(lead?.client_consents);

                let updatedConsent: any = null;
                if (existingConsent?.id) {
                    const { data, error } = await supabase
                        .from('client_consents')
                        .update({ [field]: newValue })
                        .eq('id', existingConsent.id)
                        .select()
                        .single();
                    if (error) throw error;
                    updatedConsent = data;
                } else {
                    const { data, error } = await supabase
                        .from('client_consents')
                        .insert([{
                            lead_id: leadId,
                            phone: lead?.whatsapp_number || lead?.phone || '',
                            [field]: newValue,
                            terms_accepted: field === 'terms_accepted' ? newValue : true,
                        }])
                        .select()
                        .single();
                    if (error) throw error;
                    updatedConsent = data;
                }

                // If relative_name or patient_name, sync crm_leads name and clients client_name
                if (field === 'relative_name' || field === 'patient_name') {
                    await supabase.from('crm_leads').update({ name: newValue }).eq('id', leadId);
                    try {
                        await supabase.from('clients').update({ client_name: newValue }).eq('id', leadId);
                    } catch (clErr) {
                        console.warn(clErr);
                    }
                }

                // If service_category, sync crm_leads service_interest, active services, and active worker_assignments
                if (field === 'service_category') {
                    await supabase.from('crm_leads').update({ service_interest: newValue }).eq('id', leadId);
                    try {
                        await supabase.from('services').update({ service_type: newValue }).eq('client_id', leadId).eq('status', 'active');
                        await supabase.from('worker_assignments').update({ notes: newValue }).eq('client_id', leadId).eq('assignment_status', 'active');
                    } catch (syncErr) {
                        console.warn('Error syncing service_category across services/assignments:', syncErr);
                    }
                }

                // If offered_time, sync crm_leads shift_type
                if (field === 'offered_time') {
                    await supabase.from('crm_leads').update({ shift_type: newValue }).eq('id', leadId);
                }

                // Sync state
                setSelectedInspectorLead((prev: any) => {
                    if (!prev || prev.id !== leadId) return prev;
                    const prevConsents = (prev.client_consents || []).filter((c: any) => c.id !== updatedConsent?.id);
                    return {
                        ...prev,
                        client_consents: updatedConsent ? [updatedConsent, ...prevConsents] : prev.client_consents,
                        ...(field === 'relative_name' || field === 'patient_name' ? { name: newValue } : {}),
                        ...(field === 'service_category' ? { service_interest: newValue } : {}),
                        ...(field === 'offered_time' ? { shift_type: newValue } : {}),
                    };
                });

                setLeads(prev => prev.map(l => {
                    if (l.id !== leadId) return l;
                    const prevConsents = (l.client_consents || []).filter((c: any) => c.id !== updatedConsent?.id);
                    return {
                        ...l,
                        client_consents: updatedConsent ? [updatedConsent, ...prevConsents] : l.client_consents,
                        ...(field === 'relative_name' || field === 'patient_name' ? { name: newValue } : {}),
                        ...(field === 'service_category' ? { service_interest: newValue } : {}),
                        ...(field === 'offered_time' ? { shift_type: newValue } : {}),
                    };
                }));

                setEditingInspectorConsentField(null);
                setInspectorConsentDraft('');
                toast.success(`${fieldLabel} updated in database!`);
            }
        });
    };

    const handleUpdateInspectorPhoneWithConfirm = (leadId: string, newPhone: string, currentPhone: string) => {
        if (!newPhone.trim()) {
            setEditingInspectorPhone(false);
            return;
        }
        setPendingDbConfirmation({
            isOpen: true,
            title: 'Update Phone Number',
            description: 'This will make changes from database as well.',
            fieldName: 'Phone Number',
            oldValue: currentPhone || 'None',
            newValue: newPhone.trim(),
            onConfirm: async () => {
                await supabase.from('crm_leads').update({
                    phone: newPhone.trim(),
                    whatsapp_number: newPhone.trim(),
                }).eq('id', leadId);

                try {
                    await supabase.from('clients').update({ phone_number: newPhone.trim() }).eq('id', leadId);
                } catch (e) {
                    console.warn(e);
                }

                setSelectedInspectorLead((prev: any) => prev ? { ...prev, phone: newPhone.trim(), whatsapp_number: newPhone.trim() } : null);
                setLeads(prev => prev.map(l => l.id === leadId ? { ...l, phone: newPhone.trim(), whatsapp_number: newPhone.trim() } : l));
                setEditingInspectorPhone(false);
                toast.success('Phone number updated in database!');
            }
        });
    };

    const handleUpdateInspectorEmailWithConfirm = (leadId: string, newEmail: string, currentEmail: string) => {
        setPendingDbConfirmation({
            isOpen: true,
            title: 'Update Email Address',
            description: 'This will make changes from database as well.',
            fieldName: 'Email Address',
            oldValue: currentEmail || 'None',
            newValue: newEmail.trim() || 'None',
            onConfirm: async () => {
                await supabase.from('crm_leads').update({ email: newEmail.trim() || null }).eq('id', leadId);
                try {
                    await supabase.from('clients').update({ email: newEmail.trim() || null }).eq('id', leadId);
                } catch (e) {
                    console.warn(e);
                }

                setSelectedInspectorLead((prev: any) => prev ? { ...prev, email: newEmail.trim() } : null);
                setLeads(prev => prev.map(l => l.id === leadId ? { ...l, email: newEmail.trim() } : l));
                setEditingInspectorEmail(false);
                toast.success('Email updated in database!');
            }
        });
    };

    const handleUpdateLeadValueWithConfirm = (leadId: string, newAmountStr: string, currentAmount: number) => {
        const newValue = parseInt(newAmountStr.replace(/\D/g, ''), 10);
        if (isNaN(newValue)) {
            setEditingLeadValueId(null);
            return;
        }

        setPendingDbConfirmation({
            isOpen: true,
            title: 'Update Monthly Lead Value',
            description: 'This will make changes from database as well.',
            fieldName: 'Monthly Lead Value',
            oldValue: `₹${Number(currentAmount || 0).toLocaleString('en-IN')}`,
            newValue: `₹${Number(newValue).toLocaleString('en-IN')}`,
            onConfirm: async () => {
                const { error } = await supabase
                    .from('crm_leads')
                    .update({ estimated_value_monthly: newValue })
                    .eq('id', leadId);
                if (error) throw error;

                setSelectedInspectorLead((prev: any) => prev ? {
                    ...prev,
                    estimated_value_monthly: newValue,
                    valueAmount: newValue,
                    value: `₹${newValue.toLocaleString('en-IN')}/mo`
                } : null);

                setLeads(prev => prev.map(l => l.id === leadId ? { ...l, estimated_value_monthly: newValue } : l));
                setEditingLeadValueId(null);
                toast.success('Monthly value updated in database!');
            }
        });
    };

    // Kanban Accordion State
    const [expandedStages, setExpandedStages] = useState<Record<string, boolean>>({});
    const [stageLimits, setStageLimits] = useState<Record<string, number>>({});

    // Badges visibility state: hidden by default
    const [showBadges, setShowBadges] = useState<boolean>(false);
    const [cardBadgesOverrides, setCardBadgesOverrides] = useState<Record<string, boolean>>({});

    const toggleShowBadges = () => {
        setShowBadges(prev => {
            const next = !prev;
            setCardBadgesOverrides({});
            return next;
        });
    };

    const toggleCardBadges = (leadId: string) => {
        setCardBadgesOverrides(prev => ({
            ...prev,
            [leadId]: prev[leadId] !== undefined ? !prev[leadId] : !showBadges,
        }));
    };

    // Stages start closed by default (empty expandedStages object means false)

    const toggleStage = (stageName: string) => {
        setExpandedStages(prev => ({ ...prev, [stageName]: !prev[stageName] }));
    };

    const loadMoreInStage = (stageName: string) => {
        setStageLimits(prev => ({ ...prev, [stageName]: (prev[stageName] || 8) + 8 }));
    }; const [workflows, setWorkflows] = useState({
        greeting: true
    });

    // Voice AI State
    const [calls, setCalls] = useState<any[]>([]);
    const [isLoadingVoice, setIsLoadingVoice] = useState(true);
    const [capturingCallId, setCapturingCallId] = useState<string | number | null>(null);
    const [voiceMetrics, setVoiceMetrics] = useState({
        totalCallsToday: 0,
        avgDurationSeconds: 0,
        leadsCaptured: 0
    });

    // --- ELEVENLABS INTEGRATION ---
    const conversation = useConversation({
        onConnect: () => {
            setCallStatus('active');
            console.log('ElevenLabs: connected');
        },
        onDisconnect: () => {
            setCallStatus('idle');
            console.log('ElevenLabs: disconnected');
            setTimeout(() => fetchVoiceData(), 3000);
        },
        onError: (error: any) => {
            console.error('ElevenLabs Error', error);
            setCallStatus('idle');
            toast.error('ElevenLabs encountered an error. Please check console.');
        },
    });
    const [callStatus, setCallStatus] = useState<'idle' | 'loading' | 'active'>('idle');

    const sendWorkerProfileWhatsApp = async (lead: any, worker: any) => {
        try {
            // Guard: mock workers have numeric IDs — skip DB and open WhatsApp directly
            const isRealWorker = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(worker.id);
            if (!isRealWorker) {
                const text = `99Care: Namaste ${lead.name}! Aapke liye ${worker.name || worker.full_name} (${worker.role || worker.job_title}) ko assign kiya gaya hai. Dhanyawad! ✅`;
                let phone = (lead.whatsapp_number || lead.phone || '').replace(/\D/g, '') || '917575041313';
                if (phone.length === 10) phone = `91${phone}`;
                window.open(`https://wa.me/${phone}?text=${encodeURIComponent(text)}`, '_blank');
                toast.success(`Message opened for ${lead.name}! (Demo worker — no ID card link)`);
                return;
            }
            // Create assignment + ID card link
            const result = await assignWorkerToClient(worker.id, lead.id, undefined, 0, true);
            const text = `99Care: Namaste ${lead.name}! Aapke liye ${worker.name || worker.full_name} (${worker.role || worker.job_title}) ko assign kiya gaya hai.\n\n🔗 Unki verified ID Card dekhein: ${result.shareableUrl}\n\nYe link 30 din tak valid hai. Dhanyawad! ✅`;
            let phone = (lead.whatsapp_number || lead.phone || '').replace(/\D/g, '') || '917575041313';
            if (phone.length === 10) phone = `91${phone}`;
            window.open(`https://wa.me/${phone}?text=${encodeURIComponent(text)}`, '_blank');
            toast.success(`ID card link shared with ${lead.name}!`);
        } catch (err: any) {
            toast.error(`Failed to create assignment: ${err.message}`);
        }
    };



    const toggleCall = async () => {
        if (callStatus === 'active') {
            await conversation.endSession();
            setCallStatus('idle');
        } else {
            setCallStatus('loading');
            try {
                await navigator.mediaDevices.getUserMedia({ audio: true });
                await conversation.startSession({ agentId: String(ELEVENLABS_AGENT_ID), connectionType: 'webrtc' });
            } catch (err) {
                console.error('Failed to start ElevenLabs call', err);
                setCallStatus('idle');
                toast.error('Failed to start call. Please allow microphone access.');
            }
        }
    };

    // ── Helper: Relative time (e.g. "2h ago") ──────────────────────────────
    const getRelativeTime = (dateStr: string) => {
        const diff = Date.now() - new Date(dateStr).getTime();
        const mins = Math.floor(diff / 60000);
        if (mins < 1) return 'just now';
        if (mins < 60) return `${mins}m ago`;
        const hrs = Math.floor(mins / 60);
        if (hrs < 24) return `${hrs}h ago`;
        const days = Math.floor(hrs / 24);
        return `${days}d ago`;
    };

    // ── Helper: Initials from name ─────────────────────────────────────────
    const getInitials = (name: string) => {
        const parts = (name || '').trim().split(' ').filter(Boolean);
        if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
        return (parts[0]?.[0] || '?').toUpperCase();
    };

    // ── Helper: Consistent avatar color from name ──────────────────────────
    const AVATAR_COLORS = [
        'bg-teal-500', 'bg-blue-500', 'bg-purple-500', 'bg-rose-500',
        'bg-amber-500', 'bg-emerald-500', 'bg-indigo-500', 'bg-pink-500'
    ];
    const getAvatarColor = (name: string) => {
        let hash = 0;
        for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
        return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
    };

    // ── Helper: Format activity timestamp ─────────────────────────────────
    const formatActivityTime = (dateStr: string) => {
        const d = new Date(dateStr);
        const today = new Date();
        const isToday = d.toDateString() === today.toDateString();
        const timeStr = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
        return isToday ? `Today, ${timeStr}` : `${d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}, ${timeStr}`;
    };

    // ── Activity: Fetch from DB ────────────────────────────────────────────
    const fetchLeadActivity = async (leadId: string, duplicateOfId?: string | null) => {
        setIsLoadingActivity(true);
        try {
            let query = supabase.from('crm_lead_activity').select('*');
            if (duplicateOfId) {
                query = query.in('lead_id', [leadId, duplicateOfId]);
            } else {
                query = query.eq('lead_id', leadId);
            }
            const { data, error } = await query.order('created_at', { ascending: true });
            if (error) throw error;
            setInspectorActivity(data || []);
        } catch (e: any) {
            console.error('Failed to load lead activity:', e.message);
            setInspectorActivity([]);
        } finally {
            setIsLoadingActivity(false);
        }
    };

    const navigate = useNavigate();
    const location = useLocation();

    // Auto-open lead if passed via navigation state (e.g. from Global Search)
    useEffect(() => {
        if (location.state?.openLeadId && leads.length > 0) {
            const lead = leads.find(l => l.id === location.state.openLeadId);
            if (lead && (!selectedInspectorLead || selectedInspectorLead.id !== lead.id)) {
                if (clientStages.includes(lead.pipeline_stage)) {
                    setActiveTab('clients');
                } else if (pipelineStages.includes(lead.pipeline_stage)) {
                    setActiveTab('pipeline');
                }
                setSelectedInspectorLead(lead);
                fetchLeadActivity(lead.id, lead.duplicate_of_lead_id);
            }
        }
    }, [location.state?.openLeadId, leads, clientStages, pipelineStages]);

    // ── Activity: Log a new event ──────────────────────────────────────────
    const logActivity = async (leadId: string, eventType: string, description: string, metadata: any = {}) => {
        if (!leadId || leadId.length < 10) return; // Skip mock leads
        try {
            const { error } = await supabase.from('crm_lead_activity').insert([{
                lead_id: leadId,
                event_type: eventType,
                description,
                metadata
            }]);
            if (error) console.warn("Activity logging skipped:", error.message);
        } catch (e) {
            console.error("Activity log error:", e);
        }
    };

    // ── Inspector: Save a field (email or service_interest) ───────────────
    const saveInspectorField = async (leadId: string, field: string, value: string) => {
        const previousLead = leads.find(l => l.id === leadId);
        const previousVal = previousLead ? (previousLead as any)[field] : undefined;

        // Optimistic update
        setSelectedInspectorLead((prev: any) => prev ? { ...prev, [field]: value } : null);
        setLeads(prev => prev.map(l => l.id === leadId ? { ...l, [field]: value } : l));

        if (leadId.length >= 10) {
            const { error } = await supabase.from('crm_leads').update({ [field]: value }).eq('id', leadId);
            if (error) {
                console.error('saveInspectorField failed:', error.message);
                toast.error(`Failed to save ${field}: ${error.message}`);
                // Revert optimistic update accurately
                if (previousVal !== undefined) {
                    setSelectedInspectorLead((prev: any) => prev ? { ...prev, [field]: previousVal } : null);
                    setLeads(prev => prev.map(l => l.id === leadId ? { ...l, [field]: previousVal } : l));
                } else {
                    fetchLeads();
                }
            } else {
                // Keep clients table in sync if identity fields changed
                try {
                    if (field === 'name') {
                        await supabase.from('clients').update({ client_name: value }).eq('id', leadId);
                    } else if (field === 'phone') {
                        await supabase.from('clients').update({ phone_number: value }).eq('id', leadId);
                    } else if (field === 'email') {
                        await supabase.from('clients').update({ email: value }).eq('id', leadId);
                    }
                } catch (clientSyncErr) {
                    console.warn('Clients sync warning:', clientSyncErr);
                }
            }
        }
    };

    const handleAddNote = async (leadId: string, noteText: string) => {
        if (!noteText.trim()) return;
        setIsSavingNote(true);
        try {
            // Fetch current notes so we can append
            const { data: current } = await supabase.from('crm_leads').select('notes').eq('id', leadId).single();
            const timestamp = new Date().toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
            const newEntry = `[${timestamp}] ${noteText.trim()}`;
            const updatedNotes = current?.notes ? `${current.notes}\n\n${newEntry}` : newEntry;
            await supabase.from('crm_leads').update({ notes: updatedNotes }).eq('id', leadId);
            setSelectedInspectorLead((prev: any) => prev ? { ...prev, notes: updatedNotes } : null);
            setLeads(prev => prev.map(l => l.id === leadId ? { ...l, notes: updatedNotes } : l));
            // Log as activity
            await supabase.from('crm_lead_activity').insert([{
                lead_id: leadId,
                event_type: 'note_added',
                description: `Note: ${noteText.trim().substring(0, 80)}${noteText.length > 80 ? '...' : ''}`,
                metadata: { note: noteText.trim() }
            }]);
            // Refresh activity feed
            fetchLeadActivity(leadId, selectedInspectorLead?.duplicate_of_lead_id);
            setInspectorNoteDraft('');
            toast.success('Note saved!');
        } catch (err) {
            toast.error('Failed to save note.');
        } finally {
            setIsSavingNote(false);
        }
    };

    const upsertCallTranscriptStatus = async (
        call: any,
        phoneDigits: string,
        automationError: string
    ) => {
        const { error } = await supabase.from('call_transcripts').upsert(
            {
                conversation_id: String(call.id),
                phone_number: phoneDigits,
                called_at: call.created_at || new Date().toISOString(),
                lead_id: call.lead_id || null,
                automation_error: automationError,
            },
            { onConflict: 'conversation_id' }
        );
        if (error) {
            console.warn('[Greeting] call_transcripts upsert failed:', error.message);
        }
    };

    const dismissToast = (toastId?: string | number) => {
        if (toastId !== undefined) toast.dismiss(toastId);
    };

    const handleResendCallGreeting = (call: any) => {
        processingCalls.current.delete(String(call.id));
        void handleSendCallGreeting(call, true);
    };

    const resolveCallLeadId = async (call: any, last10: string) => {
        if (call.lead_id) return call.lead_id;

        const localLead = leads.find(l => {
            const leadPhone = `${l.phone || ''} ${l.whatsapp_number || ''}`.replace(/\D/g, '');
            return leadPhone.endsWith(last10);
        });
        if (localLead?.id) return localLead.id;

        const { data: transcriptRow } = await supabase
            .from('call_transcripts')
            .select('lead_id')
            .eq('conversation_id', String(call.id))
            .maybeSingle();
        if (transcriptRow?.lead_id) return transcriptRow.lead_id;

        const { data: dbLeads } = await supabase
            .from('crm_leads')
            .select('id')
            .or(`phone.ilike.%${last10}%,whatsapp_number.ilike.%${last10}%`)
            .is('deleted_at', null)
            .order('created_at', { ascending: false })
            .limit(1);
        return dbLeads?.[0]?.id ?? null;
    };

    // Send WhatsApp post_call_intake template manually from call log card
    const handleSendCallGreeting = async (call: any, isManual = false) => {
        const callKey = String(call.id);
        if (processingCalls.current.has(callKey)) {
            if (!isManual) return;
            processingCalls.current.delete(callKey);
        }
        processingCalls.current.add(callKey);

        const rawPhone = call.capturedWhatsapp;
        if (!rawPhone) {
            processingCalls.current.delete(callKey);
            toast.error('No WhatsApp number found for this call.');
            return;
        }

        let digits = rawPhone.replace(/\D/g, '');
        if (digits.length === 10) digits = `91${digits}`;
        if (digits.length < 11) {
            processingCalls.current.delete(callKey);
            toast.error(`Invalid phone number: ${rawPhone}`);
            return;
        }
        const last10 = digits.slice(-10);
        const callPhoneDigits = normalizePhoneDigits(
            call.phone && call.phone !== 'Unknown Number' ? call.phone : rawPhone
        );

        setCallGreetingStatus(prev => ({ ...prev, [callKey]: 'sending' }));

        const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
        const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;
        const firstName = (call.capturedName || 'there').split(' ')[0];
        const manualToastId = isManual
            ? toast.loading(`Resending greeting to ${firstName}…`)
            : undefined;

        // Declare callForTranscript before try so the catch block can reference it
        let callForTranscript: any = call;

        try {
            let finalLeadId: string | null = null;
            for (let attempt = 1; attempt <= 6; attempt += 1) {
                finalLeadId = await resolveCallLeadId(call, last10);
                if (finalLeadId) break;
                console.log(`[Auto-Greet] Lead not linked yet for ${last10}; retry ${attempt}/6`);
                await wait(attempt === 1 ? 700 : 1200);
            }

            callForTranscript = finalLeadId ? { ...call, lead_id: finalLeadId } : call;

            if (finalLeadId) {
                setCalls((prev) =>
                    prev.map((c) => (String(c.id) === callKey ? { ...c, lead_id: finalLeadId } : c))
                );
            }
            const matchingLead = finalLeadId ? leads.find(l => l.id === finalLeadId) : null;

            // --- ATOMIC IDEMPOTENCY CHECK ---
            // Use claim_greeting_send RPC for atomic check-and-set.
            // This prevents race conditions between auto-trigger, manual clicks, and multiple tabs.
            if (finalLeadId) {
                const { data: claimResult } = await supabase.rpc('claim_greeting_send', {
                    target_lead_id: finalLeadId
                });

                const claimSucceeded = claimResult && claimResult.length > 0;

                if (!claimSucceeded && !isManual) {
                    // Another process already sent the greeting — skip silently
                    console.log(`[Greeting] Atomic claim failed for lead ${finalLeadId} — greeting already sent. Skipping.`);
                    await supabase.from('crm_lead_activity').insert([{
                        lead_id: finalLeadId,
                        event_type: 'greeting_attempt',
                        description: 'Greeting skipped: already sent (atomic claim failed)',
                        metadata: { trigger_source: 'call_end', outcome: 'skipped_already_sent', conversation_id: callKey }
                    }]);
                    await upsertCallTranscriptStatus(callForTranscript, callPhoneDigits || digits, 'GREETING_SENT');
                    setCallGreetingStatus(prev => ({ ...prev, [callKey]: 'sent' }));
                    dismissToast(manualToastId);
                    return;
                }

                if (!claimSucceeded && isManual) {
                    // Manual resend — warn but allow
                    console.log(`[Greeting] Manual resend for lead ${finalLeadId} — greeting was already sent, proceeding anyway.`);
                    await supabase.from('crm_lead_activity').insert([{
                        lead_id: finalLeadId,
                        event_type: 'greeting_attempt',
                        description: 'Greeting resent manually (override)',
                        metadata: { trigger_source: 'manual', outcome: 'resent_override', conversation_id: callKey }
                    }]);
                }
            }

            await upsertCallTranscriptStatus(
                callForTranscript,
                callPhoneDigits || digits,
                isManual ? 'GREETING_RESENDING' : 'GREETING_PROCESSING'
            );

            // Step 2: Send post_call_intake — template body + Flow prefill from Voice AI summary
            const intakePrefill = buildVoiceCallIntakePrefill(call);
            const requestBody = {
                phone: digits,
                leadId: finalLeadId,
                useTemplate: true,
                templateName: 'post_call_intake',
                leadName: intakePrefill.flowData.name || matchingLead?.name?.split(/\s+/)[0] || firstName,
                templateParams: intakePrefill.templateParams,
                flowData: intakePrefill.flowData,
            };

            const controller = new AbortController();
            const timeoutId = window.setTimeout(() => controller.abort(), 25000);
            const res = await fetch(`${SUPABASE_URL}/functions/v1/meta-whatsapp-outbound`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
                    'apikey': SUPABASE_ANON_KEY,
                },
                body: JSON.stringify(requestBody),
                signal: controller.signal,
            }).finally(() => window.clearTimeout(timeoutId));

            const data = await res.json().catch(() => ({}));

            if (!res.ok) {
                throw new Error(`Edge Function error: HTTP ${res.status}`);
            }
            if (data.success === false || data.error) {
                throw new Error(data.error || 'WhatsApp delivery failed — template may be paused or rejected by Meta.');
            }

            // Step 3: Verify delivery by checking whatsapp_logs was written
            await new Promise(r => setTimeout(r, 1500)); // give DB a moment to write
            const { data: verifyLog } = await supabase
                .from('whatsapp_logs')
                .select('id, status')
                .or(`payload->>'original_recipient'.ilike.%${last10}%`)
                .filter('payload->>templateName', 'eq', 'post_call_intake')
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle();

            const deliveryConfirmed =
                verifyLog?.status === 'success' || verifyLog?.status === 'accepted_by_meta';

            await upsertCallTranscriptStatus(callForTranscript, callPhoneDigits || digits, 'GREETING_SENT');
            setCalls((prev) =>
                prev.map((c) =>
                    String(c.id) === callKey
                        ? { ...c, capturedWhatsapp: digits, automation_error: 'GREETING_SENT' }
                        : c
                )
            );

            // Sync parsed call details onto matching CRM lead (if any)
            const { data: linkedLeads } = await supabase
                .from('crm_leads')
                .select('id, notes, service_interest, phone, whatsapp_number')
                .is('deleted_at', null)
                .or(`phone.ilike.%${last10}%,whatsapp_number.ilike.%${last10}%`)
                .limit(3);
            const linkedLead = (linkedLeads || []).find(
                (l) => phonesMatch(l.phone, rawPhone) || phonesMatch(l.whatsapp_number, rawPhone)
            );
            const leadToUpdateId = linkedLead?.id || finalLeadId;
            if (leadToUpdateId) {
                const noteLines = [
                    `Service: ${intakePrefill.service}`,
                    `Shift: ${intakePrefill.shiftType}`,
                    intakePrefill.startDateDisplay ? `Start date: ${intakePrefill.startDateDisplay}` : null,
                    'Source: AI Phone Call (WhatsApp greeting)',
                ].filter(Boolean);
                const priorNotes = linkedLead?.notes?.trim() || '';
                const mergedNotes = priorNotes
                    ? `${priorNotes}\n${noteLines.join('\n')}`
                    : noteLines.join('\n');
                await supabase
                    .from('crm_leads')
                    .update({
                        service_interest: intakePrefill.service,
                        whatsapp_number: digits,
                        notes: mergedNotes,
                        last_greeted_at: new Date().toISOString(),
                    })
                    .eq('id', leadToUpdateId);

                // Log successful greeting send for audit trail
                await supabase.from('crm_lead_activity').insert([{
                    lead_id: leadToUpdateId,
                    event_type: 'greeting_attempt',
                    description: `Greeting ${isManual ? 'resent manually' : 'sent automatically'} to ${firstName}`,
                    metadata: {
                        trigger_source: isManual ? 'manual' : 'call_end',
                        outcome: 'sent',
                        conversation_id: callKey,
                        phone: digits
                    }
                }]);
            }

            setCallGreetingStatus(prev => ({ ...prev, [callKey]: 'sent' }));
            const prefillHint = `${intakePrefill.flowData.service || intakePrefill.service} · ${intakePrefill.shiftLabel}`;
            const resendNote = isManual ? ' (resent)' : '';
            dismissToast(manualToastId);
            if (deliveryConfirmed) {
                toast.success(`✅ Greeting sent to ${firstName}${resendNote}! Prefill: ${prefillHint}`);
            } else {
                toast.success(`✅ Greeting dispatched to ${firstName}${resendNote}. Prefill: ${prefillHint}`);
            }
        } catch (err: any) {
            console.error('[Greeting Error]', err.message);

            const errCode = `GREETING_ERROR: ${err.message?.slice(0, 200)}`;
            await upsertCallTranscriptStatus(callForTranscript, callPhoneDigits || digits, errCode);
            setCalls((prev) =>
                prev.map((c) => (String(c.id) === callKey ? { ...c, automation_error: errCode } : c))
            );

            setCallGreetingStatus(prev => ({ ...prev, [callKey]: 'error' }));
            dismissToast(manualToastId);
            toast.error(`❌ Greeting failed: ${err.message}`, { duration: 8000 });
        } finally {
            processingCalls.current.delete(callKey);
        }
    };

    // --- FETCH VOICE DATA (From ElevenLabs webhook via crm_call_logs) ---
    const fetchVoiceData = async () => {
        setIsLoadingVoice(true);
        try {
            // Fetch calls natively from Edge Function to bypass SDK wrapper issues
            const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
            const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;
            let callsData: any[] = [];

            try {
                const res = await fetch(`${SUPABASE_URL}/functions/v1/get-elevenlabs-calls`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
                        'apikey': SUPABASE_ANON_KEY
                    },
                    body: JSON.stringify({ limit: 30 })
                });

                if (res.ok) {
                    const edgeResponse = await res.json();
                    callsData = edgeResponse?.data || [];
                } else {
                    console.warn(`Edge Function responded with HTTP ${res.status}, falling back to Supabase call_transcripts.`);
                }
            } catch (edgeErr) {
                console.warn('Edge Function fetch failed, falling back to Supabase call_transcripts:', edgeErr);
            }

            // Fallback: If edge function returned empty or encountered an error, fetch directly from Supabase call_transcripts
            if (!callsData || callsData.length === 0) {
                const { data: dbTranscripts, error: dbErr } = await supabase
                    .from('call_transcripts')
                    .select('*')
                    .order('called_at', { ascending: false })
                    .limit(50);

                if (!dbErr && dbTranscripts && dbTranscripts.length > 0) {
                    callsData = dbTranscripts.map((t: any) => {
                        const transcriptArr = Array.isArray(t.transcript_json) ? t.transcript_json : [];
                        const transcriptStr = t.transcript_text || transcriptArr.map((turn: any) => `${turn.role === 'agent' ? 'AI' : 'User'}: ${turn.message}`).join('\n');
                        
                        let capturedName = null;
                        let capturedWhatsapp = null;
                        let intent = "Inquiry";
                        
                        for (const turn of transcriptArr) {
                            if (turn.tool_calls) {
                                for (const tc of turn.tool_calls) {
                                    if (tc.params_as_json) {
                                        try {
                                            const p = JSON.parse(tc.params_as_json);
                                            if (p.name) capturedName = p.name;
                                            if (p.service) intent = p.service;
                                        } catch (_) {}
                                    }
                                }
                            }
                        }

                        return {
                            id: t.conversation_id,
                            created_at: t.called_at || t.created_at || new Date().toISOString(),
                            duration_seconds: t.call_duration_secs || 0,
                            intent: intent,
                            summary: t.transcript_text ? t.transcript_text.slice(0, 160) + '...' : "Call recorded.",
                            transcript: transcriptStr,
                            recording_url: null,
                            phone_number: t.phone_number || null,
                            capturedName: capturedName,
                            capturedWhatsapp: capturedWhatsapp,
                            lead_id: t.lead_id || null,
                            automation_error: t.automation_error || null
                        };
                    });
                }
            }

            // Process and format calls
            let todayCalls = 0;
            let totalDurationToday = 0;
            const todayStr = new Date().toDateString();

            const formattedCalls = (callsData || []).map((call: any) => {
                const startedAt = new Date(call.created_at);
                const isToday = startedAt.toDateString() === todayStr;

                const durationSeconds = call.duration_seconds || 0;

                if (isToday) {
                    todayCalls++;
                    totalDurationToday += durationSeconds;
                }

                // Format duration string (e.g. "1m 45s")
                const dMins = Math.floor(durationSeconds / 60);
                const dSecs = durationSeconds % 60;
                const durationStr = dMins > 0 ? `${dMins}m ${dSecs}s` : `${dSecs}s`;

                // If lead_id exists, it means the webhook successfully associated this call with a lead
                const callStatus = call.lead_id ? 'Processed' : 'Unprocessed';

                return {
                    id: call.id,
                    phone: call.phone_number || "Unknown Number",
                    type: 'Inbound',
                    duration: durationStr,
                    time: isToday ? startedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : startedAt.toLocaleDateString(),
                    intent: call.intent || "Inquiry",
                    summary: call.summary || "No summary available.",
                    recordingUrl: call.recording_url,
                    capturedName: call.lead_id ? (call.capturedName || "Known Lead") : call.capturedName,
                    capturedWhatsapp: call.capturedWhatsapp || null,
                    status: callStatus,
                    transcript: call.transcript,
                    lead_id: call.lead_id,
                    automation_error: call.automation_error,
                    created_at: call.created_at // Preserve raw timestamp for auto-trigger logic
                };
            });

            // Filter calls that successfully captured a lead (lead_id is not null) or phone matches existing lead
            const actualVoiceLeadsCaptured = formattedCalls.filter((c: any) => {
                const isAlreadyInPipeline = leads.some(l => {
                    if (c.lead_id && l.id === c.lead_id) return true;
                    return false;
                });
                return isAlreadyInPipeline;
            }).length;

            setCalls(formattedCalls);
            setVoiceMetrics({
                totalCallsToday: todayCalls,
                avgDurationSeconds: todayCalls > 0 ? Math.round(totalDurationToday / todayCalls) : 0,
                leadsCaptured: actualVoiceLeadsCaptured
            });

        } catch (error: any) {
            console.error('CRITICAL: Voice logs fetch failed:', {
                message: error.message,
                details: error.details,
                hint: error.hint,
                code: error.code,
                stack: error.stack
            });
            toast.error(`Unable to load voice logs: ${error.message}. Check console for network details.`);
        } finally {
            setIsLoadingVoice(false);
        }
    };

    const handleRetryVoice = () => {
        fetchVoiceData();
    };

    useEffect(() => {
        if (activeTab === 'voice') {
            fetchVoiceData();
        }
        if (activeTab === 'trash') {
            fetchTrashedLeads();
        }
        if (activeTab === 'automations') {
            fetchReviews();
        }
    }, [activeTab]);

    const fetchReviews = async () => {
        setIsLoadingReviews(true);
        try {
            const { data, error } = await supabase
                .from('reviews')
                .select('*')
                .order('created_at', { ascending: false })
                .limit(20);
            if (error) throw error;
            setReviews(data || []);
        } catch (err: any) {
            console.warn('Reviews table not available:', err.message);
            setReviews([]);
        } finally {
            setIsLoadingReviews(false);
        }
    };
    // -------------------------

    const toggleWorkflow = async (key: keyof typeof workflows) => {
        const isTurningOn = !workflows[key];

        // Update local state immediately for responsiveness
        setWorkflows(prev => ({ ...prev, [key]: isTurningOn }));

        const updatePayload = {
            id: 'global',
            greeting_enabled: key === 'greeting' ? isTurningOn : workflows.greeting
        };

        const { error } = await supabase
            .from('automation_settings')
            .upsert(updatePayload, { onConflict: 'id' });

        if (error) {
            console.error("[Settings Update Error]:", error);
            toast.error(`Failed to update ${key} setting.`);
            // Rollback
            setWorkflows(prev => ({ ...prev, [key]: !isTurningOn }));
        } else {
            toast.success(`Instant Greeting ${isTurningOn ? 'Enabled' : 'Disabled'}`);
            fetchDeliveryLogs();
        }
    };




    // Bulk greeting removed — greetings are now sent only from individual call cards or the pipeline inspector.

    const computeLeadDepositStatus = (l: any, allAssignments: any[], paidDepositNames?: Set<string>) => {
        const clientServices = (l.services || []) as any[];
        const clientAssignments = (allAssignments || []).filter((a: any) => a.client_id === l.id);

        const activeService = clientServices.find((s: any) => s.status === 'active');
        const activeAssignment = clientAssignments.find((a: any) => a.assignment_status === 'active');

        let hasPaidDeposit = false;
        let isDepositPending = false;

        if (activeService) {
            // Sole source of truth is services table
            if (activeService.deposit_status === 'collected') {
                hasPaidDeposit = true;
                isDepositPending = false;
            } else if (activeService.deposit_status === 'pending') {
                hasPaidDeposit = false;
                isDepositPending = true;
            } else {
                hasPaidDeposit = false;
                isDepositPending = false;
            }
        } else if (activeAssignment) {
            // Fallback for legacy records without services table entry
            const depositAmount = Number(activeAssignment.deposit_amount) || 0;
            if (depositAmount > 0 && Number(activeAssignment.deposit_paid || 0) >= depositAmount) {
                hasPaidDeposit = true;
                isDepositPending = false;
            } else if (depositAmount > 0 && Number(activeAssignment.deposit_paid || 0) < depositAmount) {
                isDepositPending = true;
                hasPaidDeposit = false;
            }
        } else {
            // If service or assignments are completed/ended, check if deposits were paid or settled, or recorded in payments
            const anySettledOrCollected = clientServices.some((s: any) => s.deposit_status === 'collected' || s.deposit_status === 'settled');
            const normName = (l.name || '').trim().toLowerCase();
            if (anySettledOrCollected || (paidDepositNames && paidDepositNames.has(normName))) {
                hasPaidDeposit = true;
                isDepositPending = false;
            }
        }

        return { hasPaidDeposit, isDepositPending };
    };

    const fetchLeads = async () => {
        setIsLoading(true);
        try {
            const [leadsResult, assignmentsResult, paymentsResult] = await Promise.all([
                supabase
                    .from('crm_leads')
                    .select('*, crm_quotations(start_date, duration, service_category, service_name, shift_type, hours_per_day, complete_month_rate, incomplete_month_rate, deposit, estimated_monthly_total, created_at), client_consents(*), services(id, status, deposit_amount, deposit_status, start_date, created_at, complete_month_daily_rate, incomplete_month_daily_rate, service_worker_assignments(id, employee_id, start_date, end_date, employees(full_name, employee_id)))')
                    .is('deleted_at', null)
                    .not('pipeline_stage', 'eq', 'Archived')
                    .order('created_at', { ascending: false }),
                supabase
                    .from('worker_assignments')
                    .select('id, client_id, assignment_status, deposit_amount, deposit_paid, start_date, assigned_at'),
                supabase
                    .from('payments')
                    .select('client_name, amount, payment_type')
                    .eq('payment_type', 'deposit')
            ]);

            const allAssignments = assignmentsResult.data || [];
            const paidDepositNames = new Set((paymentsResult.data || []).map((p: any) => (p.client_name || '').trim().toLowerCase()));
            const data = leadsResult.data;
            const error = leadsResult.error;

            if (error) {
                // If deleted_at column doesn't exist yet, fall back to fetching all leads
                if (error.message?.includes('deleted_at') || error.code === '42703') {
                    const { data: fallback, error: fallbackError } = await supabase
                        .from('crm_leads')
                        .select('*, crm_quotations(start_date, duration, service_category, service_name, shift_type, hours_per_day, complete_month_rate, incomplete_month_rate, deposit, estimated_monthly_total, created_at), client_consents(*), services(id, status, deposit_amount, deposit_status, start_date, created_at, complete_month_daily_rate, incomplete_month_daily_rate, service_worker_assignments(id, employee_id, start_date, end_date, employees(full_name, employee_id)))')
                        .not('pipeline_stage', 'eq', 'Archived')
                        .order('created_at', { ascending: false });
                    if (fallbackError) throw fallbackError;

                    const fallbackRows = fallback || [];
                    
                    // Filter duplicates
                    const uniqueFallbackRows = [];
                    const phoneMap = new Set();
                    for (const l of fallbackRows) {
                        const phoneDigits = (l.phone || l.whatsapp_number || '').replace(/\D/g, '').slice(-10);
                        if (phoneDigits && phoneDigits.length === 10) {
                            if (phoneMap.has(phoneDigits)) continue;
                            phoneMap.add(phoneDigits);
                        }
                        uniqueFallbackRows.push(l);
                    }
                    const finalFallbackRows = uniqueFallbackRows;

                    const firstStage = NEW_LEAD_PIPELINE_STAGE;
                    const legacyIds = finalFallbackRows
                        .filter((l) => isLegacyPipelineStage(l.pipeline_stage))
                        .map((l) => l.id);
                    if (legacyIds.length > 0) {
                        supabase
                            .from('crm_leads')
                            .update({ pipeline_stage: firstStage })
                            .in('id', legacyIds);
                    }
                    setLeads(
                        finalFallbackRows.map((l) => {
                            const { hasPaidDeposit, isDepositPending } = computeLeadDepositStatus(l, allAssignments, paidDepositNames);
                            return {
                                ...l,
                                pipeline_stage: isLegacyPipelineStage(l.pipeline_stage) ? firstStage : l.pipeline_stage,
                                has_paid_deposit: hasPaidDeposit,
                                is_deposit_pending: isDepositPending
                            };
                        })
                    );
                    setSelectedInspectorLead((prev: any) => {
                        if (!prev?.id) return prev;
                        const latest = fallbackRows.find((l) => l.id === prev.id);
                        return latest ? { ...prev, ...latest } : prev;
                    });
                    return;
                }
                throw error;
            }

            const rawRows = data || [];
            // Filter duplicates
            const uniqueRows = [];
            const phoneMap3 = new Set();
            for (const l of rawRows) {
                const phoneDigits = (l.phone || l.whatsapp_number || '').replace(/\D/g, '').slice(-10);
                if (phoneDigits && phoneDigits.length === 10) {
                    if (phoneMap3.has(phoneDigits)) continue;
                    phoneMap3.add(phoneDigits);
                }
                uniqueRows.push(l);
            }
            const rows = uniqueRows;

            const firstStage = NEW_LEAD_PIPELINE_STAGE;
            const legacyIds = rows
                .filter((l) => isLegacyPipelineStage(l.pipeline_stage))
                .map((l) => l.id);
            if (legacyIds.length > 0) {
                supabase
                    .from('crm_leads')
                    .update({ pipeline_stage: firstStage })
                    .in('id', legacyIds)
                    .then(({ error }) => {
                        if (error) console.warn('Legacy stage migration failed:', error.message);
                    });
            }
            setLeads(
                rows.map((l) => {
                    const { hasPaidDeposit, isDepositPending } = computeLeadDepositStatus(l, allAssignments, paidDepositNames);
                    return {
                        ...l,
                        pipeline_stage: isLegacyPipelineStage(l.pipeline_stage) ? firstStage : l.pipeline_stage,
                        has_paid_deposit: hasPaidDeposit,
                        is_deposit_pending: isDepositPending
                    };
                })
            );
            
            setSelectedInspectorLead((prev: any) => {
                if (!prev?.id) return prev;
                const latest = rows.find((l) => l.id === prev.id);
                return latest ? { ...prev, ...latest } : prev;
            });
        } catch (err: any) {
            console.error('Error fetching leads:', err);
            toast.error('Failed to load CRM leads. Please refresh the page.');
        } finally {
            setIsLoading(false);
        }
    };

    const fetchTrashedLeads = async () => {
        setIsLoadingTrash(true);
        try {
            const { data, error } = await supabase
                .from('crm_leads')
                .select('*')
                .not('deleted_at', 'is', null)
                .order('deleted_at', { ascending: false });
            if (error) throw error;
            // Auto-clear leads trashed more than 30 days ago
            const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
            const expired = (data || []).filter((l: any) => l.deleted_at < thirtyDaysAgo);
            if (expired.length > 0) {
                await Promise.all(expired.map((l: any) =>
                    supabase.rpc('delete_crm_lead_robust', { target_lead_id: l.id })
                ));
            }
            setTrashedLeads((data || []).filter((l: any) => l.deleted_at >= thirtyDaysAgo));
        } catch (err: any) {
            console.error('Error fetching trash:', err);
        } finally {
            setIsLoadingTrash(false);
        }
    };

    // AI WhatsApp Agent Logic
    const generateWhatsappDraft = (
        leadName: string,
        action: string,
        lang: string,
        worker?: any,
        shareableUrl?: string
    ) => {
        let tpl = whatsappTemplates[action]?.[lang] || '';
        const link = shareableUrl || `${window.location.origin}/id-card/${Math.random().toString(36).substr(2, 6)}`;

        // Strip out hardcoded legacy domain prefixes if they exist in the template
        // e.g. "https://99care.in/staff/{{link}}" -> "{{link}}"
        if (tpl.includes('https://99care.in/staff/')) {
            tpl = tpl.replace(/https:\/\/99care\.in\/staff\//g, '');
        }

        return tpl
            .replace(/\{\{name\}\}/g, leadName)
            .replace(/\{\{link\}\}/g, link)
            .replace(/\{\{workerName\}\}/g, worker?.name || worker?.full_name || 'your assigned staff')
            .replace(/\{\{workerRole\}\}/g, worker?.role || worker?.job_title || 'Healthcare Worker')
            .replace(/\{\{v1\}\}/g, quotationVars.v1 || '[Service]')
            .replace(/\{\{v2\}\}/g, quotationVars.v2 || '[Hours]')
            .replace(/\{\{v3\}\}/g, quotationVars.v3 || '[Price]')
            .replace(/\{\{v4\}\}/g, quotationVars.v4 || '[Price]');
    };

    const generateWorkerAssignmentDraft = (
        worker: any,
        lead: any,
        startDateStr: string,
        hours: number,
        addressOverride?: string
    ) => {
        const workerName = worker?.name || worker?.full_name || 'Care Professional';
        const clientName = lead?.client_consents?.[0]?.patient_name || lead?.name || 'Client';
        const relativeName = lead?.client_consents?.[0]?.relative_name;
        const clientDisplayName = relativeName && relativeName !== clientName ? `${clientName} (Attn: ${relativeName})` : clientName;

        let addr = addressOverride || lead?.client_consents?.[0]?.address || lead?.location || lead?.work_form_data?.address || '';
        if (!addr && lead?.notes) {
            const m = lead.notes.match(/address[:\s-]*([^\n\r]+)/i);
            if (m && m[1]) addr = m[1].trim();
        }
        if (!addr) addr = '[Address to be shared]';

        const shiftTypeLabel = hours === 24 ? '24-Hour Shift (Live-in)' : '10-Hour Shift';

        let formattedDate = 'Today';
        if (startDateStr) {
            try {
                const [y, m, d] = startDateStr.split('-');
                formattedDate = `${d}/${m}/${y}`;
            } catch {
                formattedDate = startDateStr;
            }
        }

        return `Namaste ${workerName}! 🙏\n\nYou have been assigned to a new client by 99 Care:\n\n👤 Client: ${clientDisplayName}\n📅 Start Date: ${formattedDate}\n🕒 Shift: ${shiftTypeLabel}\n📍 Address: ${addr}\n\nPlease ensure you reach the location on time in proper uniform. For any assistance or questions, please call the office at +91 9016116564.\n\n- 99 Care Team`;
    };

    const fetchWorkers = async (targetLead: any) => {
        setIsLoadingWorkers(true);
        try {
            const { data, error } = await supabase.from('employees').select('*').is('deleted_at', null);

            if (error) throw error;

            if (data && data.length > 0) {
                // Get IDs of workers already assigned to this lead
                const activeEmployeeIds = new Set(
                    (targetLead?.services || []).flatMap((s: any) => 
                        (s.service_worker_assignments || [])
                            .filter((swa: any) => !swa.end_date)
                            .map((swa: any) => swa.employee_id)
                    )
                );

                // Filter in JS to be safe against schema variations, and remove already assigned workers
                const available = data.filter(w =>
                    (w.status && w.status.toLowerCase() === 'available') && !w.deleted_at && !activeEmployeeIds.has(w.id)
                );
                // Map unified fields to legacy UI expectations
                const mapped = available.map(w => ({ ...w, name: w.full_name || w.name, role: w.job_title || w.role }));
                setAvailableWorkers(mapped);
            } else {
                setAvailableWorkers([]);
            }
        } catch (err) {
            console.warn("Failed to fetch employees from DB:", err);
            setAvailableWorkers([]);
        } finally {
            setIsLoadingWorkers(false);
        }
    };

    const openStaffPicker = (lead: any) => {
        setStaffPickerTargetLead(lead);
        setSelectedWorker(null);
        fetchWorkers(lead);
        setIsStaffPickerOpen(true);
    };

    const confirmWorkerSelection = async (worker: any) => {
        // Guard: mock workers have numeric IDs (e.g. '3') — not valid UUIDs
        const isRealWorker = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(worker.id);
        if (!isRealWorker) {
            toast.error(
                `Cannot assign "${worker.name || worker.full_name}" — this is a demo worker. Please add real workers in the HR module first.`
            );
            return;
        }

        setSelectedWorker(worker);
        setIsStaffPickerOpen(false);
        setIsServicePeriodOpen(true);

        // Fetch quotation and consent to auto-fill dates
        let autoStartDate = new Date().toISOString().split('T')[0];
        let autoEndDate = '';
        let autoHours = 12;

        if (staffPickerTargetLead?.id) {
            const { data: quote } = await supabase
                .from('crm_quotations')
                .select('*')
                .eq('lead_id', staffPickerTargetLead.id)
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle();

            const { data: consent } = await supabase
                .from('client_consents')
                .select('*')
                .eq('lead_id', staffPickerTargetLead.id)
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle();

            if (quote?.start_date) {
                autoStartDate = quote.start_date.split('T')[0];
            } else if (consent?.service_start_date) {
                autoStartDate = consent.service_start_date.split('T')[0];
            }

            if (quote?.hours_per_day) {
                autoHours = quote.hours_per_day;
            } else if (consent?.offered_time) {
                const hourMatch = consent.offered_time.match(/(\d+)/);
                if (hourMatch) autoHours = parseInt(hourMatch[1]);
            }
        }

        // Check if THIS worker was previously assigned and released/completed for this client
        let latestLockedDate: string | null = null;
        if (worker?.id && staffPickerTargetLead?.id) {
            const { data: pastAssignments } = await supabase
                .from('worker_assignments')
                .select('id, end_date, start_date')
                .eq('employee_id', worker.id)
                .eq('client_id', staffPickerTargetLead.id)
                .in('assignment_status', ['completed', 'relieved', 'ended']);

            (pastAssignments || []).forEach((p: any) => {
                const ed = p.end_date ? p.end_date.split('T')[0] : null;
                if (ed && (!latestLockedDate || ed > latestLockedDate)) {
                    latestLockedDate = ed;
                }
            });

            const pastIds = (pastAssignments || []).map((p: any) => p.id);
            if (pastIds.length > 0) {
                const { data: attLogs } = await supabase
                    .from('attendance')
                    .select('duty_date')
                    .eq('worker_id', worker.id)
                    .in('assignment_id', pastIds)
                    .order('duty_date', { ascending: false })
                    .limit(1);
                if (attLogs?.[0]?.duty_date && (!latestLockedDate || attLogs[0].duty_date > latestLockedDate)) {
                    latestLockedDate = attLogs[0].duty_date;
                }
            }

            // Check locked final payroll records
            const { data: finalPayrolls } = await supabase
                .from('payroll')
                .select('period_end')
                .eq('worker_id', worker.id)
                .eq('type', 'final')
                .order('period_end', { ascending: false })
                .limit(1);
            if (finalPayrolls?.[0]?.period_end && (!latestLockedDate || finalPayrolls[0].period_end > latestLockedDate)) {
                latestLockedDate = finalPayrolls[0].period_end;
            }
        }

        if (latestLockedDate) {
            const d = new Date(latestLockedDate + 'T00:00:00');
            d.setDate(d.getDate() + 1);
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            const nextAllowed = `${yyyy}-${mm}-${dd}`;
            if (!autoStartDate || autoStartDate <= latestLockedDate) {
                autoStartDate = nextAllowed;
            }
            toast.info(`Worker was previously relieved up to ${latestLockedDate}. Next assignment can only start from ${nextAllowed}.`);
        }

        // Always default to ongoing (empty string / null)
        setServiceType('date_range');
        setServiceStartDate(autoStartDate);
        setServiceEndDate(autoEndDate);
        setServiceHours(autoHours);
        setCalculatedBill(0);
    };

    const handleConfirmServicePeriod = async () => {
        if (!selectedWorker || !staffPickerTargetLead) return;

        setIsServicePeriodOpen(false);
        const toastId = toast.loading(`Creating assignment for ${selectedWorker.name || selectedWorker.full_name}...`);

        try {
            // Create the assignment + ID card link FIRST
            const result = await assignWorkerToClient(
                selectedWorker.id,
                staffPickerTargetLead.id,
                undefined,
                0,   // depositPaid
                true, // skipWhatsApp
                {
                    startDate: serviceStartDate,
                    endDate: serviceEndDate ? serviceEndDate : undefined,
                    serviceType: 'date_range',
                    hoursPerDay: serviceHours,
                    totalBillAmount: 0
                }
            );

            // Store the result
            setAssignmentResult(result);

            // ── Also create or update the Services model record ──────────
            try {
                // Check Supabase directly for an active service to prevent duplicate service rows
                const { data: dbActiveSvc } = await supabase
                    .from('services')
                    .select('id, deposit_amount')
                    .or(`client_id.eq.${staffPickerTargetLead.id},lead_id.eq.${staffPickerTargetLead.id}`)
                    .eq('status', 'active')
                    .maybeSingle();

                let targetServiceId = dbActiveSvc?.id;

                // Fetch quotation and lead info for rates
                const { data: quote } = await supabase
                    .from('crm_quotations')
                    .select('complete_month_rate, incomplete_month_rate, deposit')
                    .eq('lead_id', staffPickerTargetLead.id)
                    .order('created_at', { ascending: false })
                    .limit(1)
                    .maybeSingle();

                const { data: lead } = await supabase
                    .from('crm_leads')
                    .select('complete_month_daily_rate, incomplete_month_daily_rate, assigned_worker_role, notes, quoted_monthly_rate, estimated_value_monthly')
                    .eq('id', staffPickerTargetLead.id)
                    .maybeSingle();

                const cmRate = quote?.complete_month_rate || lead?.complete_month_daily_rate || (serviceHours === 24 ? 1000 : 500);
                const icmRate = quote?.incomplete_month_rate || lead?.incomplete_month_daily_rate || (serviceHours === 24 ? 2000 : 1000);
                const depositAmt = quote?.deposit || lead?.quoted_monthly_rate || lead?.estimated_value_monthly || 15000;

                if (!targetServiceId) {
                    let resolvedServiceName = lead?.assigned_worker_role;
                    if (!resolvedServiceName && lead?.notes) {
                        const match = lead.notes.match(/Service:\s*([^\n\r]+)/i);
                        if (match && match[1]) resolvedServiceName = match[1].trim();
                    }
                    if (!resolvedServiceName) resolvedServiceName = 'Home Care Service';

                    // Create service record
                    const { data: newService, error: svcError } = await supabase
                        .from('services')
                        .insert({
                            client_id: staffPickerTargetLead.id,
                            lead_id: staffPickerTargetLead.id,
                            service_type: resolvedServiceName,
                            hours_per_day: serviceHours,
                            start_date: serviceStartDate,
                            end_date: serviceEndDate ? serviceEndDate : null,
                            status: 'active',
                            deposit_amount: depositAmt,
                            deposit_status: 'pending',
                            complete_month_daily_rate: cmRate,
                            incomplete_month_daily_rate: icmRate,
                            legacy_assignment_id: result.assignment?.id || null,
                        })
                        .select('id')
                        .single();

                    if (svcError) throw svcError;
                    targetServiceId = newService?.id;
                } else {
                    // Update existing active service with proper rates, legacy assignment, and deposit amount
                    await supabase
                        .from('services')
                        .update({
                            legacy_assignment_id: result.assignment?.id || null,
                            start_date: serviceStartDate,
                            end_date: serviceEndDate ? serviceEndDate : null,
                            hours_per_day: serviceHours,
                            complete_month_daily_rate: cmRate,
                            incomplete_month_daily_rate: icmRate,
                            deposit_amount: (dbActiveSvc?.deposit_amount && dbActiveSvc.deposit_amount > 0) ? dbActiveSvc.deposit_amount : depositAmt,
                        })
                        .eq('id', targetServiceId);
                }

                if (targetServiceId) {
                    const { data: existingSwa } = await supabase
                        .from('service_worker_assignments')
                        .select('id')
                        .eq('service_id', targetServiceId)
                        .eq('employee_id', selectedWorker.id)
                        .is('end_date', null)
                        .maybeSingle();

                    if (!existingSwa) {
                        await supabase.from('service_worker_assignments').insert({
                            service_id: targetServiceId,
                            employee_id: selectedWorker.id,
                            start_date: serviceStartDate,
                            end_date: serviceEndDate ? serviceEndDate : null,
                        });
                    }
                }
            } catch (svcErr) {
                console.error('Failed to sync with services table:', svcErr);
            }

            // Open the WhatsApp modal with ID card link
            setAgentTargetLead(staffPickerTargetLead);
            setAgentTargetAction('staff');
            setIsEditingTemplate(false);
            setStaffModalActiveTab('client');
            setWorkerSentSuccess(false);

            const draft = generateWhatsappDraft(
                staffPickerTargetLead.name,
                'staff',
                agentDraftLang,
                selectedWorker,
                result.shareableUrl
            );
            setAgentDraftText(draft);

            // Generate worker assignment draft
            const wDraft = generateWorkerAssignmentDraft(
                selectedWorker,
                staffPickerTargetLead,
                serviceStartDate,
                serviceHours
            );
            setWorkerDraftText(wDraft);

            setIsAgentModalOpen(true);

            toast.success(
                `${selectedWorker.name || selectedWorker.full_name} assigned! Review message to client and worker below.`,
                { id: toastId, duration: 3000 }
            );
            fetchLeads();
        } catch (err: any) {
            console.error('Assignment creation failed:', err);
            toast.error(
                `Failed to create assignment: ${err.message}`,
                { id: toastId }
            );
        }
    };

    const fetchClientInvoiceAttendanceCRM = async (startStr: string, endStr: string, targetLead?: any, targetSvc?: any, targetAsgn?: any) => {
        const lead = targetLead || clientInvoiceLead;
        if (!lead || !startStr || !endStr) return;

        setIsCiLoadingAttendance(true);
        try {
            const svc = targetSvc || lead._svc;
            const asgn = targetAsgn || lead._asgn;
            const clientId = lead.client_id || lead.id || svc?.client_id;
            let workerIds: string[] = [];

            if (lead.assigned_worker_id) workerIds.push(lead.assigned_worker_id);
            if (asgn?.employee_id) workerIds.push(asgn.employee_id);
            if (svc) {
                (svc.service_worker_assignments || []).forEach((a: any) => {
                    if (a.employee_id || a.worker_id) workerIds.push(a.employee_id || a.worker_id);
                });
            }

            // Only fallback to worker_assignments if no workers are found on the service/lead, and only active ones
            if (workerIds.length === 0 && clientId) {
                const { data: wAssignments } = await supabase
                    .from('worker_assignments')
                    .select('employee_id')
                    .eq('client_id', clientId)
                    .eq('assignment_status', 'active');
                if (wAssignments) {
                    wAssignments.forEach((a: any) => {
                        if (a.employee_id) workerIds.push(a.employee_id);
                    });
                }
            }

            workerIds = Array.from(new Set(workerIds)).filter(Boolean);

            if (workerIds.length === 0) {
                const d1 = new Date(startStr);
                const d2 = new Date(endStr);
                const calDays = (!isNaN(d1.getTime()) && !isNaN(d2.getTime()) && d2 >= d1)
                    ? Math.max(1, Math.round((d2.getTime() - d1.getTime()) / (1000 * 60 * 60 * 24)) + 1)
                    : 1;
                setCiAttendanceSummary({
                    totalCalendarDays: calDays,
                    fullDays: calDays,
                    halfDays: 0,
                    absentDays: 0,
                    effectiveDays: calDays,
                    halfDayDates: [],
                    absentDates: [],
                    fullDayDates: [],
                });
                setCiDays(calDays);
                setCiAttendanceVerified(false);
                return;
            }

            const { data, error } = await supabase
                .from('attendance')
                .select('worker_id, duty_date, status, is_half_day, is_absent')
                .in('worker_id', workerIds)
                .gte('duty_date', startStr)
                .lte('duty_date', endStr);

            if (error) throw error;

            const summary = calculateClientAttendanceSummary(startStr, endStr, data || []);
            setCiAttendanceSummary(summary);
            setCiDays(summary.effectiveDays);
            setCiAttendanceVerified(Boolean(data && data.length > 0));
        } catch (err) {
            console.error('Error fetching CRM client invoice attendance:', err);
            const d1 = new Date(startStr);
            const d2 = new Date(endStr);
            const calDays = (!isNaN(d1.getTime()) && !isNaN(d2.getTime()) && d2 >= d1)
                ? Math.max(1, Math.round((d2.getTime() - d1.getTime()) / (1000 * 60 * 60 * 24)) + 1)
                : 1;
            setCiAttendanceSummary({
                totalCalendarDays: calDays,
                fullDays: calDays,
                halfDays: 0,
                absentDays: 0,
                effectiveDays: calDays,
                halfDayDates: [],
                absentDates: [],
                fullDayDates: [],
            });
        } finally {
            setIsCiLoadingAttendance(false);
        }
    };

    const openClientInvoiceGenerator = async (lead: any) => {
        setCiLeadId(lead.id || '');

        const [asgnRes, svcRes, quoteRes, billsRes] = await Promise.all([
            supabase.from('worker_assignments').select('*').eq('client_id', lead.id).eq('assignment_status', 'active').maybeSingle(),
            supabase.from('services').select('*, service_worker_assignments(*)').or(`client_id.eq.${lead.id},lead_id.eq.${lead.id}`).eq('status', 'active').maybeSingle(),
            supabase.from('crm_quotations').select('*').eq('lead_id', lead.id).order('created_at', { ascending: false }).limit(1).maybeSingle(),
            supabase.from('service_bills').select('*').order('period_end', { ascending: false }),
        ]);

        const asgn = asgnRes.data;
        const svc = svcRes.data;
        const quote = quoteRes.data;

        const fullLead = { ...lead, _svc: svc, _asgn: asgn };
        setClientInvoiceLead(fullLead);

        // 1. Resolve agreed daily rate & deposit
        const resolvedRate = svc?.complete_month_daily_rate || quote?.complete_month_rate || lead.complete_month_daily_rate || asgn?.client_billing_rate || parseInt(lead.quoted_monthly_rate?.replace(/[^0-9]/g, '') || '0') || 500;
        const resolvedDeposit = svc?.deposit_amount || quote?.deposit || asgn?.deposit_amount || 5000;

        // 2. Resolve start date (day after latest bill, or service start date)
        let defaultStart = svc?.start_date ? svc.start_date.split('T')[0] : (asgn?.start_date ? asgn.start_date.split('T')[0] : '');
        if (svc?.id) {
            const svcBills = (billsRes.data || []).filter((b: any) => b.service_id === svc.id && b.period_end);
            if (svcBills.length > 0) {
                const nextDay = new Date(svcBills[0].period_end);
                nextDay.setDate(nextDay.getDate() + 1);
                defaultStart = nextDay.toISOString().split('T')[0];
            }
        }

        // 3. Resolve end date (service end date if set, otherwise calendar month-end of defaultStart)
        let defaultEnd = (svc?.end_date || asgn?.end_date) ? (svc?.end_date || asgn?.end_date).split('T')[0] : '';
        if (!defaultEnd && defaultStart) {
            const [y, m] = defaultStart.split('-').map(Number);
            const lastDay = new Date(y, m, 0);
            defaultEnd = `${y}-${String(m).padStart(2, '0')}-${String(lastDay.getDate()).padStart(2, '0')}`;
        } else if (!defaultEnd) {
            defaultEnd = new Date().toISOString().split('T')[0];
        }

        setCiRate(resolvedRate);
        setCiDeposit(resolvedDeposit);
        setCiStartDate(defaultStart);
        setCiEndDate(defaultEnd);

        // 4. Calculate initial calendar days
        let calculatedDays = 1;
        if (defaultStart && defaultEnd) {
            const d1 = new Date(defaultStart);
            const d2 = new Date(defaultEnd);
            if (!isNaN(d1.getTime()) && !isNaN(d2.getTime()) && d2 >= d1) {
                calculatedDays = Math.max(1, Math.round((d2.getTime() - d1.getTime()) / (1000 * 60 * 60 * 24)) + 1);
            }
        }
        setCiDays(calculatedDays);
        setIsClientInvoiceOpen(true);

        fetchClientInvoiceAttendanceCRM(defaultStart, defaultEnd, fullLead, svc, asgn);
    };

    const openAgentModal = async (lead: any, action: 'inquiry' | 'quotation' | 'consent' | 'staff' | 'deposit' | 'billing' | 'custom') => {
        if (action === 'quotation') {
            setQuotationTargetLead(lead);
            setIsQuotationModalOpen(true);
            return;
        }
        setAgentTargetLead(lead);
        setAgentTargetAction(action);
        setIsEditingTemplate(false);
        const draft = generateWhatsappDraft(lead.name, action, agentDraftLang, selectedWorker);
        setAgentDraftText(draft);
        if (action === 'deposit' || action === 'billing') {
            await prefillInvoiceFields(lead, action);
        }
        setIsAgentModalOpen(true);
    };

    useEffect(() => {
        if (agentTargetLead && !isEditingTemplate) {
            setAgentDraftText(generateWhatsappDraft(
                agentTargetLead.name,
                agentTargetAction,
                agentDraftLang,
                selectedWorker,
                assignmentResult?.shareableUrl // pass real URL if available
            ));
        } else if (isEditingTemplate) {
            setTemplateDraftText(whatsappTemplates[agentTargetAction]?.[agentDraftLang] || '');
        }
    }, [agentDraftLang, agentTargetAction, agentTargetLead, whatsappTemplates, isEditingTemplate, selectedWorker, assignmentResult]);

    // Initialize per-call greeting status from DB and auto-trigger for the latest new call
    useEffect(() => {
        if (calls.length > 0) {
            const initialStatus: Record<string, 'sending' | 'sent' | 'error'> = {};

            calls.forEach((call: any) => {
                if (call.automation_error === 'GREETING_SENT') {
                    initialStatus[call.id] = 'sent';
                } else if (call.automation_error?.startsWith('GREETING_ERROR') && !isRetryableGreetingError(call.automation_error)) {
                    initialStatus[call.id] = 'error';
                }
            });

            // Update UI state with DB data
            setCallGreetingStatus(prev => {
                const next = { ...prev };
                calls.forEach((call: any) => {
                    if (isRetryableGreetingError(call.automation_error) && next[call.id] === 'error') {
                        delete next[call.id];
                    }
                });
                return { ...initialStatus, ...next };
            });

            // Step 2: AUTO-TRIGGER — only for the SINGLE most recent ungreeted call.
            // Uses a 3-minute window (tightened from 5) and a persistent ref to prevent
            // the useEffect from re-evaluating the same call on every `calls` state change.
            const threeMinsAgo = Date.now() - (3 * 60 * 1000);

            const targetCall = calls.find((call: any) => {
                const phone = (call.capturedWhatsapp || '').toString();
                const digits = phone.replace(/\D/g, '');
                const isToday = new Date(call.created_at).toDateString() === new Date().toDateString();
                const callTime = new Date(call.created_at).getTime();
                const isRecent = callTime > threeMinsAgo;

                // CRITICAL: Check local UI state + the mutex ref + persistent ref to avoid double-firing
                const alreadyHandled = !!callGreetingStatus[call.id] || processingCalls.current.has(call.id);
                const alreadyAttempted = autoGreetAttempted.current.has(String(call.id));
                const dbAlreadyLogged = !!call.automation_error;
                const lead = leads.find(l => l.id === call.lead_id);
                const isNewLead = !lead || ['New', 'New Lead', 'New Inquiry'].includes(lead.pipeline_stage);

                return digits.length >= 10 && isRecent && isToday && !dbAlreadyLogged && !alreadyHandled && !alreadyAttempted && isNewLead;
            });

            if (targetCall) {
                // Mark as attempted BEFORE calling to prevent re-fires from subsequent renders
                autoGreetAttempted.current.add(String(targetCall.id));
                console.log(`[Auto-Greet] Triggering for: ${targetCall.id}`);
                handleSendCallGreeting(targetCall);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [calls]);

    const handleSaveTemplate = () => {
        const updated = {
            ...whatsappTemplates,
            [agentTargetAction]: {
                ...whatsappTemplates[agentTargetAction],
                [agentDraftLang]: templateDraftText
            }
        };
        setWhatsappTemplates(updated);

        setIsEditingTemplate(false);
        toast.success("Default template saved successfully!");
    };

    // Helper to extract numeric values from strings like "₹45,000" or "1500/day"
    const parsePrice = (str: string) => {
        if (!str) return 0;
        const matched = str.replace(/[^\d]/g, '');
        return parseFloat(matched) || 0;
    };

    const formatLocalDateInput = (date: Date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    const toDateInputValue = (value: any) => {
        if (!value) return '';
        if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
        const parsed = new Date(value);
        return isNaN(parsed.getTime()) ? '' : formatLocalDateInput(parsed);
    };

    const addInclusivePeriod = (startValue: string, amount: number, unit: 'day' | 'month' | 'year') => {
        const start = new Date(`${startValue}T00:00:00`);
        if (isNaN(start.getTime())) return '';
        if (unit === 'day') start.setDate(start.getDate() + amount - 1);
        if (unit === 'month') start.setMonth(start.getMonth() + amount);
        if (unit === 'year') start.setFullYear(start.getFullYear() + amount);
        if (unit !== 'day') start.setDate(start.getDate() - 1);
        return formatLocalDateInput(start);
    };

    const deriveInvoiceEndDate = (startValue: string, duration?: string | null) => {
        if (!startValue) return '';
        const raw = (duration || '').trim();
        if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;

        const lower = raw.toLowerCase();
        if (!lower || lower.includes('open') || lower.includes('ongoing') || lower.includes('indefinite')) {
            return addInclusivePeriod(startValue, 1, 'month');
        }

        const match = lower.match(/(\d+)\s*(day|month|year)s?/);
        if (!match) return addInclusivePeriod(startValue, 1, 'month');
        return addInclusivePeriod(startValue, parseInt(match[1], 10), match[2] as 'day' | 'month' | 'year');
    };

    const prefillInvoiceFields = async (lead: any, action: 'deposit' | 'billing') => {
        const today = new Date();
        const due = new Date(today);
        due.setDate(due.getDate() + 2);

        let assignment: any = null;
        let quote = getLatestByCreatedAt(lead?.crm_quotations);
        let consent = getLatestByCreatedAt(lead?.client_consents);

        if (lead?.id) {
            const [{ data: asgn }, { data: latestQuote }, { data: latestConsent }] = await Promise.all([
                supabase
                    .from('worker_assignments')
                    .select('*')
                    .eq('client_id', lead.id)
                    .order('created_at', { ascending: false })
                    .limit(1)
                    .maybeSingle(),
                supabase
                    .from('crm_quotations')
                    .select('*')
                    .eq('lead_id', lead.id)
                    .order('created_at', { ascending: false })
                    .limit(1)
                    .maybeSingle(),
                supabase
                    .from('client_consents')
                    .select('*')
                    .eq('lead_id', lead.id)
                    .order('created_at', { ascending: false })
                    .limit(1)
                    .maybeSingle(),
            ]);

            assignment = asgn;
            quote = latestQuote || quote;
            consent = latestConsent || consent;
        }

        const startDate = toDateInputValue(
            assignment?.start_date
            || quote?.start_date
            || consent?.service_start_date
            || today
        );
        const endDate = toDateInputValue(assignment?.end_date) || deriveInvoiceEndDate(startDate, quote?.duration);
        const amount = action === 'deposit'
            ? assignment?.deposit_amount || quote?.deposit || lead?.quoted_monthly_rate || lead?.estimated_value_monthly || 15000
            : assignment?.client_billing_rate || quote?.estimated_monthly_total || lead?.estimated_value_monthly || lead?.quoted_monthly_rate || 15000;

        setInvoiceDepositAmount(String(amount || ''));
        setInvoiceDueDate(formatLocalDateInput(due));
        setInvoiceStartDate(startDate);
        setInvoiceEndDate(endDate);
    };

    const normalizeConsentOfferedTime = (value?: string | null) => {
        const raw = (value || '').trim().toLowerCase();
        if (raw.includes('24') || raw.includes('live')) return '24 Hours (Live-in)';
        if (raw.includes('10') || raw.includes('12') || raw.includes('day') || raw.includes('night')) return '10 Hours';
        return raw ? 'Other' : '';
    };

    const buildConsentFlowPrefill = (lead: any, extra: Record<string, string> = {}) =>
        buildConsentFlowActionData(lead, {
            consent: getLatestByCreatedAt(lead?.client_consents),
            quote: getLatestByCreatedAt(lead?.crm_quotations),
            normalizeOfferedTime: normalizeConsentOfferedTime,
            extra,
        });

    const buildBabyCareFlowPrefill = (lead: any, extra: Record<string, string> = {}) =>
        buildBabyCareFlowActionData(lead, {
            consent: getLatestByCreatedAt(lead?.client_consents),
            quote: getLatestByCreatedAt(lead?.crm_quotations),
            extra,
        });

    const buildPatientCareFlowPrefill = (lead: any, extra: Record<string, string> = {}) =>
        buildPatientCareFlowActionData(lead, {
            consent: getLatestByCreatedAt(lead?.client_consents),
            quote: getLatestByCreatedAt(lead?.crm_quotations),
            extra,
        });

    // Resolves lead service and dispatches the correct secondary work form
    const dispatchWorkFormForLead = async (lead: any, refQuote?: any) => {
        const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
        const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

        let phoneDigits = (lead.whatsapp_number || lead.phone || '').replace(/\D/g, '');
        if (phoneDigits.length === 10) phoneDigits = `91${phoneDigits}`;
        if (!phoneDigits) return;

        // Resolve service from provided quote, then latest quotation, then latest consent
        const quote = refQuote || getLatestByCreatedAt(lead?.crm_quotations);
        const consent = getLatestByCreatedAt(lead?.client_consents);
        const service = String(
            quote?.service_category || quote?.service_name ||
            consent?.service_category || consent?.service_name ||
            lead?.service_category || ''
        ).toLowerCase();

        console.log('[WorkForm] Resolving service for secondary dispatch:', service);

        let templateName: string | null = null;
        let flowData: any = null;

        if (/baby care|new born baby care|maternity care|jhapa care|japa care/.test(service)) {
            templateName = 'baby_care_form';
            flowData = buildBabyCareFlowPrefill(lead);
        } else if (/old age care|nursing care/.test(service)) {
            templateName = 'patient_care_form';
            flowData = buildPatientCareFlowPrefill(lead);
        } else {
            console.warn('[WorkForm] No matching service category for work form dispatch. Service was:', service);
            return;
        }

        const res = await fetch(`${SUPABASE_URL}/functions/v1/meta-whatsapp-outbound`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${SUPABASE_ANON_KEY}`, 'apikey': SUPABASE_ANON_KEY },
            body: JSON.stringify({
                phone: phoneDigits,
                leadName: lead?.name?.split(/\s+/)[0] || 'there',
                leadId: lead.id,
                useTemplate: true,
                templateName,
                flowData,
            })
        });

        const resData = await res.json().catch(() => ({}));
        if (!res.ok || resData.success === false) {
            throw new Error(resData.error || `Dispatch failed with status ${res.status}`);
        }

        toast.success(`Work Form dispatched to ${lead.name}!`);
    };

    const handleOpenManualWorkForm = (lead: any) => {
        if (!lead) return;
        const existing = lead.work_form_data;

        // Auto-detect form type from service or existing form
        let initialType: 'baby_care_form' | 'patient_care_form' = 'patient_care_form';
        if (existing?.form_type === 'baby_care_form') {
            initialType = 'baby_care_form';
        } else if (existing?.form_type === 'patient_care_form') {
            initialType = 'patient_care_form';
        } else {
            const s = String(
                lead.crm_quotations?.[0]?.service_category ||
                lead.crm_quotations?.[0]?.service_name ||
                lead.client_consents?.[0]?.service_category ||
                lead.service_category ||
                lead.notes ||
                ''
            ).toLowerCase();
            if (/baby|maternity|jhapa|japa|infant|child/.test(s)) {
                initialType = 'baby_care_form';
            }
        }

        const patientName = existing?.patient_name || lead.client_consents?.[0]?.patient_name || lead.name || '';
        const duties = Array.isArray(existing?.duties) ? [...existing.duties] : [];
        const otherWork = existing?.other_work || '';

        setManualWorkFormModal({
            isOpen: true,
            lead,
            formType: initialType,
            patientName,
            duties,
            otherWork,
            isSaving: false
        });
    };

    const toggleManualDuty = (dutyKey: string) => {
        if (!manualWorkFormModal) return;
        const current = manualWorkFormModal.duties;
        const next = current.includes(dutyKey)
            ? current.filter(k => k !== dutyKey)
            : [...current, dutyKey];
        setManualWorkFormModal({
            ...manualWorkFormModal,
            duties: next
        });
    };

    const handleSaveManualWorkForm = async () => {
        if (!manualWorkFormModal || !manualWorkFormModal.lead) return;
        const { lead, formType, patientName, duties, otherWork } = manualWorkFormModal;

        setManualWorkFormModal(prev => prev ? { ...prev, isSaving: true } : null);
        const toastId = toast.loading('Saving work duties checklist...');
        try {
            const payload = {
                form_type: formType,
                patient_name: patientName.trim(),
                duties,
                other_work: otherWork.trim(),
                updated_at: new Date().toISOString()
            };

            const { error: leadErr } = await supabase
                .from('crm_leads')
                .update({ work_form_data: payload })
                .eq('id', lead.id);

            if (leadErr) throw leadErr;

            // Mirror to client_work_forms for redundancy
            try {
                await supabase.from('client_work_forms').insert([{
                    lead_id: lead.id,
                    form_type: formType,
                    patient_name: patientName.trim(),
                    duties: duties,
                    other_work: otherWork.trim()
                }]);
            } catch (e) {
                console.warn('Could not mirror to client_work_forms', e);
            }

            // Log activity
            await logActivity(
                lead.id,
                'form_filled',
                `Work duties checklist saved manually (${formType === 'baby_care_form' ? 'Baby Care' : 'Patient Care'}: ${duties.length} duties)`,
                { duties_count: duties.length, form_type: formType }
            );

            // Update local state
            setSelectedInspectorLead((prev: any) => prev && prev.id === lead.id ? { ...prev, work_form_data: payload } : prev);
            setLeads((prev: any[]) => prev.map(l => l.id === lead.id ? { ...l, work_form_data: payload } : l));

            toast.success('✅ Work duties recorded successfully!', { id: toastId });
            setManualWorkFormModal(null);
        } catch (err: any) {
            console.error('Failed to save manual work form:', err);
            toast.error(`Failed to save: ${err.message || 'Unknown error'}`, { id: toastId });
            setManualWorkFormModal(prev => prev ? { ...prev, isSaving: false } : null);
        }
    };

    const handleDispatchWorkFormFromDrawer = async (lead: any) => {
        setIsDispatchingWorkForm(true);
        const toastId = toast.loading('Dispatching Work Form via WhatsApp...');
        try {
            await dispatchWorkFormForLead(lead);
        } catch (err: any) {
            console.error('Failed to dispatch work form:', err);
            toast.error(`Dispatch failed: ${err.message || 'Unknown error'}`, { id: toastId });
        } finally {
            setIsDispatchingWorkForm(false);
        }
    };


    const dispatchWhatsAppTemplate = async (lead: any, action: string, params?: string[], flowData?: any) => {
        const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
        const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

        let phoneDigits = (lead.whatsapp_number || lead.phone || '').replace(/\D/g, '');
        if (phoneDigits.length === 10) phoneDigits = `91${phoneDigits}`;
        if (!phoneDigits) throw new Error("No phone number found.");

        const templateMap: Record<string, string> = {
            inquiry: 'post_call_intake',
            quotation: 'quote_client_v2',
            consent: 'consent_form',
            baby_care: 'baby_care_form',
            deposit: 'deposit_request',
            staff: 'staff_assignment'
        };

        const response = await fetch(`${SUPABASE_URL}/functions/v1/meta-whatsapp-outbound`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
                'apikey': SUPABASE_ANON_KEY,
            },
            body: JSON.stringify({
                phone: phoneDigits,
                leadId: lead.id,
                leadName: lead.name?.split(/\s+/)[0] || 'there',
                leadFullName: resolveLeadDisplayName(lead) || undefined,
                useTemplate: true,
                templateName: templateMap[action],
                templateParams: params || (action === 'inquiry' ? buildLeadIntakePrefill(lead).templateParams : [lead.name?.split(' ')[0] || 'there']),
                flowData: flowData || (action === 'inquiry' ? buildLeadIntakePrefill(lead).flowData : action === 'consent' ? buildConsentFlowPrefill(lead) : action === 'baby_care' ? buildBabyCareFlowPrefill(lead) : undefined),
            })
        });

        const resData = await response.json().catch(() => ({}));
        if (!response.ok || resData.success === false) {
            throw new Error(resData.error || `Dispatch failed: HTTP ${response.status}`);
        }
        return resData;
    };

    const handleDispatchQuotation = async (quotationData: any) => {
        setIsQuotationModalOpen(false);
        const toastId = toast.loading(`Sending quotation to ${quotationTargetLead.name}...`);

        try {
            // 1. Save to DB
            const { error: dbError } = await supabase.from('crm_quotations').insert({
                lead_id: quotationTargetLead.id,
                service_name: quotationData.serviceName,
                service_category: quotationData.serviceCategory,
                recipient_age_condition: quotationData.recipientCondition,
                hours_per_day: quotationData.hoursPerDay,
                days_per_week: quotationData.daysPerWeek,
                shift_type: quotationData.shiftType,
                start_date: quotationData.startDate || null,
                duration: quotationData.duration,
                complete_month_rate: quotationData.completeMonthRate,
                incomplete_month_rate: quotationData.incompleteMonthRate,
                setup_fee: quotationData.setupFee,
                deposit: quotationData.deposit,
                estimated_monthly_total: quotationData.estimatedTotal,
                inclusions: quotationData.inclusions,
                message_template: quotationData.messageTemplate,
                language: quotationData.language,
                custom_message: quotationData.customMessage,
                valid_until: quotationData.validUntil || null
            });

            if (dbError) throw dbError;

            // 2. Format Whatsapp message payload (Structured text)
            let msgText = `*SERVICE QUOTATION*\n`;
            msgText += `*${quotationData.serviceName}*\n`;
            if (quotationData.serviceCategory) msgText += `${quotationData.serviceCategory}\n\n`;

            if (quotationData.hoursPerDay) msgText += `Hours per day: ${quotationData.hoursPerDay} hrs - ${quotationData.shiftType}\n`;
            if (quotationData.daysPerWeek) msgText += `Days per week: ${quotationData.daysPerWeek} days\n`;
            if (quotationData.startDate) msgText += `Proposed start: ${new Date(quotationData.startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}\n`;
            if (quotationData.duration) msgText += `Duration: ${quotationData.duration}\n`;

            msgText += `Rate (full month): ₹${quotationData.completeMonthRate} / day\n`;
            msgText += `Rate (partial month): ₹${quotationData.incompleteMonthRate} / day\n`;
            if (quotationData.deposit) msgText += `Deposit: ₹${quotationData.deposit}\n`;

            if (quotationData.includeEstimatedTotal) {
                if (quotationData.isShortTerm) {
                    const days = quotationData.serviceDays || 1;
                    const rate = quotationData.incompleteMonthRate || 0;
                    msgText += `\n*Estimated service total: ₹${quotationData.estimatedTotal.toLocaleString('en-IN')}*\n`;
                    if (rate > 0) {
                        msgText += `${days} day${days !== 1 ? 's' : ''} × ₹${Number(rate).toLocaleString('en-IN')}/day\n\n`;
                    } else {
                        msgText += `${days} day${days !== 1 ? 's' : ''} service\n\n`;
                    }
                } else {
                    msgText += `\n*Estimated monthly total: ₹${quotationData.estimatedTotal.toLocaleString('en-IN')} / mo*\n\n`;
                }
            }

            if (quotationData.inclusions && quotationData.inclusions.length > 0) {
                msgText += `*What is included*\n${quotationData.inclusions.join(', ')}\n\n`;
            }

            if (quotationData.customMessage) {
                msgText += `${quotationData.customMessage}\n`;
            }

            if (quotationData.validUntil) {
                msgText += `_This quote is valid until ${new Date(quotationData.validUntil).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}_`;
            }

            // Meta template params cannot contain newlines or multiple spaces.
            // So we send the full formatted message as a standard text first,
            // then send the template with a summarized param to show the buttons.

            const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
            const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

            // 1. Send full quotation as standard text (preserves newlines & formatting)
            await fetch(`${SUPABASE_URL}/functions/v1/meta-whatsapp-outbound`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
                    'apikey': SUPABASE_ANON_KEY,
                },
                body: JSON.stringify({
                    phone: quotationTargetLead.whatsapp_number || quotationTargetLead.phone,
                    message: msgText,
                    useTemplate: false,
                    leadId: quotationTargetLead.id
                })
            });

            // 2. Send the template with buttons using a newline-free summary
            let summaryParam = '';
            if (quotationData.includeEstimatedTotal) {
                summaryParam = quotationData.isShortTerm
                    ? `Estimated service total: ₹${quotationData.estimatedTotal.toLocaleString('en-IN')} (${quotationData.serviceDays || 1} days)`
                    : `Estimated monthly total: ₹${quotationData.estimatedTotal.toLocaleString('en-IN')}/mo`;
            } else {
                summaryParam = `Service quotation for ${quotationData.serviceName}`;
            }
            const templateLogText = `Hello, please find the quotation details for your care request below:\n${summaryParam}\nPlease use the buttons below to respond or schedule a follow-up. We look forward to assisting your family.`;

            const payload = {
                phone: quotationTargetLead.whatsapp_number || quotationTargetLead.phone,
                leadName: quotationTargetLead.name,
                message: templateLogText, // passed for logging so it looks accurate in CRM
                useTemplate: true,
                templateName: 'quote_client_v2',
                templateParams: [summaryParam],
                leadId: quotationTargetLead.id
            };

            const response = await fetch(`${SUPABASE_URL}/functions/v1/meta-whatsapp-outbound`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
                    'apikey': SUPABASE_ANON_KEY,
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) throw new Error('Failed to send WhatsApp message');

            const data = await response.json();
            if (data.success === false) {
                throw new Error(data.error || 'Meta API rejected the message.');
            }

            // 3. Log Activity (stage is NOT changed here — it stays 'In Discussion'
            //    until the lead accepts the quote via WhatsApp or admin clicks 'Quotation Approved')
            await logActivity(
                quotationTargetLead.id,
                'quotation_sent',
                `Quotation dispatched via WhatsApp: ₹${quotationData.estimatedTotal}${quotationData.isShortTerm ? '' : '/mo'} for ${quotationData.serviceName}`
            );

            // 5. Update Lead Value and Rates in DB
            const { error: updateError } = await supabase
                .from('crm_leads')
                .update({
                    estimated_value_monthly: quotationData.estimatedTotal,
                    complete_month_daily_rate: quotationData.completeMonthRate || null,
                    incomplete_month_daily_rate: quotationData.incompleteMonthRate || null,
                })
                .eq('id', quotationTargetLead.id);

            if (updateError) {
                console.error('Failed to update lead value & rates:', updateError);
            }

            // 6. Auto-populate Service + Shift into notes so bubbles show on card
            const { data: existingLead } = await supabase.from('crm_leads').select('notes').eq('id', quotationTargetLead.id).single();
            const serviceEntry = `Service: ${quotationData.serviceName}${quotationData.shiftType ? `\nShift: ${quotationData.shiftType}` : ''}`;
            let updatedNotes = serviceEntry;
            if (existingLead?.notes) {
                // Replace old Service/Shift lines if present, otherwise prepend
                const hasServiceLine = /^Service:/m.test(existingLead.notes);
                if (!hasServiceLine) {
                    updatedNotes = `${serviceEntry}\n\n${existingLead.notes}`;
                } else {
                    // Leave existing notes untouched (already has service info)
                    updatedNotes = existingLead.notes;
                }
            }
            await supabase.from('crm_leads').update({ notes: updatedNotes }).eq('id', quotationTargetLead.id);

            // Sync UI: Update Lead Value, Notes and Activity Timeline
            if (selectedInspectorLead && selectedInspectorLead.id === quotationTargetLead.id) {
                const serviceDays = quotationData.serviceDays || 1;
                setSelectedInspectorLead((prev: any) => ({
                    ...prev,
                    estimated_value_monthly: quotationData.estimatedTotal,
                    valueAmount: quotationData.estimatedTotal,
                    complete_month_daily_rate: quotationData.completeMonthRate || prev?.complete_month_daily_rate,
                    incomplete_month_daily_rate: quotationData.incompleteMonthRate || prev?.incomplete_month_daily_rate,
                    serviceDays,
                    isShortTermQuote: quotationData.isShortTerm,
                    value: formatLeadValueDisplay(quotationData.estimatedTotal, serviceDays),
                    notes: updatedNotes
                }));
                fetchLeadActivity(quotationTargetLead.id, quotationTargetLead.duplicate_of_lead_id);
            }

            // Also update the main leads list
            const serviceDays = quotationData.serviceDays || 1;
            setLeads(prev => prev.map(l => l.id === quotationTargetLead.id ? {
                ...l,
                estimated_value_monthly: quotationData.estimatedTotal,
                valueAmount: quotationData.estimatedTotal,
                complete_month_daily_rate: quotationData.completeMonthRate || l.complete_month_daily_rate,
                incomplete_month_daily_rate: quotationData.incompleteMonthRate || l.incomplete_month_daily_rate,
                serviceDays,
                isShortTermQuote: quotationData.isShortTerm,
                value: formatLeadValueDisplay(quotationData.estimatedTotal, serviceDays),
                notes: updatedNotes
            } : l));

            toast.success(`Quotation successfully sent!`, { id: toastId });
        } catch (error: any) {
            console.error('Quotation dispatch error:', error);
            toast.error(`Error: ${error.message}`, { id: toastId });
        }
    };

    const handleDispatchToWorker = async () => {
        if (!selectedWorker) {
            toast.error("No worker selected!");
            return;
        }

        const workerPhone = selectedWorker?.phone;
        if (!workerPhone) {
            toast.error(`⚠️ No phone number found for ${selectedWorker?.name || 'worker'}. Please update their profile in HR.`);
            return;
        }

        let digits = workerPhone.replace(/\D/g, '');
        if (digits.length === 10) digits = `91${digits}`;

        setIsSendingToWorker(true);
        const toastId = toast.loading(`Sending assignment details to ${selectedWorker?.name || 'worker'} (+${digits})...`);

        try {
            const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
            const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

            const response = await fetch(`${SUPABASE_URL}/functions/v1/meta-whatsapp-outbound`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
                    'apikey': SUPABASE_ANON_KEY,
                },
                body: JSON.stringify({
                    phone: digits,
                    message: workerDraftText,
                    useTemplate: false,
                    leadId: agentTargetLead?.id,
                })
            });

            const resData = await response.json().catch(() => ({}));
            if (!response.ok || resData.success === false) {
                // Seamless fallback to wa.me if session closed or API error
                const encoded = encodeURIComponent(workerDraftText);
                window.open(`https://wa.me/${digits}?text=${encoded}`, '_blank');
                toast.success(`Opened WhatsApp to send to ${selectedWorker?.name || 'worker'}!`, { id: toastId });
            } else {
                toast.success(`✅ Assignment message sent to ${selectedWorker?.name || 'worker'} via WhatsApp!`, { id: toastId, duration: 5000 });
            }

            setWorkerSentSuccess(true);

            if (agentTargetLead?.id) {
                try {
                    await supabase.from('crm_lead_activity').insert([{
                        lead_id: agentTargetLead.id,
                        event_type: 'worker_notified',
                        description: `Assignment details sent to worker ${selectedWorker?.name || selectedWorker?.full_name} (+${digits})`,
                        metadata: {
                            worker_id: selectedWorker.id,
                            worker_phone: digits,
                            start_date: serviceStartDate,
                            shift_hours: serviceHours
                        }
                    }]);
                } catch (actErr: any) {
                    console.error('Activity log error:', actErr);
                }
            }
        } catch (err: any) {
            console.error("Worker dispatch error:", err);
            const encoded = encodeURIComponent(workerDraftText);
            window.open(`https://wa.me/${digits}?text=${encoded}`, '_blank');
            toast.success(`Opened WhatsApp for ${selectedWorker?.name || 'worker'}`, { id: toastId });
            setWorkerSentSuccess(true);
        } finally {
            setIsSendingToWorker(false);
        }
    };

    const handleDispatchMessage = async () => {
        setIsAgentModalOpen(false);
        const toastId = toast.loading(`Dispatching AI Message to ${agentTargetLead?.name || 'Lead'}...`);

        try {
            let phoneDigits = '918000044090'; // Default to test number
            if (agentTargetLead) {
                const targetNumber = agentTargetLead.whatsapp_number || agentTargetLead.phone;
                console.log(`[Debug] Lead: ${agentTargetLead.name}, Raw Phone: ${agentTargetLead.phone}, Raw WhatsApp: ${agentTargetLead.whatsapp_number}`);

                if (targetNumber && targetNumber !== 'Unknown Number' && targetNumber !== 'Unknown') {
                    phoneDigits = targetNumber.replace(/\D/g, '');
                    if (phoneDigits.length === 10) phoneDigits = `91${phoneDigits}`;
                } else {
                    toast.error(`⚠️ No valid number for ${agentTargetLead.name}. Falling back to test number.`, { id: toastId });
                }
            } else {
                toast.error("⚠️ No target lead selected!", { id: toastId });
            }

            console.log(`[Dispatch] Sending WhatsApp via Twilio API to ${agentTargetLead?.name}: +${phoneDigits}`);

            let finalLogMessage = agentDraftText;
            if (agentTargetAction === 'quotation') {
                finalLogMessage = `Quotation\n\nService Details for: ${quotationVars.v1}\n\nAs per your request, here are the details of the service:\n\n💰 Pricing Information\n${quotationVars.v2} Service\nFull Month: ₹${quotationVars.v3} per day\nFlexible Days: ₹${quotationVars.v4} per day\n\n🕒 Service Policy\n• 1 day leave: No replacement provided\n• More than 1 day leave: Replacement available (subject to availability)\n\n⚠️ Trial Policy\nIf the service is discontinued after a 2-day trial, charges will be same as Flexible Days rate.\n\nThis information is shared based on your inquiry. Please let us know if you need any further details.`;
            }

            const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
            const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

            let invoicePdfUrl = null;

            if (agentTargetAction === 'deposit' || agentTargetAction === 'billing') {
                toast.loading("Generating PDF Invoice...", { id: toastId });

                const formatDateStr = (dateStr: string) => {
                    if (!dateStr) return '';
                    const [y, m, d] = dateStr.split('-');
                    return `${d}/${m}/${y}`;
                };

                const formattedPeriod = (invoiceStartDate && !isInvoiceOngoing && invoiceEndDate)
                    ? `${formatDateStr(invoiceStartDate)} To ${formatDateStr(invoiceEndDate)}`
                    : invoiceStartDate
                        ? `${formatDateStr(invoiceStartDate)} To Ongoing`
                        : 'Ongoing';

                const depositVal = Number(invoiceDepositAmount || agentTargetLead?.quoted_monthly_rate || 15000);
                const invResp = await fetch(`${SUPABASE_URL}/functions/v1/generate-invoice`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
                    },
                    body: JSON.stringify({
                        lead_id: agentTargetLead?.id,
                        deposit_amount: depositVal,
                        service_period: formattedPeriod,
                        invoice_date: formatLocalDateInput(new Date()),
                        start_date: invoiceStartDate || null,
                        end_date: isInvoiceOngoing ? 'Ongoing' : (invoiceEndDate || null),
                        due_date: invoiceDueDate,
                        is_deposit: agentTargetAction === 'deposit'
                    })
                });

                if (!invResp.ok) {
                    const err = await invResp.text();
                    throw new Error(`Failed to generate invoice: ${err}`);
                }

                const invData = await invResp.json();
                invoicePdfUrl = invData.public_url;
                toast.loading("Sending via WhatsApp...", { id: toastId });
            }

            const inquiryPrefill = agentTargetAction === 'inquiry' && agentTargetLead
                ? buildLeadIntakePrefill(agentTargetLead)
                : null;
            const consentPrefill = agentTargetAction === 'consent' && agentTargetLead
                ? buildConsentFlowPrefill(agentTargetLead)
                : null;

            // Atomic greeting claim for inquiry (greeting) sends from the pipeline inspector
            if (agentTargetAction === 'inquiry' && agentTargetLead?.id) {
                const { data: claimResult } = await supabase.rpc('claim_greeting_send', {
                    target_lead_id: agentTargetLead.id
                });
                const claimSucceeded = claimResult && claimResult.length > 0;
                // Log the attempt regardless
                await supabase.from('crm_lead_activity').insert([{
                    lead_id: agentTargetLead.id,
                    event_type: 'greeting_attempt',
                    description: claimSucceeded
                        ? 'Greeting sent via pipeline inspector'
                        : 'Greeting resent via pipeline inspector (was already sent)',
                    metadata: {
                        trigger_source: 'manual',
                        outcome: claimSucceeded ? 'sent' : 'resent_override',
                        phone: phoneDigits
                    }
                }]);
            }

            const response = await fetch(`${SUPABASE_URL}/functions/v1/meta-whatsapp-outbound`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
                    'apikey': SUPABASE_ANON_KEY,
                },
                body: JSON.stringify({
                    phone: phoneDigits,
                    leadName: inquiryPrefill?.flowData?.name || agentTargetLead?.name?.split(/\s+/)[0] || 'there',
                    leadFullName: agentTargetLead ? resolveLeadDisplayName(agentTargetLead) || undefined : undefined,
                    message: finalLogMessage,
                    leadId: agentTargetLead?.id,
                    sendInvoicePdf: agentTargetAction === 'deposit' || agentTargetAction === 'billing',
                    invoicePdfUrl: invoicePdfUrl,
                    useTemplate: agentTargetAction !== 'custom' && (agentTargetAction === 'inquiry' || agentTargetAction === 'quotation' || agentTargetAction === 'consent' || agentTargetAction === 'deposit' || agentTargetAction === 'staff'),
                    templateName: agentTargetAction === 'inquiry' ? 'post_call_intake'
                        : (agentTargetAction === 'quotation' ? 'quote_client_v2'
                            : (agentTargetAction === 'consent' ? 'consent_form'
                                : (agentTargetAction === 'deposit' ? 'deposit_request'
                                    : (agentTargetAction === 'staff' ? 'staff_assignment' : undefined)))),
                    templateParams: agentTargetAction === 'quotation'
                        ? [finalLogMessage.replace(/\n+/g, ' | ')]
                        : agentTargetAction === 'staff'
                            ? [
                                selectedWorker?.full_name || selectedWorker?.name || 'Your Care Professional',
                                selectedWorker?.job_title || 'Care Staff',
                                assignmentResult?.shareableUrl || ''
                            ]
                            : agentTargetAction === 'inquiry' && inquiryPrefill
                                ? inquiryPrefill.templateParams
                                : (agentTargetAction === 'consent'
                                    ? [resolveLeadDisplayName(agentTargetLead) || agentTargetLead?.name?.split(/\s+/)[0] || 'there']
                                    : agentTargetAction === 'deposit' || agentTargetAction === 'billing'
                                        ? [(agentTargetLead?.name || 'there'), String(invoiceDepositAmount || '')]
                                        : undefined),
                    flowData: inquiryPrefill?.flowData || consentPrefill || undefined,
                })
            });

            const textRes = await response.text().catch(() => '');
            let resData: any = {};
            try { resData = textRes ? JSON.parse(textRes) : {}; } catch (e) { }

            if (!response.ok || resData.success === false) {
                let errMsg = response.statusText;

                if (resData.error && typeof resData.error === 'object') {
                    errMsg = resData.error.message || resData.error.error_user_title || "Unknown API Error";
                } else if (typeof resData.error === 'string') {
                    errMsg = resData.error;
                } else if (resData.message) {
                    errMsg = resData.message;
                } else if (resData.details?.message) {
                    errMsg = resData.details.message;
                }

                throw new Error(errMsg.trim());
            }

            // Post-dispatch pipeline advancement
            if (agentTargetLead) {
                // If Inquiry -> move to In Discussion
                if (agentTargetAction === 'inquiry') {
                    // Update greeting_sent flag so it doesn't get sent again
                    await supabase.from('crm_leads').update({
                        greeting_sent: true,
                        greeting_sent_at: new Date().toISOString()
                    }).eq('id', agentTargetLead.id);

                    await handleMoveLead(agentTargetLead.id, 'In Discussion');
                    toast.success(`Greeting dispatched! Moved ${agentTargetLead.name} to In Discussion.`, { id: toastId, duration: 4000 });
                }
                // If it was quotations -> move to Quotation Sent and SAVE the rates for auto-estimation later
                else if (agentTargetAction === 'quotation') {
                    const monthlyRate = parsePrice(quotationVars.v3);
                    const dailyRate = parsePrice(quotationVars.v4);

                    await supabase.from('crm_leads').update({
                        pipeline_stage: 'Quotation Sent',
                        estimated_value_monthly: monthlyRate // Primary lead value
                    }).eq('id', agentTargetLead.id);

                    await logActivity(agentTargetLead.id, 'quotation_sent', `Quotation sent: ₹${monthlyRate}/mo`);
                    toast.success(`Quotation sent! Moved ${agentTargetLead.name} to Quotation Sent and saved pricing for auto-estimation.`, { id: toastId, duration: 4000 });
                }
                else if (agentTargetAction === 'consent') {
                    await logActivity(agentTargetLead.id, 'consent_sent', 'Consent form link sent');
                    toast.success(`Consent Form dispatched to ${agentTargetLead.name}!`, { id: toastId, duration: 4000 });
                    // Dispatch secondary work form after 1.5s
                    setTimeout(() => { dispatchWorkFormForLead(agentTargetLead).catch(e => console.error('Work form dispatch failed', e)); }, 1500);
                }
                // If staff assignment -> move to Staff Assigned
                else if (agentTargetAction === 'staff' && (selectedWorker || agentTargetLead.assigned_staff)) {
                    await handleMoveLead(agentTargetLead.id, 'Staff Assigned');

                    if (selectedWorker && assignmentResult) {
                        // Assignment was already created in confirmWorkerSelection
                        // The WhatsApp message (with real ID card link) was just dispatched above
                        toast.success(
                            `✅ ${selectedWorker.name || selectedWorker.full_name} assigned to ${agentTargetLead.name}! ID card link sent via WhatsApp.`,
                            { id: toastId, duration: 6000 }
                        );
                        // Clean up
                        setAssignmentResult(null);
                    } else {
                        toast.success(`Staff assignment updated locally!`, { id: toastId, duration: 4000 });
                    }
                    setSelectedWorker(null);
                    setWorkerSentSuccess(false);
                    setStaffModalActiveTab('client');
                }
                // If Deposit Invoice -> move to Deposit Pending
                else if (agentTargetAction === 'deposit') {
                    const depositVal = Number(invoiceDepositAmount || agentTargetLead?.quoted_monthly_rate || 15000);

                    if (invoicePdfUrl) {
                        await supabase
                            .from('worker_assignments')
                            .update({
                                deposit_amount: depositVal,
                                deposit_invoice_sent: true,
                                invoice_pdf_url: invoicePdfUrl
                            })
                            .eq('client_id', agentTargetLead.id)
                            .eq('assignment_status', 'active');

                        await supabase
                            .from('services')
                            .update({
                                deposit_amount: depositVal,
                                deposit_status: 'pending'
                            })
                            .or(`client_id.eq.${agentTargetLead.id},lead_id.eq.${agentTargetLead.id}`)
                            .eq('status', 'active');

                        await supabase
                            .from('crm_leads')
                            .update({
                                deposit_amount: depositVal,
                                pipeline_stage: 'Deposit Pending'
                            })
                            .eq('id', agentTargetLead.id);
                    }
                    await handleMoveLead(agentTargetLead.id, 'Deposit Pending');
                    toast.success(`Deposit Invoice dispatched! Moved ${agentTargetLead.name} to Deposit Pending.`, { id: toastId, duration: 4000 });
                }
                else if (agentTargetAction === 'billing') {
                    if (invoicePdfUrl) {
                        await supabase
                            .from('worker_assignments')
                            .update({
                                invoice_pdf_url: invoicePdfUrl
                            })
                            .eq('client_id', agentTargetLead.id)
                            .eq('assignment_status', 'active');
                    }
                    await handleMoveLead(agentTargetLead.id, 'Monthly Billing');
                    toast.success(`Billing Invoice dispatched! Moved ${agentTargetLead.name} to Monthly Billing.`, { id: toastId, duration: 4000 });
                }
                // Default acknowledgment
                else {
                    toast.success(`WhatsApp message delivered to +${phoneDigits}!`, { id: toastId, duration: 4000 });
                }

                fetchDeliveryLogs();
            }

        } catch (error: any) {
            console.error('Dispatch error:', error);
            toast.error(error.message || 'Error pushing to WhatsApp Cloud API', { id: toastId });
        }
    };

    const handleExportLeadsToCSV = () => {
        const exportList = filteredLeads && filteredLeads.length > 0 ? filteredLeads : leads;
        if (!exportList || exportList.length === 0) {
            toast.error("No leads available to export.");
            return;
        }

        const headers = ["ID", "Name", "Phone", "Email", "City", "Source", "Status", "Pipeline Stage", "Est. Monthly Value", "Created At"];
        const rows = exportList.map(l => [
            l.id,
            l.name,
            l.phone ? (canViewPhoneNumbers ? l.phone : maskPhoneNumber(l.phone, false)) : "",
            l.email || "",
            extractCity(l),
            l.source || "",
            l.status || "",
            l.pipeline_stage || "",
            `INR ${l.estimated_value_monthly || 0}`,
            l.created_at || ""
        ]);

        const csvContent = [
            headers.join(","),
            ...rows.map(r => r.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(","))
        ].join("\n");

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `CRM_Leads_Pipeline_${crmSelectedCity !== 'all' ? crmSelectedCity + '_' : ''}${!crmViewAllMonths ? crmSelectedMonth + '_' : 'All_'}${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("Leads pipeline exported successfully!");
    };

    // Fetch leads from Supabase and Subscribe to Realtime Updates
    useEffect(() => {
        fetchLeads();
        // Also load all active available workers for Staff Picker / Add Client Modal
        supabase
            .from('employees')
            .select('id, full_name, job_title, status, rate_10hr, rate_24hr')
            .is('deleted_at', null)
            .eq('status', 'available')
            .order('full_name', { ascending: true })
            .then(({ data }) => {
                if (data && data.length > 0) {
                    setAllWorkers(data.map(w => ({ ...w, name: w.full_name || '', role: w.job_title || 'Staff' })));
                } else {
                    setAllWorkers([]);
                }
            });

        // Enable real-time magic for AI Pipeline Automation
        const subscription = supabase
            .channel('crm_leads_ai_updates')
            .on(
                'postgres_changes',
                { event: 'UPDATE', schema: 'public', table: 'crm_leads' },
                (payload) => {
                    const newLead = payload.new as any;
                    // Merge the updated lead directly — preserves assigned_worker_name from DB
                    setLeads(prev => prev.map(lead => lead.id === newLead.id ? { ...lead, ...newLead } : lead));

                    // Trigger a toast notification if the AI moved the lead
                    const oldLead = payload.old as any;
                    if (oldLead && newLead.pipeline_stage !== oldLead.pipeline_stage) {
                        toast.success(`🤖 AI Agent moved ${newLead.name} to "${newLead.pipeline_stage}"!`);
                    }
                }
            )
            .on(
                'postgres_changes',
                { event: 'INSERT', schema: 'public', table: 'crm_leads' },
                (payload) => {
                    setLeads(prev => [payload.new as any, ...prev]);
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(subscription);
        };
    }, []);

    const handleMoveLead = async (id: string, newStage: string): Promise<boolean> => {
        // If this is a hardcoded mock lead (ID length < 10), move and return
        if (id.length < 10) {
            setLeads(prev => prev.map(lead => lead.id === id ? { ...lead, pipeline_stage: newStage } : lead));
            toast.info(`Mock lead moved to ${newStage}`);
            return true;
        }

        const currentLead = leads.find(l => l.id === id);
        const previousStage = currentLead?.pipeline_stage;

        const toastId = toast.loading(`Moving lead to "${newStage}"...`);

        try {
            // ── Guard: Require staff assignment before advancing to staff stages ──────
            const staffRequiredStages = ['Staff Assigned', 'Deposit Pending', 'Active Client'];
            if (staffRequiredStages.includes(newStage)) {
                const { data: activeAssignments } = await supabase
                    .from('worker_assignments')
                    .select('id')
                    .eq('client_id', id)
                    .eq('assignment_status', 'active')
                    .limit(1);

                if (!activeAssignments || activeAssignments.length === 0) {
                    toast.error(
                        `Cannot move to "${newStage}" — please assign a staff member first using the "Assign Staff Member" button.`,
                        { id: toastId, duration: 5000 }
                    );
                    return false;
                }
            }

            // Rule: Automatic staff release if moved to pre-assignment stages
            const preAssignmentStages = ['New Inquiry', 'In Discussion', 'Quotation Sent', 'Form Submitted'];
            if (preAssignmentStages.includes(newStage)) {
                try {
                    await releaseWorkerByClientId(id);
                } catch (err) {
                    console.error("Auto-release worker failed:", err);
                }
            }


            const { error } = await supabase
                .from('crm_leads')
                .update({ pipeline_stage: newStage })
                .eq('id', id);

            if (error) throw error;

            // Ensure client exists in clients table with same UUID when promoted to active/master stages
            if (['Active Client', 'Closed Won', 'Monthly Billing'].includes(newStage)) {
                try {
                    const targetLead = leads.find(l => l.id === id);
                    if (targetLead) {
                        await supabase.from('clients').upsert({
                            id: targetLead.id,
                            client_name: targetLead.name,
                            phone_number: targetLead.phone || targetLead.whatsapp_number || null,
                            email: targetLead.email || null,
                            source: targetLead.source || null,
                        }, { onConflict: 'id' });
                    }
                } catch (clientUpsertErr) {
                    console.warn('Clients upsert warning on stage move:', clientUpsertErr);
                }
            }

            // Sync UI only after database confirms
            setLeads(prev => prev.map(lead => lead.id === id ? { ...lead, pipeline_stage: newStage } : lead));
            if (selectedInspectorLead && selectedInspectorLead.id === id) {
                const oldStage = selectedInspectorLead.pipeline_stage;
                setSelectedInspectorLead((prev: any) => ({ ...prev, pipeline_stage: newStage }));

                // Log the movement activity
                await logActivity(id, 'stage_changed', `Pipeline moved: ${oldStage} → ${newStage}`, { from: oldStage, to: newStage });
                fetchLeadActivity(id, selectedInspectorLead?.duplicate_of_lead_id);
            } else {
                // Still log even if not selected in inspector
                await logActivity(id, 'stage_changed', `Pipeline moved to ${newStage}`, { to: newStage });
            }

            toast.success(`Pipeline state updated successfully!`, { id: toastId });

            // Background refresh to ensure full consistency (assigned worker names, etc)
            fetchLeads();
            return true;
        } catch (error: any) {
            console.error("Error updating lead stage:", error);
            toast.error(`Persistence Error: ${error.message}. Change reverted.`, { id: toastId });
            // Revert state if previous stage was known
            if (previousStage) {
                setLeads(prev => prev.map(lead => lead.id === id ? { ...lead, pipeline_stage: previousStage } : lead));
                if (selectedInspectorLead && selectedInspectorLead.id === id) {
                    setSelectedInspectorLead((prev: any) => ({ ...prev, pipeline_stage: previousStage }));
                }
            }
            fetchLeads(); // Force re-fetch to restore correct state
            return false;
        }
    };

    const parseMoneyValue = (value: any) => {
        if (typeof value === 'number') return value;
        if (typeof value === 'string') return parseFloat(value.replace(/[^\d.-]/g, '')) || 0;
        return 0;
    };

    const sendDepositCollectionAlert = async (lead: any, amount: number) => {
        const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
        const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;
        let phoneDigits = (lead.whatsapp_number || lead.phone || '').replace(/\D/g, '');
        if (phoneDigits.length === 10) phoneDigits = `91${phoneDigits}`;
        if (!phoneDigits) throw new Error('No phone number found for this client.');

        const firstName = lead.name?.split(/\s+/)[0] || 'there';
        const formattedAmount = `₹${amount.toLocaleString('en-IN')}`;
        const resp = await fetch(`${SUPABASE_URL}/functions/v1/meta-whatsapp-outbound`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
                'apikey': SUPABASE_ANON_KEY,
            },
            body: JSON.stringify({
                phone: phoneDigits,
                leadId: lead.id,
                message: `Deposit payment received from ${lead.name}: ${formattedAmount}`,
                useTemplate: true,
                templateName: 'deposit_invoice_alert',
                templateParams: [firstName],
            }),
        });

        const data = await resp.json().catch(() => ({}));
        if (!resp.ok || data.success === false) {
            throw new Error(data.error || `WhatsApp dispatch failed: HTTP ${resp.status}`);
        }
    };

    const handleDepositReceived = (lead: any) => {
        if (!lead?.id) return;
        const isPaid = lead.has_paid_deposit ||
            lead.deposit_status === 'collected' ||
            lead.services?.some((s: any) => s.deposit_status === 'collected') ||
            lead.worker_assignments?.some((a: any) => Number(a.deposit_paid || 0) >= Number(a.deposit_amount || 1));

        if (isPaid) {
            toast.info("Deposit is already collected for this client. Moving lead to Active Client...");
            handleMoveLead(lead.id, 'Active Client');
            return;
        }
        setDepositLeadTarget(lead);
        setDepositMethod('Online Transfer');
        const d = new Date();
        setDepositDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
        setIsDepositModalOpen(true);
    };

    const confirmDepositReceived = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        const lead = depositLeadTarget;
        if (!lead?.id) return;
        setIsDepositModalOpen(false);

        if (lead.id.length < 10) {
            setLeads(prev => prev.map(item => item.id === lead.id ? { ...item, pipeline_stage: 'Active Client' } : item));
            setSelectedInspectorLead((prev: any) => prev?.id === lead.id ? { ...prev, pipeline_stage: 'Active Client' } : prev);
            toast.success('Deposit confirmed! Mock lead moved to Active Client.');
            return;
        }

        const toastId = toast.loading('Recording deposit collection...');

        try {
            const [{ data: assignment, error: assignmentError }, { data: latestQuote, error: quoteError }, { data: existingPayment, error: paymentLookupError }] = await Promise.all([
                supabase
                    .from('worker_assignments')
                    .select('id, deposit_amount, deposit_paid')
                    .eq('client_id', lead.id)
                    .eq('assignment_status', 'active')
                    .order('assigned_at', { ascending: false })
                    .limit(1)
                    .maybeSingle(),
                supabase
                    .from('crm_quotations')
                    .select('deposit, complete_month_rate')
                    .eq('lead_id', lead.id)
                    .order('created_at', { ascending: false })
                    .limit(1)
                    .maybeSingle(),
                supabase
                    .from('payments')
                    .select('id, amount')
                    .eq('client_name', lead.name)
                    .eq('payment_type', 'deposit')
                    .order('payment_date', { ascending: false })
                    .limit(1)
                    .maybeSingle(),
            ]);

            if (assignmentError) throw assignmentError;
            if (quoteError) throw quoteError;
            if (paymentLookupError) throw paymentLookupError;
            if (!assignment) throw new Error('No active staff assignment found. Assign staff before recording the deposit.');

            const amount =
                parseMoneyValue(assignment.deposit_paid)
                || parseMoneyValue(existingPayment?.amount)
                || parseMoneyValue(assignment.deposit_amount)
                || parseMoneyValue(latestQuote?.deposit)
                || parseMoneyValue(lead.quoted_monthly_rate)
                || parseMoneyValue(lead.estimated_value_monthly)
                || 15000;

            if (!existingPayment && parseMoneyValue(assignment.deposit_paid) <= 0) {
                const depositDateISO = depositDate
                    ? (depositDate.includes('T') ? new Date(depositDate).toISOString() : new Date(`${depositDate}T12:00:00`).toISOString())
                    : new Date().toISOString();

                const { error: insertPaymentError } = await supabase.from('payments').insert([{
                    amount,
                    client_name: lead.name,
                    recorded_by: 'admin',
                    transaction_ref: `${depositMethod.toUpperCase().replace(/\s+/g, '-')}-${crypto.randomUUID().replace(/-/g, '').substring(0, 8).toUpperCase()}`,
                    payment_date: depositDateISO,
                    payment_type: 'deposit'
                }]);

                if (insertPaymentError) throw insertPaymentError;
            }

            const assignmentUpdate: any = {
                deposit_paid: amount,
                deposit_invoice_sent: true
            };
            if (parseMoneyValue(assignment.deposit_amount) <= 0) {
                assignmentUpdate.deposit_amount = amount;
            }

            const { error: updateAssignmentError } = await supabase
                .from('worker_assignments')
                .update(assignmentUpdate)
                .eq('id', assignment.id);

            if (updateAssignmentError) throw updateAssignmentError;

            // Also synchronize active service deposit status in services table
            try {
                const { data: existingSvc } = await supabase
                    .from('services')
                    .select('id')
                    .or(`client_id.eq.${lead.id},lead_id.eq.${lead.id}`)
                    .eq('status', 'active')
                    .maybeSingle();

                if (existingSvc) {
                    await supabase
                        .from('services')
                        .update({
                            deposit_status: 'collected',
                            deposit_amount: amount,
                            updated_at: new Date().toISOString(),
                        })
                        .eq('id', existingSvc.id);
                } else {
                    let serviceType = lead.assigned_worker_role || 'Home Care Service';
                    let startDate = formatLocalDateInput(new Date());
                    if (lead.notes) {
                        const sMatch = lead.notes.match(/Service:\s*([^\n\r]+)/i);
                        if (sMatch && sMatch[1]?.trim()) serviceType = sMatch[1].trim();
                        const dMatch = lead.notes.match(/Start Date:\s*([^\n\r]+)/i);
                        if (dMatch && dMatch[1]?.trim()) startDate = dMatch[1].trim();
                    }

                    await supabase.from('services').insert([{
                        client_id: lead.id,
                        lead_id: lead.id,
                        service_type: serviceType,
                        hours_per_day: 10,
                        start_date: startDate,
                        status: 'active',
                        deposit_amount: amount,
                        deposit_status: 'collected',
                        complete_month_daily_rate: Number(lead.complete_month_daily_rate) || 0,
                        incomplete_month_daily_rate: Number(lead.incomplete_month_daily_rate) || 0,
                    }]);
                }
            } catch (svcSyncErr) {
                console.warn('Could not sync active service deposit_status:', svcSyncErr);
            }

            const { error: updateLeadError } = await supabase
                .from('crm_leads')
                .update({ pipeline_stage: 'Active Client' })
                .eq('id', lead.id);

            if (updateLeadError) throw updateLeadError;

            // Ensure client exists in clients table with matching UUID
            try {
                await supabase.from('clients').upsert({
                    id: lead.id,
                    client_name: lead.name,
                    phone_number: lead.phone || lead.whatsapp_number || null,
                    email: lead.email || null,
                    source: lead.source || null,
                }, { onConflict: 'id' });
            } catch (clientUpsertErr) {
                console.warn('Clients upsert warning on deposit confirm:', clientUpsertErr);
            }

            setLeads(prev => prev.map(item => item.id === lead.id ? { ...item, pipeline_stage: 'Active Client' } : item));
            setSelectedInspectorLead((prev: any) => prev?.id === lead.id ? { ...prev, pipeline_stage: 'Active Client' } : prev);
            await logActivity(lead.id, 'payment_recorded', `Deposit collection recorded: ₹${amount.toLocaleString('en-IN')}`, { amount, payment_type: 'deposit' });
            await logActivity(lead.id, 'stage_changed', 'Deposit received — moved to Active Client', { from: lead.pipeline_stage, to: 'Active Client' });

            let alertSent = false;
            try {
                await sendDepositCollectionAlert(lead, amount);
                alertSent = true;
                await logActivity(lead.id, 'whatsapp_deposit_invoice_alert', `Deposit received alert sent to ${lead.name}`, { amount, templateName: 'deposit_invoice_alert' });
            } catch (alertError: any) {
                console.warn('Deposit received alert failed:', alertError);
                await logActivity(lead.id, 'whatsapp_deposit_invoice_alert_failed', `Deposit received alert failed: ${alertError.message}`, { amount, templateName: 'deposit_invoice_alert' });
                toast.warning(`Deposit recorded, but WhatsApp alert failed: ${alertError.message}`);
            }

            fetchLeadActivity(lead.id, lead?.duplicate_of_lead_id);
            fetchLeads();

            toast.success(
                alertSent
                    ? `Deposit collection recorded. Client notified for ₹${amount.toLocaleString('en-IN')}.`
                    : `Deposit collection recorded. Finance invoice marked paid for ₹${amount.toLocaleString('en-IN')}.`,
                { id: toastId, duration: 5000 }
            );
            setSelectedInspectorLead(null);
        } catch (err: any) {
            console.error('Error recording deposit collection:', err);
            toast.error(`Failed to record deposit: ${err.message}`, { id: toastId, duration: 6000 });
        }
    };

    const handleDeleteLead = async (leadId: string, leadName: string) => {
        if (user?.role !== 'admin') {
            setRequestDeleteLead({ id: leadId, name: leadName, defaultActionType: 'move_to_trash' });
            setSelectedInspectorLead(null);
            return;
        }
        // Show choice modal instead of window.confirm
        setDeleteChoiceModal({ id: leadId, name: leadName });
        setSelectedInspectorLead(null);
    };

    const handleMoveToTrash = async (leadId: string, leadName: string) => {
        setDeleteChoiceModal(null);
        if (user?.role !== 'admin') {
            setRequestDeleteLead({ id: leadId, name: leadName, defaultActionType: 'move_to_trash' });
            return;
        }
        if (leadId.length < 10) {
            setLeads(prev => prev.filter(l => l.id !== leadId));
            toast.success(`Lead "${leadName}" removed.`);
            return;
        }
        const toastId = toast.loading(`Moving "${leadName}" to Trash...`);
        try {
            const { error } = await supabase
                .from('crm_leads')
                .update({ deleted_at: new Date().toISOString() })
                .eq('id', leadId);
            if (error) throw error;
            setLeads(prev => prev.filter(l => l.id !== leadId));
            toast.success(`"${leadName}" moved to Trash. Recoverable for 30 days.`, { id: toastId });
        } catch (err: any) {
            toast.error(`Failed to trash lead: ${err.message}`, { id: toastId });
        }
    };

    const handleDeletePermanently = async (leadId: string, leadName: string) => {
        setDeleteChoiceModal(null);
        if (user?.role !== 'admin') {
            setRequestDeleteLead({ id: leadId, name: leadName, defaultActionType: 'permanent_delete' });
            return;
        }
        if (leadId.length < 10) {
            setLeads(prev => prev.filter(l => l.id !== leadId));
            setTrashedLeads(prev => prev.filter(l => l.id !== leadId));
            toast.success(`Lead "${leadName}" deleted permanently.`);
            return;
        }
        const toastId = toast.loading(`Permanently deleting "${leadName}"...`);
        try {
            const { data, error: rpcError } = await supabase.rpc('delete_crm_lead_robust', { target_lead_id: leadId });
            if (rpcError) throw new Error(rpcError.message);
            if (data && data.success === false) throw new Error(data.error || 'Deletion blocked by database.');
            setLeads(prev => prev.filter(l => l.id !== leadId));
            setTrashedLeads(prev => prev.filter(l => l.id !== leadId));
            toast.success(`"${leadName}" deleted permanently.`, { id: toastId });
        } catch (err: any) {
            toast.error(`Deletion Failed: ${err.message}`, { id: toastId, duration: 6000 });
        }
    };

    const handleRestoreLead = async (lead: any) => {
        const toastId = toast.loading(`Restoring "${lead.name}"...`);
        try {
            const { error } = await supabase
                .from('crm_leads')
                .update({ deleted_at: null })
                .eq('id', lead.id);
            if (error) throw error;
            setTrashedLeads(prev => prev.filter(l => l.id !== lead.id));
            setLeads(prev => [{ ...lead, deleted_at: null }, ...prev]);
            toast.success(`"${lead.name}" restored to ${lead.pipeline_stage}.`, { id: toastId });
        } catch (err: any) {
            toast.error(`Restore failed: ${err.message}`, { id: toastId });
        }
    };

    const sendReviewRequest = async (lead: any) => {
        const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
        const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;
        const toastId = toast.loading(`Sending review request to ${lead.name}...`);
        try {
            let phoneDigits = (lead.whatsapp_number || lead.phone || '').replace(/\D/g, '');
            if (phoneDigits.length === 10) phoneDigits = `91${phoneDigits}`;
            if (!phoneDigits) throw new Error('No phone number on file for this client.');

            const resp = await fetch(`${SUPABASE_URL}/functions/v1/meta-whatsapp-outbound`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${SUPABASE_ANON_KEY}`, 'apikey': SUPABASE_ANON_KEY },
                body: JSON.stringify({
                    phone: phoneDigits,
                    leadId: lead.id,
                    useTemplate: true,
                    templateName: 'client_review_request',
                    templateParams: [lead.name?.split(' ')[0] || 'there'],
                })
            });
            const data = await resp.json();
            if (!data.success) throw new Error(data.error || 'WhatsApp dispatch failed');
            await logActivity(lead.id, 'review_requested', `Review request sent to ${lead.name}`);
            toast.success(`Review request sent to ${lead.name}! ✅`, { id: toastId, duration: 4000 });
        } catch (err: any) {
            toast.error(err.message || 'Failed to send review request', { id: toastId });
        }
    };

    const handleEmptyTrash = async () => {
        if (trashedLeads.length === 0) return;
        if (user?.role !== 'admin') {
            toast.error('Only System Admin can empty the trash.');
            return;
        }
        const toastId = toast.loading(`Permanently deleting ${trashedLeads.length} lead(s)...`);
        try {
            await Promise.all(trashedLeads.map(l =>
                supabase.rpc('delete_crm_lead_robust', { target_lead_id: l.id })
            ));
            setTrashedLeads([]);
            toast.success('Trash emptied. All leads permanently deleted.', { id: toastId });
        } catch (err: any) {
            toast.error(`Failed to empty trash: ${err.message}`, { id: toastId });
        }
    };

    const activeStages = activeTab === 'clients' ? clientStages : pipelineStages;
    const setActiveStages = activeTab === 'clients' ? setClientStages : setPipelineStages;

    const handleAddStage = () => {
        if (newStageName.trim() && !activeStages.includes(newStageName.trim())) {
            const nextStages = [...activeStages, newStageName.trim()];
            setActiveStages(nextStages);

            // Sync to cloud
            const p = activeTab === 'clients' ? pipelineStages : nextStages;
            const c = activeTab === 'clients' ? nextStages : clientStages;
            syncStagesToCloud(p, c);

            setNewStageName('');
            setIsAddingStage(false);
            toast.success(`Stage "${newStageName.trim()}" added!`);
        }
    };

    const handleDeleteStage = (stageToDelete: string, idx: number) => {
        if (PROTECTED_STAGES.includes(stageToDelete)) {
            toast.error(`Cannot delete protected stage: ${stageToDelete}`);
            return;
        }

        // Ensure no leads are in this stage before deleting
        const leadsInStage = leads.filter(l => l.pipeline_stage === stageToDelete).length;
        if (leadsInStage > 0) {
            toast.error(`Cannot delete stage. Please move ${leadsInStage} leads to another stage first.`);
            return;
        }

        if (window.confirm(`Are you sure you want to delete the "${stageToDelete}" stage?`)) {
            const newStages = [...activeStages];
            newStages.splice(idx, 1);
            setActiveStages(newStages);

            // Sync to cloud
            const p = activeTab === 'clients' ? pipelineStages : newStages;
            const c = activeTab === 'clients' ? newStages : clientStages;
            syncStagesToCloud(p, c);

            toast.success(`Stage "${stageToDelete}" deleted successfully.`);
        }
    };

    const handleRenameStage = (oldName: string, idx: number) => {
        if (!editingStageName.trim() || editingStageName.trim() === oldName) {
            setEditingStageIdx(null);
            return;
        }
        if (activeStages.includes(editingStageName.trim())) {
            toast.error('A stage with this name already exists.');
            return;
        }

        // Update stage in list
        const newStages = [...activeStages];
        newStages[idx] = editingStageName.trim();
        setActiveStages(newStages);

        // Sync to cloud
        const p = activeTab === 'clients' ? pipelineStages : newStages;
        const c = activeTab === 'clients' ? newStages : clientStages;
        syncStagesToCloud(p, c);

        // Update all leads currently in this stage (optimistic)
        setLeads(prev => prev.map(l => l.pipeline_stage === oldName ? { ...l, pipeline_stage: editingStageName.trim() } : l));

        // Note: Update all leads in DB to match the new stage naming
        const syncLeadsInDB = async () => {
            try {
                const { error } = await supabase
                    .from('crm_leads')
                    .update({ pipeline_stage: editingStageName.trim() })
                    .eq('pipeline_stage', oldName);
                if (error) throw error;
                toast.success(`Updated leads in database to match "${editingStageName.trim()}"`);
            } catch (err) {
                console.error("Failed to sync leads to renamed stage:", err);
            }
        };
        syncLeadsInDB();

        setEditingStageIdx(null);
        toast.success(`Stage renamed to "${editingStageName.trim()}"`);
    };

    const handleSlideStage = (idx: number, direction: 'left' | 'right') => {
        if ((direction === 'left' && idx === 0) || (direction === 'right' && idx === activeStages.length - 1)) return;

        const newStages = [...activeStages];
        const targetIdx = direction === 'left' ? idx - 1 : idx + 1;

        // Swap
        const temp = newStages[idx];
        newStages[idx] = newStages[targetIdx];
        newStages[targetIdx] = temp;

        setActiveStages(newStages);

        // Sync to cloud
        const p = activeTab === 'clients' ? pipelineStages : newStages;
        const c = activeTab === 'clients' ? newStages : clientStages;
        syncStagesToCloud(p, c);
    };

    // Compute duplicate phone numbers so we can warn the user
    const phoneCounts = leads.reduce((acc, l) => {
        const p = (l.whatsapp_number || l.phone || '').replace(/\D/g, '').slice(-10);
        if (p && p.length === 10) {
            acc[p] = (acc[p] || 0) + 1;
        }
        return acc;
    }, {} as Record<string, number>);

    // Filtered leads based on Month and City
    const filteredLeads = useMemo(() => {
        return leads.filter(l => {
            // Month filter
            if (!crmViewAllMonths && !isLeadInMonth(l, crmSelectedMonth)) {
                return false;
            }
            // City filter
            if (crmSelectedCity !== 'all') {
                const cCity = extractCity(l);
                if (cCity.toLowerCase() !== crmSelectedCity.toLowerCase()) {
                    return false;
                }
            }
            return true;
        });
    }, [leads, crmViewAllMonths, crmSelectedMonth, crmSelectedCity]);

    // Tab-level leads (for current active tab: pipeline or clients)
    const currentTabAllLeads = useMemo(() => {
        const targetStages = activeTab === 'clients' ? clientStages : pipelineStages;
        return leads.filter(l => targetStages.includes(l.pipeline_stage));
    }, [leads, activeTab, clientStages, pipelineStages]);

    // Month-scoped tab leads (used for counts)
    const monthScopedTabLeads = useMemo(() => {
        return currentTabAllLeads.filter(l => isLeadInMonth(l, crmSelectedMonth));
    }, [currentTabAllLeads, crmSelectedMonth]);

    // Available cities with counts for the active tab (respecting current month scope if not viewing all months)
    const availableCities = useMemo(() => {
        const baseLeads = crmViewAllMonths ? currentTabAllLeads : monthScopedTabLeads;
        const counts: Record<string, number> = {};
        baseLeads.forEach(l => {
            const c = extractCity(l);
            counts[c] = (counts[c] || 0) + 1;
        });
        const list = Object.keys(counts).sort((a, b) => counts[b] - counts[a]);
        if (!list.includes('Surat')) list.push('Surat');
        return list.map(city => ({
            name: city,
            count: counts[city] || 0
        }));
    }, [crmViewAllMonths, currentTabAllLeads, monthScopedTabLeads]);

    // Counts for the toggle buttons (respects city selection if active)
    const tabTotalCount = useMemo(() => {
        if (crmSelectedCity === 'all') return currentTabAllLeads.length;
        return currentTabAllLeads.filter(l => extractCity(l).toLowerCase() === crmSelectedCity.toLowerCase()).length;
    }, [currentTabAllLeads, crmSelectedCity]);

    const tabMonthCount = useMemo(() => {
        if (crmSelectedCity === 'all') return monthScopedTabLeads.length;
        return monthScopedTabLeads.filter(l => extractCity(l).toLowerCase() === crmSelectedCity.toLowerCase()).length;
    }, [monthScopedTabLeads, crmSelectedCity]);

    // Organize leads into columns based on active tab and filtered leads
    const columns = activeStages.map(stage => ({
        title: stage,
        count: filteredLeads.filter(l => l.pipeline_stage === stage).length,
        items: filteredLeads.filter(l => l.pipeline_stage === stage).map(l => {
            const p = (l.whatsapp_number || l.phone || '').replace(/\D/g, '').slice(-10);

            // Extract latest quote dates
            let plannedStart = null;
            let plannedDuration = null;
            if (l.crm_quotations && l.crm_quotations.length > 0) {
                const latestQuote = [...l.crm_quotations].sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
                plannedStart = latestQuote.start_date;
                plannedDuration = latestQuote.duration;
            }

            const serviceDays = getLeadServiceDays(l);
            const isShortTermQuote = isShortTermService(serviceDays);
            const valueAmount = l.estimated_value_monthly || 0;

            return {
                ...l,
                time: new Date(l.created_at).toLocaleDateString(),
                valueAmount,
                serviceDays,
                isShortTermQuote,
                value: formatLeadValueDisplay(valueAmount, serviceDays),
                priority: l.priority || 'medium',
                isDuplicate: p && p.length === 10 ? phoneCounts[p] > 1 : false,
                plannedStart,
                plannedDuration
            };
        })
    }));

    // ── Table View Data & Sorting (Flat list of visible leads) ───────────────
    const tableLeads = useMemo(() => {
        const allItems = columns.flatMap(c => c.items);
        if (!crmSearchQuery.trim()) return allItems;
        const q = crmSearchQuery.toLowerCase().trim();
        return allItems.filter(item => {
            const name = (item.name || '').toLowerCase();
            const phone = (item.phone || item.whatsapp_number || '').replace(/\D/g, '');
            const city = extractCity(item).toLowerCase();
            const stage = (item.pipeline_stage || '').toLowerCase();
            const displayId = `ld-${(item.id || '').substring(0, 4)}`.toLowerCase();
            const notes = (item.notes || '').toLowerCase();
            const service = (item.service_interest || '').toLowerCase();
            return name.includes(q) || phone.includes(q) || city.includes(q) || stage.includes(q) || displayId.includes(q) || notes.includes(q) || service.includes(q);
        });
    }, [columns, crmSearchQuery]);

    const sortedTableLeads = useMemo(() => {
        return [...tableLeads].sort((a, b) => {
            let comp = 0;
            if (crmSortField === 'id') {
                const idA = `LD-${(a.id || '').substring(0, 4).toUpperCase()}`;
                const idB = `LD-${(b.id || '').substring(0, 4).toUpperCase()}`;
                comp = idA.localeCompare(idB);
            } else if (crmSortField === 'name') {
                comp = (a.name || '').localeCompare(b.name || '');
            } else if (crmSortField === 'city') {
                comp = extractCity(a).localeCompare(extractCity(b));
            } else if (crmSortField === 'phone') {
                comp = (a.phone || a.whatsapp_number || '').localeCompare(b.phone || b.whatsapp_number || '');
            } else if (crmSortField === 'priority') {
                const prioRank = { hot: 3, medium: 2, cold: 1 };
                const rA = prioRank[a.priority as 'hot' | 'medium' | 'cold'] || 2;
                const rB = prioRank[b.priority as 'hot' | 'medium' | 'cold'] || 2;
                comp = rA - rB;
            } else if (crmSortField === 'stage') {
                comp = (a.pipeline_stage || '').localeCompare(b.pipeline_stage || '');
            } else if (crmSortField === 'rate') {
                const rA = Number(a.complete_month_daily_rate || a.incomplete_month_daily_rate || a.valueAmount || 0);
                const rB = Number(b.complete_month_daily_rate || b.incomplete_month_daily_rate || b.valueAmount || 0);
                comp = rA - rB;
            } else if (crmSortField === 'created_at') {
                comp = new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime();
            }
            return crmSortAsc ? comp : -comp;
        });
    }, [tableLeads, crmSortField, crmSortAsc]);

    const handleUpdateLeadValue = async (leadId: string) => {
        const newValue = parseInt(editingLeadValueAmount.replace(/\D/g, ''), 10);

        if (isNaN(newValue)) {
            setEditingLeadValueId(null);
            return;
        }

        // Optimistic update
        setLeads(prev => prev.map(l => l.id === leadId ? { ...l, estimated_value_monthly: newValue } : l));
        setEditingLeadValueId(null);

        // Supabase update if not mock lead
        if (leadId.length >= 10) {
            try {
                const { error } = await supabase
                    .from('crm_leads')
                    .update({ estimated_value_monthly: newValue })
                    .eq('id', leadId);

                if (error) throw error;
                toast.success('Lead value updated');
            } catch (err: any) {
                console.error("Error updating lead value", err);
                toast.error("Failed to update lead value");
                fetchLeads(); // revert
            }
        } else {
            toast.success('Lead value updated (Mock lead)');
        }
    };

    const handleUpdateLeadDetails = async (leadId: string, customName?: string, customPhone?: string) => {
        const newName = (customName !== undefined ? customName : editingLeadName).trim();
        const newPhone = (customPhone !== undefined ? customPhone : editingLeadPhone).trim();

        if (!newName) {
            setEditingLeadDetailsId(null);
            return;
        }

        // Optimistic update
        setLeads(prev => prev.map(l => l.id === leadId ? { ...l, name: newName, phone: newPhone || null, whatsapp_number: newPhone || null } : l));
        setEditingLeadDetailsId(null);

        if (leadId.length >= 10) {
            try {
                const { error } = await supabase
                    .from('crm_leads')
                    .update({ name: newName, phone: newPhone || null, whatsapp_number: newPhone || null })
                    .eq('id', leadId);

                if (error) throw error;
                toast.success('Lead details updated');
            } catch (err: any) {
                console.error("Error updating lead details", err);
                toast.error("Failed to update details");
                fetchLeads(); // revert
            }
        }
    };

    const handleUpdatePriority = async (leadId: string, newPriority: string) => {
        // If mock lead, handle locally and return
        if (leadId.length < 10) {
            setLeads(prev => prev.map(l => l.id === leadId ? { ...l, priority: newPriority } : l));
            toast.success('Mock priority updated');
            return;
        }

        const toastId = toast.loading('Updating priority...');
        try {
            const { error } = await supabase.from('crm_leads').update({ priority: newPriority }).eq('id', leadId);
            if (error) throw error;

            // Sync UI only after database confirms
            setLeads(prev => prev.map(l => l.id === leadId ? { ...l, priority: newPriority } : l));
            toast.success('Priority updated permanently', { id: toastId });
        } catch (err: any) {
            console.error("Persistence error (priority):", err);
            toast.error(`Fail to persist priority: ${err.message}`, { id: toastId });
            fetchLeads(); // revert
        }
    };

    const convertToClient = async (leadId: string, leadName: string) => {
        if (!window.confirm(`Convert "${leadName}" to a Client Master entry?\n\nThis will:\n• Create a permanent client record\n• Move the lead to Closed Won\n• Keep the full history`)) return;

        const toastId = toast.loading(`Converting ${leadName} to Client Master...`);
        try {
            const lead = leads.find(l => l.id === leadId);

            // 1. Create entry in clients table (used by Billing, HR assignments)
            const { error: clientError } = await supabase.from('clients').insert([{
                id: leadId, // Use same UUID so worker_assignments can link by client_id
                client_name: leadName,
                phone_number: lead?.phone || lead?.whatsapp_number || null,
                email: lead?.email || null,
                source: lead?.source || null,
            }]);

            // Ignore duplicate key error — client may already exist
            if (clientError && !clientError.message.includes('duplicate') && !clientError.message.includes('unique')) {
                throw new Error(clientError.message);
            }

            // 2. Move lead to Closed Won
            const { error: stageError } = await supabase
                .from('crm_leads')
                .update({ pipeline_stage: 'Closed Won' })
                .eq('id', leadId);
            if (stageError) throw new Error(stageError.message);

            // 3. Log the conversion
            await logActivity(leadId, 'converted_to_client', `${leadName} converted to Client Master`, { lead_id: leadId });

            // 4. Update local state
            setLeads(prev => prev.map(l => l.id === leadId ? { ...l, pipeline_stage: 'Closed Won' } : l));

            toast.success(`${leadName} is now a Client Master! ✅`, { id: toastId, duration: 4000 });
        } catch (err: any) {
            console.error('Conversion failed:', err);
            toast.error(`Conversion failed: ${err.message}`, { id: toastId });
        }
    };

    type VoiceCallSnapshot = {
        id: string | number;
        phone?: string;
        capturedName?: string;
        capturedWhatsapp?: string;
        capturedValue?: number;
    };

    const resolveVoiceCallPhone = (call: VoiceCallSnapshot) => {
        const leadPhone = !call.phone || call.phone === 'Unknown Number' ? '' : call.phone;
        const leadWhatsapp =
            !call.capturedWhatsapp || call.capturedWhatsapp === 'Unknown Number' ? '' : call.capturedWhatsapp;
        return { leadPhone, leadWhatsapp, phoneForLookup: leadPhone || leadWhatsapp };
    };

    const createLeadFromVoiceCall = async (
        call: VoiceCallSnapshot,
        opts?: { skipReturningCheck?: boolean }
    ) => {
        const { leadPhone, leadWhatsapp, phoneForLookup } = resolveVoiceCallPhone(call);

        if (!opts?.skipReturningCheck && phoneLast10(phoneForLookup).length >= 8) {
            try {
                const existingClient = await findClientMasterByPhone(supabase, phoneForLookup);
                if (existingClient) {
                    setReturningClientModal({
                        phone: phoneForLookup,
                        name: call.capturedName || 'Voice Lead',
                        existingClient,
                        pendingLeadData: {
                            kind: 'voice',
                            callSnapshot: {
                                id: call.id,
                                phone: call.phone,
                                capturedName: call.capturedName,
                                capturedWhatsapp: call.capturedWhatsapp,
                                capturedValue: call.capturedValue,
                            },
                        },
                    });
                    return null;
                }
            } catch (lookupErr: any) {
                console.warn('Client master lookup failed:', lookupErr?.message || lookupErr);
            }
        }

        const toastId = toast.loading('Adding lead to pipeline…');
        try {
            const standardized = normalizePhoneDigits(leadWhatsapp || leadPhone);
            const { data: newLead, error } = await supabase
                .from('crm_leads')
                .insert([
                    {
                        name: call.capturedName || 'Voice Lead',
                        phone: leadPhone || null,
                        whatsapp_number: standardized || leadWhatsapp || null,
                        source: 'AI Phone Call',
                        status: 'AI Handled',
                        pipeline_stage: NEW_LEAD_PIPELINE_STAGE,
                        estimated_value_monthly: Number(call.capturedValue) || 0,
                    },
                ])
                .select('*')
                .single();

            if (error) throw error;

            if (newLead?.id && call.id) {
                const { error: transcriptErr } = await supabase.from('call_transcripts').upsert(
                    {
                        conversation_id: String(call.id),
                        lead_id: newLead.id,
                        phone_number: leadPhone || standardized || null,
                        called_at: new Date().toISOString(),
                    },
                    { onConflict: 'conversation_id' }
                );
                if (transcriptErr) console.warn('call_transcripts upsert:', transcriptErr.message);

                await logActivity(newLead.id, 'lead_created', 'Lead created from AI phone call', {
                    source: 'AI Phone Call',
                    call_id: String(call.id),
                });
            }

            setCalls((prev) =>
                prev.map((c) =>
                    String(c.id) === String(call.id)
                        ? { ...c, status: 'Processed', lead_id: newLead?.id ?? c.lead_id }
                        : c
                )
            );
            setLeads((prev) => [newLead, ...prev.filter((l) => l.id !== newLead.id)]);
            fetchLeads();
            setActiveTab('pipeline');
            setSelectedInspectorLead(newLead);
            toast.success(`Added ${call.capturedName || 'Lead'} to ${NEW_LEAD_PIPELINE_STAGE}!`, { id: toastId });
            return newLead;
        } catch (error: any) {
            console.error('Error creating lead from call:', error);
            toast.error(`Failed to create lead: ${error.message}`, { id: toastId });
            throw error;
        }
    };

    const captureCallAsLead = async (callId: string | number) => {
        const call = calls.find((c) => String(c.id) === String(callId));
        if (!call) {
            toast.error('Call not found. Refresh Voice AI and try again.');
            return;
        }
        setCapturingCallId(callId);
        try {
            await createLeadFromVoiceCall(call);
        } catch {
            /* toast shown in createLeadFromVoiceCall */
        } finally {
            setCapturingCallId(null);
        }
    };

    const checkAddLeadDuplicate = async (phone: string) => {
        if (!phone || phone.replace(/\D/g, '').length < 8) {
            setAddLeadDuplicateWarning(null);
            return;
        }
        setAddLeadCheckingDuplicate(true);
        const last10 = phoneLast10(phone);
        const { data } = await supabase
            .from('crm_leads')
            .select('id, name, pipeline_stage, whatsapp_number, phone, deleted_at')
            .is('deleted_at', null)
            .or(`phone.ilike.%${last10}%,whatsapp_number.ilike.%${last10}%`);
        setAddLeadCheckingDuplicate(false);

        const matches = (data || []).filter(
            (l) => phonesMatch(l.phone, phone) || phonesMatch(l.whatsapp_number, phone)
        );

        // Stale ghost in legacy "New Lead" stage (hidden from pipeline after permanent delete)
        const legacyOnly = matches.filter((l) => isLegacyPipelineStage(l.pipeline_stage));
        if (legacyOnly.length > 0 && legacyOnly.length === matches.length) {
            for (const ghost of legacyOnly) {
                await supabase.rpc('delete_crm_lead_robust', { target_lead_id: ghost.id });
            }
            setAddLeadDuplicateWarning(null);
            toast.success(
                legacyOnly.length === 1
                    ? `Removed hidden stale record (${legacyOnly[0].name}). You can add this lead now.`
                    : `Removed ${legacyOnly.length} hidden stale records. You can add this lead now.`
            );
            fetchLeads();
            return;
        }

        const match = matches.find((l) => isPipelineVisibleLead(l, pipelineStages, clientStages));
        if (match) {
            setAddLeadDuplicateWarning(match);
            setAddLeadConfirmDuplicate(false);
        } else {
            setAddLeadDuplicateWarning(null);
        }
    };

    const handleAddManualLead = async (opts?: { name: string; phone: string; isDuplicate?: boolean; duplicateOfId?: string; skipReturningCheck?: boolean }) => {
        const leadName = opts?.name?.trim() || 'New Lead';
        const leadPhone = opts?.phone?.trim() || '';
        const standardized = normalizePhoneDigits(leadPhone);

        if (opts?.isDuplicate && opts?.duplicateOfId) {
            try {
                const { error } = await supabase.from('crm_leads').update({ name: leadName }).eq('id', opts.duplicateOfId);
                if (error) throw error;
                await logActivity(opts.duplicateOfId, 'lead_updated', `Lead name updated to "${leadName}"`);
                toast.success(`Lead name updated to "${leadName}"!`);
                setIsAddLeadModalOpen(false);
                setReturningClientModal(null);
                setAddLeadName('');
                setAddLeadPhone('');
                setAddLeadDuplicateWarning(null);
                setAddLeadConfirmDuplicate(false);
                fetchLeads();
            } catch (err: any) {
                toast.error("Failed to update lead: " + err.message);
                setIsAddLeadModalOpen(true);
            }
            return;
        }

        // Check if this phone belongs to an existing client (returning client detection)
        if (!opts?.skipReturningCheck && phoneLast10(leadPhone).length >= 8) {
            const existingClient = await findClientMasterByPhone(supabase, leadPhone);

            if (existingClient) {
                setReturningClientModal({
                    phone: leadPhone,
                    name: leadName,
                    existingClient,
                    pendingLeadData: {
                        name: leadName,
                        phone: leadPhone,
                        isDuplicate: opts?.isDuplicate,
                        duplicateOfId: opts?.duplicateOfId,
                    },
                });
                setIsAddLeadModalOpen(false);
                return; // Wait for user choice on returning-client modal
            }
        }

        try {
            const { data: newLead, error } = await supabase.from('crm_leads').insert([{
                name: leadName,
                phone: leadPhone || null,
                whatsapp_number: standardized || null,
                source: 'Manual Add',
                status: 'New',
                pipeline_stage: NEW_LEAD_PIPELINE_STAGE,
                estimated_value_monthly: 0,
                is_duplicate: opts?.isDuplicate || false,
                duplicate_of_lead_id: opts?.duplicateOfId || null,
            }]).select('*').single();

            if (error) throw error;
            if (newLead?.id) {
                await logActivity(newLead.id, 'lead_created', opts?.isDuplicate ? 'Lead created manually (duplicate confirmed by staff)' : 'Lead created manually', { source: 'Manual Add' });
            }
            toast.success(`Lead "${leadName}" created!`);
            setIsAddLeadModalOpen(false);
            setReturningClientModal(null);
            setAddLeadName('');
            setAddLeadPhone('');
            setAddLeadDuplicateWarning(null);
            setAddLeadConfirmDuplicate(false);
            if (newLead) setSelectedInspectorLead(newLead);
            fetchLeads();
        } catch (err: any) {
            toast.error("Failed to add lead: " + err.message);
            setAddLeadName(leadName);
            setAddLeadPhone(leadPhone);
            setIsAddLeadModalOpen(true);
        }
    };

    const resetAddClientForm = () => {
        setAddClientName('');
        setAddClientPhone('');
        setAddClientAddress('');
        setAddClientPriority('medium');
        setAddClientServiceName('Old Age Care');
        setAddClientShiftHours(10);
        setAddClientShiftType('10-Hour Day Shift');
        setAddClientStartDate(new Date().toISOString().split('T')[0]);
        setAddClientIsOpenEnded(true);
        setAddClientEndDate('');
        setAddClientCompleteMonthRate(600);
        setAddClientIncompleteMonthRate(1000);
        setAddClientDepositAmount(5000);
        setAddClientDepositMethod('UPI');
        setAddClientDepositDate(new Date().toISOString().split('T')[0]);
        setAddClientDepositNotes('');
        setAddClientSelectedWorkers([]);
        setAddClientAutoMarkAttendance(true);
        setAddClientAbsentDays(0);
        setAddClientWorkerSearch('');
        // Care Scope & Work Form resets
        setAddClientPatientName('');
        setAddClientAge('');
        setAddClientAgeUnit('Years');
        setAddClientWeight('');
        setAddClientRelativeName('');
        setAddClientAltPhone('');
        setAddClientMedicalCondition('');
        setAddClientFormType('patient_care_form');
        setAddClientDuties([]);
        setAddClientCareInstructions('');
        setIsAddClientCareExpanded(true);
    };

    const handleAddManualClient = async () => {
        if (!addClientName.trim()) {
            toast.error('Please enter the client full name.');
            return;
        }
        if (!addClientPhone.trim()) {
            toast.error('Please enter the client phone number.');
            return;
        }
        if (addClientSelectedWorkers.length === 0) {
            toast.error('Please assign at least one worker to this client.');
            return;
        }

        setIsSubmittingAddClient(true);
        const toastId = toast.loading('Creating client and configuring service...');

        try {
            const leadName = addClientName.trim();
            const leadPhone = addClientPhone.trim();
            const standardized = normalizePhoneDigits(leadPhone);
            const cmRate = Number(addClientCompleteMonthRate) || 0;
            const icmRate = Number(addClientIncompleteMonthRate) || 0;
            const depAmount = Number(addClientDepositAmount) || 0;
            const estMonthly = cmRate > 0 ? cmRate * 30 : 0;

            // 1. Create Lead in crm_leads (Active Client stage)
            const patientNameClean = addClientPatientName.trim() || leadName;
            const workerNames = addClientSelectedWorkers.map(w => w.workerName).join(', ');

            const workFormData = {
                form_type: addClientFormType,
                patient_name: patientNameClean,
                age: addClientAge.trim(),
                age_unit: addClientAgeUnit,
                weight: addClientWeight.trim(),
                relative_name: addClientRelativeName.trim() || leadName,
                contact_number: leadPhone || null,
                alternate_contact_number: addClientAltPhone.trim() || null,
                address: addClientAddress.trim() || null,
                medical_condition: addClientMedicalCondition.trim() || null,
                duties: addClientDuties,
                other_work: addClientCareInstructions.trim() || null,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
            };

            const serviceNote = `Service: ${addClientServiceName}\nShift: ${addClientShiftType}${addClientAddress.trim() ? `\nAddress: ${addClientAddress.trim()}` : ''}${patientNameClean !== leadName ? `\nPatient: ${patientNameClean}` : ''}${addClientMedicalCondition.trim() ? `\nCondition: ${addClientMedicalCondition.trim()}` : ''}`;
            const { data: newLead, error: leadError } = await supabase
                .from('crm_leads')
                .insert([{
                    name: leadName,
                    phone: leadPhone || null,
                    whatsapp_number: standardized || null,
                    location: addClientAddress.trim() || null,
                    source: 'Manual Client Setup',
                    status: 'Active',
                    pipeline_stage: 'Active Client',
                    complete_month_daily_rate: cmRate,
                    incomplete_month_daily_rate: icmRate,
                    estimated_value_monthly: estMonthly,
                    priority: addClientPriority,
                    notes: serviceNote,
                    work_form_data: workFormData,
                    assigned_worker_name: workerNames || null,
                    assigned_worker_role: addClientServiceName || null,
                }])
                .select('*')
                .single();

            if (leadError) throw leadError;

            // 2. Ensure Client record exists in clients table
            const { error: clientError } = await supabase
                .from('clients')
                .upsert({
                    id: newLead.id,
                    client_name: leadName,
                    phone_number: leadPhone || null,
                    source: 'Manual Client Setup',
                }, { onConflict: 'id' });

            if (clientError) console.warn('Clients table upsert warning:', clientError);

            // 3. Create Service in services table
            const serviceCareScopePayload = {
                type: 'service_start',
                service_type: addClientServiceName || 'Home Care Service',
                patient_name: patientNameClean,
                age: addClientAge.trim(),
                age_unit: addClientAgeUnit,
                weight: addClientWeight.trim(),
                relative_name: addClientRelativeName.trim() || leadName,
                contact_number: leadPhone || null,
                alternate_contact_number: addClientAltPhone.trim() || null,
                address: addClientAddress.trim() || null,
                medical_condition: addClientMedicalCondition.trim() || null,
                form_type: addClientFormType,
                duties: addClientDuties,
                instructions: addClientCareInstructions.trim(),
                shift_type: addClientShiftType,
                setup_notes: `Shift: ${addClientShiftType}. Setup for ongoing client.`,
            };

            const { data: newService, error: serviceError } = await supabase
                .from('services')
                .insert([{
                    client_id: newLead.id,
                    lead_id: newLead.id,
                    service_type: addClientServiceName || 'Home Care Service',
                    hours_per_day: addClientShiftHours,
                    start_date: addClientStartDate,
                    end_date: addClientIsOpenEnded ? null : (addClientEndDate || null),
                    status: 'active',
                    deposit_amount: depAmount,
                    deposit_status: depAmount > 0 ? 'collected' : 'pending',
                    complete_month_daily_rate: cmRate,
                    incomplete_month_daily_rate: icmRate,
                    notes: JSON.stringify(serviceCareScopePayload),
                }])
                .select('*')
                .single();

            if (serviceError) throw serviceError;

            // 3b. Sync client_consents snapshot
            try {
                await supabase.from('client_consents').insert([{
                    lead_id: newLead.id,
                    patient_name: patientNameClean,
                    age: addClientAge.trim() || null,
                    age_unit: addClientAgeUnit,
                    weight: addClientWeight.trim() || null,
                    relative_name: addClientRelativeName.trim() || leadName,
                    contact_number: leadPhone || null,
                    alternate_contact_number: addClientAltPhone.trim() || null,
                    address: addClientAddress.trim() || null,
                    other_details: addClientMedicalCondition.trim() || null,
                    service_category: addClientServiceName,
                    service_start_date: addClientStartDate,
                    offered_time: `${addClientShiftHours} Hours`,
                    created_at: new Date().toISOString()
                }]);
            } catch (e) {
                console.warn('client_consents insert error in handleAddManualClient:', e);
            }

            // 3c. Sync client_work_forms
            try {
                await supabase.from('client_work_forms').insert([{
                    lead_id: newLead.id,
                    form_type: addClientFormType,
                    patient_name: patientNameClean,
                    duties: addClientDuties,
                    other_work: addClientCareInstructions.trim() || null
                }]);
            } catch (e) {
                console.warn('client_work_forms insert error in handleAddManualClient:', e);
            }

            // 4. Record Deposit in payments table (if depAmount > 0)
            if (depAmount > 0) {
                const depRef = `${addClientDepositMethod.toUpperCase().replace(/\s+/g, '-')}-${Math.random().toString(36).substring(2, 9).toUpperCase()}`;
                const { error: payError } = await supabase
                    .from('payments')
                    .insert([{
                        client_name: leadName,
                        amount: depAmount,
                        payment_type: 'deposit',
                        payment_date: addClientDepositDate || new Date().toISOString(),
                        transaction_ref: depRef,
                        recorded_by: 'admin',
                    }]);

                if (payError) console.warn('Payment recording warning:', payError);
            }

            // 5. Worker Assignments & Past Attendance
            const todayStr = new Date().toISOString().split('T')[0];
            const absentDaysCount = Number(addClientAbsentDays) || 0;

            for (const worker of addClientSelectedWorkers) {
                const wStart = worker.startDate || addClientStartDate;

                // 5a. Insert into service_worker_assignments
                await supabase
                    .from('service_worker_assignments')
                    .insert([{
                        service_id: newService.id,
                        employee_id: worker.workerId,
                        start_date: wStart,
                        end_date: null,
                    }]);

                // 5b. Insert into legacy worker_assignments for compatibility
                await supabase
                    .from('worker_assignments')
                    .insert([{
                        client_id: newLead.id,
                        employee_id: worker.workerId,
                        service_type: addClientServiceName,
                        hours_per_day: addClientShiftHours,
                        start_date: wStart,
                        end_date: addClientIsOpenEnded ? null : addClientEndDate,
                        assignment_status: 'active',
                        deposit_amount: depAmount,
                        deposit_paid: depAmount,
                        client_billing_rate: cmRate,
                    }]);

                // 5c. Update Employee status
                await supabase
                    .from('employees')
                    .update({
                        status: 'assigned',
                        assigned_client: leadName,
                    })
                    .eq('id', worker.workerId);

                // 5d. Auto-generate past attendance if requested and start date is in past
                if (addClientAutoMarkAttendance && wStart <= todayStr) {
                    const startObj = new Date(wStart);
                    const endObj = new Date(todayStr);
                    const dates: string[] = [];

                    const cur = new Date(startObj);
                    while (cur <= endObj) {
                        dates.push(cur.toISOString().split('T')[0]);
                        cur.setDate(cur.getDate() + 1);
                    }

                    if (dates.length > 0) {
                        // Mark up to absentDaysCount as absent, rest as present
                        const attendancePayloads = dates.map((dateStr, idx) => {
                            const isAbsent = idx < absentDaysCount;
                            return {
                                worker_id: worker.workerId,
                                duty_date: dateStr,
                                status: isAbsent ? 'absent' : 'present',
                                is_absent: isAbsent,
                                client_name: leadName,
                            };
                        });

                        await supabase
                            .from('attendance')
                            .insert(attendancePayloads);
                    }
                }
            }

            // 6. Log Activity
            await logActivity(
                newLead.id,
                'lead_created',
                `Existing client setup: Service started on ${addClientStartDate} (${addClientServiceName} - ${addClientShiftType}) with ${addClientSelectedWorkers.length} worker(s). Deposit: ₹${depAmount}`
            );

            toast.success(`Client "${leadName}" successfully added with active service & workers!`, { id: toastId });
            setIsAddClientModalOpen(false);
            resetAddClientForm();
            fetchLeads();
        } catch (err: any) {
            console.error('Failed to add client:', err);
            toast.error('Failed to add client: ' + (err.message || err), { id: toastId });
        } finally {
            setIsSubmittingAddClient(false);
        }
    };

    const handleOpenExistingLead = async () => {
        if (!returningClientModal) return;
        const { existingClient, pendingLeadData } = returningClientModal;
        setReturningClientModal(null);
        
        const toastId = toast.loading('Opening client record...');
        
        try {
            if (pendingLeadData?.kind === 'voice' && pendingLeadData.callSnapshot) {
                const call = pendingLeadData.callSnapshot;
                const { error: transcriptErr } = await supabase.from('call_transcripts').upsert(
                    {
                        conversation_id: String(call.id),
                        lead_id: existingClient.id,
                        phone_number: existingClient.phone_number,
                        called_at: new Date().toISOString(),
                    },
                    { onConflict: 'conversation_id' }
                );
                
                if (!transcriptErr) {
                    await logActivity(existingClient.id, 'lead_contacted', 'Returning client contacted via AI Phone Call.', {
                        source: 'AI Phone Call',
                        call_id: String(call.id),
                    });
                    
                    setCalls((prev) =>
                        prev.map((c) =>
                            String(c.id) === String(call.id)
                                ? { ...c, status: 'Processed', lead_id: existingClient.id }
                                : c
                        )
                    );
                }
                toast.success(`Voice call attached to existing client ${existingClient.client_name}`, { id: toastId });
            } else {
                await logActivity(existingClient.id, 'lead_contacted', 'Returning client contacted manually.', { source: 'Manual Add' });
                toast.success(`Opened existing record for ${existingClient.client_name}`, { id: toastId });
            }
            
            // Fetch the latest full lead record
            const { data } = await supabase
                .from('crm_leads')
                .select('*, crm_quotations(start_date, duration, service_category, service_name, shift_type, hours_per_day, created_at), client_consents(*), services(id, status, service_worker_assignments(id, employee_id, start_date, end_date, employees(full_name, employee_id)))')
                .eq('id', existingClient.id)
                .single();
                
            if (data) {
                setSelectedInspectorLead(data);
                setLeads(prev => {
                    const filtered = prev.filter(l => l.id !== data.id);
                    return [data, ...filtered]; // Move to top of local state
                });
            }
            
            setIsAddLeadModalOpen(false);
            setAddLeadName('');
            setAddLeadPhone('');
            
        } catch (err: any) {
            toast.error("Failed to open existing lead: " + err.message, { id: toastId });
        }
    };

    // Helper to standardize phone number display
    const formatPhoneNumber = (num: string) => {
        if (!num) return '';
        if (!canViewPhoneNumbers) {
            return maskPhoneNumber(num, false);
        }
        const digits = num.replace(/\D/g, '');
        if (digits.length === 10) return `+91 ${digits.slice(0, 5)} ${digits.slice(5)}`;
        if (digits.startsWith('91') && digits.length === 12) {
            return `+91 ${digits.slice(2, 7)} ${digits.slice(7)}`;
        }
        return `+${digits}`; // Fallback for other countries or malformed numbers
    };

    return (
        <div className="p-4 sm:p-6 lg:p-8 flex flex-col space-y-6">
            <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center shrink-0">
                        <Bot className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans'] tracking-tight">Leads & Inquiries Pipeline</h1>
                        <p className="text-sm text-slate-500 font-medium">Customer inquiries, voice call recordings, and lead conversion</p>
                    </div>
                </div>

                <div className="flex items-center gap-3 sm:gap-4 flex-wrap sm:flex-nowrap">
                    {/* Notification Bell */}
                    <div className="relative z-50 shrink-0">
                        <button
                            onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
                            className="p-3 rounded-2xl bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 transition-all relative group shadow-sm"
                        >
                            <Bot className="w-6 h-6 group-hover:scale-110 transition-transform text-primary" />
                            {attentionNotifications.length > 0 && (
                                <span className="absolute top-2.5 right-2.5 w-3 h-3 bg-red-500 border-2 border-white rounded-full animate-pulse shadow-sm"></span>
                            )}
                        </button>

                        {isNotificationsOpen && (
                            <div className="absolute right-0 sm:right-0 -right-4 mt-3 w-[calc(100vw-2rem)] sm:w-80 max-w-80 bg-white rounded-3xl shadow-2xl border border-slate-100 z-[100] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                                <div className="p-5 border-b border-slate-50 flex items-center justify-between bg-slate-50/50">
                                    <h3 className="font-bold text-slate-900 text-sm">Needs Attention</h3>
                                    <span className="text-[10px] font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-full">{attentionNotifications.length} LIVE</span>
                                </div>
                                <div className="max-h-96 overflow-y-auto">
                                    {attentionNotifications.length === 0 ? (
                                        <div className="p-6 text-center">
                                            <p className="text-sm font-semibold text-slate-500">No active attention alerts.</p>
                                            <p className="text-xs text-slate-400 mt-1">Routine automation logs stay in the full activity view.</p>
                                        </div>
                                    ) : attentionNotifications.map(log => (
                                        <div key={log.id} className="p-4 border-b border-slate-50 hover:bg-slate-50 transition-colors cursor-pointer group">
                                            <div className="flex gap-4">
                                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 group-hover:bg-white transition-colors border ${log.status === 'error' ? 'bg-red-50 border-red-100' : 'bg-amber-50 border-amber-100'}`}>
                                                    <log.icon className={`w-5 h-5 ${log.status === 'error' ? 'text-red-600' : 'text-amber-600'}`} />
                                                </div>
                                                <div>
                                                    <p className="text-xs font-bold text-slate-900 leading-tight">{log.title}</p>
                                                    <p className="text-[11px] text-slate-500 mt-1 line-clamp-2 leading-relaxed">{log.desc}</p>
                                                    <p className="text-[9px] text-slate-400 mt-2 uppercase font-bold tracking-tighter flex items-center gap-1.5">
                                                        <span className="w-1.5 h-1.5 rounded-full bg-[#1AA6A8] shadow-[0_0_5px_rgba(16,185,129,0.5)]"></span>
                                                        {log.time}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="p-4 text-center bg-slate-50/30 border-t border-slate-50">
                                    <button onClick={() => { setActiveTab('automations'); setIsNotificationsOpen(false); }} className="text-xs font-bold text-primary hover:underline transition-transform inline-block">View Full Intelligence Logs</button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Module Tabs */}
                    <div className="flex items-center p-1 sm:p-1.5 bg-slate-200/50 rounded-xl sm:rounded-2xl shrink-0 border border-slate-200 shadow-inner overflow-x-auto hide-scrollbar max-w-full">
                        <button
                            onClick={() => setActiveTab('pipeline')}
                            className={`px-3 sm:px-5 py-2 sm:py-2.5 rounded-lg sm:rounded-xl text-xs sm:text-sm font-bold transition-all duration-300 whitespace-nowrap flex items-center gap-1.5 ${activeTab === 'pipeline' ? 'bg-white text-primary shadow-lg scale-105' : 'text-slate-500 hover:text-slate-900'}`}
                        >
                            <span>Pipeline</span>
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${activeTab === 'pipeline' ? 'bg-primary/10 text-primary' : 'bg-slate-200 text-slate-600'}`}>
                                {filteredLeads.filter(l => pipelineStages.includes(l.pipeline_stage)).length}
                            </span>
                        </button>
                        <button
                            onClick={() => setActiveTab('clients')}
                            className={`px-3 sm:px-5 py-2 sm:py-2.5 rounded-lg sm:rounded-xl text-xs sm:text-sm font-bold transition-all duration-300 whitespace-nowrap flex items-center gap-1.5 ${activeTab === 'clients' ? 'bg-white text-primary shadow-lg scale-105' : 'text-slate-500 hover:text-slate-900'}`}
                        >
                            <span>Clients</span>
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${activeTab === 'clients' ? 'bg-primary/10 text-primary' : 'bg-slate-200 text-slate-600'}`}>
                                {filteredLeads.filter(l => clientStages.includes(l.pipeline_stage)).length}
                            </span>
                        </button>
                        <button
                            onClick={() => setActiveTab('automations')}
                            className={`px-3 sm:px-5 py-2 sm:py-2.5 rounded-lg sm:rounded-xl text-xs sm:text-sm font-bold transition-all duration-300 whitespace-nowrap ${activeTab === 'automations' ? 'bg-white text-primary shadow-lg scale-105' : 'text-slate-500 hover:text-slate-900'}`}
                        >
                            AI Auto
                        </button>
                        <button
                            onClick={() => setActiveTab('voice')}
                            className={`px-3 sm:px-5 py-2 sm:py-2.5 rounded-lg sm:rounded-xl text-xs sm:text-sm font-bold transition-all duration-300 whitespace-nowrap ${activeTab === 'voice' ? 'bg-white text-primary shadow-lg scale-105' : 'text-slate-500 hover:text-slate-900'}`}
                        >
                            Voice AI
                        </button>
                        <button
                            onClick={() => setActiveTab('trash')}
                            className={`px-3 sm:px-5 py-2 sm:py-2.5 rounded-lg sm:rounded-xl text-xs sm:text-sm font-bold transition-all duration-300 whitespace-nowrap flex items-center gap-1.5 ${activeTab === 'trash' ? 'bg-white text-red-500 shadow-lg scale-105' : 'text-slate-500 hover:text-slate-900'}`}
                        >
                            <Trash2 className="w-3.5 h-3.5" />
                            Trash
                            {trashedLeads.length > 0 && (
                                <span className="bg-red-100 text-red-600 text-[10px] font-bold px-1.5 py-0.5 rounded-full">{trashedLeads.length}</span>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {(activeTab === 'pipeline' || activeTab === 'clients') && (
                <div className="flex flex-col flex-1 h-full min-h-0">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-4 bg-white p-2.5 sm:p-3 rounded-xl border border-slate-200/80 shadow-xs">
                        <div className="flex flex-wrap items-center gap-2.5 sm:gap-3">
                            {/* Live Sync Status */}
                            <div className="flex items-center gap-1.5 text-xs text-slate-500 font-medium pr-2 border-r border-slate-200 hidden md:flex">
                                <span className="w-2 h-2 rounded-full bg-[#1AA6A8] animate-pulse"></span>
                                Live Sync
                            </div>

                            {/* View Mode Toggle: All vs By Month */}
                            <div className="flex items-center bg-slate-100 p-0.5 rounded-lg border border-slate-200">
                                <button
                                    onClick={() => setCrmViewAllMonths(true)}
                                    className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all cursor-pointer ${
                                        crmViewAllMonths
                                            ? 'bg-[#1AA6A8] text-white shadow-2xs'
                                            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/60'
                                    }`}
                                >
                                    All {activeTab === 'clients' ? 'Clients' : 'Leads'} ({tabTotalCount})
                                </button>
                                <button
                                    onClick={() => setCrmViewAllMonths(false)}
                                    className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all cursor-pointer ${
                                        !crmViewAllMonths
                                            ? 'bg-[#1AA6A8] text-white shadow-2xs'
                                            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/60'
                                    }`}
                                >
                                    By Month ({tabMonthCount})
                                </button>
                            </div>

                            {/* Month Navigator */}
                            <div className={`flex items-center bg-white border rounded-lg overflow-hidden transition-all ${
                                !crmViewAllMonths 
                                    ? 'border-[#1AA6A8] ring-2 ring-[#1AA6A8]/20 shadow-xs' 
                                    : 'border-slate-200 opacity-80 hover:opacity-100'
                            }`}>
                                <button 
                                    onClick={() => {
                                        setCrmViewAllMonths(false);
                                        crmPrevMonth();
                                    }} 
                                    className="px-2.5 py-1.5 text-slate-600 hover:bg-slate-100 transition-colors font-bold text-sm cursor-pointer"
                                    title="Previous Month"
                                >
                                    ‹
                                </button>
                                <span 
                                    onClick={() => setCrmViewAllMonths(false)}
                                    className={`px-3 py-1.5 text-xs font-bold min-w-[125px] text-center border-x border-slate-200 select-none cursor-pointer transition-colors ${
                                        !crmViewAllMonths ? 'text-[#1AA6A8] bg-teal-50/50' : 'text-slate-700 hover:bg-slate-50'
                                    }`}
                                    title="Click to view by this month"
                                >
                                    {crmMonthLabel(crmSelectedMonth)}
                                </span>
                                <button 
                                    onClick={() => {
                                        setCrmViewAllMonths(false);
                                        crmNextMonth();
                                    }} 
                                    className="px-2.5 py-1.5 text-slate-600 hover:bg-slate-100 transition-colors font-bold text-sm cursor-pointer"
                                    title="Next Month"
                                >
                                    ›
                                </button>
                            </div>

                            {/* City Filter Dropdown */}
                            <div className="relative">
                                <button
                                    onClick={() => setIsCityDropdownOpen(!isCityDropdownOpen)}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg border transition-all cursor-pointer ${
                                        crmSelectedCity !== 'all'
                                            ? 'bg-sky-50 border-sky-300 text-sky-700 ring-2 ring-sky-200/50'
                                            : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                                    }`}
                                >
                                    <MapPin className="w-3.5 h-3.5 text-sky-600" />
                                    <span>{crmSelectedCity === 'all' ? 'All Cities' : crmSelectedCity}</span>
                                    <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${isCityDropdownOpen ? 'rotate-180' : ''}`} />
                                </button>

                                {isCityDropdownOpen && (
                                    <>
                                        <div
                                            className="fixed inset-0 z-30"
                                            onClick={() => setIsCityDropdownOpen(false)}
                                        />
                                        <div className="absolute left-0 mt-1.5 w-48 bg-white rounded-xl shadow-xl border border-slate-200 py-1.5 z-40 animate-in fade-in zoom-in-95">
                                            <div className="px-3 py-1 text-[10px] font-extrabold uppercase tracking-wider text-slate-400">
                                                Filter by City
                                            </div>
                                            <button
                                                onClick={() => {
                                                    setCrmSelectedCity('all');
                                                    setIsCityDropdownOpen(false);
                                                }}
                                                className={`w-full text-left px-3 py-1.5 text-xs font-semibold flex items-center justify-between transition-colors cursor-pointer ${
                                                    crmSelectedCity === 'all' ? 'bg-sky-50 text-sky-700 font-bold' : 'text-slate-700 hover:bg-slate-50'
                                                }`}
                                            >
                                                <span>All Cities</span>
                                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 font-bold">
                                                    {crmViewAllMonths ? currentTabAllLeads.length : monthScopedTabLeads.length}
                                                </span>
                                            </button>
                                            {availableCities.map(c => (
                                                <button
                                                    key={c.name}
                                                    onClick={() => {
                                                        setCrmSelectedCity(c.name);
                                                        setIsCityDropdownOpen(false);
                                                    }}
                                                    className={`w-full text-left px-3 py-1.5 text-xs font-semibold flex items-center justify-between transition-colors cursor-pointer ${
                                                        crmSelectedCity === c.name ? 'bg-sky-50 text-sky-700 font-bold' : 'text-slate-700 hover:bg-slate-50'
                                                    }`}
                                                >
                                                    <span className="flex items-center gap-1.5">
                                                        <MapPin className="w-3 h-3 text-slate-400" />
                                                        {c.name}
                                                    </span>
                                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 font-bold">
                                                        {c.count}
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* View Mode Switcher: Table vs Pipeline */}
                            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 shadow-2xs">
                                <button
                                    type="button"
                                    onClick={() => handleSetCrmViewMode('table')}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-md transition-all cursor-pointer ${
                                        crmViewMode === 'table'
                                            ? 'bg-[#1AA6A8] text-white shadow-2xs'
                                            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                                    }`}
                                    title="Compact table view with rows and columns"
                                >
                                    <Table className="w-3.5 h-3.5" />
                                    <span>Table</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleSetCrmViewMode('pipeline')}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-md transition-all cursor-pointer ${
                                        crmViewMode === 'pipeline'
                                            ? 'bg-[#1AA6A8] text-white shadow-2xs'
                                            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                                    }`}
                                    title="Pipeline accordion view with stage cards"
                                >
                                    <Kanban className="w-3.5 h-3.5" />
                                    <span>Pipeline</span>
                                </button>
                            </div>

                            {/* Live Search */}
                            <div className="relative min-w-[160px] sm:min-w-[210px]">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                                <input
                                    type="text"
                                    value={crmSearchQuery}
                                    onChange={(e) => setCrmSearchQuery(e.target.value)}
                                    placeholder={`Search ${activeTab === 'clients' ? 'clients' : 'leads'}...`}
                                    className="w-full pl-8 pr-7 py-1.5 text-xs font-medium bg-slate-50 hover:bg-white focus:bg-white border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-[#1AA6A8]/20 focus:border-[#1AA6A8] transition-all"
                                />
                                {crmSearchQuery && (
                                    <button
                                        type="button"
                                        onClick={() => setCrmSearchQuery('')}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5 cursor-pointer"
                                        title="Clear search"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                )}
                            </div>

                            {/* Badges Visibility Toggle */}
                            {crmViewMode === 'pipeline' && activeTab === 'pipeline' && (
                                <button
                                    type="button"
                                    onClick={toggleShowBadges}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg border transition-all cursor-pointer ${
                                        showBadges
                                            ? 'bg-teal-50 border-teal-300 text-teal-700 ring-2 ring-teal-200/50'
                                            : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                                    }`}
                                    title={showBadges ? "Hide all badges across lead cards" : "Show all badges across lead cards"}
                                >
                                    {showBadges ? (
                                        <>
                                            <EyeOff className="w-3.5 h-3.5 text-teal-600" />
                                            <span>Hide Badges</span>
                                        </>
                                    ) : (
                                        <>
                                            <Eye className="w-3.5 h-3.5 text-slate-500" />
                                            <span>Show Badges</span>
                                        </>
                                    )}
                                </button>
                            )}
                        </div>

                        {/* Action buttons (Add Lead/Client & Export CSV) */}
                        <div className="flex items-center gap-2 sm:gap-3 ml-auto">
                            {activeTab === 'clients' ? (
                                <button
                                    onClick={() => {
                                        resetAddClientForm();
                                        setIsAddClientModalOpen(true);
                                    }}
                                    className="px-3 sm:px-4 py-2 bg-[#E6F7F7] text-[#1AA6A8] border border-[#1AA6A8]/20 text-xs sm:text-sm font-bold rounded-lg hover:bg-[#EAFBFB] transition-colors flex items-center gap-1.5 sm:gap-2 shadow-sm cursor-pointer"
                                >
                                    <UserPlus className="w-4 h-4" /> Add Client
                                </button>
                            ) : (
                                <button
                                    onClick={() => {
                                        setIsAddLeadModalOpen(true);
                                        setAddLeadName('');
                                        setAddLeadPhone('');
                                        setAddLeadDuplicateWarning(null);
                                        setAddLeadConfirmDuplicate(false);
                                    }}
                                    className="px-3 sm:px-4 py-2 bg-[#E6F7F7] text-[#1AA6A8] border border-[#1AA6A8]/20 text-xs sm:text-sm font-bold rounded-lg hover:bg-[#EAFBFB] transition-colors flex items-center gap-1.5 sm:gap-2 shadow-sm cursor-pointer"
                                >
                                    <Plus className="w-4 h-4" /> Add Lead
                                </button>
                            )}
                            <button onClick={handleExportLeadsToCSV} className="px-3 sm:px-4 py-2 bg-white text-slate-700 border border-slate-200 text-xs sm:text-sm font-bold rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-1.5 sm:gap-2 shadow-sm hidden sm:flex cursor-pointer">
                                Export CSV
                            </button>
                        </div>
                    </div>

                    <div className={`flex-1 flex flex-col ${crmViewMode === 'table' ? 'min-h-0 overflow-hidden' : 'gap-4 overflow-y-auto pb-4 pr-2 custom-scrollbar'}`}>
                        {isLoading ? (
                            <div className="flex-1 flex items-center justify-center">
                                <Loader2 className="w-8 h-8 text-primary animate-spin" />
                                <span className="ml-3 text-slate-500 font-medium">Loading live pipeline...</span>
                            </div>
                        ) : crmViewMode === 'table' ? (
                            /* ── TABLE VIEW (Rows & Columns View) ── */
                            <div className="flex-1 flex flex-col min-h-0 bg-white rounded-xl border border-slate-200/80 shadow-xs overflow-hidden">
                                <div className="overflow-x-auto flex-1 custom-scrollbar">
                                    <table className="w-full text-left border-collapse text-xs">
                                        <thead>
                                            <tr className="bg-slate-50/90 border-b border-slate-200 text-[11px] font-extrabold uppercase tracking-wider text-slate-600 select-none sticky top-0 z-10 backdrop-blur-xs">
                                                <th className="py-2.5 px-3 w-12 text-center whitespace-nowrap">#</th>
                                                <th onClick={() => handleCrmSort('id')} className="py-2.5 px-3 cursor-pointer hover:bg-slate-100 transition-colors whitespace-nowrap" title="Sort by Lead ID">
                                                    <div className="flex items-center gap-1">
                                                        <span>ID</span>
                                                        {crmSortField === 'id' ? (crmSortAsc ? <ArrowUp className="w-3 h-3 text-[#1AA6A8]" /> : <ArrowDown className="w-3 h-3 text-[#1AA6A8]" />) : <ArrowUpDown className="w-3 h-3 text-slate-300" />}
                                                    </div>
                                                </th>
                                                <th onClick={() => handleCrmSort('name')} className="py-2.5 px-3 cursor-pointer hover:bg-slate-100 transition-colors whitespace-nowrap" title="Sort by Name">
                                                    <div className="flex items-center gap-1">
                                                        <span>Lead Name & Service</span>
                                                        {crmSortField === 'name' ? (crmSortAsc ? <ArrowUp className="w-3 h-3 text-[#1AA6A8]" /> : <ArrowDown className="w-3 h-3 text-[#1AA6A8]" />) : <ArrowUpDown className="w-3 h-3 text-slate-300" />}
                                                    </div>
                                                </th>
                                                <th onClick={() => handleCrmSort('city')} className="py-2.5 px-2.5 cursor-pointer hover:bg-slate-100 transition-colors whitespace-nowrap" title="Sort by City">
                                                    <div className="flex items-center gap-1">
                                                        <span>City</span>
                                                        {crmSortField === 'city' ? (crmSortAsc ? <ArrowUp className="w-3 h-3 text-[#1AA6A8]" /> : <ArrowDown className="w-3 h-3 text-[#1AA6A8]" />) : <ArrowUpDown className="w-3 h-3 text-slate-300" />}
                                                    </div>
                                                </th>
                                                <th onClick={() => handleCrmSort('phone')} className="py-2.5 px-2.5 cursor-pointer hover:bg-slate-100 transition-colors whitespace-nowrap" title="Sort by Phone">
                                                    <div className="flex items-center gap-1">
                                                        <span>Phone</span>
                                                        {crmSortField === 'phone' ? (crmSortAsc ? <ArrowUp className="w-3 h-3 text-[#1AA6A8]" /> : <ArrowDown className="w-3 h-3 text-[#1AA6A8]" />) : <ArrowUpDown className="w-3 h-3 text-slate-300" />}
                                                    </div>
                                                </th>
                                                <th onClick={() => handleCrmSort('priority')} className="py-2.5 px-2.5 cursor-pointer hover:bg-slate-100 transition-colors whitespace-nowrap" title="Sort by Priority">
                                                    <div className="flex items-center gap-1">
                                                        <span>Priority</span>
                                                        {crmSortField === 'priority' ? (crmSortAsc ? <ArrowUp className="w-3 h-3 text-[#1AA6A8]" /> : <ArrowDown className="w-3 h-3 text-[#1AA6A8]" />) : <ArrowUpDown className="w-3 h-3 text-slate-300" />}
                                                    </div>
                                                </th>
                                                <th onClick={() => handleCrmSort('stage')} className="py-2.5 px-2.5 cursor-pointer hover:bg-slate-100 transition-colors whitespace-nowrap" title="Sort by Stage">
                                                    <div className="flex items-center gap-1">
                                                        <span>Stage</span>
                                                        {crmSortField === 'stage' ? (crmSortAsc ? <ArrowUp className="w-3 h-3 text-[#1AA6A8]" /> : <ArrowDown className="w-3 h-3 text-[#1AA6A8]" />) : <ArrowUpDown className="w-3 h-3 text-slate-300" />}
                                                    </div>
                                                </th>
                                                <th onClick={() => handleCrmSort('rate')} className="py-2.5 px-2.5 cursor-pointer hover:bg-slate-100 transition-colors whitespace-nowrap" title="Sort by Daily Rate / Budget">
                                                    <div className="flex items-center gap-1">
                                                        <span>Rates / Budget</span>
                                                        {crmSortField === 'rate' ? (crmSortAsc ? <ArrowUp className="w-3 h-3 text-[#1AA6A8]" /> : <ArrowDown className="w-3 h-3 text-[#1AA6A8]" />) : <ArrowUpDown className="w-3 h-3 text-slate-300" />}
                                                    </div>
                                                </th>
                                                <th onClick={() => handleCrmSort('created_at')} className="py-2.5 px-2.5 cursor-pointer hover:bg-slate-100 transition-colors whitespace-nowrap" title="Sort by Date Added">
                                                    <div className="flex items-center gap-1">
                                                        <span>Date Added</span>
                                                        {crmSortField === 'created_at' ? (crmSortAsc ? <ArrowUp className="w-3 h-3 text-[#1AA6A8]" /> : <ArrowDown className="w-3 h-3 text-[#1AA6A8]" />) : <ArrowUpDown className="w-3 h-3 text-slate-300" />}
                                                    </div>
                                                </th>
                                                <th className="py-2.5 px-3 text-right whitespace-nowrap">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100 bg-white">
                                            {sortedTableLeads.length === 0 ? (
                                                <tr>
                                                    <td colSpan={10} className="py-16 text-center">
                                                        <div className="flex flex-col items-center justify-center">
                                                            <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                                                                <Users className="w-6 h-6 text-slate-400" />
                                                            </div>
                                                            <p className="text-sm font-semibold text-slate-600">
                                                                {crmSearchQuery 
                                                                    ? `No leads found matching "${crmSearchQuery}"` 
                                                                    : crmSelectedCity !== 'all' 
                                                                        ? `No leads found for ${crmSelectedCity}` 
                                                                        : 'No leads found for this period'}
                                                            </p>
                                                            <p className="text-xs text-slate-400 mt-1 max-w-sm">
                                                                {crmSearchQuery ? 'Try clearing or modifying your search filter.' : 'Try selecting "All Leads" or choosing a different month.'}
                                                            </p>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : (
                                                sortedTableLeads.map((item, itemIdx) => {
                                                    const priorityMeta = item.priority === 'hot'
                                                        ? { label: 'Hot', cls: 'bg-red-100 text-red-700 border-red-200' }
                                                        : item.priority === 'cold'
                                                            ? { label: 'Low', cls: 'bg-blue-100 text-blue-700 border-blue-200' }
                                                            : { label: 'Medium', cls: 'bg-amber-100 text-amber-700 border-amber-200' };

                                                    let serviceName: string | null = item.service_interest || null;
                                                    let shiftBubble: string | null = null;
                                                    if (item.notes) {
                                                        const serviceMatch = item.notes.match(/^Service:\s*(.+)$/im);
                                                        const shiftMatch = item.notes.match(/^Shift:\s*(.+)$/im);
                                                        if (!serviceName && serviceMatch) serviceName = serviceMatch[1].trim();
                                                        if (shiftMatch) shiftBubble = shiftMatch[1].trim();
                                                    }
                                                    if (!serviceName) serviceName = item.intent || null;
                                                    if (serviceName === 'Unknown' || serviceName === '') serviceName = null;
                                                    if (shiftBubble === '' || shiftBubble === 'Unknown') shiftBubble = null;

                                                    const city = extractCity(item);
                                                    const displayId = `LD-${(item.id || '').substring(0, 4).toUpperCase()}`;

                                                    const lQuote = item.crm_quotations?.[0];
                                                    const cRate = item.complete_month_daily_rate || lQuote?.complete_month_rate || item.services?.[0]?.complete_month_daily_rate;
                                                    const incRate = item.incomplete_month_daily_rate || lQuote?.incomplete_month_rate || item.services?.[0]?.incomplete_month_daily_rate;

                                                    const rawPhone = (item.whatsapp_number || item.phone || '').trim();
                                                    const cleanDigits = rawPhone.replace(/\D/g, '');
                                                    const waDigits = cleanDigits.length === 10 ? `91${cleanDigits}` : cleanDigits;
                                                    const telDigits = rawPhone.startsWith('+') ? rawPhone : (cleanDigits.length === 10 ? `+91${cleanDigits}` : `+${cleanDigits}`);
                                                    const isCopied = copiedLeadPhoneBadge === rawPhone;
                                                    const displayPhone = formatPhoneNumber(rawPhone);

                                                    return (
                                                        <tr
                                                            key={item.id}
                                                            onClick={async () => {
                                                                setSelectedInspectorLead(item);
                                                                fetchLeadActivity(item.id, item.duplicate_of_lead_id);
                                                                if (item.needs_attention) {
                                                                    setLeads(prev => prev.map(l => l.id === item.id ? { ...l, needs_attention: false } : l));
                                                                    await supabase.from('crm_leads').update({ needs_attention: false }).eq('id', item.id);
                                                                }
                                                            }}
                                                            className="hover:bg-teal-50/40 transition-colors cursor-pointer group"
                                                        >
                                                            {/* Sr No */}
                                                            <td className="py-2.5 px-3 text-center text-slate-400 font-mono text-[11px] select-none whitespace-nowrap">
                                                                {itemIdx + 1}
                                                            </td>

                                                            {/* ID badge with 1-click copy */}
                                                            <td className="py-2.5 px-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) => handleCopyLeadId(displayId, e)}
                                                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-mono text-[11px] font-bold text-teal-800 bg-teal-50 border border-teal-200/80 hover:bg-teal-100 transition-colors cursor-pointer shadow-2xs"
                                                                    title={`Click to copy ${displayId}\nFull UUID: ${item.id}`}
                                                                >
                                                                    <span>{displayId}</span>
                                                                    {copiedLeadIdBadge === displayId ? (
                                                                        <Check className="w-2.5 h-2.5 text-emerald-600" />
                                                                    ) : (
                                                                        <Copy className="w-2.5 h-2.5 text-teal-500 opacity-60 group-hover:opacity-100" />
                                                                    )}
                                                                </button>
                                                            </td>

                                                            {/* Lead Name & Service */}
                                                            <td className="py-2.5 px-3 whitespace-nowrap">
                                                                <div className="flex items-center gap-2.5 min-w-0">
                                                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-white text-xs font-bold shadow-2xs ${getAvatarColor(item.name || 'Lead')}`}>
                                                                        {getInitials(item.name || 'L')}
                                                                    </div>
                                                                    <div className="min-w-0 flex flex-col">
                                                                        <div className="font-bold text-slate-900 group-hover:text-primary transition-colors flex items-center gap-1.5 truncate">
                                                                            <span className="truncate">{item.name || 'Unnamed Lead'}</span>
                                                                            {item.needs_attention && (
                                                                                <span className="w-2 h-2 rounded-full bg-red-500 shrink-0 animate-pulse" title="Needs attention" />
                                                                            )}
                                                                        </div>
                                                                        {serviceName ? (
                                                                            <span className="text-[10px] text-slate-500 truncate font-medium">
                                                                                {serviceName}{shiftBubble ? ` • ${shiftBubble}` : ''}
                                                                            </span>
                                                                        ) : (
                                                                            <span className="text-[10px] text-slate-400 italic">General Inquiry</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </td>

                                                            {/* City */}
                                                            <td className="py-2.5 px-2.5 whitespace-nowrap">
                                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-sky-50 text-sky-700 border border-sky-200 uppercase tracking-wider">
                                                                    <MapPin className="w-2.5 h-2.5 text-sky-600" /> {city}
                                                                </span>
                                                            </td>

                                                            {/* Phone */}
                                                            <td className="py-2.5 px-2.5 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                                                {!rawPhone ? (
                                                                    <span className="font-mono text-xs text-slate-400">N/A</span>
                                                                ) : !canViewPhoneNumbers ? (
                                                                    <div className="inline-flex items-center gap-1.5 py-0.5">
                                                                        <span className="font-mono text-xs font-semibold text-slate-700 inline-flex items-center gap-1" title="Phone masked for role privacy">
                                                                            <Lock className="w-3 h-3 text-slate-400 shrink-0" />
                                                                            <span>{displayPhone}</span>
                                                                        </span>
                                                                        <button
                                                                            type="button"
                                                                            onClick={(e) => handleCopyLeadPhone(rawPhone, e)}
                                                                            className="p-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
                                                                            title="Copy phone"
                                                                        >
                                                                            {isCopied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                                                                        </button>
                                                                    </div>
                                                                ) : (
                                                                    <div className="inline-flex items-center gap-1.5 py-0.5">
                                                                        <a
                                                                            href={`tel:${telDigits}`}
                                                                            className="font-mono text-xs font-semibold text-slate-800 hover:text-primary hover:underline transition-colors inline-flex items-center gap-1 group/call"
                                                                            title={`Click to call ${rawPhone}`}
                                                                        >
                                                                            <Phone className="w-3 h-3 text-slate-400 group-hover/call:text-primary transition-colors shrink-0" />
                                                                            <span>{displayPhone}</span>
                                                                        </a>
                                                                        <div className="inline-flex items-center gap-0.5 shrink-0">
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => fetchWhatsappChat(item)}
                                                                                className="p-1 rounded-md text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 transition-colors cursor-pointer"
                                                                                title={`Chat with ${item.name} on WhatsApp`}
                                                                            >
                                                                                <MessageCircle className="w-3.5 h-3.5" />
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                onClick={(e) => handleCopyLeadPhone(rawPhone, e)}
                                                                                className="p-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
                                                                                title="Copy phone number"
                                                                            >
                                                                                {isCopied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </td>

                                                            {/* Priority */}
                                                            <td className="py-2.5 px-2.5 whitespace-nowrap">
                                                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold border uppercase tracking-wider ${priorityMeta.cls}`}>
                                                                    {priorityMeta.label}
                                                                </span>
                                                            </td>

                                                            {/* Stage / Status with quick dropdown */}
                                                            <td className="py-2.5 px-2.5 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                                                <select
                                                                    value={item.pipeline_stage}
                                                                    onChange={async (e) => {
                                                                        const newStage = e.target.value;
                                                                        if (newStage && newStage !== item.pipeline_stage) {
                                                                            await handleMoveLead(item.id, newStage);
                                                                        }
                                                                    }}
                                                                    className="text-xs font-semibold bg-slate-50 hover:bg-teal-50 border border-slate-200 hover:border-teal-300 text-slate-700 rounded-lg px-2 py-1 cursor-pointer outline-none focus:ring-2 focus:ring-teal-200 transition-colors"
                                                                    title="Change pipeline stage"
                                                                >
                                                                    {activeStages.map((stg) => (
                                                                        <option key={stg} value={stg}>{stg}</option>
                                                                    ))}
                                                                </select>
                                                            </td>

                                                            {/* Rates / Budget */}
                                                            <td className="py-2.5 px-2.5 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                                                <div className="flex flex-col gap-0.5">
                                                                    {cRate ? (
                                                                        <span
                                                                            onClick={() => openQuickEdit(item)}
                                                                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 cursor-pointer hover:bg-emerald-100 transition-colors w-fit"
                                                                            title="Full Month Daily Rate (Click to edit)"
                                                                        >
                                                                            Full: ₹{Number(cRate).toLocaleString('en-IN')}/d
                                                                        </span>
                                                                    ) : null}
                                                                    {incRate ? (
                                                                        <span
                                                                            onClick={() => openQuickEdit(item)}
                                                                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200 cursor-pointer hover:bg-blue-100 transition-colors w-fit"
                                                                            title="Partial Month Daily Rate (Click to edit)"
                                                                        >
                                                                            Part: ₹{Number(incRate).toLocaleString('en-IN')}/d
                                                                        </span>
                                                                    ) : null}
                                                                    {!cRate && !incRate && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => openQuickEdit(item)}
                                                                            className="text-[10px] text-slate-400 hover:text-primary font-medium flex items-center gap-1 cursor-pointer transition-colors"
                                                                        >
                                                                            <Plus className="w-2.5 h-2.5" /> Set Rates
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </td>

                                                            {/* Date Added */}
                                                            <td className="py-2.5 px-2.5 whitespace-nowrap">
                                                                <div className="flex flex-col text-slate-600">
                                                                    <span className="font-semibold text-xs text-slate-800">
                                                                        {item.created_at ? format(new Date(item.created_at), 'dd MMM yyyy') : '—'}
                                                                    </span>
                                                                    <span className="text-[10px] text-slate-400 font-mono">
                                                                        {item.created_at ? format(new Date(item.created_at), 'hh:mm a') : ''}
                                                                    </span>
                                                                </div>
                                                            </td>

                                                            {/* Actions */}
                                                            <td className="py-2.5 px-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                                                <div className="flex items-center justify-end gap-1">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => openQuickEdit(item)}
                                                                        className="p-1.5 rounded-lg text-slate-400 hover:text-primary hover:bg-teal-50 transition-colors cursor-pointer"
                                                                        title="Quick edit lead info & rates"
                                                                    >
                                                                        <Edit3 className="w-3.5 h-3.5" />
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={async () => {
                                                                            setSelectedInspectorLead(item);
                                                                            fetchLeadActivity(item.id, item.duplicate_of_lead_id);
                                                                            if (item.needs_attention) {
                                                                                setLeads(prev => prev.map(l => l.id === item.id ? { ...l, needs_attention: false } : l));
                                                                                await supabase.from('crm_leads').update({ needs_attention: false }).eq('id', item.id);
                                                                            }
                                                                        }}
                                                                        className="p-1.5 rounded-lg text-slate-400 hover:text-primary hover:bg-teal-50 transition-colors cursor-pointer"
                                                                        title="View lead inspector drawer"
                                                                    >
                                                                        <Eye className="w-3.5 h-3.5" />
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => fetchWhatsappChat(item)}
                                                                        className="p-1.5 rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors cursor-pointer"
                                                                        title="WhatsApp chat"
                                                                    >
                                                                        <MessageCircle className="w-3.5 h-3.5" />
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleDeleteLead(item.id, item.name)}
                                                                        className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                                                                        title="Delete / Trash lead"
                                                                    >
                                                                        <Trash2 className="w-3.5 h-3.5" />
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                                {/* Table footer with lead count summary */}
                                <div className="p-2.5 px-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between text-xs text-slate-500 font-medium">
                                    <div>
                                        Showing <span className="font-bold text-slate-800">{sortedTableLeads.length}</span> {activeTab === 'clients' ? 'clients' : 'leads'}
                                        {crmSearchQuery ? ` matching "${crmSearchQuery}"` : ''}
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className="text-[11px] text-slate-400">Click any row to view full details</span>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <>
                                {columns.map((col, idx) => {
                                    const isExpanded = expandedStages[col.title] === true;
                                    const limit = stageLimits[col.title] || 8;
                                    const displayedItems = col.items.slice(0, limit);
                                    const hasMore = col.items.length > limit;

                                    return (
                                        <div key={idx} className={`flex bg-slate-50 rounded-xl border border-slate-200 shadow-sm transition-all duration-300 ${isExpanded ? 'flex-col sm:flex-row sm:items-stretch' : 'flex-col'}`}>
                                            <div
                                                className={`p-3 sm:p-3.5 bg-white relative group/header cursor-pointer select-none transition-colors hover:bg-slate-50 flex-shrink-0 flex flex-col ${isExpanded ? 'sm:w-[230px] lg:w-[250px] rounded-t-xl sm:rounded-t-none sm:rounded-l-xl border-b sm:border-b-0 sm:border-r border-slate-200' : 'rounded-xl'}`}
                                                onClick={() => toggleStage(col.title)}
                                            >
                                                {/* Stage Header w/ Edit toggle */}
                                                <div className="flex items-center justify-between w-full">
                                                    <div className={`flex items-center ${isExpanded ? 'justify-between' : 'gap-2.5'} gap-2 flex-1 min-w-0`}>
                                                        <div className={`flex items-center gap-2 min-w-0 ${isExpanded ? 'flex-1' : ''}`}>
                                                            <div className={`transition-transform duration-200 shrink-0 ${isExpanded ? '-rotate-90' : 'rotate-0'}`}>
                                                                <ChevronDown className="w-4 h-4 text-slate-400" />
                                                            </div>
                                                            {editingStageIdx === idx ? (
                                                                <input
                                                                    type="text"
                                                                    value={editingStageName}
                                                                    onChange={(e) => setEditingStageName(e.target.value)}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter') handleRenameStage(col.title, idx);
                                                                        if (e.key === 'Escape') setEditingStageIdx(null);
                                                                    }}
                                                                    onBlur={() => handleRenameStage(col.title, idx)}
                                                                    autoFocus
                                                                    className={`font-semibold text-slate-900 bg-slate-50 border border-slate-200 px-2 py-0.5 rounded outline-none ring-2 ring-primary/20 text-xs sm:text-sm min-w-0 ${isExpanded ? 'w-full flex-1' : 'w-[160px] sm:w-[180px]'}`}
                                                                />
                                                            ) : (
                                                                <h3
                                                                    className={`font-semibold text-slate-900 cursor-text hover:text-primary transition-colors truncate text-xs sm:text-sm ${isExpanded ? 'min-w-0 flex-1' : ''}`}
                                                                    onDoubleClick={() => {
                                                                        if (!PROTECTED_STAGES.includes(col.title)) {
                                                                            setEditingStageIdx(idx);
                                                                            setEditingStageName(col.title);
                                                                        } else {
                                                                            toast.info("Protected stages cannot be renamed.");
                                                                        }
                                                                    }}
                                                                    title={PROTECTED_STAGES.includes(col.title) ? "Protected Stage" : `${col.title} (Double-click to rename)`}
                                                                >
                                                                    {col.title}
                                                                </h3>
                                                            )}
                                                        </div>

                                                        <span className={`shrink-0 min-w-[1.75rem] h-6 sm:h-7 px-1.5 rounded-full flex items-center justify-center text-xs font-bold transition-all shadow-xs ${col.count > 0 ? 'bg-gradient-to-br from-[#1AA6A8] to-[#0E7C7E] text-white ring-1.5 ring-[#1AA6A8]/30' : 'bg-slate-100 text-slate-500 border border-slate-200'}`}>
                                                            {col.count}
                                                        </span>
                                                    </div>

                                                    <div className="flex items-center gap-2">
                                                        {/* Header Dropdown Menu (Hover based) */}
                                                        <div className={`absolute opacity-0 group-hover/header:opacity-100 transition-opacity bg-white shadow-sm border border-slate-200 rounded-md flex overflow-hidden z-20 ${isExpanded ? 'right-4 top-1/2 -translate-y-1/2 sm:right-auto sm:top-auto sm:translate-y-0 sm:bottom-4 sm:left-4' : 'right-4 top-1/2 -translate-y-1/2'}`} onClick={e => e.stopPropagation()}>
                                                            <button
                                                                disabled={idx === 0}
                                                                onClick={(e) => { e.stopPropagation(); handleSlideStage(idx, 'left'); }}
                                                                className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-50 disabled:opacity-30 transition-colors border-r border-slate-100" title="Slide Left"
                                                            >
                                                                <ArrowLeft className="w-3.5 h-3.5" />
                                                            </button>
                                                            <button
                                                                disabled={idx === activeStages.length - 1}
                                                                onClick={(e) => { e.stopPropagation(); handleSlideStage(idx, 'right'); }}
                                                                className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-50 disabled:opacity-30 transition-colors border-r border-slate-100" title="Slide Right"
                                                            >
                                                                <ArrowRight className="w-3.5 h-3.5" />
                                                            </button>

                                                            {!PROTECTED_STAGES.includes(col.title) && (
                                                                <button
                                                                    onClick={(e) => { e.stopPropagation(); handleDeleteStage(col.title, idx); }}
                                                                    className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors" title="Delete Stage"
                                                                >
                                                                    <Trash2 className="w-3.5 h-3.5" />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {isExpanded && (
                                                <div className="p-2 sm:p-3.5 flex-1 overflow-x-auto min-w-0 custom-scrollbar bg-slate-50/50">
                                                    {col.items.length === 0 ? (
                                                        <div className="text-center text-slate-400 text-sm py-8 h-full flex flex-col justify-center">No leads in this stage</div>
                                                    ) : (
                                                        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-2.5 min-w-0 sm:min-w-min">
                                                            {displayedItems.map((item, itemIdx) => {
                                                                const priorityMeta = item.priority === 'hot'
                                                                    ? { label: 'Hot', cls: 'bg-red-100 text-red-700 border-red-200' }
                                                                    : item.priority === 'cold'
                                                                        ? { label: 'Low', cls: 'bg-blue-100 text-blue-700 border-blue-200' }
                                                                        : { label: 'Medium', cls: 'bg-amber-100 text-amber-700 border-amber-200' };
                                                                // Extract service name: prefer service_interest column, then parse from notes
                                                                let serviceName: string | null = item.service_interest || null;
                                                                let shiftBubble: string | null = null;
                                                                let serviceLocation: string | null = null;

                                                                if (item.notes) {
                                                                    // Parse structured notes: "Service: X\nShift: Y\nLocation: Z"
                                                                    const serviceMatch = item.notes.match(/^Service:\s*(.+)$/im);
                                                                    const shiftMatch = item.notes.match(/^Shift:\s*(.+)$/im);
                                                                    const locMatch = item.notes.match(/^Location:\s*(.+)$/im);
                                                                    if (!serviceName && serviceMatch) {
                                                                        serviceName = serviceMatch[1].trim();
                                                                    }
                                                                    if (shiftMatch) {
                                                                        shiftBubble = shiftMatch[1].trim();
                                                                    }
                                                                    if (locMatch) {
                                                                        serviceLocation = locMatch[1].replace(/^,\s*|,\s*$/g, '').trim();
                                                                    }
                                                                }

                                                                // Fallback: use intent if still nothing
                                                                if (!serviceName) serviceName = item.intent || null;

                                                                // Sanitize: drop placeholder / empty values
                                                                if (serviceName === 'Unknown' || serviceName === '') serviceName = null;
                                                                if (shiftBubble === '' || shiftBubble === 'Unknown') shiftBubble = null;
                                                                if (serviceLocation === ', , , ' || serviceLocation === '') serviceLocation = null;
                                                                const deliveryLog = deliveryLogs
                                                                    .filter((l) => l.payload?.lead_id === item.id)
                                                                    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
                                                                    
                                                                // Extract active workers from services
                                                                let activeWorkers: string[] = [];
                                                                if (item.services && Array.isArray(item.services)) {
                                                                    const activeAssignments = item.services
                                                                        .filter((s: any) => s.status === 'active' || s.status === 'pending')
                                                                        .flatMap((s: any) => s.service_worker_assignments || [])
                                                                        .filter((wa: any) => wa.employees?.full_name && !wa.end_date); // Ignore ended assignments
                                                                    
                                                                    const names = new Set<string>();
                                                                    activeAssignments.forEach((wa: any) => names.add(wa.employees.full_name));
                                                                    activeWorkers = Array.from(names);
                                                                }
                                                                
                                                                // Fallback to assigned_worker_name column if nothing found in services
                                                                if (activeWorkers.length === 0 && item.assigned_worker_name) {
                                                                    activeWorkers = [item.assigned_worker_name];
                                                                }
                                                                const isCardBadgesVisible = cardBadgesOverrides[item.id] !== undefined
                                                                    ? cardBadgesOverrides[item.id]
                                                                    : showBadges;

                                                                const lQuote = item.crm_quotations?.[0];
                                                                const cRate = item.complete_month_daily_rate || lQuote?.complete_month_rate || item.services?.[0]?.complete_month_daily_rate;
                                                                const incRate = item.incomplete_month_daily_rate || lQuote?.incomplete_month_rate || item.services?.[0]?.incomplete_month_daily_rate;

                                                                return (
                                                                    <div key={item.id} className={`relative w-full sm:w-[260px] shrink-0 bg-white rounded-xl shadow-2xs sm:shadow-xs border hover:shadow-md transition-all cursor-default flex flex-col ${item.needs_attention ? 'border-red-300 ring-2 ring-red-100' : 'border-slate-200 hover:border-slate-300'}`}>
                                                                        {item.needs_attention && (
                                                                            <div className="absolute -top-1.5 -right-1.5 w-3.5 h-3.5 sm:w-4 sm:h-4 bg-red-500 rounded-full border-2 border-white shadow-sm z-10 animate-pulse"></div>
                                                                        )}

                                                                        {!isCardBadgesVisible ? (
                                                                            /* ── SIMPLE LEAD CARD (Sr No, Full Name, Priority, Phone, Actions, Rates) ── */
                                                                            <div className="flex flex-col flex-1">
                                                                                <div className="p-3 flex flex-col gap-2.5 flex-1">
                                                                                    {/* Row 1: Sr No. + Full Name + Priority Badge */}
                                                                                    <div className="flex items-start justify-between gap-2">
                                                                                        <div className="flex items-baseline gap-1.5 min-w-0 flex-1">
                                                                                            <span className="text-xs font-bold text-slate-400 font-mono shrink-0 select-none">
                                                                                                {itemIdx + 1}.
                                                                                            </span>
                                                                                            <h4
                                                                                                onClick={async (e) => {
                                                                                                    e.stopPropagation();
                                                                                                    setSelectedInspectorLead(item);
                                                                                                    fetchLeadActivity(item.id, item.duplicate_of_lead_id);
                                                                                                    if (item.needs_attention) {
                                                                                                        setLeads(prev => prev.map(l => l.id === item.id ? { ...l, needs_attention: false } : l));
                                                                                                        await supabase.from('crm_leads').update({ needs_attention: false }).eq('id', item.id);
                                                                                                    }
                                                                                                }}
                                                                                                className="text-xs sm:text-sm font-bold text-slate-900 leading-snug break-words flex-1 min-w-0 hover:text-primary cursor-pointer transition-colors"
                                                                                                title={`View details for ${item.name}`}
                                                                                            >
                                                                                                {item.name}
                                                                                            </h4>
                                                                                        </div>
                                                                                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold border uppercase tracking-wider shrink-0 ${priorityMeta.cls}`}>
                                                                                            {priorityMeta.label}
                                                                                        </span>
                                                                                    </div>

                                                                                    {/* Row 2: Phone number + Actions (Edit, Expand Eye, Chat) */}
                                                                                    <div className="flex items-center justify-between gap-2 text-[11px] text-slate-600">
                                                                                        <span className="truncate flex items-center gap-1 font-medium text-slate-600">
                                                                                            <Phone className="w-3 h-3 text-slate-400 shrink-0" />
                                                                                            {formatPhoneNumber(item.whatsapp_number || item.phone) || 'No phone'}
                                                                                        </span>
                                                                                        <div className="flex items-center gap-0.5 shrink-0">
                                                                                            <button
                                                                                                type="button"
                                                                                                onClick={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                    openQuickEdit(item);
                                                                                                }}
                                                                                                className="p-1 rounded-md text-slate-400 hover:text-primary hover:bg-slate-100 transition-colors cursor-pointer"
                                                                                                title="Quick edit lead info & rates"
                                                                                            >
                                                                                                <Edit3 className="w-3.5 h-3.5" />
                                                                                            </button>
                                                                                            <button
                                                                                                type="button"
                                                                                                onClick={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                    toggleCardBadges(item.id);
                                                                                                }}
                                                                                                className="p-1 rounded-md text-slate-400 hover:text-primary hover:bg-slate-100 transition-colors cursor-pointer"
                                                                                                title="Expand card & show details"
                                                                                            >
                                                                                                <Eye className="w-3.5 h-3.5" />
                                                                                            </button>
                                                                                            <button
                                                                                                type="button"
                                                                                                title="Chat on WhatsApp"
                                                                                                onClick={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                    fetchWhatsappChat(item);
                                                                                                }}
                                                                                                className="p-1 rounded-md text-slate-400 hover:text-[#1AA6A8] hover:bg-emerald-50 transition-colors cursor-pointer"
                                                                                            >
                                                                                                <MessageCircle className="w-3.5 h-3.5" />
                                                                                            </button>
                                                                                        </div>
                                                                                    </div>

                                                                                    {/* Row 3: Rates & Service Tag */}
                                                                                    <div className="flex items-center justify-between gap-1 pt-1.5 border-t border-slate-100 text-[10px]">
                                                                                        <div className="flex items-center gap-1.5 flex-wrap">
                                                                                            {cRate ? (
                                                                                                <span
                                                                                                    onClick={(e) => { e.stopPropagation(); openQuickEdit(item); }}
                                                                                                    title="Full Month Rate (Click to edit)"
                                                                                                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 cursor-pointer hover:bg-emerald-100 transition-colors"
                                                                                                >
                                                                                                    Full: ₹{Number(cRate).toLocaleString('en-IN')}/d
                                                                                                </span>
                                                                                            ) : null}
                                                                                            {incRate ? (
                                                                                                <span
                                                                                                    onClick={(e) => { e.stopPropagation(); openQuickEdit(item); }}
                                                                                                    title="Partial Month Rate (Click to edit)"
                                                                                                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200 cursor-pointer hover:bg-blue-100 transition-colors"
                                                                                                >
                                                                                                    Part: ₹{Number(incRate).toLocaleString('en-IN')}/d
                                                                                                </span>
                                                                                            ) : null}
                                                                                            {!cRate && !incRate && (
                                                                                                <button
                                                                                                    type="button"
                                                                                                    onClick={(e) => { e.stopPropagation(); openQuickEdit(item); }}
                                                                                                    className="text-[10px] text-slate-400 hover:text-primary font-medium flex items-center gap-1 cursor-pointer transition-colors"
                                                                                                >
                                                                                                    <Plus className="w-2.5 h-2.5" /> Set Rates
                                                                                                </button>
                                                                                            )}
                                                                                        </div>
                                                                                        {serviceName && (
                                                                                            <span className="text-[9px] font-semibold text-slate-500 bg-slate-50 px-1.5 py-0.5 rounded border border-slate-200 truncate max-w-[95px]" title={serviceName}>
                                                                                                {serviceName}
                                                                                            </span>
                                                                                        )}
                                                                                    </div>
                                                                                </div>

                                                                                {/* View Details */}
                                                                                <button
                                                                                    onClick={async (e) => {
                                                                                        e.stopPropagation();
                                                                                        setSelectedInspectorLead(item);
                                                                                        fetchLeadActivity(item.id, item.duplicate_of_lead_id);
                                                                                        if (item.needs_attention) {
                                                                                            setLeads(prev => prev.map(l => l.id === item.id ? { ...l, needs_attention: false } : l));
                                                                                            await supabase.from('crm_leads').update({ needs_attention: false }).eq('id', item.id);
                                                                                        }
                                                                                    }}
                                                                                    className="w-full py-1.5 border-t border-slate-100 text-slate-500 hover:text-primary hover:bg-slate-50 text-[11px] font-semibold rounded-b-xl transition-all flex items-center justify-center gap-1 group mt-auto cursor-pointer"
                                                                                >
                                                                                    View Details <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                                                                                </button>
                                                                            </div>
                                                                        ) : (
                                                                            <>
                                                                                {/* ── PHONE VIEW COMPACT CARD (< sm) ── */}
                                                                        <div
                                                                            onClick={async () => {
                                                                                setSelectedInspectorLead(item);
                                                                                fetchLeadActivity(item.id, item.duplicate_of_lead_id);
                                                                                if (item.needs_attention) {
                                                                                    setLeads(prev => prev.map(l => l.id === item.id ? { ...l, needs_attention: false } : l));
                                                                                    await supabase.from('crm_leads').update({ needs_attention: false }).eq('id', item.id);
                                                                                }
                                                                            }}
                                                                            className="sm:hidden px-3 py-2.5 flex flex-col gap-1.5 cursor-pointer active:bg-slate-50 transition-colors"
                                                                        >
                                                                            {/* Row 1: Serial + Name + Badges + Arrow */}
                                                                            <div className="flex items-center justify-between gap-1.5 min-w-0">
                                                                                <div className="flex items-center gap-1.5 min-w-0 flex-1 flex-wrap">
                                                                                    <span className="text-xs font-bold text-slate-400 font-mono shrink-0 select-none">
                                                                                        {itemIdx + 1}.
                                                                                    </span>
                                                                                    <span className="font-bold text-slate-900 text-xs break-words">
                                                                                        {item.name}
                                                                                    </span>
                                                                                    <span className={`inline-flex items-center px-1.5 py-0.2 rounded text-[9px] font-bold border uppercase tracking-wider shrink-0 ${priorityMeta.cls}`}>
                                                                                        {priorityMeta.label}
                                                                                    </span>
                                                                                    {(() => {
                                                                                        const city = extractCity(item);
                                                                                        return (
                                                                                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.2 rounded text-[9px] font-bold bg-sky-50 text-sky-700 border border-sky-200 uppercase shrink-0">
                                                                                                <MapPin className="w-2.5 h-2.5 text-sky-600" /> {city}
                                                                                            </span>
                                                                                        );
                                                                                    })()}
                                                                                    {serviceName && (
                                                                                        <span className="inline-flex items-center px-1.5 py-0.2 rounded text-[9px] font-bold bg-emerald-100 text-emerald-700 border border-emerald-200 uppercase tracking-wider truncate shrink-0 max-w-[130px]">
                                                                                            {serviceName}
                                                                                        </span>
                                                                                    )}
                                                                                    {item.has_paid_deposit ? (
                                                                                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.2 rounded text-[9px] font-bold bg-green-100 text-green-700 border border-green-200 uppercase shrink-0">
                                                                                            <CheckCircle2 className="w-2.5 h-2.5" /> Deposit
                                                                                        </span>
                                                                                    ) : item.is_deposit_pending ? (
                                                                                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.2 rounded text-[9px] font-bold bg-amber-100 text-amber-800 border border-amber-300 uppercase shrink-0">
                                                                                            <Clock className="w-2.5 h-2.5" /> Pending
                                                                                        </span>
                                                                                    ) : null}
                                                                                    {activeWorkers.length > 0 && (
                                                                                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.2 rounded text-[9px] font-bold bg-purple-100 text-purple-700 border border-purple-200 uppercase shrink-0">
                                                                                            <User className="w-2.5 h-2.5" /> {activeWorkers[0]}
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                                <div className="flex items-center gap-1 shrink-0">
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            toggleCardBadges(item.id);
                                                                                        }}
                                                                                        className="p-1 rounded text-slate-400 hover:text-primary transition-colors cursor-pointer"
                                                                                        title="Hide badges for this lead"
                                                                                    >
                                                                                        <EyeOff className="w-3.5 h-3.5 text-primary" />
                                                                                    </button>
                                                                                    <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
                                                                                </div>
                                                                            </div>

                                                                            {/* Row 2: Phone Number + Time */}
                                                                            <div className="flex items-center justify-between text-[11px] text-slate-500 min-w-0">
                                                                                <span className="flex items-center gap-1 font-medium text-slate-600 truncate">
                                                                                    <Phone className="w-3 h-3 text-slate-400 shrink-0" />
                                                                                    {formatPhoneNumber(item.whatsapp_number || item.phone) || 'No phone'}
                                                                                </span>
                                                                                <span className="text-[10px] text-slate-400 shrink-0 ml-2">{getRelativeTime(item.created_at)}</span>
                                                                            </div>

                                                                            {/* Row 3: Rates & Quick Edit */}
                                                                            {(() => {
                                                                                const lQuote = item.crm_quotations?.[0];
                                                                                const cRate = item.complete_month_daily_rate || lQuote?.complete_month_rate || item.services?.[0]?.complete_month_daily_rate;
                                                                                const incRate = item.incomplete_month_daily_rate || lQuote?.incomplete_month_rate || item.services?.[0]?.incomplete_month_daily_rate;

                                                                                return (
                                                                                    <div className="flex items-center justify-between gap-1 pt-1 border-t border-slate-100 text-[10px]">
                                                                                        <div className="flex items-center gap-1 flex-wrap">
                                                                                            {cRate ? (
                                                                                                <span className="font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded text-[9px]">
                                                                                                    Full: ₹{Number(cRate).toLocaleString('en-IN')}/d
                                                                                                </span>
                                                                                            ) : null}
                                                                                            {incRate ? (
                                                                                                <span className="font-bold text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded text-[9px]">
                                                                                                    Part: ₹{Number(incRate).toLocaleString('en-IN')}/d
                                                                                                </span>
                                                                                            ) : null}
                                                                                        </div>
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                openQuickEdit(item);
                                                                                            }}
                                                                                            className="text-primary hover:underline font-bold text-[10px] flex items-center gap-0.5 shrink-0 ml-auto cursor-pointer"
                                                                                        >
                                                                                            <Edit3 className="w-2.5 h-2.5" /> Edit Info
                                                                                        </button>
                                                                                    </div>
                                                                                );
                                                                            })()}

                                                                            {col.title === 'Closed Won' && (
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        setDetailsModalClient(item);
                                                                                    }}
                                                                                    className="w-full py-1 px-2 mt-0.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded text-[10px] font-bold flex items-center justify-center gap-1 cursor-pointer"
                                                                                >
                                                                                    <RotateCcw className="w-3 h-3 text-emerald-600" /> Start New Service
                                                                                </button>
                                                                            )}
                                                                        </div>

                                                                        {/* ── DESKTOP FULL CARD (sm+) ── */}
                                                                        <div className="hidden sm:flex flex-col flex-1">
                                                                            <div className="p-3.5 flex flex-col gap-2.5 flex-1">
                                                                                {/* Row 1: Serial + Name + Actions */}
                                                                                <div className="flex items-start justify-between gap-2">
                                                                                    <div className="flex items-baseline gap-1.5 min-w-0 flex-1">
                                                                                        <span className="text-xs font-bold text-slate-400 font-mono shrink-0 select-none">
                                                                                            {itemIdx + 1}.
                                                                                        </span>
                                                                                        <p
                                                                                            onClick={async (e) => {
                                                                                                e.stopPropagation();
                                                                                                setSelectedInspectorLead(item);
                                                                                                fetchLeadActivity(item.id, item.duplicate_of_lead_id);
                                                                                                if (item.needs_attention) {
                                                                                                    setLeads(prev => prev.map(l => l.id === item.id ? { ...l, needs_attention: false } : l));
                                                                                                    await supabase.from('crm_leads').update({ needs_attention: false }).eq('id', item.id);
                                                                                                }
                                                                                            }}
                                                                                            className="text-sm font-bold text-slate-900 leading-snug break-words flex-1 hover:text-primary cursor-pointer transition-colors"
                                                                                            title={`View details for ${item.name}`}
                                                                                        >
                                                                                            {item.name}
                                                                                        </p>
                                                                                    </div>
                                                                                    <div className="flex items-center gap-0.5 shrink-0">
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                openQuickEdit(item);
                                                                                            }}
                                                                                            className="p-1 rounded-md text-slate-400 hover:text-primary hover:bg-slate-100 transition-colors shrink-0 cursor-pointer"
                                                                                            title="Quick edit lead info & rates"
                                                                                        >
                                                                                            <Edit3 className="w-3.5 h-3.5" />
                                                                                        </button>
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                toggleCardBadges(item.id);
                                                                                            }}
                                                                                            className="p-1 rounded-md text-slate-400 hover:text-primary hover:bg-slate-100 transition-colors shrink-0 cursor-pointer"
                                                                                            title="Collapse card & hide details"
                                                                                        >
                                                                                            <EyeOff className="w-3.5 h-3.5 text-primary" />
                                                                                        </button>
                                                                                    </div>
                                                                                </div>
                                                                                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                                                                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border uppercase tracking-wider ${priorityMeta.cls}`}>
                                                                                                {priorityMeta.label}
                                                                                            </span>
                                                                                            {(() => {
                                                                                                const city = extractCity(item);
                                                                                                return (
                                                                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-sky-50 text-sky-700 border border-sky-200 uppercase tracking-wider">
                                                                                                        <MapPin className="w-2.5 h-2.5 text-sky-600" /> {city}
                                                                                                    </span>
                                                                                                );
                                                                                            })()}
                                                                                            {serviceName && (
                                                                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700 border border-emerald-200 uppercase tracking-wider">
                                                                                                    {serviceName}
                                                                                                </span>
                                                                                            )}
                                                                                            {item.has_paid_deposit ? (
                                                                                                <button
                                                                                                    onClick={(e) => {
                                                                                                        e.stopPropagation();
                                                                                                        navigate('/admin/billing?tab=history');
                                                                                                    }}
                                                                                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-100 text-green-700 border border-green-200 uppercase tracking-wider cursor-pointer hover:bg-green-200 transition-colors"
                                                                                                    title="Deposit Paid — View in Finance > Collection History"
                                                                                                >
                                                                                                    <CheckCircle2 className="w-2.5 h-2.5" /> Deposit Paid
                                                                                                </button>
                                                                                            ) : item.is_deposit_pending ? (
                                                                                                <button
                                                                                                    onClick={(e) => {
                                                                                                        e.stopPropagation();
                                                                                                        navigate('/admin/clients');
                                                                                                    }}
                                                                                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-300 uppercase tracking-wider cursor-pointer hover:bg-amber-200 transition-colors"
                                                                                                    title="Deposit Pending for current service — Click to manage in Clients"
                                                                                                >
                                                                                                    <Clock className="w-2.5 h-2.5" /> Deposit Pending
                                                                                                </button>
                                                                                            ) : null}
                                                                                            {activeWorkers.length > 0 && (
                                                                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-100 text-purple-700 border border-purple-200 uppercase tracking-wider">
                                                                                                    <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                                                                    </svg>
                                                                                                    {activeWorkers[0]}
                                                                                                    {activeWorkers.length > 1 && (
                                                                                                        <span className="ml-0.5 text-[9px] font-bold text-purple-600 bg-purple-200/60 px-1 rounded-sm">
                                                                                                            +{activeWorkers.length - 1}
                                                                                                        </span>
                                                                                                    )}
                                                                                                </span>
                                                                                            )}
                                                                                        </div>
                                                                                        {/* Service Details Breakdown */}
                                                                                        <div className="flex flex-col gap-1.5">
                                                                                    {shiftBubble && (
                                                                                        <div className="flex items-center gap-2 bg-slate-50 px-2.5 py-1.5 rounded-lg border border-slate-100">
                                                                                            <Clock className="w-3 h-3 text-primary shrink-0" />
                                                                                            <span className="text-[10px] font-medium text-slate-600 truncate">
                                                                                                {shiftBubble}
                                                                                            </span>
                                                                                        </div>
                                                                                    )}
                                                                                    {serviceLocation && (
                                                                                        <div className="flex items-center gap-2 bg-slate-50 px-2.5 py-1.5 rounded-lg border border-slate-100">
                                                                                            <Globe className="w-3 h-3 text-primary shrink-0" />
                                                                                            <span className="text-[10px] font-medium text-slate-600 truncate">
                                                                                                {serviceLocation}
                                                                                            </span>
                                                                                        </div>
                                                                                    )}
                                                                                    {(item.plannedStart || item.plannedDuration) && (
                                                                                        <div className="flex items-center gap-2 bg-indigo-50 px-2.5 py-1.5 rounded-lg border border-indigo-100 mt-0.5">
                                                                                            <svg className="w-3 h-3 text-indigo-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                                                            </svg>
                                                                                            <span className="text-[10px] font-bold text-indigo-700 truncate tracking-wide">
                                                                                                {item.plannedStart ? new Date(item.plannedStart).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : 'TBD'}
                                                                                                {item.plannedDuration ? ` • ${item.plannedDuration}` : ''}
                                                                                            </span>
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                                {/* Rates Row */}
                                                                                {(() => {
                                                                                    const lQuote = item.crm_quotations?.[0];
                                                                                    const cRate = item.complete_month_daily_rate || lQuote?.complete_month_rate || item.services?.[0]?.complete_month_daily_rate;
                                                                                    const incRate = item.incomplete_month_daily_rate || lQuote?.incomplete_month_rate || item.services?.[0]?.incomplete_month_daily_rate;

                                                                                    return (
                                                                                        <div className="flex items-center gap-1.5 flex-wrap">
                                                                                            {cRate ? (
                                                                                                <span
                                                                                                    onClick={(e) => { e.stopPropagation(); openQuickEdit(item); }}
                                                                                                    title="Click to edit Full Month Rate"
                                                                                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors cursor-pointer"
                                                                                                >
                                                                                                    <TrendingUp className="w-2.5 h-2.5 text-emerald-600" /> Full: ₹{Number(cRate).toLocaleString('en-IN')}/d
                                                                                                </span>
                                                                                            ) : null}
                                                                                            {incRate ? (
                                                                                                <span
                                                                                                    onClick={(e) => { e.stopPropagation(); openQuickEdit(item); }}
                                                                                                    title="Click to edit Partial Month Rate"
                                                                                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors cursor-pointer"
                                                                                                >
                                                                                                    <TrendingUp className="w-2.5 h-2.5 text-blue-600" /> Part: ₹{Number(incRate).toLocaleString('en-IN')}/d
                                                                                                </span>
                                                                                            ) : null}
                                                                                            {(!cRate && !incRate) && (
                                                                                                <button
                                                                                                    type="button"
                                                                                                    onClick={(e) => { e.stopPropagation(); openQuickEdit(item); }}
                                                                                                    className="w-full py-1 px-2 bg-slate-50 hover:bg-primary/5 text-slate-500 hover:text-primary border border-dashed border-slate-300 hover:border-primary/40 rounded-md text-[10px] font-semibold flex items-center justify-center gap-1 transition-all cursor-pointer"
                                                                                                >
                                                                                                    <Plus className="w-2.5 h-2.5 text-primary" /> Set Rates / Care Info
                                                                                                </button>
                                                                                            )}
                                                                                        </div>
                                                                                    );
                                                                                })()}
                                                                                {/* Phone row */}
                                                                                <div className="flex items-center gap-2">
                                                                                    <span className="text-xs text-slate-600 truncate flex items-center gap-1">
                                                                                        <Phone className="w-3 h-3 text-slate-400 shrink-0" />
                                                                                        {formatPhoneNumber(item.whatsapp_number || item.phone) || 'No phone'}
                                                                                    </span>
                                                                                    {deliveryLog && (
                                                                                        <span className={`shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${deliveryLog.status === 'failed' || deliveryLog.status === 'error'
                                                                                                ? 'bg-red-100 text-red-700'
                                                                                                : deliveryLog.status === 'delivered'
                                                                                                    ? 'bg-green-100 text-green-700'
                                                                                                    : deliveryLog.status === 'read'
                                                                                                        ? 'bg-blue-100 text-blue-700'
                                                                                                        : 'bg-slate-100 text-slate-500'
                                                                                            }`} title={deliveryLog.error_message || undefined}>
                                                                                            {deliveryLog.status === 'accepted_by_meta' ? 'sent' : deliveryLog.status}
                                                                                        </span>
                                                                                    )}
                                                                                    <button
                                                                                        type="button"
                                                                                        title="View WhatsApp Chat History"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            fetchWhatsappChat(item);
                                                                                        }}
                                                                                        className="ml-auto p-1 rounded-md text-slate-400 hover:text-[#1AA6A8] hover:bg-emerald-50 transition-colors"
                                                                                    >
                                                                                        <MessageCircle className="w-3.5 h-3.5" />
                                                                                    </button>
                                                                                </div>
                                                                                {/* Time */}
                                                                                <p className="text-[11px] text-slate-400">{getRelativeTime(item.created_at)}</p>
                                                                                <div className="flex-1"></div>
                                                                            </div>
                                                                            {col.title === 'Closed Won' && (
                                                                                <div className="px-3 pb-2 pt-0">
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            setDetailsModalClient(item);
                                                                                        }}
                                                                                        className="w-full py-1.5 px-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-lg text-[11px] font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-2xs"
                                                                                    >
                                                                                        <RotateCcw className="w-3 h-3 text-emerald-600" /> Start New Service
                                                                                    </button>
                                                                                </div>
                                                                            )}
                                                                            {/* View Details */}
                                                                            <button
                                                                                onClick={async (e) => {
                                                                                    e.stopPropagation();
                                                                                    setSelectedInspectorLead(item);
                                                                                    fetchLeadActivity(item.id, item.duplicate_of_lead_id);
                                                                                    if (item.needs_attention) {
                                                                                        setLeads(prev => prev.map(l => l.id === item.id ? { ...l, needs_attention: false } : l));
                                                                                        await supabase.from('crm_leads').update({ needs_attention: false }).eq('id', item.id);
                                                                                    }
                                                                                }}
                                                                                className="w-full py-2 border-t border-slate-100 text-slate-500 hover:text-primary hover:bg-slate-50 text-[12px] font-semibold rounded-b-2xl transition-all flex items-center justify-center gap-1.5 group"
                                                                            >
                                                                                View Details <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                                                                            </button>
                                                                        </div>
                                                                    </>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                            {hasMore && (
                                                                <button
                                                                    onClick={(e) => { e.stopPropagation(); loadMoreInStage(col.title); }}
                                                                    className="w-full sm:w-[260px] shrink-0 flex items-center sm:flex-col justify-center gap-1.5 sm:gap-2 py-2 px-3 sm:p-2 bg-slate-100 border-2 border-dashed border-slate-300 rounded-xl text-slate-500 hover:text-primary hover:border-primary/50 transition-colors hover:bg-primary/5 min-h-[40px] sm:min-h-[85px] cursor-pointer"
                                                                >
                                                                    <div className="w-5 h-5 sm:w-7 sm:h-7 rounded-full bg-white flex items-center justify-center shadow-xs">
                                                                        <Plus className="w-3 h-3 sm:w-4 sm:h-4 text-primary" />
                                                                    </div>
                                                                    <span className="font-semibold text-xs">Load More ({col.items.length - limit} left)</span>
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                {/* Add Column Button */}
                                <div className="w-[320px] shrink-0 flex flex-col bg-transparent rounded-xl border-2 border-dashed border-slate-300 hover:border-slate-400 transition-colors">
                                    {isAddingStage ? (
                                        <div className="p-4 flex flex-col gap-3">
                                            <input
                                                type="text"
                                                value={newStageName}
                                                onChange={(e) => setNewStageName(e.target.value)}
                                                placeholder="Enter column name..."
                                                className="w-full text-sm py-2 px-3 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                                                autoFocus
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') handleAddStage();
                                                    if (e.key === 'Escape') { setIsAddingStage(false); setNewStageName(''); }
                                                }}
                                            />
                                            <div className="flex justify-end gap-2">
                                                <button onClick={() => { setIsAddingStage(false); setNewStageName(''); }} className="px-3 py-1.5 text-xs text-slate-500 hover:bg-slate-100 rounded-md">Cancel</button>
                                                <button onClick={handleAddStage} className="px-3 py-1.5 text-xs bg-primary text-white hover:bg-primary/90 rounded-md" disabled={!newStageName.trim()}>Add</button>
                                            </div>
                                        </div>
                                    ) : (
                                        <button onClick={() => setIsAddingStage(true)} className="flex items-center justify-center p-4 text-slate-500 hover:text-slate-800 transition-colors group flex-1">
                                            <div className="flex flex-col items-center gap-2">
                                                <div className="w-10 h-10 rounded-full bg-slate-100 group-hover:bg-slate-200 flex items-center justify-center transition-colors">
                                                    <Plus className="w-5 h-5 text-slate-400 group-hover:text-slate-600" />
                                                </div>
                                                <span className="font-medium">+ Create a new one</span>
                                            </div>
                                        </button>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
            {activeTab === 'voice' && (
                /* Voice AI Dashboard View */
                <div className="flex-1 flex flex-col gap-6">
                    <div className="grid lg:grid-cols-4 gap-4">
                        <div className="bg-slate-900 text-white rounded-xl p-5 border border-slate-800 shadow-sm">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="font-semibold text-slate-300">Total AI Calls Today</h3>
                                <Mic className="w-5 h-5 text-emerald-400" />
                            </div>
                            <p className="text-3xl font-bold">{isLoadingVoice ? '-' : voiceMetrics.totalCallsToday}</p>
                            <div className="flex items-center gap-2 mt-2 text-sm">
                                <span className="text-emerald-400 font-medium">Auto-tracked</span>
                                <span className="text-slate-400">via ElevenLabs</span>
                            </div>
                        </div>
                        <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="font-semibold text-slate-600">Leads Captured via Voice</h3>
                                <Users className="w-5 h-5 text-primary" />
                            </div>
                            <p className="text-3xl font-bold text-slate-900">{isLoadingVoice ? '-' : voiceMetrics.leadsCaptured}</p>
                            <div className="flex items-center gap-2 mt-2 text-sm">
                                <div className="w-full bg-slate-100 rounded-full h-1.5 flex-1 mt-1">
                                    <div className="bg-primary h-1.5 rounded-full" style={{ width: voiceMetrics.totalCallsToday > 0 ? `${Math.min(100, (voiceMetrics.leadsCaptured / voiceMetrics.totalCallsToday) * 100)}%` : '0%' }}></div>
                                </div>
                            </div>
                        </div>
                        <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="font-semibold text-slate-600">Avg. Call Duration</h3>
                                <Phone className="w-5 h-5 text-purple-500" />
                            </div>
                            <p className="text-3xl font-bold text-slate-900">{isLoadingVoice ? '-' : `${Math.floor(voiceMetrics.avgDurationSeconds / 60)}m ${voiceMetrics.avgDurationSeconds % 60}s`}</p>
                        </div>
                        <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm flex items-center justify-center">
                            <button
                                onClick={toggleCall}
                                disabled={callStatus === 'loading'}
                                className={`w-full h-full min-h-[100px] border-2 border-dashed rounded-xl font-medium transition-colors flex flex-col items-center justify-center gap-2 ${callStatus === 'active'
                                    ? 'border-red-200 hover:border-red-300 text-red-500 bg-red-50/50'
                                    : callStatus === 'loading'
                                        ? 'border-slate-200 text-slate-400 cursor-not-allowed'
                                        : 'border-slate-200 hover:border-primary/50 text-slate-500 hover:text-primary'
                                    }`}
                            >
                                {callStatus === 'loading' ? (
                                    <>
                                        <Loader2 className="w-6 h-6 animate-spin" />
                                        Connecting...
                                    </>
                                ) : callStatus === 'active' ? (
                                    <>
                                        <span className="relative flex h-6 w-6">
                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                            <span className="relative inline-flex rounded-full h-6 w-6 bg-red-500 items-center justify-center text-white">
                                                <PhoneOff className="w-3.5 h-3.5" />
                                            </span>
                                        </span>
                                        End Test Call
                                    </>
                                ) : (
                                    <>
                                        <Plus className="w-6 h-6" />
                                        Start Web Call (ElevenLabs)
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex-1 flex flex-col overflow-hidden">
                        <div className="p-5 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
                            <div className="mb-6">
                                <h2 className="text-lg font-bold text-slate-900">Call Logs & Audio recordings</h2>
                                <p className="text-sm text-slate-500">Listen to AI voice interactions and review transcripts instantly.</p>
                            </div>
                            <button
                                onClick={handleRetryVoice}
                                disabled={isLoadingVoice}
                                className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-bold text-slate-700 hover:bg-slate-50 transition-colors shadow-sm disabled:opacity-50"
                            >
                                Refresh Logs
                            </button>
                        </div>
                        <div className="flex-1 overflow-auto p-5 space-y-4">
                            {calls.map((call) => (
                                <div key={call.id} className="p-5 rounded-xl border border-slate-200 flex flex-col xl:flex-row gap-6 hover:shadow-sm transition-shadow">
                                    <div className="flex items-start gap-4 xl:w-1/3 shrink-0 border-b xl:border-b-0 xl:border-r border-slate-100 pb-4 xl:pb-0 xl:pr-6">
                                        <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${call.type === 'Inbound' ? 'bg-primary/10 text-primary' : 'bg-purple-50 text-purple-600'}`}>
                                            <Phone className="w-5 h-5" />
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2 mb-1">
                                                <h3 className="font-bold text-slate-900 text-lg">{call.capturedName || call.phone}</h3>
                                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${call.type === 'Inbound' ? 'bg-primary/10 text-primary' : 'bg-purple-100 text-purple-700'}`}>
                                                    {call.type}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-3 text-sm text-slate-500 mb-3">
                                                <span className="flex items-center gap-1"><Mic className="w-3.5 h-3.5" /> {call.duration}</span>
                                                <span>•</span>
                                                <span className="font-medium">{call.time}</span>
                                            </div>
                                            <div className="mt-3 pt-3 border-t border-slate-100/80">
                                                <VoicePlayer src={`https://sgyladamwnanudnropwl.supabase.co/functions/v1/get-call-audio?conversation_id=${call.id}`} />
                                                <div className="flex items-center gap-2 mt-3">
                                                    <button onClick={() => { setSelectedCall(call); setIsTranscriptModalOpen(true); }} className="text-sm font-bold text-[#1AA6A8] hover:text-[#0E7C7E] flex items-center gap-1.5 transition-colors bg-[#E6F7F7] px-4 py-1.5 rounded-lg border border-[#1AA6A8]/20 shadow-sm flex-1 justify-center">
                                                        <FileText className="w-4 h-4" /> View Full Transcript
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex-1 flex flex-col justify-between">
                                        <div>
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="flex items-center gap-1.5">
                                                    <Bot className="w-4 h-4 text-[#1AA6A8]" />
                                                    <span className="text-sm font-semibold text-slate-900">AI Summary & Intent: <span className="font-normal text-slate-600 bg-slate-100 px-2 py-0.5 rounded ml-1">{call.intent}</span></span>
                                                </div>
                                                {(() => {
                                                    const isAlreadyInPipeline = leads.some(l => {
                                                        if (call.lead_id && l.id === call.lead_id) return true;
                                                        return false;
                                                    });
                                                    const isProcessed = call.status === 'Processed' || isAlreadyInPipeline;

                                                    if (
                                                        call.automation_error &&
                                                        call.automation_error !== 'GREETING_SENT' &&
                                                        !isRetryableGreetingError(call.automation_error)
                                                    ) {
                                                        return (
                                                            <span className="flex items-center gap-1.5 px-2 py-1 bg-rose-50 border border-rose-100 text-rose-600 rounded text-[10px] font-bold" title={call.automation_error}>
                                                                <AlertTriangle className="w-3 h-3" /> AUTOMATION FAILED
                                                            </span>
                                                        );
                                                    }

                                                    return isProcessed ? (
                                                        <span className="flex items-center gap-1 text-xs font-bold text-[#1AA6A8] bg-[#E6F7F7] px-2 py-1 rounded" title={isAlreadyInPipeline ? "Found existing Lead with this phone number." : ""}>
                                                            <CheckCircle2 className="w-3.5 h-3.5" /> ADDED TO CRM
                                                        </span>
                                                    ) : null;
                                                })()}
                                            </div>
                                            <p className="text-sm text-slate-600 leading-relaxed italic bg-slate-50 p-3 rounded-lg border border-slate-100">"{call.summary}"</p>
                                        </div>

                                        {(() => {
                                            const isAlreadyInPipeline = leads.some(l => {
                                                if (call.lead_id && l.id === call.lead_id) return true;
                                                return false;
                                            });
                                            const isProcessed = isAlreadyInPipeline;

                                            return (!isProcessed && (call.capturedName || (call.summary && call.summary !== 'No summary available.' && call.summary !== 'Call completed.'))) ? (
                                                <div className="mt-4 flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-xl border border-primary/20 bg-primary/5 gap-4">
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-[10px] font-bold text-primary uppercase tracking-widest mb-1 opacity-70">Lead Data Captured</p>
                                                        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                                                            <p className="text-sm text-slate-900 font-semibold truncate max-w-[200px]">{call.capturedName}</p>
                                                            <span className="text-sm text-[#1AA6A8] font-medium whitespace-nowrap">Est. ₹{call.capturedValue}/mo</span>
                                                            <span className="flex items-center gap-1.5 text-xs font-bold text-slate-600 bg-slate-100 px-2.5 py-1 rounded-lg border border-slate-200">
                                                                <Phone className="w-3.5 h-3.5" /> {call.phone}
                                                            </span>
                                                            {call.capturedWhatsapp && (
                                                                <span className="flex items-center gap-1.5 text-xs font-bold text-[#1AA6A8] bg-[#EAFBFB] px-2.5 py-1 rounded-lg border border-[#1AA6A8]/10">
                                                                    <MessageCircle className="w-3.5 h-3.5" /> {call.capturedWhatsapp}
                                                                </span>
                                                            )}

                                                        </div>
                                                    </div>
                                                    <div className="flex flex-wrap items-center gap-2 shrink-0">
                                                        {(() => {
                                                            const greetStatus = callGreetingStatus[call.id];
                                                            const dbSent = call.automation_error === 'GREETING_SENT';
                                                            const phone = call.capturedWhatsapp;
                                                            if (!phone) return null;
                                                            if (greetStatus === 'sending') return (
                                                                <button disabled className="px-3 py-2 bg-slate-100 text-slate-400 text-xs font-bold rounded-lg flex items-center gap-1.5 shrink-0 cursor-not-allowed">
                                                                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Sending...
                                                                </button>
                                                            );
                                                            if (greetStatus === 'sent' || dbSent) return (
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleResendCallGreeting(call);
                                                                    }}
                                                                    className="px-3 py-2 bg-emerald-50 text-emerald-600 text-xs font-bold rounded-lg border border-emerald-100 flex items-center gap-1.5 shrink-0 hover:bg-emerald-100 transition-colors disabled:opacity-50"
                                                                >
                                                                    <RotateCcw className="w-3.5 h-3.5" /> Sent (Resend?)
                                                                </button>
                                                            );
                                                            if (greetStatus === 'error') {
                                                                const errorDetail = call.automation_error?.replace('GREETING_ERROR: ', '') || 'Unknown error';
                                                                return (
                                                                    <button
                                                                        onClick={() => handleSendCallGreeting(call, true)}
                                                                        className="px-3 py-2 bg-red-50 text-red-600 text-xs font-bold rounded-lg border border-red-100 flex items-center gap-1.5 shrink-0 hover:bg-red-100 transition-colors"
                                                                        title={`Error: ${errorDetail}`}
                                                                    >
                                                                        <AlertTriangle className="w-3.5 h-3.5" /> Retry Greeting
                                                                    </button>
                                                                );
                                                            }
                                                            return (
                                                                <button onClick={() => handleSendCallGreeting(call, true)} className="px-3 py-2 bg-[#E6F7F7] text-[#0E7C7E] text-xs font-bold rounded-lg border border-[#1AA6A8]/20 flex items-center gap-1.5 shrink-0 hover:bg-[#1AA6A8] hover:text-white transition-colors">
                                                                    <MessageCircle className="w-3.5 h-3.5" /> Send Greeting
                                                                </button>
                                                            );
                                                        })()}
                                                        {(() => {
                                                            const isNumberInCRM = [call.phone, call.capturedWhatsapp].some(num => 
                                                                num && leads.some(l => phonesMatch(l.phone, num) || phonesMatch(l.whatsapp_number, num))
                                                            );
                                                            
                                                            if (call.lead_id || call.status === 'Processed') {
                                                                return (
                                                                    <div className="flex items-center gap-1.5 text-xs font-bold text-purple-600 bg-purple-50 px-3 py-2 rounded-lg border border-purple-100 shrink-0">
                                                                        <CheckCircle2 className="w-3.5 h-3.5" /> Added to CRM
                                                                    </div>
                                                                );
                                                            }
                                                            
                                                            if (isNumberInCRM) {
                                                                return (
                                                                    <button
                                                                        type="button"
                                                                        disabled
                                                                        className="px-4 py-2 bg-slate-100 text-slate-400 text-xs font-bold rounded-lg flex items-center gap-2 shadow-sm shrink-0 whitespace-nowrap cursor-not-allowed opacity-70"
                                                                        title="This contact number is already in the pipeline"
                                                                    >
                                                                        <CheckCircle2 className="w-3.5 h-3.5" /> Already in CRM
                                                                    </button>
                                                                );
                                                            }

                                                            return (
                                                                <button
                                                                    type="button"
                                                                    disabled={capturingCallId != null && String(capturingCallId) === String(call.id)}
                                                                    onClick={() => captureCallAsLead(call.id)}
                                                                    className="px-4 py-2 bg-primary text-white text-xs font-bold rounded-lg hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2 transition-colors shadow-sm shrink-0 whitespace-nowrap"
                                                                >
                                                                    {capturingCallId != null && String(capturingCallId) === String(call.id) ? (
                                                                        <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Adding…</>
                                                                    ) : (
                                                                        <><Plus className="w-3.5 h-3.5" /> Add to Pipeline</>
                                                                    )}
                                                                </button>
                                                            );
                                                        })()}
                                                    </div>
                                                </div>
                                            ) : null;
                                        })()}
                                    </div>
                                </div>
                            ))}
                            {calls.length === 0 && !isLoadingVoice && (
                                <div className="text-center py-10">
                                    <Mic className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                                    <h3 className="text-slate-500 font-medium">No calls found for today.</h3>
                                </div>
                            )}
                            {isLoadingVoice && (
                                <div className="text-center py-10">
                                    <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto mb-3" />
                                    <h3 className="text-slate-500 font-medium">Loading voice data...</h3>
                                </div>
                            )}
                            {calls.length > 0 && (
                                <div className="text-center pt-2">
                                    <button onClick={handleRetryVoice} className="text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors">Refresh Calls</button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {activeTab === 'trash' && (
                /* Trash Can View */
                <div className="flex-1 flex flex-col gap-4">
                    {/* Header */}
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div>
                            <div className="flex items-center gap-2 mb-1">
                                <Trash2 className="w-5 h-5 text-red-400" />
                                <h2 className="text-lg font-bold text-slate-900">Trash</h2>
                                {trashedLeads.length > 0 && (
                                    <span className="bg-red-100 text-red-600 text-xs font-bold px-2 py-0.5 rounded-full">{trashedLeads.length}</span>
                                )}
                            </div>
                            <p className="text-sm text-slate-500">Leads are automatically deleted after 30 days. Restore to recover all data.</p>
                        </div>
                        {trashedLeads.length > 0 && (
                            <button
                                onClick={() => {
                                    toast(`Permanently delete all ${trashedLeads.length} lead(s) in trash?`, {
                                        action: { label: 'Empty Trash', onClick: handleEmptyTrash },
                                        cancel: { label: 'Cancel', onClick: () => { } },
                                        duration: 8000,
                                    });
                                }}
                                className="px-4 py-2 bg-red-50 text-red-600 border border-red-200 text-sm font-bold rounded-lg hover:bg-red-100 transition-colors flex items-center gap-2 shrink-0"
                            >
                                <Trash2 className="w-4 h-4" /> Empty Trash
                            </button>
                        )}
                    </div>

                    {/* Trash list */}
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex-1">
                        {isLoadingTrash ? (
                            <div className="flex flex-col items-center justify-center py-20">
                                <Loader2 className="w-8 h-8 text-red-400 animate-spin mb-4" />
                                <span className="text-slate-500 font-medium">Loading trash...</span>
                            </div>
                        ) : trashedLeads.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-20 text-center">
                                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                                    <Trash2 className="w-8 h-8 text-slate-300" />
                                </div>
                                <h3 className="text-lg font-bold text-slate-900 mb-1">Trash is Empty</h3>
                                <p className="text-slate-500 text-sm max-w-xs">Deleted leads will appear here for 30 days before being permanently removed.</p>
                            </div>
                        ) : (
                            <div className="divide-y divide-slate-100">
                                {trashedLeads.map(lead => {
                                    const deletedAt = new Date(lead.deleted_at);
                                    const expiresAt = new Date(deletedAt.getTime() + 30 * 24 * 60 * 60 * 1000);
                                    const daysLeft = Math.ceil((expiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
                                    return (
                                        <div key={lead.id} className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:bg-slate-50/50 transition-colors">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-red-50 text-red-400 flex items-center justify-center font-bold text-sm shrink-0">
                                                    {(lead.name || '?').charAt(0).toUpperCase()}
                                                </div>
                                                <div>
                                                    <p className="font-bold text-slate-900">{lead.name}</p>
                                                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                                        <span className="text-xs text-slate-500">{lead.phone || lead.whatsapp_number || 'No phone'}</span>
                                                        <span className="w-1 h-1 rounded-full bg-slate-300 inline-block"></span>
                                                        <span className="text-xs font-medium text-slate-600">{lead.pipeline_stage}</span>
                                                        <span className="w-1 h-1 rounded-full bg-slate-300 inline-block"></span>
                                                        <span className={`text-xs font-semibold ${daysLeft <= 3 ? 'text-red-500' : 'text-slate-400'}`}>
                                                            {daysLeft <= 0 ? 'Expires today' : `${daysLeft}d left`}
                                                        </span>
                                                    </div>
                                                    <p className="text-xs text-slate-400 mt-0.5">
                                                        Deleted {deletedAt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2 shrink-0">
                                                <button
                                                    onClick={() => handleRestoreLead(lead)}
                                                    className="px-3 py-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-bold rounded-lg hover:bg-emerald-100 transition-colors flex items-center gap-1.5"
                                                >
                                                    <RotateCcw className="w-3.5 h-3.5" /> Restore
                                                </button>
                                                <button
                                                    onClick={() => handleDeletePermanently(lead.id, lead.name)}
                                                    className="px-3 py-1.5 bg-red-50 text-red-600 border border-red-100 text-xs font-bold rounded-lg hover:bg-red-100 transition-colors flex items-center gap-1.5"
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" /> Delete
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            )}
            {activeTab === 'automations' && (
                /* AI Automations View */
                <div className="flex-1 flex flex-col gap-6 pb-8">
                    <div className="bg-white rounded-xl border border-amber-200 shadow-sm overflow-hidden">
                        <div className="p-5 border-b border-amber-100 bg-amber-50/60 flex items-center justify-between">
                            <div>
                                <h2 className="text-lg font-bold text-slate-900">Needs Attention</h2>
                                <p className="text-sm text-slate-500 mt-1">Operational events that should be reviewed by staff.</p>
                            </div>
                            <span className="text-xs font-bold text-amber-700 bg-white border border-amber-200 px-3 py-1 rounded-full">{attentionNotifications.length} active</span>
                        </div>
                        <div className="p-5 grid md:grid-cols-2 gap-3">
                            {attentionNotifications.length === 0 ? (
                                <p className="text-sm text-slate-400 font-medium">No attention alerts right now.</p>
                            ) : attentionNotifications.slice(0, 6).map((log: any) => (
                                <div key={log.id} className={`rounded-lg border p-3 flex gap-3 ${log.status === 'error' ? 'bg-red-50/70 border-red-100' : 'bg-amber-50/70 border-amber-100'}`}>
                                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${log.status === 'error' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                                        <log.icon className="w-4 h-4" />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <h4 className="text-sm font-bold text-slate-900 truncate">{log.title}</h4>
                                            <span className="text-[10px] text-slate-400 shrink-0">{log.time}</span>
                                        </div>
                                        <p className="text-xs text-slate-600 line-clamp-2 mt-1">{log.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                        {/* Active Workflows */}
                        <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col">
                            <div className="p-5 border-b border-slate-100">
                                <h2 className="text-lg font-bold text-slate-900">Active AI Workflows</h2>
                                <p className="text-sm text-slate-500 mt-1">Configure how the AI responds to new leads.</p>
                            </div>
                            <div className="p-5 flex-1 space-y-4">
                                <div className={`p-4 rounded-lg border transition-colors ${workflows.greeting ? 'border-primary/30 bg-primary/5' : 'border-slate-200'}`}>
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                            <MessageSquare className={`w-5 h-5 ${workflows.greeting ? 'text-primary' : 'text-slate-400'}`} />
                                            <h3 className={`font-semibold ${workflows.greeting ? 'text-primary' : 'text-slate-600'}`}>Instant Greeting & Triage</h3>
                                        </div>
                                        <button
                                            onClick={() => toggleWorkflow('greeting')}
                                            className={`w-10 h-5 rounded-full relative cursor-pointer transition-colors ${workflows.greeting ? 'bg-primary' : 'bg-slate-200'}`}
                                        >
                                            <div className={`w-4 h-4 rounded-full bg-white absolute top-0.5 shadow-sm transition-all ${workflows.greeting ? 'right-0.5' : 'left-0.5'}`}></div>
                                        </button>
                                    </div>
                                    <p className="text-sm text-slate-500 mb-3">Automatically replies to inbound web chats and SMS. Classifies intent (Booking vs. Inquiry).</p>
                                    <p className="text-xs font-semibold text-slate-500 bg-slate-50 border border-slate-200 px-3 py-2 rounded-md mt-3">
                                        Greetings are sent automatically after a call ends, or manually from a lead card. Each lead receives at most one greeting.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Execution Log */}
                        <div className="bg-slate-50 rounded-xl border border-slate-200 flex flex-col overflow-hidden">
                            <div className="p-5 border-b border-slate-200 bg-white">
                                <h2 className="text-lg font-bold text-slate-900">Recent Workflow Executions</h2>
                                <p className="text-sm text-slate-500 mt-1">Live log of actions taken by the AI agent.</p>
                            </div>
                            <div className="p-5 flex-1 max-h-[460px] overflow-y-auto space-y-4">
                                {automationLogs.length === 0 ? (
                                    <div className="text-center py-10">
                                        <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                            <Bot className="w-6 h-6 text-slate-300" />
                                        </div>
                                        <p className="text-sm text-slate-400 font-medium">No recent activity logged.</p>
                                    </div>
                                ) : automationLogs.map((log) => (
                                    <div key={log.id} className="flex gap-4">
                                        <div className="flex flex-col items-center">
                                            <div className="w-8 h-8 rounded-full bg-white border border-slate-200 flex items-center justify-center shrink-0 z-10">
                                                <log.icon className="w-4 h-4 text-slate-600" />
                                            </div>
                                            {log.id !== automationLogs.length && <div className="w-px h-full bg-slate-200 my-1"></div>}
                                        </div>
                                        <div className="pb-4">
                                            <div className="flex items-center gap-2 mb-1">
                                                <h4 className="text-sm font-bold text-slate-900">{log.title}</h4>
                                                <span className="text-xs text-slate-400">{log.time}</span>
                                            </div>
                                            <p className="text-sm text-slate-600 mb-2">{log.desc}</p>
                                            <div className="flex items-center gap-1.5">
                                                <CheckCircle2 className="w-3.5 h-3.5 text-[#1AA6A8]" />
                                                <span className="text-xs font-medium text-[#1AA6A8] uppercase tracking-wide">Executed</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                <div className="text-center pt-4">
                                    <button className="text-sm font-medium text-primary hover:text-primary/80 transition-colors">View All Logs</button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Recent Reviews Panel */}
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col overflow-hidden">
                        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
                            <div>
                                <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                                    <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
                                    Recent Reviews
                                </h2>
                                <p className="text-sm text-slate-500 mt-1">Client feedback collected via WhatsApp.</p>
                            </div>
                            <button onClick={fetchReviews} className="text-xs font-semibold text-primary hover:text-primary/80 transition-colors">Refresh</button>
                        </div>
                        <div className="p-5 flex-1 overflow-y-auto">
                            {isLoadingReviews ? (
                                <div className="flex items-center justify-center py-10">
                                    <Loader2 className="w-6 h-6 text-primary animate-spin" />
                                </div>
                            ) : reviews.length === 0 ? (
                                <div className="text-center py-10">
                                    <div className="w-12 h-12 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-3">
                                        <Star className="w-6 h-6 text-amber-300" />
                                    </div>
                                    <p className="text-sm text-slate-400 font-medium">No reviews yet.</p>
                                    <p className="text-xs text-slate-400 mt-1">Send a review request from a client's inspector panel.</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {reviews.map(review => (
                                        <div key={review.id} className="p-4 rounded-xl border border-slate-100 bg-slate-50/50">
                                            <div className="flex items-start justify-between gap-2 mb-2">
                                                <div>
                                                    <p className="font-bold text-slate-900 text-sm">{review.client_name}</p>
                                                    <p className="text-xs text-slate-400">{new Date(review.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                                                </div>
                                                {review.rating && (
                                                    <div className="flex items-center gap-0.5 shrink-0">
                                                        {[1, 2, 3, 4, 5].map(s => (
                                                            <Star key={s} className={`w-3.5 h-3.5 ${s <= review.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-200 fill-slate-200'}`} />
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            {review.review_text && (
                                                <p className="text-sm text-slate-600 leading-relaxed">"{review.review_text}"</p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {/* Staff Picker Modal */}
            {isStaffPickerOpen && staffPickerTargetLead && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-[60]">
                    <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[85vh]">
                        <div className="p-5 border-b border-slate-100 bg-purple-500/10 flex justify-between items-center shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                                    <Users className="w-5 h-5 text-purple-600" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-bold text-slate-900">Assign Staff Member</h2>
                                    <p className="text-xs text-slate-500 font-medium">FOR: {staffPickerTargetLead.name} — select an available worker below</p>
                                </div>
                            </div>
                            <button onClick={() => setIsStaffPickerOpen(false)} className="text-slate-400 hover:text-slate-600 p-2 rounded-full hover:bg-slate-100 transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="px-5 pt-5 pb-2 border-b border-slate-100 flex-shrink-0">
                            <div className="relative">
                                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                <input
                                    type="text"
                                    placeholder="Search workers by name or role..."
                                    value={staffSearchQuery}
                                    onChange={(e) => setStaffSearchQuery(e.target.value)}
                                    className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all text-sm"
                                />
                            </div>
                        </div>

                        <div className="p-5 flex-1 overflow-y-auto">
                            {isLoadingWorkers ? (
                                <div className="flex flex-col items-center justify-center py-16">
                                    <Loader2 className="w-8 h-8 text-purple-500 animate-spin mb-3" />
                                    <p className="text-slate-500 font-medium">Loading available staff...</p>
                                </div>
                            ) : availableWorkers.filter(w => (w.name || '').toLowerCase().includes(staffSearchQuery.toLowerCase()) || (w.role || '').toLowerCase().includes(staffSearchQuery.toLowerCase())).length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-16 text-center">
                                    <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                                        <Users className="w-8 h-8 text-slate-400" />
                                    </div>
                                    <h3 className="font-bold text-slate-800 mb-2">{staffSearchQuery ? 'No Matching Staff Found' : 'No Available Staff'}</h3>
                                    <p className="text-sm text-slate-500">{staffSearchQuery ? `No workers found matching "${staffSearchQuery}".` : 'All workers are currently assigned. Add more staff from the HR page.'}</p>
                                </div>
                            ) : (
                                <div className="grid sm:grid-cols-2 gap-3">
                                    {availableWorkers.filter(w => (w.name || '').toLowerCase().includes(staffSearchQuery.toLowerCase()) || (w.role || '').toLowerCase().includes(staffSearchQuery.toLowerCase())).map((worker, index) => (
                                        <div key={worker.id || index} className="p-4 border-2 border-slate-200 hover:border-purple-400 rounded-xl transition-all cursor-pointer group hover:shadow-md bg-white">
                                            <div className="flex items-start gap-3 mb-3">
                                                <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0">
                                                    {(worker.name || 'W').split(' ').map((n: string) => n[0]).join('').slice(0, 2)}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <h4 className="font-bold text-slate-900 truncate">{worker.name || 'Unknown Member'}</h4>
                                                    <p className="text-xs text-slate-500 truncate">{worker.role || 'Worker'}</p>
                                                </div>
                                                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#1AA6A8] bg-[#E6F7F7] px-2 py-0.5 rounded-full shrink-0">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-[#1AA6A8] animate-pulse"></span>
                                                    Available
                                                </span>
                                            </div>
                                            {worker.phone && (
                                                <p className="text-xs text-slate-500 flex items-center gap-1 mb-3">
                                                    <Phone className="w-3 h-3" /> {worker.phone}
                                                </p>
                                            )}
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => confirmWorkerSelection(worker)}
                                                    className="flex-1 py-2 bg-purple-50 hover:bg-purple-600 hover:text-white text-purple-700 text-xs font-bold rounded-lg border border-purple-200 transition-all group-hover:border-purple-400 flex items-center justify-center gap-1.5"
                                                >
                                                    <CheckCircle2 className="w-3.5 h-3.5" />
                                                    Assign to {(staffPickerTargetLead?.name || 'Lead').split(' ')[0]}
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        const lead = leads.find(l => l.id === staffPickerTargetLead?.id);
                                                        if (lead) sendWorkerProfileWhatsApp(lead, worker);
                                                    }}
                                                    className="p-2 bg-[#E6F7F7] text-[#1AA6A8] border border-[#1AA6A8]/20 rounded-lg hover:bg-[#1AA6A8] hover:text-white transition-all"
                                                    title="Share Profile via WhatsApp"
                                                >
                                                    <Send className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Send Quotation Modal */}
            <SendQuotationModal
                isOpen={isQuotationModalOpen}
                onClose={() => setIsQuotationModalOpen(false)}
                lead={quotationTargetLead}
                onDispatch={handleDispatchQuotation}
            />

            {/* AI WhatsApp Draft Modal */}
            {isAgentModalOpen && agentTargetLead && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-md flex items-center justify-center p-4 z-[100] transition-all">
                    <div className="bg-white/95 backdrop-blur-xl border border-white/40 rounded-2xl w-full max-w-lg shadow-2xl overflow-visible animate-in zoom-in-95 duration-200 flex flex-col">
                        <div className="p-5 border-b border-slate-100 bg-[#1AA6A8]/10 flex justify-between items-center">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-[#EAFBFB] rounded-full flex items-center justify-center">
                                    <Bot className="w-5 h-5 text-[#1AA6A8]" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-bold text-slate-900">AI WhatsApp Agent</h2>
                                    <p className="text-xs text-slate-500 font-medium tracking-wide">
                                        {agentTargetAction === 'staff' && selectedWorker
                                            ? `ASSIGNING: ${selectedWorker.name} → ${agentTargetLead.name}`
                                            : `DRAFTING TO: ${agentTargetLead.name}`}
                                    </p>
                                </div>
                            </div>
                            <button onClick={() => setIsAgentModalOpen(false)} className="text-slate-400 hover:text-slate-600 p-2 rounded-full hover:bg-slate-100 transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-5 space-y-4 flex-1">
                            {/* Simplified Message Type Selector */}
                            <div className="flex items-center justify-between">
                                <label className="block text-sm font-semibold text-slate-700 flex items-center gap-2">
                                    <MessageSquare className="w-4 h-4 text-primary" /> Message Type
                                </label>
                                <select
                                    value={agentTargetAction === 'custom' ? 'custom' : 'stage'}
                                    onChange={(e) => {
                                        if (e.target.value === 'custom') {
                                            setAgentTargetAction('custom');
                                        } else {
                                            // Derive the correct template action based on the lead's current stage
                                            const stage = agentTargetLead?.pipeline_stage;
                                            let restoreAction: any = 'inquiry';
                                            if (stage === 'In Discussion') restoreAction = 'quotation';
                                            else if (stage === 'Quotation Sent') restoreAction = 'consent';
                                            else if (stage === 'Form Submitted') restoreAction = 'staff';
                                            else if (stage === 'Staff Assigned') restoreAction = 'deposit';
                                            else if (stage === 'Monthly Billing') restoreAction = 'billing';

                                            // Handle special case for staff sharing which might not be stage-based
                                            if (selectedWorker) restoreAction = 'staff';

                                            setAgentTargetAction(restoreAction);
                                        }
                                    }}
                                    className="text-xs font-semibold text-slate-700 bg-slate-100 border-none rounded-lg px-3 py-1.5 outline-none focus:ring-1 focus:ring-primary"
                                >
                                    <option value="stage">
                                        {(() => {
                                            // Determine what the "Automated" label should be even if current action is 'custom'
                                            const stage = agentTargetLead?.pipeline_stage;
                                            let action = agentTargetAction;
                                            if (action === 'custom') {
                                                if (stage === 'In Discussion') action = 'quotation';
                                                else if (stage === 'Quotation Sent') action = 'consent';
                                                else if (stage === 'Form Submitted') action = 'staff';
                                                else if (stage === 'Staff Assigned') action = 'deposit';
                                                else if (stage === 'Monthly Billing') action = 'billing';
                                                else action = 'inquiry';

                                                if (selectedWorker) action = 'staff';
                                            }

                                            return action === 'inquiry' ? 'Greeting Message' :
                                                action === 'quotation' ? 'Quotation Template' :
                                                    action === 'consent' ? 'Consent Form' :
                                                        action === 'staff' ? 'Profile Sharing' :
                                                            action === 'deposit' ? 'Deposit Invoice' :
                                                                action === 'billing' ? 'Monthly Bill' :
                                                                    'Automated Template';
                                        })()}
                                    </option>
                                    <option value="custom">Manual Message</option>
                                </select>
                            </div>
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                                        <Edit3 className="w-4 h-4 text-primary" /> {isEditingTemplate ? 'Edit Default Template' : 'Review Editable Draft'}
                                    </label>
                                    {agentTargetAction !== 'deposit' && agentTargetAction !== 'consent' && agentTargetAction !== 'billing' && (
                                        <button
                                            onClick={() => setIsEditingTemplate(!isEditingTemplate)}
                                            className="text-xs font-semibold text-[#1AA6A8] hover:text-[#1AA6A8] transition-colors"
                                        >
                                            {isEditingTemplate ? 'Back to Draft View' : '⚙️ Edit Default Template'}
                                        </button>
                                    )}
                                </div>

                                {/* Recipient Switcher when assigning a worker */}
                                {agentTargetAction === 'staff' && selectedWorker && !isEditingTemplate && (
                                    <div className="bg-slate-100/90 p-1 rounded-xl flex items-center gap-1 border border-slate-200/80 mb-3">
                                        <button
                                            type="button"
                                            onClick={() => setStaffModalActiveTab('client')}
                                            className={`flex-1 py-1.5 px-3 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                                                staffModalActiveTab === 'client'
                                                    ? 'bg-white text-slate-800 shadow-xs'
                                                    : 'text-slate-500 hover:text-slate-700'
                                            }`}
                                        >
                                            <User className="w-3.5 h-3.5 text-[#1AA6A8]" />
                                            <span>Client Message (ID Card)</span>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setStaffModalActiveTab('worker')}
                                            className={`flex-1 py-1.5 px-3 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                                                staffModalActiveTab === 'worker'
                                                    ? 'bg-white text-slate-800 shadow-xs'
                                                    : 'text-slate-500 hover:text-slate-700'
                                            }`}
                                        >
                                            <UserCheck className="w-3.5 h-3.5 text-emerald-600" />
                                            <span>Worker Message</span>
                                            {workerSentSuccess ? (
                                                <span className="inline-flex items-center text-[10px] bg-emerald-100 text-emerald-700 font-bold px-1.5 py-0.2 rounded-full">
                                                    Sent ✓
                                                </span>
                                            ) : selectedWorker?.phone ? (
                                                <span className="text-[10px] text-slate-400 font-normal">
                                                    (+91 {selectedWorker.phone.replace(/\D/g, '').slice(-10)})
                                                </span>
                                            ) : null}
                                        </button>
                                    </div>
                                )}

                                <div className="relative">
                                    {agentTargetAction === 'consent' && !isEditingTemplate ? (
                                        <div className="w-full h-48 bg-[#EAFBFB] border border-[#1AA6A8]/20 rounded-xl p-6 flex flex-col items-center justify-center text-center shadow-inner">
                                            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mb-3 shadow-sm text-[#1AA6A8]">
                                                <FileText className="w-6 h-6" />
                                            </div>
                                            <h3 className="font-bold text-slate-800 mb-2">WhatsApp Consent Flow</h3>
                                            <p className="text-sm text-slate-600 leading-relaxed mx-auto max-w-xs">
                                                This will dispatch the interactive WhatsApp Flow template. {agentTargetLead?.name?.split(' ')[0] || 'They'} will receive a button to open and submit the consent form natively inside WhatsApp.
                                            </p>
                                        </div>
                                    ) : agentTargetAction === 'quotation' && !isEditingTemplate ? (
                                        <div className="space-y-3 bg-white p-4 rounded-xl border border-[#1AA6A8]/20 shadow-sm relative z-10 w-full mb-6">
                                            <div>
                                                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1">Service Name</label>
                                                <input type="text" value={quotationVars.v1} onChange={e => setQuotationVars({ ...quotationVars, v1: e.target.value })} className="w-full text-sm font-medium border border-slate-200 bg-slate-50 rounded px-2.5 py-1.5 outline-none focus:ring-1 focus:ring-[#1AA6A8]" placeholder="e.g. Registered Nurse" />
                                            </div>
                                            <div className="grid grid-cols-2 gap-3">
                                                <div>
                                                    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1">Hours of Service</label>
                                                    <input type="text" value={quotationVars.v2} onChange={e => setQuotationVars({ ...quotationVars, v2: e.target.value })} className="w-full text-sm font-medium border border-slate-200 bg-slate-50 rounded px-2.5 py-1.5 outline-none focus:ring-1 focus:ring-[#1AA6A8]" placeholder="e.g. 10 Hours or 24 Hours" />
                                                </div>
                                                <div>
                                                    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1">Complete Month (Per Day Rate)</label>
                                                    <input type="text" value={quotationVars.v3} onChange={e => setQuotationVars({ ...quotationVars, v3: e.target.value })} className="w-full text-sm font-medium border border-slate-200 bg-slate-50 rounded px-2.5 py-1.5 outline-none focus:ring-1 focus:ring-[#1AA6A8]" placeholder="e.g. ₹ 800/day" />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1">Incomplete Month (Per Day Rate)</label>
                                                <input type="text" value={quotationVars.v4} onChange={e => setQuotationVars({ ...quotationVars, v4: e.target.value })} className="w-full text-sm font-medium border border-slate-200 bg-slate-50 rounded px-2.5 py-1.5 outline-none focus:ring-1 focus:ring-[#1AA6A8]" placeholder="e.g. ₹ 1,500/day" />
                                            </div>
                                        </div>
                                    ) : (agentTargetAction === 'deposit' || agentTargetAction === 'billing') && !isEditingTemplate ? (
                                        <div className="space-y-3 bg-white p-4 rounded-xl border border-[#1AA6A8]/20 shadow-sm relative z-10 w-full mb-6">
                                            <div className="flex items-center gap-2 mb-2 pb-2 border-b border-slate-100">
                                                <FileText className="w-4 h-4 text-[#1AA6A8]" />
                                                <span className="text-xs font-bold text-slate-700">Invoice Details (Auto-generated PDF)</span>
                                            </div>
                                            <div className="grid grid-cols-2 gap-3">
                                                <div>
                                                    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1">
                                                        {agentTargetAction === 'billing' ? 'Billing Amount (₹)' : 'Deposit Amount (₹)'}
                                                    </label>
                                                    <input
                                                        type="number"
                                                        value={invoiceDepositAmount}
                                                        onChange={e => setInvoiceDepositAmount(e.target.value)}
                                                        className="w-full text-sm font-semibold border-2 border-slate-200 bg-white rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-[#1AA6A8]/30 focus:border-[#1AA6A8] text-slate-800"
                                                        placeholder={agentTargetLead?.quoted_monthly_rate || '15000'}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1">Due Date</label>
                                                    <input
                                                        type="date"
                                                        value={invoiceDueDate}
                                                        onChange={e => setInvoiceDueDate(e.target.value)}
                                                        className="w-full text-sm font-semibold border-2 border-slate-200 bg-white rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-[#1AA6A8]/30 focus:border-[#1AA6A8] text-slate-800"
                                                        onFocus={e => { if (!e.target.value) { const d = new Date(); d.setDate(d.getDate()+7); e.target.value = d.toISOString().split('T')[0]; setInvoiceDueDate(d.toISOString().split('T')[0]); } }}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1">Start Date</label>
                                                    <input
                                                        type="date"
                                                        value={invoiceStartDate}
                                                        onChange={e => setInvoiceStartDate(e.target.value)}
                                                        className="w-full text-sm font-semibold border-2 border-slate-200 bg-white rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-[#1AA6A8]/30 focus:border-[#1AA6A8] text-slate-800"
                                                    />
                                                </div>
                                                <div>
                                                    <div className="flex items-center justify-between mb-1">
                                                        <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide">End Date</label>
                                                        <label className="inline-flex items-center gap-1.5 cursor-pointer select-none text-[11px] font-bold text-[#1AA6A8] hover:text-[#15898A]">
                                                            <input
                                                                type="checkbox"
                                                                checked={isInvoiceOngoing}
                                                                onChange={e => {
                                                                    const checked = e.target.checked;
                                                                    setIsInvoiceOngoing(checked);
                                                                    if (checked) {
                                                                        setInvoiceEndDate('');
                                                                    } else {
                                                                        const d = new Date(invoiceStartDate || new Date());
                                                                        d.setDate(d.getDate() + 30);
                                                                        setInvoiceEndDate(d.toISOString().split('T')[0]);
                                                                    }
                                                                }}
                                                                className="w-3.5 h-3.5 rounded border-slate-300 text-[#1AA6A8] focus:ring-[#1AA6A8] cursor-pointer"
                                                            />
                                                            <span>Ongoing</span>
                                                        </label>
                                                    </div>
                                                    {isInvoiceOngoing ? (
                                                        <div
                                                            onClick={() => setIsInvoiceOngoing(false)}
                                                            className="w-full text-sm font-semibold border-2 border-dashed border-[#1AA6A8]/50 bg-[#EAFBFB] rounded-lg px-3 py-2 text-[#1AA6A8] flex items-center justify-between cursor-pointer hover:bg-[#E0F8F8] transition-colors"
                                                            title="Click to specify a custom end date"
                                                        >
                                                            <span className="flex items-center gap-1.5 text-xs">
                                                                <span className="w-2 h-2 rounded-full bg-[#1AA6A8] animate-pulse"></span>
                                                                Ongoing Service
                                                            </span>
                                                            <span className="text-[10px] font-bold uppercase tracking-wider bg-white/80 border border-[#1AA6A8]/30 px-2 py-0.5 rounded text-[#1AA6A8]">
                                                                No End Date
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <input
                                                            type="date"
                                                            value={invoiceEndDate}
                                                            onChange={e => setInvoiceEndDate(e.target.value)}
                                                            className="w-full text-sm font-semibold border-2 border-slate-200 bg-white rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-[#1AA6A8]/30 focus:border-[#1AA6A8] text-slate-800"
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                            <div className="mt-3 bg-slate-50 p-3 rounded-lg border border-slate-100 flex items-start gap-3">
                                                <div className="p-2 bg-white rounded shadow-sm border border-slate-200 shrink-0">
                                                    <QrCode className="w-6 h-6 text-slate-700" />
                                                </div>
                                                <div>
                                                    <p className="text-xs font-semibold text-slate-900 mb-0.5">Dynamic QR Code Attached</p>
                                                    <p className="text-xs text-slate-500">
                                                        The client can scan the QR code to securely pay ₹{invoiceDepositAmount || agentTargetLead?.quoted_monthly_rate || '15000'} via their preferred UPI app.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <textarea
                                            value={
                                                isEditingTemplate
                                                    ? templateDraftText
                                                    : (agentTargetAction === 'staff' && selectedWorker && staffModalActiveTab === 'worker')
                                                        ? workerDraftText
                                                        : agentDraftText
                                            }
                                            onChange={(e) => {
                                                if (isEditingTemplate) {
                                                    setTemplateDraftText(e.target.value);
                                                } else if (agentTargetAction === 'staff' && selectedWorker && staffModalActiveTab === 'worker') {
                                                    setWorkerDraftText(e.target.value);
                                                } else {
                                                    setAgentDraftText(e.target.value);
                                                }
                                            }}
                                            readOnly={!isEditingTemplate && agentTargetAction === 'inquiry'}
                                            className={`w-full h-36 px-4 py-3 rounded-xl border border-[#1AA6A8]/20 outline-none focus:ring-2 focus:ring-[#1AA6A8] focus:border-transparent text-sm resize-none font-medium leading-relaxed mb-3 ${
                                                (!isEditingTemplate && agentTargetAction === 'inquiry')
                                                    ? 'bg-slate-100 text-slate-500 cursor-not-allowed'
                                                    : (agentTargetAction === 'staff' && staffModalActiveTab === 'worker')
                                                        ? 'bg-emerald-50/50 text-emerald-900 border-emerald-300 focus:ring-emerald-500'
                                                        : 'bg-[#E6F7F7] text-[#0E7C7E]'
                                            }`}
                                        />
                                    )}

                                    {(!isEditingTemplate && agentTargetAction !== 'quotation' && agentTargetAction !== 'deposit') && (
                                        <div className="absolute bottom-6 right-3 flex gap-1">
                                            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                                            <span className="w-2 h-2 rounded-full bg-[#1AA6A8] animate-pulse delay-75"></span>
                                            <span className="w-2 h-2 rounded-full bg-[#1AA6A8] animate-pulse delay-150"></span>
                                        </div>
                                    )}
                                </div>

                                {isEditingTemplate ? (
                                    <p className="text-xs text-slate-500 mt-2 flex items-center gap-1.5 font-medium">Use <code className="px-1 bg-slate-100 border border-slate-200 rounded text-slate-700 font-mono">{"{{name}}"}</code> and <code className="px-1 bg-slate-100 border border-slate-200 rounded text-slate-700 font-mono">{"{{link}}"}</code> as dynamic variables.</p>
                                ) : (agentTargetAction === 'staff' && selectedWorker && staffModalActiveTab === 'worker') ? (
                                    <div className="mt-1 flex items-center justify-between text-xs">
                                        <p className="text-emerald-700 font-medium flex items-center gap-1.5">
                                            <UserCheck className="w-3.5 h-3.5 text-emerald-600" />
                                            Worker: <span className="font-bold">{selectedWorker.name || selectedWorker.full_name}</span> ({selectedWorker.phone ? `+91 ${selectedWorker.phone.replace(/\D/g, '').slice(-10)}` : '⚠️ No phone'})
                                        </p>
                                        <span className="text-slate-400 text-[11px]">Shift: {serviceHours === 24 ? '24h Live-in' : '10h'}</span>
                                    </div>
                                ) : (
                                    <p className="text-xs text-slate-400 mt-2 flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-[#1AA6A8]" /> Human conformation ensures quality outbound interactions.</p>
                                )}
                            </div>
                        </div>
                        <div className="p-4 border-t border-slate-100 bg-slate-50 flex flex-wrap sm:flex-nowrap items-center gap-2.5">
                            <button onClick={() => setIsAgentModalOpen(false)} className="px-4 py-2.5 rounded-xl font-semibold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 transition-colors shrink-0">
                                Cancel
                            </button>

                            {/* Optional: Send to Worker Button */}
                            {agentTargetAction === 'staff' && selectedWorker && !isEditingTemplate && (
                                <button
                                    type="button"
                                    onClick={handleDispatchToWorker}
                                    disabled={isSendingToWorker}
                                    title={workerSentSuccess ? 'Assignment message sent to worker' : `Send assignment notification to ${selectedWorker.name || 'worker'}`}
                                    className={`py-2.5 px-4 rounded-xl font-bold text-xs sm:text-sm transition-all shadow-xs flex items-center justify-center gap-2 shrink-0 ${
                                        workerSentSuccess
                                            ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                                            : 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-emerald-600/20'
                                    }`}
                                >
                                    {isSendingToWorker ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            <span>Sending to Worker...</span>
                                        </>
                                    ) : workerSentSuccess ? (
                                        <>
                                            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                                            <span>Sent to Worker ✓</span>
                                        </>
                                    ) : (
                                        <>
                                            <MessageSquare className="w-4 h-4" />
                                            <span>Send to Worker</span>
                                        </>
                                    )}
                                </button>
                            )}

                            {isEditingTemplate ? (
                                <button onClick={handleSaveTemplate} className="flex-1 py-2.5 rounded-xl font-bold text-white bg-[#1AA6A8] hover:bg-[#1AA6A8] transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2">
                                    Save as Default
                                </button>
                            ) : (
                                <button onClick={handleDispatchMessage} className="flex-1 py-2.5 px-4 rounded-xl font-bold text-white bg-gradient-to-r from-[#1AA6A8] to-[#0E7C7E] hover:from-[#1AA6A8] hover:to-[#0E7C7E] transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2 text-xs sm:text-sm">
                                    <Send className="w-4 h-4" /> Confirm & Dispatch
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}



            {/* Transcript Modal */}
            {isTranscriptModalOpen && selectedCall && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl overflow-hidden flex flex-col max-h-[85vh]">
                        <div className="flex items-center justify-between p-6 border-b border-slate-100 bg-slate-50/80 sticky top-0 z-10">
                            <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${selectedCall.type === 'Inbound' ? 'bg-primary/10 text-primary' : 'bg-purple-100 text-purple-600'}`}>
                                    <FileText className="w-5 h-5" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-slate-900">Call Transcript</h2>
                                    <p className="text-sm text-slate-500 mt-0.5">Contact: {selectedCall.phone} • {selectedCall.duration}</p>
                                </div>
                            </div>
                            <button onClick={() => setIsTranscriptModalOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-200 transition-colors text-slate-500 bg-white border border-slate-200 shadow-sm">
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="p-6 flex-1 overflow-y-auto space-y-6 bg-slate-50/30">
                            {/* Call Summary Block */}
                            <div className="bg-primary/5 border border-primary/10 rounded-xl p-4">
                                <h3 className="text-xs font-bold text-primary uppercase tracking-wider mb-2 flex items-center gap-2">
                                    <Bot className="w-4 h-4" /> AI Summary
                                </h3>
                                <p className="text-sm font-medium text-slate-700 leading-relaxed italic">
                                    "{selectedCall.summary || 'Summary unavailable.'}"
                                </p>
                                {selectedCall.intent && (
                                    <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white border border-primary/10 text-xs font-bold text-primary shadow-sm">
                                        Intent: {selectedCall.intent}
                                    </div>
                                )}

                                {/* Modal Audio Player */}
                                <div className="mt-4 pt-4 border-t border-primary/10">
                                    <p className="text-[10px] font-bold text-primary uppercase tracking-wider mb-3">Call Recording</p>
                                    <VoicePlayer src={`https://sgyladamwnanudnropwl.supabase.co/functions/v1/get-call-audio?conversation_id=${selectedCall.id}`} />
                                </div>
                            </div>

                            {/* Transcript Dialogue */}
                            <div className="space-y-4">
                                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider pl-1">Full Dialogue</h3>
                                {selectedCall.transcript ? (
                                    <div className="space-y-4">
                                        {selectedCall.transcript.split('\n').filter((l: string) => l.trim()).map((line: string, idx: number) => {
                                            const isAI = line.toLowerCase().startsWith('ai:') || line.toLowerCase().startsWith('assistant:');
                                            const isUser = line.toLowerCase().startsWith('user:') || line.toLowerCase().startsWith('lead:');

                                            if (!isAI && !isUser) {
                                                return <p key={idx} className="text-xs text-slate-400 text-center py-2">{line}</p>;
                                            }

                                            return (
                                                <div key={idx} className={`flex ${isAI ? 'justify-start' : 'justify-end'}`}>
                                                    <div className={`max-w-[85%] rounded-2xl px-4 py-3 ${isAI ? 'bg-white border border-slate-200 shadow-sm rounded-tl-none' : 'bg-primary text-white rounded-tr-none shadow-md'}`}>
                                                        <span className={`block text-[10px] uppercase font-bold mb-1 tracking-wider ${isAI ? 'text-primary' : 'text-emerald-100'}`}>
                                                            {isAI ? 'AI Agent' : 'Contact'}
                                                        </span>
                                                        <p className={`text-sm ${isAI ? 'text-slate-800' : 'text-emerald-50'}`}>{line.replace(/^(AI|Assistant|User|Lead):\s*/i, '')}</p>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center justify-center p-8 text-center bg-white rounded-xl border border-dashed border-slate-200">
                                        <div className="w-12 h-12 rounded-full bg-slate-50 flex items-center justify-center mb-3">
                                            <FileText className="w-6 h-6 text-slate-300" />
                                        </div>
                                        <p className="text-sm font-medium text-slate-600">No detailed transcript available for this call.</p>
                                        <p className="text-xs text-slate-400 mt-1">This could be due to a short drop-off or tracking limitations.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* WhatsApp Chat Modal */}
            {isWhatsappChatOpen && selectedWhatsappLead && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[85vh] border border-slate-200">
                        <div className="flex items-center justify-between p-5 border-b border-slate-100 bg-slate-50/90 sticky top-0 z-10">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full flex items-center justify-center bg-[#EAFBFB] text-[#1AA6A8] shadow-sm">
                                    <MessageCircle className="w-5 h-5" />
                                </div>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <h2 className="text-lg font-bold text-slate-900">{selectedWhatsappLead.name}</h2>
                                        {whatsappChat.length > 0 && (
                                            <span className="text-[11px] font-bold px-2 py-0.5 bg-primary/10 text-primary rounded-full">
                                                {whatsappChat.length} {whatsappChat.length === 1 ? 'message' : 'messages'}
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5 font-medium">
                                        <Phone className="w-3 h-3 text-slate-400" />
                                        {formatPhoneNumber(selectedWhatsappLead.whatsapp_number || selectedWhatsappLead.phone) || 'No phone'}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => fetchWhatsappChat(selectedWhatsappLead)}
                                    disabled={isFetchingChat}
                                    title="Refresh chat history"
                                    className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-200 transition-colors text-slate-500 bg-white border border-slate-200 shadow-sm disabled:opacity-50"
                                >
                                    <RefreshCw className={`w-3.5 h-3.5 ${isFetchingChat ? 'animate-spin text-primary' : ''}`} />
                                </button>
                                <button
                                    onClick={() => setIsWhatsappChatOpen(false)}
                                    className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-200 transition-colors text-slate-500 bg-white border border-slate-200 shadow-sm"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        </div>

                        <div ref={chatScrollRef} className="p-6 flex-1 overflow-y-auto space-y-4 bg-[#efeae2] custom-scrollbar">
                            {isFetchingChat ? (
                                <div className="flex flex-col items-center justify-center py-16">
                                    <Loader2 className="w-8 h-8 text-[#1AA6A8] animate-spin mb-3" />
                                    <p className="text-slate-600 font-medium text-sm">Loading full WhatsApp conversation...</p>
                                </div>
                            ) : whatsappChat.length > 0 ? (
                                <div className="space-y-3">
                                    {whatsappChat.map((msg, idx) => {
                                        const isAI = msg.role === 'assistant';
                                        const time = new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                                        const date = new Date(msg.created_at).toLocaleDateString(undefined, {
                                            weekday: 'short',
                                            month: 'short',
                                            day: 'numeric',
                                            year: 'numeric'
                                        });

                                        const showDate = idx === 0 || new Date(whatsappChat[idx - 1].created_at).toLocaleDateString() !== new Date(msg.created_at).toLocaleDateString();

                                        return (
                                            <div key={msg.id || idx}>
                                                {showDate && (
                                                    <div className="flex justify-center my-3">
                                                        <span className="bg-white/85 backdrop-blur-sm rounded-md px-3 py-1 text-[11px] font-bold text-slate-600 shadow-sm border border-slate-200/50">
                                                            {date}
                                                        </span>
                                                    </div>
                                                )}
                                                <div className={`flex ${isAI ? 'justify-start' : 'justify-end'}`}>
                                                    <div className={`max-w-[78%] rounded-xl px-3.5 py-2.5 shadow-sm flex flex-col relative ${isAI ? 'bg-white rounded-tl-none border border-slate-100' : 'bg-[#d9fdd3] rounded-tr-none border border-emerald-100'}`}>
                                                        <div className="flex items-center gap-1.5 mb-1">
                                                            <span className={`text-[10px] font-bold uppercase tracking-wider ${isAI ? 'text-primary' : 'text-emerald-800'}`}>
                                                                {isAI ? '99 Care AI' : (selectedWhatsappLead.name || 'Lead')}
                                                            </span>
                                                            {msg.source === 'log' && (
                                                                <span className="text-[9px] font-bold text-slate-400 bg-slate-100 px-1.5 py-0.2 rounded">
                                                                    System Log
                                                                </span>
                                                            )}
                                                        </div>
                                                        <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed break-words">{msg.content}</p>
                                                        <div className="flex items-center justify-end gap-1 mt-1 ml-4">
                                                            <span className="text-[10px] text-slate-400">{time}</span>
                                                            {isAI && (
                                                                <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center py-16 text-center bg-white/70 backdrop-blur-sm rounded-2xl border border-dashed border-slate-300 p-8">
                                    <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mb-3 border border-emerald-100">
                                        <MessageCircle className="w-6 h-6 text-emerald-600" />
                                    </div>
                                    <h3 className="font-bold text-slate-800 text-base">No Chat History Found</h3>
                                    <p className="text-xs text-slate-500 mt-1 max-w-sm">
                                        No inbound or outbound WhatsApp messages recorded yet for {formatPhoneNumber(selectedWhatsappLead.whatsapp_number || selectedWhatsappLead.phone) || 'this lead'}.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}


            {/* Right Sidebar Inspector (Non-blocking) */}
            {selectedInspectorLead && (
                <div className="fixed top-0 right-0 h-full w-full sm:w-[400px] bg-white shadow-[-10px_0_30px_rgba(0,0,0,0.05)] border-l border-slate-200 z-[90] flex flex-col animate-in slide-in-from-right duration-300">

                    {/* ── Inspector Header ─────────────────────────────── */}
                    <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-slate-50/50">
                        <div className="flex items-center gap-3 min-w-0">
                            <div className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 text-white text-sm font-bold ${getAvatarColor(selectedInspectorLead.name)}`}>
                                {getInitials(selectedInspectorLead.name)}
                            </div>
                            <div className="min-w-0">
                                {editingInspectorName ? (
                                    <div className="flex items-center gap-1.5 w-full">
                                        <input
                                            autoFocus
                                            type="text"
                                            value={inspectorNameDraft}
                                            onChange={e => setInspectorNameDraft(e.target.value)}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter') { handleUpdateLeadDetails(selectedInspectorLead.id, inspectorNameDraft, inspectorPhoneDraft); setEditingInspectorName(false); setSelectedInspectorLead((prev: any) => prev ? { ...prev, name: inspectorNameDraft } : null); }
                                                if (e.key === 'Escape') setEditingInspectorName(false);
                                            }}
                                            className="text-base font-bold text-slate-900 bg-white border-b border-primary/50 outline-none flex-1 min-w-0"
                                        />
                                        <button
                                            onClick={() => { handleUpdateLeadDetails(selectedInspectorLead.id, inspectorNameDraft, inspectorPhoneDraft); setEditingInspectorName(false); setSelectedInspectorLead((prev: any) => prev ? { ...prev, name: inspectorNameDraft } : null); }}
                                            className="p-1.5 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 shadow-sm"
                                        >
                                            <Check className="w-4 h-4" />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="group/inspname flex items-center gap-2">
                                        <h2
                                            className="text-base font-bold text-slate-900 truncate cursor-pointer hover:text-primary transition-colors"
                                            onDoubleClick={() => { setInspectorNameDraft(selectedInspectorLead.name); setInspectorPhoneDraft(selectedInspectorLead.whatsapp_number || selectedInspectorLead.phone || ''); setEditingInspectorName(true); }}
                                        >
                                            {selectedInspectorLead.name}
                                        </h2>
                                        <button
                                            onClick={() => { setInspectorNameDraft(selectedInspectorLead.name); setInspectorPhoneDraft(selectedInspectorLead.whatsapp_number || selectedInspectorLead.phone || ''); setEditingInspectorName(true); }}
                                            className="opacity-0 group-hover/inspname:opacity-100 p-1 text-slate-400 hover:text-primary transition-all"
                                        >
                                            <Edit3 className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                )}
                                {(selectedInspectorLead.service_interest || selectedInspectorLead.notes) && (
                                    <p className="text-xs text-slate-500 truncate">
                                        {selectedInspectorLead.service_interest
                                            || (selectedInspectorLead.notes ? selectedInspectorLead.notes.split('|')[0].replace('Service:', '').trim() : '')}
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0 ml-2">
                            <button
                                type="button"
                                onClick={() => fetchWhatsappChat(selectedInspectorLead)}
                                title="View WhatsApp Chat History"
                                className="p-2 rounded-full hover:bg-emerald-50 hover:text-emerald-600 transition-colors text-slate-500 bg-white border border-slate-200 shadow-sm"
                            >
                                <MessageCircle className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => setSelectedInspectorLead(null)}
                                className="p-2 rounded-full hover:bg-slate-200 transition-colors text-slate-500 bg-white border border-slate-200 shadow-sm"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-5 custom-scrollbar">

                        {/* Assigned Staff Info */}
                        {/* Assigned Staff Info */}
                        {selectedInspectorLead.services?.some((s: any) => s.status === 'active' || s.status === 'pending') ? (() => {
                            const activeServices = selectedInspectorLead.services.filter((s: any) => s.status === 'active' || s.status === 'pending');
                            const rawAssignments = activeServices.flatMap((s: any) => s.service_worker_assignments?.filter((swa: any) => !swa.end_date) || []);
                            const activeAssignments = rawAssignments.filter((swa: any, idx: number, self: any[]) =>
                                idx === self.findIndex((t: any) => (t.employee_id && t.employee_id === swa.employee_id) || (t.id && t.id === swa.id))
                            );
                            return (
                                <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 flex flex-col gap-3">
                                    <div className="flex items-center justify-between">
                                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2"><Users className="w-3.5 h-3.5" /> Assigned Staff</p>
                                        <button
                                            onClick={() => openStaffPicker(selectedInspectorLead)}
                                            className="text-[10px] bg-primary text-white px-2 py-1 rounded font-bold hover:bg-primary/90 transition-colors shadow-sm"
                                        >
                                            + Add Worker
                                        </button>
                                    </div>
                                    {activeAssignments.length > 0 ? (
                                        <div className="flex flex-col gap-2">
                                            {activeAssignments.map((swa: any) => (
                                                <div key={swa.id} className="flex items-center justify-between bg-white border border-primary/10 rounded-lg p-2 shadow-sm">
                                                    <p className="text-sm font-bold text-slate-900">{swa.employees?.full_name || 'Worker'}</p>
                                                    <button
                                                        onClick={async () => {
                                                            if (!confirm('Are you sure you want to release this worker? A payslip will be generated for their days worked.')) return;
                                                            try {
                                                                const todayStr = format(new Date(), 'yyyy-MM-dd');
                                                                let effectiveReleaseDate = todayStr;
                                                                // Check if the worker has any attendance logged for today or later
                                                                try {
                                                                    const { data: latestAtt } = await supabase
                                                                        .from('attendance')
                                                                        .select('duty_date')
                                                                        .eq('worker_id', swa.employee_id)
                                                                        .order('duty_date', { ascending: false })
                                                                        .limit(1)
                                                                        .maybeSingle();
                                                                    if (latestAtt?.duty_date && latestAtt.duty_date > effectiveReleaseDate) {
                                                                        effectiveReleaseDate = latestAtt.duty_date;
                                                                    }
                                                                } catch (e) {
                                                                    console.warn('Error checking latest attendance in CRM release:', e);
                                                                }

                                                                const { error } = await supabase.rpc('release_worker', { 
                                                                    p_assignment_id: swa.id,
                                                                    p_release_date: effectiveReleaseDate 
                                                                });
                                                                if (error) throw error;

                                                                // Also complete legacy worker_assignment if present for this worker & client
                                                                if (selectedInspectorLead?.id) {
                                                                    await supabase.from('worker_assignments').update({
                                                                        assignment_status: 'completed',
                                                                        end_date: effectiveReleaseDate
                                                                    }).eq('employee_id', swa.employee_id).eq('assignment_status', 'active');
                                                                }

                                                                toast.success('Worker released! Adjust their final payslip below.', { duration: 5000 });
                                                                fetchLeads();
                                                                setReleasedAssignment({
                                                                    ...swa,
                                                                    end_date: effectiveReleaseDate,
                                                                    assignment_status: 'completed'
                                                                });
                                                            } catch (err: any) {
                                                                toast.error(err.message || 'Failed to release worker');
                                                            }
                                                        }}
                                                        className="text-[10px] text-red-500 font-bold hover:bg-red-50 px-2 py-1 rounded transition-colors border border-red-100"
                                                    >
                                                        Release
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-xs text-slate-500 italic">No workers currently assigned.</p>
                                    )}
                                </div>
                            );
                        })() : selectedInspectorLead.assigned_worker_name ? (
                            <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 flex items-center gap-3 justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-primary border border-primary/20 shrink-0">
                                        <Users className="w-4 h-4" />
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Assigned Staff</p>
                                        <p className="text-sm font-bold text-slate-900">{selectedInspectorLead.assigned_worker_name}</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => openStaffPicker(selectedInspectorLead)}
                                    className="text-[10px] bg-primary text-white px-2 py-1 rounded font-bold hover:bg-primary/90 transition-colors shadow-sm"
                                >
                                    + Add
                                </button>
                            </div>
                        ) : null}

                        {/* ── Pipeline Stage ─────────────────────────────── */}
                        <div>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Pipeline Stage</p>
                            <select
                                value={selectedInspectorLead.pipeline_stage}
                                onChange={async (e) => {
                                    const newStage = e.target.value;

                                    // Warning if manually bypassing Quotation stage
                                    if (newStage === 'Quotation Sent' && selectedInspectorLead.pipeline_stage === 'In Discussion') {
                                        const confirmed = window.confirm("⚠️ Warning: You are manually moving this lead to Quotation Sent without sending a quotation via WhatsApp.\n\nAre you sure you want to bypass the automated quotation sending process?");
                                        if (!confirmed) return;
                                    }

                                    const success = await handleMoveLead(selectedInspectorLead.id, newStage);
                                    if (success) {
                                        await logActivity(selectedInspectorLead.id, 'stage_changed', `Moved to "${newStage}"`, { from: selectedInspectorLead.pipeline_stage, to: newStage });
                                        setSelectedInspectorLead((prev: any) => prev ? { ...prev, pipeline_stage: newStage } : null);
                                        setInspectorActivity(prev => [...prev, { id: Date.now().toString(), event_type: 'stage_changed', description: `Moved to "${newStage}"`, created_at: new Date().toISOString() }]);
                                    }
                                }}
                                className="w-full text-sm font-bold bg-white border border-slate-200 text-slate-800 rounded-xl px-3 py-2.5 outline-none focus:ring-2 focus:ring-primary cursor-pointer transition-shadow shadow-sm"
                            >
                                <option disabled className="text-slate-400 font-bold bg-slate-50">-- Pipeline --</option>
                                {pipelineStages.map(stage => (
                                    <option key={stage} value={stage}>{stage}</option>
                                ))}
                                <option disabled className="text-slate-400 font-bold bg-slate-50">-- Clients --</option>
                                {clientStages.map(stage => (
                                    <option key={stage} value={stage}>{stage}</option>
                                ))}
                            </select>
                        </div>

                        {/* ── Appointment Schedule ──────────────────────── */}
                        {selectedInspectorLead.appointment_datetime && (
                            <div>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Appointment Schedule</p>
                                <div className="bg-emerald-50 rounded-xl border border-emerald-100 p-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center shrink-0 border border-emerald-100 shadow-sm">
                                            <Calendar className="w-4 h-4 text-emerald-500" />
                                        </div>
                                        <div className="flex flex-col">
                                            <p className="text-sm font-bold text-slate-900">
                                                {new Date(selectedInspectorLead.appointment_datetime).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                            </p>
                                            <p className="text-xs font-semibold text-emerald-600 mt-0.5">
                                                {new Date(selectedInspectorLead.appointment_datetime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ── Service Timeline ────────────────────────────── */}
                        {(selectedInspectorLead.plannedStart || selectedInspectorLead.plannedDuration) && (
                            <div>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Service Timeline</p>
                                <div className="bg-indigo-50 rounded-xl border border-indigo-100 p-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center shrink-0 border border-indigo-100 shadow-sm">
                                            <svg className="w-4 h-4 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                            </svg>
                                        </div>
                                        <div className="flex flex-col">
                                            <p className="text-sm font-bold text-slate-900">
                                                {selectedInspectorLead.plannedStart ? new Date(selectedInspectorLead.plannedStart).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'TBD'}
                                            </p>
                                            <p className="text-xs font-semibold text-indigo-600 mt-0.5">
                                                {selectedInspectorLead.plannedDuration ? `Duration: ${selectedInspectorLead.plannedDuration}` : 'Ongoing'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ── Contact Details ────────────────────────────── */}
                        <div>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Contact Details</p>
                            <div className="bg-slate-50 rounded-xl border border-slate-200 divide-y divide-slate-100">
                                {/* Phone */}
                                <div className="flex items-center justify-between px-4 py-3">
                                    <span className="flex items-center gap-2 text-sm text-slate-500"><Phone className="w-3.5 h-3.5 text-slate-400" /> Phone</span>
                                    {editingInspectorPhone ? (
                                        <div className="flex items-center gap-1.5">
                                            <input
                                                autoFocus
                                                type="text"
                                                value={inspectorPhoneDraft}
                                                onChange={e => setInspectorPhoneDraft(e.target.value)}
                                                onKeyDown={e => {
                                                    if (e.key === 'Enter') {
                                                        handleUpdateInspectorPhoneWithConfirm(selectedInspectorLead.id, inspectorPhoneDraft, selectedInspectorLead.whatsapp_number || selectedInspectorLead.phone || '');
                                                    }
                                                    if (e.key === 'Escape') setEditingInspectorPhone(false);
                                                }}
                                                className="text-sm text-right bg-white border border-primary/30 rounded px-2 py-0.5 outline-none focus:ring-1 focus:ring-primary w-40"
                                                placeholder="Phone number"
                                            />
                                            <button
                                                onClick={() => handleUpdateInspectorPhoneWithConfirm(selectedInspectorLead.id, inspectorPhoneDraft, selectedInspectorLead.whatsapp_number || selectedInspectorLead.phone || '')}
                                                className="p-1 bg-emerald-500 text-white rounded-md hover:bg-emerald-600 shadow-sm cursor-pointer"
                                            >
                                                <Check className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="group/inspphone flex items-center gap-2">
                                            <span
                                                className="text-sm font-semibold text-slate-800 cursor-pointer hover:text-primary transition-colors"
                                                onDoubleClick={() => { setInspectorNameDraft(selectedInspectorLead.name); setInspectorPhoneDraft(selectedInspectorLead.whatsapp_number || selectedInspectorLead.phone || ''); setEditingInspectorPhone(true); }}
                                            >
                                                {formatPhoneNumber(selectedInspectorLead.whatsapp_number || selectedInspectorLead.phone) || 'No phone'}
                                            </span>
                                            <button
                                                onClick={() => { setInspectorNameDraft(selectedInspectorLead.name); setInspectorPhoneDraft(selectedInspectorLead.whatsapp_number || selectedInspectorLead.phone || ''); setEditingInspectorPhone(true); }}
                                                className="opacity-0 group-hover/inspphone:opacity-100 p-1 text-slate-400 hover:text-primary transition-all cursor-pointer"
                                            >
                                                <Edit3 className="w-3 h-3" />
                                            </button>
                                        </div>
                                    )}
                                </div>
                                {/* Email */}
                                <div className="flex items-center justify-between px-4 py-3">
                                    <span className="flex items-center gap-2 text-sm text-slate-500"><Mail className="w-3.5 h-3.5 text-slate-400" /> Email</span>
                                    {editingInspectorEmail ? (
                                        <div className="flex items-center gap-1.5">
                                            <input
                                                autoFocus
                                                type="email"
                                                value={inspectorEmailDraft}
                                                onChange={e => setInspectorEmailDraft(e.target.value)}
                                                onKeyDown={e => {
                                                    if (e.key === 'Enter') {
                                                        handleUpdateInspectorEmailWithConfirm(selectedInspectorLead.id, inspectorEmailDraft, selectedInspectorLead.email || '');
                                                    }
                                                    if (e.key === 'Escape') setEditingInspectorEmail(false);
                                                }}
                                                className="text-sm text-right bg-white border border-primary/30 rounded px-2 py-0.5 outline-none focus:ring-1 focus:ring-primary w-40"
                                                placeholder="email@example.com"
                                            />
                                            <button
                                                onClick={() => handleUpdateInspectorEmailWithConfirm(selectedInspectorLead.id, inspectorEmailDraft, selectedInspectorLead.email || '')}
                                                className="p-1 bg-emerald-500 text-white rounded-md hover:bg-emerald-600 shadow-sm cursor-pointer"
                                            >
                                                <Check className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    ) : (
                                        <span
                                            className={`text-sm font-semibold cursor-pointer hover:text-primary transition-colors ${selectedInspectorLead.email ? 'text-primary' : 'text-slate-400 italic'}`}
                                            onDoubleClick={() => { setInspectorEmailDraft(selectedInspectorLead.email || ''); setEditingInspectorEmail(true); }}
                                            title="Double-tap to edit"
                                        >
                                            {selectedInspectorLead.email || 'Add email'}
                                        </span>
                                    )}
                                </div>
                                {/* Source */}
                                <div className="flex items-center justify-between px-4 py-3">
                                    <span className="flex items-center gap-2 text-sm text-slate-500"><Globe className="w-3.5 h-3.5 text-slate-400" /> Source</span>
                                    <span className="text-sm font-semibold text-slate-800">{selectedInspectorLead.source || '—'}</span>
                                </div>
                            </div>
                        </div>

                        {/* ── Patient & Care Details (Always Visible & Directly Editable) ──────────────────────────── */}
                        {(() => {
                            const consent = getLatestByCreatedAt(selectedInspectorLead.client_consents);
                            const startDateFormatted = consent?.service_start_date
                                ? new Date(consent.service_start_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                                : '—';
                            const leadId = selectedInspectorLead.id;

                            return (
                                <div>
                                    <div className="flex items-center justify-between mb-2 mt-4">
                                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Patient & Care Details</p>
                                        <span className="text-[10px] font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                                            Directly Editable
                                        </span>
                                    </div>
                                    <div className="bg-slate-50 rounded-xl border border-slate-200 divide-y divide-slate-100">
                                        
                                        {/* Relative Name */}
                                        {editingInspectorConsentField === 'relative_name' ? (
                                            <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-primary/5">
                                                <span className="text-sm font-semibold text-slate-700">Relative Name</span>
                                                <div className="flex items-center gap-1.5 flex-1 max-w-[220px] justify-end">
                                                    <input
                                                        autoFocus
                                                        type="text"
                                                        value={inspectorConsentDraft}
                                                        onChange={e => setInspectorConsentDraft(e.target.value)}
                                                        onKeyDown={e => {
                                                            if (e.key === 'Enter') handleSaveConsentField(leadId, 'relative_name', inspectorConsentDraft.trim(), 'Relative Name', consent?.relative_name || '—', inspectorConsentDraft.trim());
                                                            if (e.key === 'Escape') setEditingInspectorConsentField(null);
                                                        }}
                                                        className="text-sm bg-white border border-primary/40 rounded-lg px-2.5 py-1 outline-none w-full"
                                                        placeholder="Relative name"
                                                    />
                                                    <button
                                                        onClick={() => handleSaveConsentField(leadId, 'relative_name', inspectorConsentDraft.trim(), 'Relative Name', consent?.relative_name || '—', inspectorConsentDraft.trim())}
                                                        className="p-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 cursor-pointer shrink-0"
                                                    >
                                                        <Check className="w-3.5 h-3.5" />
                                                    </button>
                                                    <button onClick={() => setEditingInspectorConsentField(null)} className="p-1.5 bg-slate-200 text-slate-600 rounded-lg cursor-pointer shrink-0">
                                                        <X className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="group/field flex items-center justify-between gap-4 px-4 py-3 hover:bg-slate-100/60 transition-colors">
                                                <span className="text-sm text-slate-500 font-medium">Relative Name</span>
                                                <div className="flex items-center gap-2">
                                                    <span className={`text-sm font-semibold ${consent?.relative_name ? 'text-slate-800' : 'text-slate-400 italic'}`}>
                                                        {consent?.relative_name || '—'}
                                                    </span>
                                                    <button
                                                        onClick={() => {
                                                            setEditingInspectorConsentField('relative_name');
                                                            setInspectorConsentDraft(consent?.relative_name || selectedInspectorLead.name || '');
                                                        }}
                                                        className="p-1 rounded text-slate-400 hover:text-primary hover:bg-slate-200/60 transition-all cursor-pointer opacity-60 group-hover/field:opacity-100"
                                                        title="Edit Relative Name"
                                                    >
                                                        <Edit3 className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {/* Patient Name */}
                                        {editingInspectorConsentField === 'patient_name' ? (
                                            <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-primary/5">
                                                <span className="text-sm font-semibold text-slate-700">Patient Name</span>
                                                <div className="flex items-center gap-1.5 flex-1 max-w-[220px] justify-end">
                                                    <input
                                                        autoFocus
                                                        type="text"
                                                        value={inspectorConsentDraft}
                                                        onChange={e => setInspectorConsentDraft(e.target.value)}
                                                        onKeyDown={e => {
                                                            if (e.key === 'Enter') handleSaveConsentField(leadId, 'patient_name', inspectorConsentDraft.trim(), 'Patient Name', consent?.patient_name || '—', inspectorConsentDraft.trim());
                                                            if (e.key === 'Escape') setEditingInspectorConsentField(null);
                                                        }}
                                                        className="text-sm bg-white border border-primary/40 rounded-lg px-2.5 py-1 outline-none w-full"
                                                        placeholder="Patient name"
                                                    />
                                                    <button
                                                        onClick={() => handleSaveConsentField(leadId, 'patient_name', inspectorConsentDraft.trim(), 'Patient Name', consent?.patient_name || '—', inspectorConsentDraft.trim())}
                                                        className="p-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 cursor-pointer shrink-0"
                                                    >
                                                        <Check className="w-3.5 h-3.5" />
                                                    </button>
                                                    <button onClick={() => setEditingInspectorConsentField(null)} className="p-1.5 bg-slate-200 text-slate-600 rounded-lg cursor-pointer shrink-0">
                                                        <X className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="group/field flex items-center justify-between gap-4 px-4 py-3 hover:bg-slate-100/60 transition-colors">
                                                <span className="text-sm text-slate-500 font-medium">Patient Name</span>
                                                <div className="flex items-center gap-2">
                                                    <span className={`text-sm font-semibold ${consent?.patient_name ? 'text-slate-800' : 'text-slate-400 italic'}`}>
                                                        {consent?.patient_name || '—'}
                                                    </span>
                                                    <button
                                                        onClick={() => {
                                                            setEditingInspectorConsentField('patient_name');
                                                            setInspectorConsentDraft(consent?.patient_name || selectedInspectorLead.name || '');
                                                        }}
                                                        className="p-1 rounded text-slate-400 hover:text-primary hover:bg-slate-200/60 transition-all cursor-pointer opacity-60 group-hover/field:opacity-100"
                                                        title="Edit Patient Name"
                                                    >
                                                        <Edit3 className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {/* Patient Phone */}
                                        {editingInspectorConsentField === 'contact_number' ? (
                                            <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-primary/5">
                                                <span className="text-sm font-semibold text-slate-700">Patient Phone</span>
                                                <div className="flex items-center gap-1.5 flex-1 max-w-[220px] justify-end">
                                                    <input
                                                        autoFocus
                                                        type="text"
                                                        value={inspectorConsentDraft}
                                                        onChange={e => setInspectorConsentDraft(e.target.value)}
                                                        onKeyDown={e => {
                                                            if (e.key === 'Enter') handleSaveConsentField(leadId, 'contact_number', inspectorConsentDraft.trim(), 'Patient Phone', consent?.contact_number || '—', inspectorConsentDraft.trim());
                                                            if (e.key === 'Escape') setEditingInspectorConsentField(null);
                                                        }}
                                                        className="text-sm bg-white border border-primary/40 rounded-lg px-2.5 py-1 outline-none w-full"
                                                        placeholder="Patient phone"
                                                    />
                                                    <button
                                                        onClick={() => handleSaveConsentField(leadId, 'contact_number', inspectorConsentDraft.trim(), 'Patient Phone', consent?.contact_number || '—', inspectorConsentDraft.trim())}
                                                        className="p-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 cursor-pointer shrink-0"
                                                    >
                                                        <Check className="w-3.5 h-3.5" />
                                                    </button>
                                                    <button onClick={() => setEditingInspectorConsentField(null)} className="p-1.5 bg-slate-200 text-slate-600 rounded-lg cursor-pointer shrink-0">
                                                        <X className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="group/field flex items-center justify-between gap-4 px-4 py-3 hover:bg-slate-100/60 transition-colors">
                                                <span className="text-sm text-slate-500 font-medium">Patient Phone</span>
                                                <div className="flex items-center gap-2">
                                                    <span className={`text-sm font-semibold ${consent?.contact_number ? 'text-slate-800' : 'text-slate-400 italic'}`}>
                                                        {consent?.contact_number || '—'}
                                                    </span>
                                                    <button
                                                        onClick={() => {
                                                            setEditingInspectorConsentField('contact_number');
                                                            setInspectorConsentDraft(consent?.contact_number || selectedInspectorLead.phone || selectedInspectorLead.whatsapp_number || '');
                                                        }}
                                                        className="p-1 rounded text-slate-400 hover:text-primary hover:bg-slate-200/60 transition-all cursor-pointer opacity-60 group-hover/field:opacity-100"
                                                        title="Edit Patient Phone"
                                                    >
                                                        <Edit3 className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {/* Age & Weight */}
                                        {editingInspectorConsentField === 'age_weight' ? (
                                            <div className="flex flex-col gap-2 px-4 py-3 bg-primary/5">
                                                <span className="text-sm font-semibold text-slate-700">Age & Weight</span>
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="text"
                                                        value={inspectorConsentDraft}
                                                        onChange={e => setInspectorConsentDraft(e.target.value)}
                                                        className="text-sm bg-white border border-primary/40 rounded-lg px-2.5 py-1 outline-none flex-1"
                                                        placeholder="Age (e.g. 1 yrs or 6 months)"
                                                    />
                                                    <button
                                                        onClick={() => handleSaveConsentField(leadId, 'age', inspectorConsentDraft.trim(), 'Age', consent?.age ? `${consent.age} ${consent.age_unit || 'yrs'}` : '—', inspectorConsentDraft.trim())}
                                                        className="p-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 cursor-pointer shrink-0"
                                                    >
                                                        <Check className="w-3.5 h-3.5" />
                                                    </button>
                                                    <button onClick={() => setEditingInspectorConsentField(null)} className="p-1.5 bg-slate-200 text-slate-600 rounded-lg cursor-pointer shrink-0">
                                                        <X className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="group/field flex items-center justify-between gap-4 px-4 py-3 hover:bg-slate-100/60 transition-colors">
                                                <span className="text-sm text-slate-500 font-medium">Age & Weight</span>
                                                <div className="flex items-center gap-2">
                                                    <span className={`text-sm font-semibold ${consent?.age || consent?.weight ? 'text-slate-800' : 'text-slate-400 italic'}`}>
                                                        {consent?.age ? `${consent.age} ${consent.age_unit?.toLowerCase() === 'months' ? 'months' : (consent.age_unit?.toLowerCase() === 'days' ? 'days' : 'yrs')}` : '—'}
                                                        {consent?.weight ? `, ${consent.weight} kg` : ''}
                                                    </span>
                                                    <button
                                                        onClick={() => {
                                                            setEditingInspectorConsentField('age_weight');
                                                            setInspectorConsentDraft(consent?.age ? `${consent.age} ${consent.age_unit || 'yrs'}` : '');
                                                        }}
                                                        className="p-1 rounded text-slate-400 hover:text-primary hover:bg-slate-200/60 transition-all cursor-pointer opacity-60 group-hover/field:opacity-100"
                                                        title="Edit Age & Weight"
                                                    >
                                                        <Edit3 className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {/* Alternate Phone */}
                                        {editingInspectorConsentField === 'alternate_contact_number' ? (
                                            <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-primary/5">
                                                <span className="text-sm font-semibold text-slate-700">Alternate Phone</span>
                                                <div className="flex items-center gap-1.5 flex-1 max-w-[220px] justify-end">
                                                    <input
                                                        autoFocus
                                                        type="text"
                                                        value={inspectorConsentDraft}
                                                        onChange={e => setInspectorConsentDraft(e.target.value)}
                                                        onKeyDown={e => {
                                                            if (e.key === 'Enter') handleSaveConsentField(leadId, 'alternate_contact_number', inspectorConsentDraft.trim(), 'Alternate Phone', consent?.alternate_contact_number || '—', inspectorConsentDraft.trim());
                                                            if (e.key === 'Escape') setEditingInspectorConsentField(null);
                                                        }}
                                                        className="text-sm bg-white border border-primary/40 rounded-lg px-2.5 py-1 outline-none w-full"
                                                        placeholder="Alternate phone"
                                                    />
                                                    <button
                                                        onClick={() => handleSaveConsentField(leadId, 'alternate_contact_number', inspectorConsentDraft.trim(), 'Alternate Phone', consent?.alternate_contact_number || '—', inspectorConsentDraft.trim())}
                                                        className="p-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 cursor-pointer shrink-0"
                                                    >
                                                        <Check className="w-3.5 h-3.5" />
                                                    </button>
                                                    <button onClick={() => setEditingInspectorConsentField(null)} className="p-1.5 bg-slate-200 text-slate-600 rounded-lg cursor-pointer shrink-0">
                                                        <X className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="group/field flex items-center justify-between gap-4 px-4 py-3 hover:bg-slate-100/60 transition-colors">
                                                <span className="text-sm text-slate-500 font-medium">Alternate Phone</span>
                                                <div className="flex items-center gap-2">
                                                    <span className={`text-sm font-semibold ${consent?.alternate_contact_number ? 'text-slate-800' : 'text-slate-400 italic'}`}>
                                                        {consent?.alternate_contact_number || '—'}
                                                    </span>
                                                    <button
                                                        onClick={() => {
                                                            setEditingInspectorConsentField('alternate_contact_number');
                                                            setInspectorConsentDraft(consent?.alternate_contact_number || '');
                                                        }}
                                                        className="p-1 rounded text-slate-400 hover:text-primary hover:bg-slate-200/60 transition-all cursor-pointer opacity-60 group-hover/field:opacity-100"
                                                        title="Edit Alternate Phone"
                                                    >
                                                        <Edit3 className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {/* Service */}
                                        {editingInspectorConsentField === 'service_category' ? (
                                            <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-primary/5">
                                                <span className="text-sm font-semibold text-slate-700">Service</span>
                                                <div className="flex items-center gap-1.5 flex-1 max-w-[240px] justify-end">
                                                    <select
                                                        autoFocus
                                                        value={inspectorConsentDraft}
                                                        onChange={e => setInspectorConsentDraft(e.target.value)}
                                                        className="text-xs font-semibold bg-white border border-primary/40 rounded-lg px-2.5 py-1.5 outline-none w-full"
                                                    >
                                                        <option value="">Select Service...</option>
                                                        <option value="New Born Baby Care">New Born Baby Care</option>
                                                        <option value="Patient Care">Patient Care</option>
                                                        <option value="Old Age Care">Old Age Care</option>
                                                        <option value="Nursing Care">Nursing Care</option>
                                                        <option value="Maternity Care">Maternity Care</option>
                                                        <option value="Baby Care">Baby Care</option>
                                                        <option value="Japa Care (Post-Delivery)">Japa Care (Post-Delivery)</option>
                                                    </select>
                                                    <button
                                                        onClick={() => handleSaveConsentField(leadId, 'service_category', inspectorConsentDraft, 'Service Category', consent?.service_category || '—', inspectorConsentDraft)}
                                                        className="p-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 cursor-pointer shrink-0"
                                                    >
                                                        <Check className="w-3.5 h-3.5" />
                                                    </button>
                                                    <button onClick={() => setEditingInspectorConsentField(null)} className="p-1.5 bg-slate-200 text-slate-600 rounded-lg cursor-pointer shrink-0">
                                                        <X className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="group/field flex items-center justify-between gap-4 px-4 py-3 hover:bg-slate-100/60 transition-colors">
                                                <span className="text-sm text-slate-500 font-medium">Service</span>
                                                <div className="flex items-center gap-2">
                                                    <span className={`text-sm font-semibold ${consent?.service_category || selectedInspectorLead.service_interest ? 'text-slate-800' : 'text-slate-400 italic'}`}>
                                                        {consent?.service_category || selectedInspectorLead.service_interest || '—'}
                                                    </span>
                                                    <button
                                                        onClick={() => {
                                                            setEditingInspectorConsentField('service_category');
                                                            setInspectorConsentDraft(consent?.service_category || selectedInspectorLead.service_interest || '');
                                                        }}
                                                        className="p-1 rounded text-slate-400 hover:text-primary hover:bg-slate-200/60 transition-all cursor-pointer opacity-60 group-hover/field:opacity-100"
                                                        title="Edit Service Category"
                                                    >
                                                        <Edit3 className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {/* Offered Time */}
                                        {editingInspectorConsentField === 'offered_time' ? (
                                            <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-primary/5">
                                                <span className="text-sm font-semibold text-slate-700">Offered Time</span>
                                                <div className="flex items-center gap-1.5 flex-1 max-w-[240px] justify-end">
                                                    <select
                                                        autoFocus
                                                        value={
                                                            inspectorConsentDraft.includes('24') || inspectorConsentDraft.toLowerCase().includes('live')
                                                                ? '24 Hours (Live-in)'
                                                                : inspectorConsentDraft.includes('10')
                                                                ? '10 Hours'
                                                                : inspectorConsentDraft
                                                        }
                                                        onChange={e => setInspectorConsentDraft(e.target.value)}
                                                        className="text-xs font-semibold bg-white border border-primary/40 rounded-lg px-2.5 py-1.5 outline-none w-full"
                                                    >
                                                        <option value="">Select Shift...</option>
                                                        <option value="10 Hours">10 Hours</option>
                                                        <option value="24 Hours (Live-in)">24 Hours (Live-in)</option>
                                                    </select>
                                                    <button
                                                        onClick={() => handleSaveConsentField(leadId, 'offered_time', inspectorConsentDraft, 'Offered Time / Shift', consent?.offered_time || '—', inspectorConsentDraft)}
                                                        className="p-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 cursor-pointer shrink-0"
                                                    >
                                                        <Check className="w-3.5 h-3.5" />
                                                    </button>
                                                    <button onClick={() => setEditingInspectorConsentField(null)} className="p-1.5 bg-slate-200 text-slate-600 rounded-lg cursor-pointer shrink-0">
                                                        <X className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="group/field flex items-center justify-between gap-4 px-4 py-3 hover:bg-slate-100/60 transition-colors">
                                                <span className="text-sm text-slate-500 font-medium">Offered Time</span>
                                                <div className="flex items-center gap-2">
                                                    <span className={`text-sm font-semibold ${consent?.offered_time || selectedInspectorLead.shift_type ? 'text-slate-800' : 'text-slate-400 italic'}`}>
                                                        {consent?.offered_time || selectedInspectorLead.shift_type || '—'}
                                                    </span>
                                                    <button
                                                        onClick={() => {
                                                            setEditingInspectorConsentField('offered_time');
                                                            setInspectorConsentDraft(consent?.offered_time || selectedInspectorLead.shift_type || '');
                                                        }}
                                                        className="p-1 rounded text-slate-400 hover:text-primary hover:bg-slate-200/60 transition-all cursor-pointer opacity-60 group-hover/field:opacity-100"
                                                        title="Edit Offered Time"
                                                    >
                                                        <Edit3 className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {/* Start Date */}
                                        {editingInspectorConsentField === 'service_start_date' ? (
                                            <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-primary/5">
                                                <span className="text-sm font-semibold text-slate-700">Start Date</span>
                                                <div className="flex items-center gap-1.5 flex-1 max-w-[220px] justify-end">
                                                    <input
                                                        autoFocus
                                                        type="date"
                                                        value={inspectorConsentDraft}
                                                        onChange={e => setInspectorConsentDraft(e.target.value)}
                                                        className="text-xs font-semibold bg-white border border-primary/40 rounded-lg px-2 py-1 outline-none w-full"
                                                    />
                                                    <button
                                                        onClick={() => handleSaveConsentField(leadId, 'service_start_date', inspectorConsentDraft, 'Start Date', startDateFormatted || '—', inspectorConsentDraft)}
                                                        className="p-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 cursor-pointer shrink-0"
                                                    >
                                                        <Check className="w-3.5 h-3.5" />
                                                    </button>
                                                    <button onClick={() => setEditingInspectorConsentField(null)} className="p-1.5 bg-slate-200 text-slate-600 rounded-lg cursor-pointer shrink-0">
                                                        <X className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="group/field flex items-center justify-between gap-4 px-4 py-3 hover:bg-slate-100/60 transition-colors">
                                                <span className="text-sm text-slate-500 font-medium">Start Date</span>
                                                <div className="flex items-center gap-2">
                                                    <span className={`text-sm font-semibold ${consent?.service_start_date ? 'text-slate-800' : 'text-slate-400 italic'}`}>
                                                        {startDateFormatted}
                                                    </span>
                                                    <button
                                                        onClick={() => {
                                                            setEditingInspectorConsentField('service_start_date');
                                                            setInspectorConsentDraft(consent?.service_start_date ? String(consent.service_start_date).slice(0, 10) : '');
                                                        }}
                                                        className="p-1 rounded text-slate-400 hover:text-primary hover:bg-slate-200/60 transition-all cursor-pointer opacity-60 group-hover/field:opacity-100"
                                                        title="Edit Start Date"
                                                    >
                                                        <Edit3 className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {/* Address */}
                                        {editingInspectorConsentField === 'address' ? (
                                            <div className="flex flex-col gap-2 px-4 py-3 bg-primary/5">
                                                <span className="text-sm font-semibold text-slate-700">Address</span>
                                                <textarea
                                                    autoFocus
                                                    rows={2}
                                                    value={inspectorConsentDraft}
                                                    onChange={e => setInspectorConsentDraft(e.target.value)}
                                                    className="text-xs bg-white border border-primary/40 rounded-lg p-2 outline-none w-full"
                                                    placeholder="Enter address"
                                                />
                                                <div className="flex items-center justify-end gap-1.5">
                                                    <button
                                                        onClick={() => handleSaveConsentField(leadId, 'address', inspectorConsentDraft.trim(), 'Address', consent?.address || '—', inspectorConsentDraft.trim())}
                                                        className="px-3 py-1 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-xs font-bold cursor-pointer"
                                                    >
                                                        Save
                                                    </button>
                                                    <button onClick={() => setEditingInspectorConsentField(null)} className="px-3 py-1 bg-slate-200 text-slate-600 rounded-lg text-xs font-semibold cursor-pointer">
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="group/field flex items-start justify-between gap-4 px-4 py-3 hover:bg-slate-100/60 transition-colors">
                                                <span className="text-sm text-slate-500 font-medium shrink-0">Address</span>
                                                <div className="flex items-start gap-2 text-right">
                                                    <span className={`text-sm font-semibold max-w-[280px] whitespace-pre-wrap break-words leading-relaxed ${consent?.address ? 'text-slate-800' : 'text-slate-400 italic'}`}>
                                                        {consent?.address || '—'}
                                                    </span>
                                                    <button
                                                        onClick={() => {
                                                            setEditingInspectorConsentField('address');
                                                            setInspectorConsentDraft(consent?.address || '');
                                                        }}
                                                        className="p-1 rounded text-slate-400 hover:text-primary hover:bg-slate-200/60 transition-all cursor-pointer opacity-60 group-hover/field:opacity-100 shrink-0"
                                                        title="Edit Address"
                                                    >
                                                        <Edit3 className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {/* Referred By */}
                                        {editingInspectorConsentField === 'reference_by' ? (
                                            <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-primary/5">
                                                <span className="text-sm font-semibold text-slate-700">Referred By</span>
                                                <div className="flex items-center gap-1.5 flex-1 max-w-[220px] justify-end">
                                                    <input
                                                        autoFocus
                                                        type="text"
                                                        value={inspectorConsentDraft}
                                                        onChange={e => setInspectorConsentDraft(e.target.value)}
                                                        onKeyDown={e => {
                                                            if (e.key === 'Enter') handleSaveConsentField(leadId, 'reference_by', inspectorConsentDraft.trim(), 'Referred By', consent?.reference_by || '—', inspectorConsentDraft.trim());
                                                            if (e.key === 'Escape') setEditingInspectorConsentField(null);
                                                        }}
                                                        className="text-sm bg-white border border-primary/40 rounded-lg px-2.5 py-1 outline-none w-full"
                                                        placeholder="Referred by (e.g. Google, Doctor)"
                                                    />
                                                    <button
                                                        onClick={() => handleSaveConsentField(leadId, 'reference_by', inspectorConsentDraft.trim(), 'Referred By', consent?.reference_by || '—', inspectorConsentDraft.trim())}
                                                        className="p-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 cursor-pointer shrink-0"
                                                    >
                                                        <Check className="w-3.5 h-3.5" />
                                                    </button>
                                                    <button onClick={() => setEditingInspectorConsentField(null)} className="p-1.5 bg-slate-200 text-slate-600 rounded-lg cursor-pointer shrink-0">
                                                        <X className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="group/field flex items-center justify-between gap-4 px-4 py-3 hover:bg-slate-100/60 transition-colors">
                                                <span className="text-sm text-slate-500 font-medium">Referred By</span>
                                                <div className="flex items-center gap-2">
                                                    <span className={`text-sm font-semibold ${consent?.reference_by ? 'text-slate-800' : 'text-slate-400 italic'}`}>
                                                        {consent?.reference_by || '—'}
                                                    </span>
                                                    <button
                                                        onClick={() => {
                                                            setEditingInspectorConsentField('reference_by');
                                                            setInspectorConsentDraft(consent?.reference_by || '');
                                                        }}
                                                        className="p-1 rounded text-slate-400 hover:text-primary hover:bg-slate-200/60 transition-all cursor-pointer opacity-60 group-hover/field:opacity-100"
                                                        title="Edit Referred By"
                                                    >
                                                        <Edit3 className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {/* Terms */}
                                        <div className="flex items-center justify-between gap-4 px-4 py-3">
                                            <span className="text-sm text-slate-500 font-medium">Terms</span>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const nextVal = !consent?.terms_accepted;
                                                    handleSaveConsentField(leadId, 'terms_accepted', nextVal, 'Terms Status', consent?.terms_accepted ? 'Accepted' : 'Not accepted', nextVal ? 'Accepted' : 'Not accepted');
                                                }}
                                                className={`text-xs font-bold px-2.5 py-1 rounded-lg border transition-all cursor-pointer ${consent?.terms_accepted ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100' : 'bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200'}`}
                                            >
                                                {consent?.terms_accepted ? 'Accepted ✓' : 'Not accepted ✕'}
                                            </button>
                                        </div>

                                        {consent?.other_details && (
                                            <div className="px-4 py-3">
                                                <span className="block text-sm text-slate-500 font-medium mb-1">Other Details</span>
                                                <p className="text-sm font-semibold text-slate-800 leading-relaxed whitespace-pre-wrap">{consent.other_details}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })()}

                        {/* ── Work Form Details ──────────────────────────── */}
                        {(() => {
                            const workForm = selectedInspectorLead.work_form_data;
                            const hasWorkForm = !!workForm && Array.isArray(workForm.duties) && (workForm.form_type === 'baby_care_form' || workForm.form_type === 'patient_care_form');
                            const dutiesCount = workForm?.duties?.length || 0;
                            const title = workForm?.form_type === 'baby_care_form' ? 'Baby Care Work Form' : 'Patient Care Work Form';
                            
                            return (
                                <div className="mt-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Work Form Details</p>
                                        {hasWorkForm ? (
                                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                                                Recorded ✓
                                            </span>
                                        ) : (
                                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                                                Not Recorded
                                            </span>
                                        )}
                                    </div>

                                    {hasWorkForm ? (
                                        <div className="bg-slate-50 rounded-xl border border-slate-200 p-3.5 space-y-3">
                                            <button 
                                                onClick={() => setShowWorkFormModal(workForm)}
                                                className="w-full flex flex-col items-start gap-1.5 group text-left cursor-pointer"
                                            >
                                                <div className="flex items-center justify-between w-full">
                                                    <span className="text-sm font-semibold text-slate-800 flex items-center gap-2 group-hover:text-primary transition-colors">
                                                        <ListChecks className="w-4 h-4 text-primary" /> {title}
                                                    </span>
                                                    <ArrowUpRight className="w-4 h-4 text-slate-400 group-hover:text-primary transition-colors" />
                                                </div>
                                                {workForm?.updated_at && (
                                                    <span className="text-[10px] text-slate-400 font-medium">
                                                        Last updated: {new Date(workForm.updated_at).toLocaleString()}
                                                    </span>
                                                )}
                                                <div className="flex items-center gap-2 text-xs text-slate-600 mt-0.5">
                                                    <CheckSquare className="w-3.5 h-3.5 text-emerald-600" />
                                                    <span className="font-semibold">{dutiesCount} duties specified</span>
                                                </div>
                                                {workForm?.patient_name && (
                                                    <div className="flex items-center gap-2 text-xs text-slate-500">
                                                        <User className="w-3.5 h-3.5" />
                                                        <span>Patient: {workForm.patient_name}</span>
                                                    </div>
                                                )}
                                            </button>

                                            <div className="pt-2 border-t border-slate-200/70 flex items-center gap-2">
                                                <button
                                                    onClick={() => handleOpenManualWorkForm(selectedInspectorLead)}
                                                    className="flex-1 bg-white hover:bg-slate-100 text-slate-700 text-xs font-semibold py-1.5 px-2.5 rounded-lg border border-slate-200 transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
                                                >
                                                    <Edit3 className="w-3 h-3 text-slate-500" /> Edit Duties
                                                </button>
                                                <button
                                                    disabled={isDispatchingWorkForm}
                                                    onClick={() => handleDispatchWorkFormFromDrawer(selectedInspectorLead)}
                                                    className="flex-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-semibold py-1.5 px-2.5 rounded-lg border border-emerald-200 transition-colors flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50"
                                                    title="Resend duties form via WhatsApp"
                                                >
                                                    <Send className="w-3 h-3" /> Resend Form
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="bg-amber-50/50 rounded-xl border border-amber-200/70 p-3.5 space-y-3">
                                            <div>
                                                <div className="flex items-center gap-2 text-xs font-bold text-amber-900">
                                                    <ListChecks className="w-4 h-4 text-amber-600 shrink-0" />
                                                    <span>No Work Duties Checklist</span>
                                                </div>
                                                <p className="text-[11px] text-amber-700/90 mt-1 leading-relaxed">
                                                    Caregiver duties checklist has not been recorded for this lead yet.
                                                </p>
                                            </div>

                                            <div className="flex flex-col sm:flex-row items-stretch gap-2 pt-1">
                                                <button
                                                    onClick={() => handleOpenManualWorkForm(selectedInspectorLead)}
                                                    className="flex-1 bg-primary hover:bg-primary/90 text-white text-xs font-semibold py-2 px-3 rounded-lg shadow-xs transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                                                >
                                                    <Plus className="w-3.5 h-3.5" /> Record Duties Manually
                                                </button>
                                                <button
                                                    disabled={isDispatchingWorkForm}
                                                    onClick={() => handleDispatchWorkFormFromDrawer(selectedInspectorLead)}
                                                    className="bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold py-2 px-3 rounded-lg border border-slate-200 transition-colors flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50"
                                                    title="Send WhatsApp flow to client"
                                                >
                                                    <Send className="w-3.5 h-3.5 text-emerald-600" /> WhatsApp Flow
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })()}

                        {/* ── Lead Value ─────────────────────────────────── */}
                        <div>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Lead Value</p>
                            <div className="bg-slate-50 rounded-xl border border-slate-200 divide-y divide-slate-100">
                                {/* Service or monthly value */}
                                {(() => {
                                    const inspectorServiceDays = selectedInspectorLead.serviceDays ?? getLeadServiceDays(selectedInspectorLead);
                                    const inspectorIsShortTerm = selectedInspectorLead.isShortTermQuote ?? isShortTermService(inspectorServiceDays);
                                    const valueLabel = getLeadValueLabel(inspectorServiceDays);

                                    return (
                                <div className="flex items-center justify-between px-4 py-3">
                                    <span className="flex items-center gap-2 text-sm text-slate-500"><TrendingUp className="w-3.5 h-3.5 text-slate-400" /> {valueLabel}</span>
                                    {editingLeadValueId === selectedInspectorLead.id ? (
                                        <div className="flex items-center gap-1.5">
                                            <div className="flex items-center bg-white rounded border border-primary/30 overflow-hidden px-1">
                                                <span className="text-primary text-sm font-semibold pl-1">₹</span>
                                                <input
                                                    type="text"
                                                    value={editingLeadValueAmount}
                                                    onChange={e => setEditingLeadValueAmount(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            handleUpdateLeadValueWithConfirm(
                                                                selectedInspectorLead.id,
                                                                editingLeadValueAmount,
                                                                selectedInspectorLead.valueAmount || selectedInspectorLead.estimated_value_monthly || 0
                                                            );
                                                        }
                                                        if (e.key === 'Escape') setEditingLeadValueId(null);
                                                    }}
                                                    autoFocus
                                                    className="w-20 bg-transparent text-sm font-semibold text-primary outline-none py-1 px-1"
                                                />
                                            </div>
                                            <button
                                                onClick={() => {
                                                    handleUpdateLeadValueWithConfirm(
                                                        selectedInspectorLead.id,
                                                        editingLeadValueAmount,
                                                        selectedInspectorLead.valueAmount || selectedInspectorLead.estimated_value_monthly || 0
                                                    );
                                                }}
                                                className="p-1 bg-emerald-500 text-white rounded-md hover:bg-emerald-600 shadow-sm"
                                                title="Save Monthly Value"
                                            >
                                                <Check className="w-3.5 h-3.5" />
                                            </button>
                                            <button
                                                onClick={() => setEditingLeadValueId(null)}
                                                className="p-1 bg-slate-200 text-slate-600 rounded-md hover:bg-slate-300"
                                                title="Cancel"
                                            >
                                                <X className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-2">
                                            <span
                                                className="text-sm font-bold text-primary cursor-pointer hover:underline"
                                                title="Click to edit value"
                                                onClick={() => {
                                                    setEditingLeadValueId(selectedInspectorLead.id);
                                                    setEditingLeadValueAmount((selectedInspectorLead.valueAmount || selectedInspectorLead.estimated_value_monthly || 0).toString());
                                                }}
                                            >
                                                {selectedInspectorLead.value || formatLeadValueDisplay(selectedInspectorLead.valueAmount || selectedInspectorLead.estimated_value_monthly || 0, inspectorServiceDays)}
                                            </span>
                                            <button
                                                onClick={() => {
                                                    setEditingLeadValueId(selectedInspectorLead.id);
                                                    setEditingLeadValueAmount((selectedInspectorLead.valueAmount || selectedInspectorLead.estimated_value_monthly || 0).toString());
                                                }}
                                                className="p-1 text-slate-400 hover:text-primary hover:bg-primary/10 rounded transition-colors"
                                                title="Edit Monthly Value"
                                            >
                                                <Pencil className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    )}
                                </div>
                                    );
                                })()}
                                {/* Complete Month Quote & Partial Month Quote */}
                                {(() => {
                                    const latestQuote = selectedInspectorLead.crm_quotations?.[0];
                                    const completeRate = selectedInspectorLead.complete_month_daily_rate || latestQuote?.complete_month_rate || selectedInspectorLead.services?.[0]?.complete_month_daily_rate || 0;
                                    const incompleteRate = selectedInspectorLead.incomplete_month_daily_rate || latestQuote?.incomplete_month_rate || selectedInspectorLead.services?.[0]?.incomplete_month_daily_rate || 0;

                                    return (
                                        <>
                                            {/* Full Month Rate */}
                                            <div className="flex items-center justify-between px-4 py-3 bg-slate-50/50">
                                                <span className="flex items-center gap-2 text-sm text-slate-500">
                                                    <TrendingUp className="w-3.5 h-3.5 text-slate-400" /> Full Month Rate
                                                </span>
                                                {editingInspectorRateType === 'complete' ? (
                                                    <div className="flex items-center gap-1.5">
                                                        <div className="flex items-center bg-white rounded border border-primary/30 overflow-hidden px-2 py-0.5">
                                                            <span className="text-xs text-slate-500 font-semibold mr-1">₹</span>
                                                            <input
                                                                type="number"
                                                                value={inspectorRateDraft}
                                                                onChange={e => setInspectorRateDraft(e.target.value)}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter') handleSaveLeadRate(selectedInspectorLead.id, 'complete', inspectorRateDraft);
                                                                    if (e.key === 'Escape') setEditingInspectorRateType(null);
                                                                }}
                                                                autoFocus
                                                                placeholder="Rate/day"
                                                                className="w-20 bg-transparent text-sm font-semibold text-slate-800 outline-none"
                                                            />
                                                            <span className="text-[11px] text-slate-400">/day</span>
                                                        </div>
                                                        <button
                                                            onClick={() => handleSaveLeadRate(selectedInspectorLead.id, 'complete', inspectorRateDraft)}
                                                            className="p-1 bg-emerald-500 text-white rounded hover:bg-emerald-600 shadow-sm"
                                                            title="Save Full Month Rate"
                                                        >
                                                            <Check className="w-3.5 h-3.5" />
                                                        </button>
                                                        <button
                                                            onClick={() => setEditingInspectorRateType(null)}
                                                            className="p-1 bg-slate-200 text-slate-600 rounded hover:bg-slate-300"
                                                            title="Cancel"
                                                        >
                                                            <X className="w-3.5 h-3.5" />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-2">
                                                        <span className={`text-sm font-bold ${completeRate ? 'text-slate-800' : 'text-slate-400 italic'}`}>
                                                            {completeRate ? `₹${Number(completeRate).toLocaleString('en-IN')}/day` : 'Not set'}
                                                        </span>
                                                        <button
                                                            onClick={() => {
                                                                setEditingInspectorRateType('complete');
                                                                setInspectorRateDraft(completeRate ? String(completeRate) : '');
                                                            }}
                                                            className="p-1 text-slate-400 hover:text-primary hover:bg-primary/10 rounded transition-colors"
                                                            title="Edit Full Month Rate"
                                                        >
                                                            <Pencil className="w-3.5 h-3.5" />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Partial Month Rate */}
                                            <div className="flex items-center justify-between px-4 py-3 bg-slate-50/50">
                                                <span className="flex items-center gap-2 text-sm text-slate-500">
                                                    <TrendingUp className="w-3.5 h-3.5 text-slate-400" /> Partial Month Rate
                                                </span>
                                                {editingInspectorRateType === 'incomplete' ? (
                                                    <div className="flex items-center gap-1.5">
                                                        <div className="flex items-center bg-white rounded border border-primary/30 overflow-hidden px-2 py-0.5">
                                                            <span className="text-xs text-slate-500 font-semibold mr-1">₹</span>
                                                            <input
                                                                type="number"
                                                                value={inspectorRateDraft}
                                                                onChange={e => setInspectorRateDraft(e.target.value)}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter') handleSaveLeadRate(selectedInspectorLead.id, 'incomplete', inspectorRateDraft);
                                                                    if (e.key === 'Escape') setEditingInspectorRateType(null);
                                                                }}
                                                                autoFocus
                                                                placeholder="Rate/day"
                                                                className="w-20 bg-transparent text-sm font-semibold text-slate-800 outline-none"
                                                            />
                                                            <span className="text-[11px] text-slate-400">/day</span>
                                                        </div>
                                                        <button
                                                            onClick={() => handleSaveLeadRate(selectedInspectorLead.id, 'incomplete', inspectorRateDraft)}
                                                            className="p-1 bg-emerald-500 text-white rounded hover:bg-emerald-600 shadow-sm"
                                                            title="Save Partial Month Rate"
                                                        >
                                                            <Check className="w-3.5 h-3.5" />
                                                        </button>
                                                        <button
                                                            onClick={() => setEditingInspectorRateType(null)}
                                                            className="p-1 bg-slate-200 text-slate-600 rounded hover:bg-slate-300"
                                                            title="Cancel"
                                                        >
                                                            <X className="w-3.5 h-3.5" />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-2">
                                                        <span className={`text-sm font-bold ${incompleteRate ? 'text-slate-800' : 'text-slate-400 italic'}`}>
                                                            {incompleteRate ? `₹${Number(incompleteRate).toLocaleString('en-IN')}/day` : 'Not set'}
                                                        </span>
                                                        <button
                                                            onClick={() => {
                                                                setEditingInspectorRateType('incomplete');
                                                                setInspectorRateDraft(incompleteRate ? String(incompleteRate) : '');
                                                            }}
                                                            className="p-1 text-slate-400 hover:text-primary hover:bg-primary/10 rounded transition-colors"
                                                            title="Edit Partial Month Rate"
                                                        >
                                                            <Pencil className="w-3.5 h-3.5" />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    );
                                })()}
                                {/* Est. Annual — only for monthly assignments */}
                                {!(selectedInspectorLead.isShortTermQuote ?? isShortTermService(selectedInspectorLead.serviceDays ?? getLeadServiceDays(selectedInspectorLead))) && (
                                <div className="flex items-center justify-between px-4 py-3">
                                    <span className="flex items-center gap-2 text-sm text-slate-500"><Calendar className="w-3.5 h-3.5 text-slate-400" /> Est. annual</span>
                                    <span className="text-sm font-bold text-slate-800">₹{((selectedInspectorLead.valueAmount || selectedInspectorLead.estimated_value_monthly || 0) * 12).toLocaleString('en-IN')}</span>
                                </div>
                                )}

                                {/* Priority */}
                                <div className="flex items-center justify-between px-4 py-3">
                                    <span className="flex items-center gap-2 text-sm text-slate-500"><Star className="w-3.5 h-3.5 text-slate-400" /> Priority</span>
                                    <select
                                        value={selectedInspectorLead.priority || 'medium'}
                                        onChange={async (e) => {
                                            await handleUpdatePriority(selectedInspectorLead.id, e.target.value);
                                            setSelectedInspectorLead((prev: any) => prev ? { ...prev, priority: e.target.value } : null);
                                        }}
                                        className={`text-sm font-bold bg-transparent border-0 outline-none cursor-pointer ${selectedInspectorLead.priority === 'hot' ? 'text-red-600' : selectedInspectorLead.priority === 'cold' ? 'text-blue-600' : 'text-amber-600'}`}
                                    >
                                        <option value="hot">Hot</option>
                                        <option value="medium">Medium</option>
                                        <option value="cold">Low</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* ── Activity Timeline ──────────────────────────── */}
                        <div>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Activity Timeline</p>
                            <div className="flex flex-col gap-0">
                                {isLoadingActivity ? (
                                    <div className="flex items-center gap-2 py-4 text-slate-400 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading...</div>
                                ) : inspectorActivity.length === 0 ? (
                                    <p className="text-sm text-slate-400 italic py-2">No activity yet.</p>
                                ) : (
                                    <>
                                        {(showFullTimeline ? inspectorActivity : inspectorActivity.slice(-3)).map((evt, i, arr) => {
                                            const isLast = i === arr.length - 1 && (!showFullTimeline && inspectorActivity.length > 3 ? false : true);
                                            const dotColor = evt.event_type === 'lead_created' ? 'bg-teal-500'
                                                : evt.event_type === 'greeting_sent' ? 'bg-blue-500'
                                                    : evt.event_type === 'form_filled' ? 'bg-purple-500'
                                                        : evt.event_type === 'stage_changed' ? 'bg-amber-500'
                                                            : evt.event_type === 'quotation_sent' ? 'bg-orange-500'
                                                                : evt.event_type === 'call_received' ? 'bg-emerald-500'
                                                                    : evt.event_type === 'note_added' ? 'bg-indigo-500'
                                                                        : 'bg-slate-400';
                                            return (
                                                <div key={evt.id} className="flex gap-3">
                                                    <div className="flex flex-col items-center">
                                                        <div className={`w-2.5 h-2.5 rounded-full mt-1.5 shrink-0 ${dotColor}`} />
                                                        {!isLast && <div className="w-px flex-1 bg-slate-200 mt-1" />}
                                                    </div>
                                                    <div className={`pb-4 min-w-0 ${isLast ? '' : ''}`}>
                                                        <p className="text-sm font-semibold text-slate-800 leading-tight">{evt.description}</p>
                                                        <p className="text-[11px] text-slate-400 mt-0.5">{formatActivityTime(evt.created_at)}</p>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        {inspectorActivity.length > 3 && (
                                            <div className="flex gap-3 -mt-2 mb-2 relative z-10">
                                                <div className="flex flex-col items-center w-2.5 shrink-0 ml-1.5" />
                                                <button
                                                    onClick={() => setShowFullTimeline(!showFullTimeline)}
                                                    className="text-xs font-semibold text-primary hover:text-primary/80 transition-colors flex items-center gap-1"
                                                >
                                                    {showFullTimeline ? 'Show less' : `View ${inspectorActivity.length - 3} more past events...`}
                                                </button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        {/* ── Add Note ──────────────────────────────────────── */}
                        <div className="border-t border-slate-100 pt-5">
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Add Note</p>
                            <div className="flex flex-col gap-2">
                                <textarea
                                    rows={3}
                                    value={inspectorNoteDraft}
                                    onChange={e => setInspectorNoteDraft(e.target.value)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                                            handleAddNote(selectedInspectorLead.id, inspectorNoteDraft);
                                        }
                                    }}
                                    placeholder="Write a note… (Ctrl+Enter to save)"
                                    className="w-full text-sm text-slate-800 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 resize-none focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary placeholder-slate-400 transition-all"
                                />
                                <button
                                    onClick={() => handleAddNote(selectedInspectorLead.id, inspectorNoteDraft)}
                                    disabled={isSavingNote || !inspectorNoteDraft.trim()}
                                    className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-bold py-2 rounded-lg transition-all shadow-sm"
                                >
                                    {isSavingNote ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                                    {isSavingNote ? 'Saving…' : 'Save Note'}
                                </button>
                            </div>
                        </div>

                    </div>

                    {/* Inspector Footer Actions */}
                    <div className="p-5 border-t border-slate-100 bg-slate-50/95 mt-auto space-y-3 shrink-0 shadow-[0_-8px_24px_rgba(15,23,42,0.04)]">
                        <div>
                            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Actions & Processing</h3>
                            <div className="flex flex-col gap-2">
                                <button
                                    onClick={() => { fetchWhatsappChat(selectedInspectorLead); setSelectedInspectorLead(null); }}
                                    className="w-full bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 font-bold py-2.5 rounded-lg transition-all shadow-sm flex items-center justify-center gap-2 group"
                                >
                                    <MessageCircle className="w-4 h-4 group-hover:scale-110 transition-transform text-primary" />
                                    View AI Chat History
                                </button>

                                {(() => {
                                    const hasGreetingSent = inspectorActivity.some((evt: any) => evt.event_type === 'stage_changed' && evt.metadata?.to === 'In Discussion');
                                    const hasQuotationSent = inspectorActivity.some((evt: any) => evt.event_type === 'quotation_sent');
                                    const hasConsentSent = inspectorActivity.some((evt: any) => evt.event_type === 'consent_sent' || evt.event_type === 'quote_accepted' || (evt.event_type === 'whatsapp_message_sent' && evt.metadata?.flow === 'consent'));
                                    const hasDepositSent = inspectorActivity.some((evt: any) => evt.event_type === 'stage_changed' && evt.metadata?.to === 'Deposit Pending');
                                    const hasBillingSent = inspectorActivity.some((evt: any) => evt.event_type === 'stage_changed' && evt.metadata?.to === 'Monthly Billing');

                                    return (
                                        <>
                                            {selectedInspectorLead.pipeline_stage === 'New Inquiry' && (
                                                <button
                                                    onClick={async () => {
                                                        const isSent = hasGreetingSent || selectedInspectorLead?.greeting_sent;
                                                        if (isSent) {
                                                            const confirmed = window.confirm("Greeting message has already been sent to this lead recently. Do you want me to send it again?");
                                                            if (!confirmed) return;
                                                        }
                                                        openAgentModal(selectedInspectorLead, 'inquiry');
                                                    }}
                                                    className="w-full bg-[#E6F7F7] hover:bg-primary hover:text-white border border-[#1AA6A8]/20 text-[#0E7C7E] font-bold py-2.5 rounded-lg transition-all shadow-sm flex items-center justify-center gap-2 group"
                                                >
                                                    <Bot className="w-4 h-4 group-hover:scale-110 transition-transform" />
                                                    {(hasGreetingSent || selectedInspectorLead?.greeting_sent) ? 'Resend Greeting Message' : 'Send Greeting Message'}
                                                </button>
                                            )}

                                            {selectedInspectorLead.pipeline_stage === 'In Discussion' && (
                                                <div className="flex flex-col gap-2">
                                                    <button
                                                        onClick={async () => {
                                                            openAgentModal(selectedInspectorLead, 'quotation');
                                                        }}
                                                        className="w-full bg-amber-50 hover:bg-amber-500 hover:text-white border border-amber-100 text-amber-800 font-bold py-2.5 rounded-lg transition-all shadow-sm flex items-center justify-center gap-2 group"
                                                    >
                                                        <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                                        {hasQuotationSent ? 'Resend Quotation' : 'Send Quotation'}
                                                    </button>
                                                    <button
                                                        onClick={async () => {
                                                            const hasQuotationValue = selectedInspectorLead.quoted_monthly_rate > 0 || selectedInspectorLead.estimated_value_monthly > 0 || selectedInspectorLead.valueAmount > 0;

                                                            if (!hasQuotationValue && !hasQuotationSent) {
                                                                const confirmed = window.confirm("⚠️ Warning: You haven't sent a quotation to this lead on WhatsApp yet.\n\nAre you sure you want to bypass the quotation and send the consent form directly?");
                                                                if (!confirmed) return;
                                                            }

                                                            const toastId = toast.loading("Approving quotation and sending consent form...");
                                                            try {
                                                                // 1. Move lead to Quotation Sent
                                                                await handleMoveLead(selectedInspectorLead.id, 'Quotation Sent');

                                                                // Fetch latest quotation for autofill
                                                                const { data: latestQuote } = await supabase
                                                                    .from('crm_quotations')
                                                                    .select('*')
                                                                    .eq('lead_id', selectedInspectorLead.id)
                                                                    .order('created_at', { ascending: false })
                                                                    .limit(1)
                                                                    .maybeSingle();

                                                                let flowData: any = buildConsentFlowPrefill(selectedInspectorLead);
                                                                if (latestQuote) {
                                                                    flowData = {
                                                                        ...flowData,
                                                                        service_category: latestQuote.service_category || latestQuote.service_name || '',
                                                                        offered_time: normalizeConsentOfferedTime(latestQuote.hours_per_day || latestQuote.shift_type || ''),
                                                                        service_start_date: latestQuote.start_date ? latestQuote.start_date.split('T')[0] : ''
                                                                    };
                                                                }

                                                                // 2. Dispatch Consent Form automatically with autofill data
                                                                await dispatchWhatsAppTemplate(selectedInspectorLead, 'consent', undefined, flowData);

                                                                // 3. Dispatch the secondary Work Form after 1.5s
                                                                setTimeout(() => {
                                                                    dispatchWorkFormForLead(selectedInspectorLead, latestQuote).catch(e => console.error('Work form dispatch failed', e));
                                                                }, 1500);

                                                                toast.success("✅ Quotation Approved! Consent form sent to client.", { id: toastId });
                                                                setSelectedInspectorLead(null);
                                                            } catch (err: any) {
                                                                console.error("[Approve Error]", err);
                                                                toast.error(`Failed to complete automation: ${err.message}`, { id: toastId });
                                                            }
                                                        }}
                                                        className="w-full bg-[#E6F7F7] hover:bg-primary hover:text-white border border-[#1AA6A8]/20 text-[#0E7C7E] font-bold py-2.5 rounded-lg transition-all shadow-sm flex items-center justify-center gap-2 group"
                                                    >
                                                        <CheckCircle2 className="w-4 h-4 group-hover:scale-110 transition-transform" />
                                                        Quotation Approved
                                                    </button>
                                                </div>
                                            )}

                                            {selectedInspectorLead.pipeline_stage === 'Quotation Sent' && (
                                                <button
                                                    onClick={() => { openAgentModal(selectedInspectorLead, 'consent'); }}
                                                    className="w-full bg-primary/10 hover:bg-primary hover:text-white border border-primary/15 text-primary font-bold py-2.5 rounded-lg transition-all shadow-sm flex items-center justify-center gap-2 group"
                                                >
                                                    <FileText className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                                    {hasConsentSent ? 'Resend Consent & Work Form' : 'Send Consent & Work Form'}
                                                </button>
                                            )}

                                            {selectedInspectorLead.pipeline_stage === 'Form Submitted' && (
                                                <button
                                                    onClick={() => { openStaffPicker(selectedInspectorLead); setSelectedInspectorLead(null); }}
                                                    className="w-full bg-purple-50 hover:bg-purple-500 hover:text-white border border-purple-100 text-purple-800 font-bold py-2.5 rounded-lg transition-all shadow-sm flex items-center justify-center gap-2 group"
                                                >
                                                    <Users className="w-4 h-4 group-hover:scale-110 transition-transform" />
                                                    Assign Staff Member
                                                </button>
                                            )}

                                            {selectedInspectorLead.pipeline_stage === 'Staff Assigned' && (
                                                <button
                                                    onClick={() => { openAgentModal(selectedInspectorLead, 'deposit'); }}
                                                    className="w-full bg-orange-50 hover:bg-orange-500 hover:text-white border border-orange-100 text-orange-800 font-bold py-2.5 rounded-lg transition-all shadow-sm flex items-center justify-center gap-2 group"
                                                >
                                                    <FileText className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                                    {hasDepositSent ? 'Resend Deposit Invoice' : 'Send Deposit Invoice'}
                                                </button>
                                            )}

                                            {selectedInspectorLead.pipeline_stage === 'Deposit Pending' && (() => {
                                                const isDepositCollected = selectedInspectorLead.has_paid_deposit ||
                                                    selectedInspectorLead.deposit_status === 'collected' ||
                                                    selectedInspectorLead.services?.some((s: any) => s.deposit_status === 'collected') ||
                                                    selectedInspectorLead.worker_assignments?.some((a: any) => Number(a.deposit_paid || 0) >= Number(a.deposit_amount || 1));

                                                if (isDepositCollected) {
                                                    return (
                                                        <button
                                                            onClick={() => handleMoveLead(selectedInspectorLead.id, 'Active Client')}
                                                            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-2.5 rounded-lg transition-all shadow-sm flex items-center justify-center gap-2 group cursor-pointer"
                                                        >
                                                            <CheckCircle2 className="w-4 h-4" />
                                                            Deposit Already Collected ✓ (Activate Client)
                                                        </button>
                                                    );
                                                }

                                                return (
                                                    <button
                                                        onClick={() => handleDepositReceived(selectedInspectorLead)}
                                                        className="w-full bg-emerald-50 hover:bg-emerald-500 hover:text-white border border-emerald-200 text-emerald-800 font-bold py-2.5 rounded-lg transition-all shadow-sm flex items-center justify-center gap-2 group cursor-pointer"
                                                    >
                                                        <CheckCircle2 className="w-4 h-4 group-hover:scale-110 transition-transform" />
                                                        Record Collection → Activate Client
                                                    </button>
                                                );
                                            })()}

                                            {selectedInspectorLead.pipeline_stage === 'Active Client' && (
                                                <button
                                                    onClick={() => {
                                                        openClientInvoiceGenerator(selectedInspectorLead);
                                                    }}
                                                    className="w-full bg-slate-800 hover:bg-slate-900 text-white font-bold py-2.5 rounded-lg transition-all shadow-sm flex items-center justify-center gap-2 group"
                                                >
                                                    <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                                    {hasBillingSent ? 'Resend Monthly Bill' : 'Send Monthly Bill'}
                                                </button>
                                            )}
                                        </>
                                    );
                                })()}

                                {selectedInspectorLead.pipeline_stage === 'Monthly Billing' && (
                                    <div className="space-y-2">
                                        <button
                                            onClick={() => {
                                                setDetailsModalClient(selectedInspectorLead);
                                            }}
                                            className="w-full bg-slate-800 hover:bg-slate-900 text-white font-bold py-2.5 rounded-lg transition-all shadow-sm flex items-center justify-center gap-2 cursor-pointer"
                                        >
                                            <Users className="w-4 h-4 text-emerald-400" />
                                            View Full Client Profile
                                        </button>
                                        <button
                                            onClick={() => handleEndLeadService(selectedInspectorLead)}
                                            disabled={isEndingLeadService}
                                            className="w-full bg-red-50 hover:bg-red-100 border border-red-200 text-red-600 font-bold py-2 rounded-lg transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                                        >
                                            {isEndingLeadService ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4 text-red-500" />}
                                            End Service &amp; Settle Deposit
                                        </button>
                                    </div>
                                )}

                                {selectedInspectorLead.pipeline_stage === 'Closed Won' && (
                                    <button
                                        onClick={() => {
                                            setDetailsModalClient(selectedInspectorLead);
                                        }}
                                        className="w-full bg-gradient-to-r from-primary to-[#0E7C7E] text-white font-bold py-2.5 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2 group cursor-pointer"
                                    >
                                        <RotateCcw className="w-4 h-4 group-hover:-rotate-45 transition-transform" />
                                        Start New Service
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Review request — only for active/billing clients */}
                        {['Active Client', 'Monthly Billing', 'Closed Won'].includes(selectedInspectorLead.pipeline_stage) && (
                            <button
                                onClick={() => sendReviewRequest(selectedInspectorLead)}
                                className="w-full bg-amber-50 hover:bg-amber-500 hover:text-white border border-amber-200 text-amber-700 font-bold py-2.5 rounded-lg transition-all flex items-center justify-center gap-2 shadow-sm"
                            >
                                <Star className="w-4 h-4" />
                                Send Review Request
                            </button>
                        )}
                        <button
                            onClick={() => handleDeleteLead(selectedInspectorLead.id, selectedInspectorLead.name)}
                            className="w-full bg-red-50 hover:bg-red-500 hover:text-white border border-red-100 text-red-600 font-bold py-2.5 rounded-lg transition-all flex items-center justify-center gap-2 shadow-sm"
                        >
                            <Trash2 className="w-4 h-4" />
                            Delete Lead
                        </button>
                    </div>
                </div>
            )}

            {/* Work Form Modal */}
            {showWorkFormModal &&
                createPortal(
                    <div className="fixed inset-0 z-[400] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4" onClick={() => setShowWorkFormModal(null)}>
                        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-slate-200 flex flex-col max-h-[85vh] animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
                            <div className="p-5 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white/95 backdrop-blur z-10 rounded-t-2xl">
                                <div>
                                    <h3 className="font-bold text-slate-900 text-lg flex items-center gap-2">
                                        <ListChecks className="w-5 h-5 text-primary" /> 
                                        {showWorkFormModal.form_type === 'baby_care_form' ? 'Baby Care Form' : 'Patient Care Form'}
                                    </h3>
                                    {showWorkFormModal.patient_name && (
                                        <p className="text-sm text-slate-500 mt-1 flex items-center gap-1.5">
                                            <User className="w-4 h-4" /> {showWorkFormModal.patient_name}
                                        </p>
                                    )}
                                </div>
                                <button
                                    onClick={() => setShowWorkFormModal(null)}
                                    className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                            <div className="p-5 overflow-y-auto space-y-6">
                                <div>
                                    <h4 className="text-sm font-bold text-slate-900 mb-3 uppercase tracking-wider flex items-center gap-2">
                                        <CheckSquare className="w-4 h-4 text-emerald-500" /> Selected Duties
                                    </h4>
                                    {showWorkFormModal.duties && showWorkFormModal.duties.length > 0 ? (
                                        <ul className="space-y-2.5">
                                            {showWorkFormModal.duties.map((duty: string, i: number) => (
                                                <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700 bg-slate-50 p-2.5 rounded-lg border border-slate-100">
                                                    <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                                                    <span className="leading-tight">{getDutyLabel(duty, showWorkFormModal.form_type)}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p className="text-sm text-slate-500 italic">No specific duties selected.</p>
                                    )}
                                </div>
                                
                                {showWorkFormModal.other_work && (
                                    <div>
                                        <h4 className="text-sm font-bold text-slate-900 mb-3 uppercase tracking-wider flex items-center gap-2">
                                            <FileText className="w-4 h-4 text-blue-500" /> Other Instructions
                                        </h4>
                                        <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 text-sm text-blue-900 whitespace-pre-wrap leading-relaxed">
                                            {showWorkFormModal.other_work}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="p-4 border-t border-slate-100 bg-slate-50 rounded-b-2xl flex items-center justify-end gap-2.5">
                                <button
                                    onClick={() => {
                                        const currentLead = selectedInspectorLead;
                                        setShowWorkFormModal(null);
                                        if (currentLead) handleOpenManualWorkForm(currentLead);
                                    }}
                                    className="px-4 py-2 bg-primary hover:bg-primary/90 text-white text-xs font-semibold rounded-xl transition-all flex items-center gap-1.5 cursor-pointer"
                                >
                                    <Edit3 className="w-3.5 h-3.5" /> Edit Duties
                                </button>
                                <button
                                    onClick={() => setShowWorkFormModal(null)}
                                    className="px-4 py-2 bg-white border border-slate-200 text-slate-700 text-xs font-semibold rounded-xl hover:bg-slate-50 transition-colors cursor-pointer"
                                >
                                    Close Details
                                </button>
                            </div>
                        </div>
                    </div>,
                    document.body
                )}

            {/* Manual Work Form / Duties Checklist Modal */}
            {manualWorkFormModal && manualWorkFormModal.isOpen &&
                createPortal(
                    <div className="fixed inset-0 z-[400] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-150" onClick={() => !manualWorkFormModal.isSaving && setManualWorkFormModal(null)}>
                        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl border border-slate-200 flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-200 overflow-hidden" onClick={e => e.stopPropagation()}>
                            {/* Header */}
                            <div className="p-5 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white/95 backdrop-blur z-10">
                                <div>
                                    <h3 className="font-bold text-slate-900 text-lg flex items-center gap-2">
                                        <ListChecks className="w-5 h-5 text-primary" />
                                        {manualWorkFormModal.lead?.work_form_data?.duties?.length ? 'Edit Caregiver Duties' : 'Record Caregiver Duties'}
                                    </h3>
                                    <p className="text-xs text-slate-500 mt-0.5">
                                        Client: <span className="font-semibold text-slate-700">{manualWorkFormModal.lead?.name}</span>
                                        {manualWorkFormModal.lead?.phone ? ` • ${manualWorkFormModal.lead?.phone}` : ''}
                                    </p>
                                </div>
                                <button
                                    disabled={manualWorkFormModal.isSaving}
                                    onClick={() => setManualWorkFormModal(null)}
                                    className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors cursor-pointer disabled:opacity-50"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            {/* Body */}
                            <div className="p-5 overflow-y-auto space-y-5">
                                {/* Form Type Selector */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                                        Service / Form Type
                                    </label>
                                    <div className="grid grid-cols-2 gap-2.5">
                                        <button
                                            type="button"
                                            onClick={() => setManualWorkFormModal({ ...manualWorkFormModal, formType: 'baby_care_form' })}
                                            className={`p-3 rounded-xl border text-left transition-all cursor-pointer flex items-center gap-3 ${manualWorkFormModal.formType === 'baby_care_form' ? 'bg-[#E6F7F7] border-primary text-primary font-bold shadow-xs ring-1 ring-primary' : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'}`}
                                        >
                                            <span className="text-xl">👶</span>
                                            <div>
                                                <div className="text-sm">Baby Care Form</div>
                                                <div className="text-[11px] font-normal opacity-75">New born &amp; maternity care</div>
                                            </div>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setManualWorkFormModal({ ...manualWorkFormModal, formType: 'patient_care_form' })}
                                            className={`p-3 rounded-xl border text-left transition-all cursor-pointer flex items-center gap-3 ${manualWorkFormModal.formType === 'patient_care_form' ? 'bg-[#E6F7F7] border-primary text-primary font-bold shadow-xs ring-1 ring-primary' : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'}`}
                                        >
                                            <span className="text-xl">🩺</span>
                                            <div>
                                                <div className="text-sm">Patient Care Form</div>
                                                <div className="text-[11px] font-normal opacity-75">Old age &amp; bedside nursing</div>
                                            </div>
                                        </button>
                                    </div>
                                </div>

                                {/* Patient / Baby Name */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                                        {manualWorkFormModal.formType === 'baby_care_form' ? "Baby's Name / Care Recipient" : "Patient's Full Name"}
                                    </label>
                                    <div className="relative">
                                        <User className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                        <input
                                            type="text"
                                            value={manualWorkFormModal.patientName}
                                            onChange={e => setManualWorkFormModal({ ...manualWorkFormModal, patientName: e.target.value })}
                                            placeholder={manualWorkFormModal.formType === 'baby_care_form' ? "e.g. Baby of Tina" : "e.g. Tina Kachiwala"}
                                            className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                                        />
                                    </div>
                                </div>

                                {/* Duties Checklist */}
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                            <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                                                Caregiver Duties Checklist
                                            </label>
                                            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                                                {manualWorkFormModal.duties.length} selected
                                            </span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const dict = manualWorkFormModal.formType === 'baby_care_form' ? BABY_CARE_DUTY_LABELS : PATIENT_CARE_DUTY_LABELS;
                                                const allKeys = Object.keys(dict);
                                                const isAllSelected = allKeys.every(k => manualWorkFormModal.duties.includes(k));
                                                setManualWorkFormModal({
                                                    ...manualWorkFormModal,
                                                    duties: isAllSelected ? [] : allKeys
                                                });
                                            }}
                                            className="text-xs font-semibold text-primary hover:underline cursor-pointer"
                                        >
                                            {(() => {
                                                const dict = manualWorkFormModal.formType === 'baby_care_form' ? BABY_CARE_DUTY_LABELS : PATIENT_CARE_DUTY_LABELS;
                                                const allKeys = Object.keys(dict);
                                                return allKeys.every(k => manualWorkFormModal.duties.includes(k)) ? 'Deselect All' : 'Select All';
                                            })()}
                                        </button>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[260px] overflow-y-auto pr-1">
                                        {(() => {
                                            const dict = manualWorkFormModal.formType === 'baby_care_form' ? BABY_CARE_DUTY_LABELS : PATIENT_CARE_DUTY_LABELS;
                                            return Object.entries(dict).map(([key, label]) => {
                                                const isChecked = manualWorkFormModal.duties.includes(key);
                                                return (
                                                    <button
                                                        type="button"
                                                        key={key}
                                                        onClick={() => toggleManualDuty(key)}
                                                        className={`p-2.5 rounded-xl border text-left transition-all cursor-pointer flex items-center gap-2.5 ${isChecked ? 'bg-emerald-50/70 border-emerald-300 text-slate-900 shadow-xs' : 'bg-slate-50/80 border-slate-200/80 text-slate-600 hover:bg-slate-100/70'}`}
                                                    >
                                                        <div className={`w-4 h-4 rounded flex items-center justify-center shrink-0 border transition-all ${isChecked ? 'bg-emerald-600 border-emerald-600 text-white' : 'border-slate-300 bg-white'}`}>
                                                            {isChecked && <Check className="w-3 h-3 stroke-[3]" />}
                                                        </div>
                                                        <span className="text-xs font-medium leading-tight">{label}</span>
                                                    </button>
                                                );
                                            });
                                        })()}
                                    </div>
                                </div>

                                {/* Other Instructions */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                                        Special Instructions / Other Work
                                    </label>
                                    <textarea
                                        rows={3}
                                        value={manualWorkFormModal.otherWork}
                                        onChange={e => setManualWorkFormModal({ ...manualWorkFormModal, otherWork: e.target.value })}
                                        placeholder="e.g. Baby feeds every 2 hours, strictly sterilize feeder bottles, oil massage before 10 AM, etc."
                                        className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none"
                                    />
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="p-4 border-t border-slate-100 bg-slate-50 rounded-b-2xl flex items-center justify-end gap-2.5">
                                <button
                                    type="button"
                                    disabled={manualWorkFormModal.isSaving}
                                    onClick={() => setManualWorkFormModal(null)}
                                    className="px-4 py-2 bg-white border border-slate-200 text-slate-700 text-xs font-semibold rounded-xl hover:bg-slate-50 transition-colors cursor-pointer disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    disabled={manualWorkFormModal.isSaving}
                                    onClick={handleSaveManualWorkForm}
                                    className="px-5 py-2 bg-primary hover:bg-primary/90 text-white text-xs font-bold rounded-xl shadow-xs transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                                >
                                    {manualWorkFormModal.isSaving ? (
                                        <>
                                            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving...
                                        </>
                                    ) : (
                                        <>
                                            <Check className="w-3.5 h-3.5 stroke-[3]" /> Save Duties Checklist
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>,
                    document.body
                )}

            {/* Returning Client Modal — portal so Voice AI tab overflow cannot hide it */}
            {returningClientModal &&
                createPortal(
                    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
                        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm border border-slate-200 overflow-hidden animate-in zoom-in-95 duration-200">
                            <div className="p-5 border-b border-slate-100 bg-amber-50 flex items-center gap-3">
                                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center shrink-0">
                                    <Users className="w-5 h-5 text-amber-600" />
                                </div>
                                <div>
                                    <h3 className="font-bold text-slate-900">Returning Client Detected</h3>
                                    <p className="text-xs text-slate-500 mt-0.5">This phone matches Client Master</p>
                                </div>
                            </div>
                            <div className="p-5 space-y-3">
                                <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 text-sm">
                                    <p className="font-bold text-slate-900">{returningClientModal.existingClient.client_name}</p>
                                    <p className="text-slate-500 text-xs mt-0.5">{returningClientModal.existingClient.phone_number}</p>
                                </div>
                                <p className="text-sm text-slate-600 mb-2">This person is already in your CRM. Duplicate leads are not permitted for the same phone number.</p>
                                <button
                                    type="button"
                                    onClick={handleOpenExistingLead}
                                    className="w-full flex items-start gap-3 p-4 rounded-xl border-2 border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition-colors text-left"
                                >
                                    <ArrowUpRight className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                                    <div>
                                        <p className="font-bold text-emerald-800 text-sm">Open Existing Lead</p>
                                        <p className="text-xs text-emerald-600 mt-0.5">View and manage their existing service record.</p>
                                    </div>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        const fromVoice = returningClientModal.pendingLeadData?.kind === 'voice';
                                        setReturningClientModal(null);
                                        if (!fromVoice) setIsAddLeadModalOpen(true);
                                    }}
                                    className="w-full py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>,
                    document.body
                )}

            {/* Deposit Collection Modal */}
            {isDepositModalOpen && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-md flex items-center justify-center p-4 z-[250] transition-all">
                    <div className="bg-white/95 backdrop-blur-xl border border-white/40 rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="p-5 border-b border-slate-100 bg-white/50 flex justify-between items-center">
                            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                                <RupeeIcon className="w-5 h-5 text-emerald-500 text-lg" /> Record Deposit
                            </h2>
                        </div>
                        <form onSubmit={confirmDepositReceived} className="p-5 space-y-4">
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-1.5 flex items-center justify-between">
                                    <span>Deposit Date</span>
                                    <span className="text-[11px] font-normal text-slate-400">Date deposit received</span>
                                </label>
                                <input
                                    type="date"
                                    required
                                    value={depositDate}
                                    onChange={(e) => setDepositDate(e.target.value)}
                                    className="w-full px-4 py-2 rounded-lg border border-slate-200 outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm bg-white font-medium text-slate-700 cursor-pointer"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Payment Method</label>
                                <select
                                    value={depositMethod}
                                    onChange={(e) => setDepositMethod(e.target.value)}
                                    className="w-full px-4 py-2 rounded-lg border border-slate-200 outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm bg-white"
                                >
                                    <option value="Online Transfer">Online Transfer (NEFT/RTGS)</option>
                                    <option value="UPI">UPI Setup</option>
                                    <option value="Cheque">Cheque</option>
                                    <option value="Cash">Cash</option>
                                </select>
                            </div>
                            <p className="text-xs text-slate-500">Upon recording this payment, a formal receipt and dynamic thank-you greeting will be automatically sent to the client via Email/SMS.</p>
                            <div className="pt-2 flex gap-3">
                                <button type="button" onClick={() => setIsDepositModalOpen(false)} className="flex-1 py-2 rounded-lg font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors">
                                    Cancel
                                </button>
                                <button type="submit" className="flex-1 py-2 rounded-lg font-semibold text-white bg-emerald-500 hover:bg-emerald-600 transition-colors shadow-sm">
                                    Confirm Payment
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Choice Modal (Admin Only) */}
            {deleteChoiceModal && user?.role === 'admin' && (
                <div className="fixed inset-0 z-[250] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm border border-slate-200 overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="p-5 border-b border-slate-100 bg-slate-50 flex items-center gap-3">
                            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center shrink-0">
                                <Trash2 className="w-5 h-5 text-red-500" />
                            </div>
                            <div>
                                <h3 className="font-bold text-slate-900">Delete "{deleteChoiceModal.name}"?</h3>
                                <p className="text-xs text-slate-500 mt-0.5">Choose how to delete this lead</p>
                            </div>
                        </div>
                        <div className="p-5 space-y-3">
                            <button
                                onClick={() => handleMoveToTrash(deleteChoiceModal.id, deleteChoiceModal.name)}
                                className="w-full flex items-start gap-3 p-4 rounded-xl border-2 border-amber-200 bg-amber-50 hover:bg-amber-100 transition-colors text-left"
                            >
                                <Trash2 className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                                <div>
                                    <p className="font-bold text-amber-800 text-sm">Move to Trash</p>
                                    <p className="text-xs text-amber-600 mt-0.5">Recoverable for 30 days. All data preserved.</p>
                                </div>
                            </button>
                            <button
                                onClick={() => handleDeletePermanently(deleteChoiceModal.id, deleteChoiceModal.name)}
                                className="w-full flex items-start gap-3 p-4 rounded-xl border-2 border-red-200 bg-red-50 hover:bg-red-100 transition-colors text-left"
                            >
                                <Trash2 className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                                <div>
                                    <p className="font-bold text-red-700 text-sm">Delete Permanently</p>
                                    <p className="text-xs text-red-500 mt-0.5">Cannot be undone. All data will be lost.</p>
                                </div>
                            </button>
                            <button
                                onClick={() => setDeleteChoiceModal(null)}
                                className="w-full py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Non-Admin Deletion Request Modal */}
            {requestDeleteLead && (
                <RequestDeletionModal
                    isOpen={Boolean(requestDeleteLead)}
                    onClose={() => setRequestDeleteLead(null)}
                    entityType="lead"
                    entityId={requestDeleteLead.id}
                    entityName={requestDeleteLead.name || 'Lead'}
                    defaultActionType={requestDeleteLead.defaultActionType || 'move_to_trash'}
                />
            )}
            {/* Service Period Modal */}
            {isServicePeriodOpen && selectedWorker && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-visible flex flex-col border border-slate-200 animate-in zoom-in-95 duration-200">
                        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50 rounded-t-2xl">
                            <h3 className="font-bold text-slate-800 flex items-center gap-2">
                                <Calendar className="w-5 h-5 text-primary" />
                                Assign Worker Duty Period
                            </h3>
                            <button onClick={() => setIsServicePeriodOpen(false)} className="text-slate-400 hover:text-slate-600 transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-6 flex flex-col gap-4">
                            {/* Worker and Client Info Banner */}
                            <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl flex items-center justify-between">
                                <div>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Worker</p>
                                    <p className="text-sm font-bold text-slate-800">{selectedWorker.name || selectedWorker.full_name}</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Client</p>
                                    <p className="text-sm font-bold text-slate-800">{staffPickerTargetLead?.name || 'Client'}</p>
                                </div>
                            </div>

                            {/* Start Date */}
                            <div>
                                <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">Start Date</label>
                                <input
                                    type="date"
                                    className="w-full p-3 bg-white border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none text-slate-800 font-semibold text-sm"
                                    value={serviceStartDate}
                                    onChange={e => setServiceStartDate(e.target.value)}
                                />
                            </div>

                            {/* End Date with explicit Ongoing button */}
                            <div>
                                <div className="flex items-center justify-between mb-1.5">
                                    <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">End Date</label>
                                    <button
                                        type="button"
                                        onClick={() => setServiceEndDate('')}
                                        className={`text-xs font-bold px-2 py-0.5 rounded transition-colors ${
                                            !serviceEndDate
                                                ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                                                : 'text-slate-500 hover:text-emerald-700 hover:bg-emerald-50'
                                        }`}
                                    >
                                        ✓ Ongoing (No End Date)
                                    </button>
                                </div>

                                {!serviceEndDate ? (
                                    <div className="p-3 bg-emerald-50/80 border-2 border-dashed border-emerald-300 rounded-xl flex items-center justify-between">
                                        <div className="flex items-center gap-2 text-xs font-bold text-emerald-800">
                                            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                                            Ongoing / Open-Ended (No fixed end date)
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const d = new Date(serviceStartDate || new Date());
                                                d.setMonth(d.getMonth() + 1);
                                                setServiceEndDate(d.toISOString().split('T')[0]);
                                            }}
                                            className="text-xs font-bold text-slate-600 underline hover:text-slate-900"
                                        >
                                            Set specific date
                                        </button>
                                    </div>
                                ) : (
                                    <div className="relative">
                                        <input
                                            type="date"
                                            min={serviceStartDate}
                                            className="w-full p-3 bg-white border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none text-slate-800 font-semibold text-sm pr-10"
                                            value={serviceEndDate}
                                            onChange={e => setServiceEndDate(e.target.value)}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setServiceEndDate('')}
                                            title="Clear date and make ongoing"
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-red-500 p-1"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Shift Hours */}
                            <div>
                                <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">Shift Requirement</label>
                                <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl flex items-center justify-between">
                                    <span className="text-sm font-bold text-slate-800">
                                        {serviceHours >= 24 ? '24 Hours (Live-in Care)' : `${serviceHours} Hours / Day`}
                                    </span>
                                    <span className="text-xs font-semibold px-2.5 py-1 bg-primary/10 text-primary rounded-md">
                                        Fixed by Client
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="p-4 border-t border-slate-100 flex gap-3 bg-slate-50 rounded-b-2xl">
                            <button onClick={() => setIsServicePeriodOpen(false)} className="flex-1 py-2.5 text-sm font-bold text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">Cancel</button>
                            <button onClick={handleConfirmServicePeriod} className="flex-1 py-2.5 text-sm font-bold text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors shadow-sm">Confirm Assignment</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── ADD LEAD MODAL ─── */}
            {isAddLeadModalOpen && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[200] flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) setIsAddLeadModalOpen(false); }}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
                        {/* Header */}
                        <div className="px-6 pt-6 pb-4 border-b border-slate-100">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h2 className="text-lg font-bold text-slate-900">Add New Lead</h2>
                                    <p className="text-xs text-slate-500 mt-0.5">Fill in the lead's details to add them to the pipeline.</p>
                                </div>
                                <button onClick={() => setIsAddLeadModalOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400 transition-colors">
                                    <svg width="16" height="16" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" strokeWidth="2" strokeLinecap="round" d="M18 6 6 18M6 6l12 12" /></svg>
                                </button>
                            </div>
                        </div>

                        {/* Form */}
                        <div className="px-6 py-5 flex flex-col gap-4">
                            {/* Name */}
                            <div>
                                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Full Name <span className="text-red-400">*</span></label>
                                <input
                                    type="text"
                                    value={addLeadName}
                                    onChange={e => setAddLeadName(e.target.value)}
                                    placeholder="e.g. Maulik Kariya"
                                    className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1AA6A8]/30 focus:border-[#1AA6A8] transition-all"
                                />
                            </div>

                            {/* Phone */}
                            <div>
                                <label className="block text-xs font-semibold text-slate-600 mb-1.5">WhatsApp / Phone Number</label>
                                <div className="relative">
                                    <input
                                        type="tel"
                                        value={addLeadPhone}
                                        onChange={e => {
                                            setAddLeadPhone(e.target.value);
                                            checkAddLeadDuplicate(e.target.value);
                                        }}
                                        placeholder="e.g. 9974093920"
                                        className={`w-full px-3.5 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 transition-all ${addLeadDuplicateWarning
                                                ? 'border-amber-400 focus:ring-amber-300/30 focus:border-amber-400'
                                                : 'border-slate-200 focus:ring-[#1AA6A8]/30 focus:border-[#1AA6A8]'
                                            }`}
                                    />
                                    {addLeadCheckingDuplicate && (
                                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                            <div className="w-4 h-4 border-2 border-slate-300 border-t-[#1AA6A8] rounded-full animate-spin" />
                                        </div>
                                    )}
                                </div>

                                {/* Duplicate Warning Banner */}
                                {addLeadDuplicateWarning && (
                                    <div className="mt-2.5 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                                        <div className="flex items-start gap-2.5">
                                            <div className="text-amber-500 mt-0.5">⚠️</div>
                                            <div className="flex-1">
                                                <p className="text-xs font-bold text-amber-800">Duplicate phone number detected!</p>
                                                <p className="text-xs text-amber-700 mt-0.5">
                                                    An existing lead <strong>{addLeadDuplicateWarning.name}</strong> already uses this number
                                                    (Stage: <strong>{addLeadDuplicateWarning.pipeline_stage}</strong>).
                                                </p>
                                                <p className="text-xs text-amber-700 font-medium mt-2">
                                                    Continuing will update the existing lead's name to <strong>{addLeadName || 'the new name'}</strong>.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="px-6 pb-6 flex gap-3">
                            <button
                                onClick={() => setIsAddLeadModalOpen(false)}
                                className="flex-1 py-2.5 text-sm font-bold text-slate-600 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    if (!addLeadName.trim()) { toast.error('Please enter a name.'); return; }
                                    handleAddManualLead({
                                        name: addLeadName,
                                        phone: addLeadPhone,
                                        isDuplicate: !!addLeadDuplicateWarning,
                                        duplicateOfId: addLeadDuplicateWarning?.id,
                                    });
                                }}
                                disabled={!addLeadName.trim()}
                                className="flex-1 py-2.5 text-sm font-bold text-white bg-[#1AA6A8] rounded-xl hover:bg-[#1AA6A8]/90 transition-colors shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                {addLeadDuplicateWarning ? 'Update Lead' : 'Add Lead'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── ADD CLIENT MODAL (Comprehensive for Ongoing / Existing Clients) ─── */}
            {isAddClientModalOpen && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[200] flex items-center justify-center p-3 sm:p-4 overflow-y-auto" onClick={(e) => { if (e.target === e.currentTarget && !isSubmittingAddClient) setIsAddClientModalOpen(false); }}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[92vh] animate-in zoom-in-95 duration-200">
                        {/* Header */}
                        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/80 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-[#E6F7F7] text-[#1AA6A8] flex items-center justify-center font-bold">
                                    <UserPlus className="w-5 h-5" />
                                </div>
                                <div>
                                    <h2 className="text-base sm:text-lg font-bold text-slate-900">Add Existing / Ongoing Client</h2>
                                    <p className="text-xs text-slate-500">Register active clients, service rates, collected deposit & worker assignments.</p>
                                </div>
                            </div>
                            <button
                                onClick={() => !isSubmittingAddClient && setIsAddClientModalOpen(false)}
                                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-200/60 text-slate-400 hover:text-slate-700 transition-colors"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        {/* Scrollable Form Body */}
                        <div className="px-6 py-5 overflow-y-auto custom-scrollbar flex flex-col gap-6 text-xs sm:text-sm">
                            {/* Section 1: Client Profile */}
                            <div>
                                <div className="flex items-center gap-2 pb-2 mb-3 border-b border-slate-100">
                                    <User className="w-4 h-4 text-[#1AA6A8]" />
                                    <h3 className="font-bold text-slate-800 text-xs uppercase tracking-wider">1. Client Profile</h3>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-700 mb-1">Client Full Name <span className="text-red-500">*</span></label>
                                        <input
                                            type="text"
                                            value={addClientName}
                                            onChange={e => setAddClientName(e.target.value)}
                                            placeholder="e.g. Ramesh Patel"
                                            className="w-full px-3.5 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1AA6A8]/30 focus:border-[#1AA6A8] text-sm"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-700 mb-1">WhatsApp / Phone <span className="text-red-500">*</span></label>
                                        <input
                                            type="tel"
                                            value={addClientPhone}
                                            onChange={e => setAddClientPhone(e.target.value)}
                                            placeholder="e.g. 9876543210"
                                            className="w-full px-3.5 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1AA6A8]/30 focus:border-[#1AA6A8] text-sm"
                                        />
                                    </div>
                                    <div className="sm:col-span-2">
                                        <label className="block text-xs font-semibold text-slate-700 mb-1">Residential Address / Area</label>
                                        <input
                                            type="text"
                                            value={addClientAddress}
                                            onChange={e => setAddClientAddress(e.target.value)}
                                            placeholder="e.g. 102, Shivalik Heights, Vesu, Surat"
                                            className="w-full px-3.5 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1AA6A8]/30 focus:border-[#1AA6A8] text-sm"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Section 2: Service & Quoted Rates */}
                            <div>
                                <div className="flex items-center gap-2 pb-2 mb-3 border-b border-slate-100">
                                    <Clock className="w-4 h-4 text-[#1AA6A8]" />
                                    <h3 className="font-bold text-slate-800 text-xs uppercase tracking-wider">2. Service & Quoted Rates</h3>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-700 mb-1">Service Type</label>
                                        <select
                                            value={addClientServiceName}
                                            onChange={e => {
                                                const val = e.target.value;
                                                setAddClientServiceName(val);
                                                const isBaby = /baby|maternity|jhapa|japa|infant|child/i.test(val);
                                                if (isBaby) {
                                                    setAddClientFormType('baby_care_form');
                                                    if (addClientAgeUnit === 'Years') setAddClientAgeUnit('Months');
                                                } else {
                                                    setAddClientFormType('patient_care_form');
                                                    if (addClientAgeUnit === 'Months' || addClientAgeUnit === 'Days') setAddClientAgeUnit('Years');
                                                }
                                            }}
                                            className="w-full px-3.5 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1AA6A8]/30 focus:border-[#1AA6A8] bg-white text-sm font-medium text-slate-800"
                                        >
                                            {CARE_SERVICES.map(service => (
                                                <option key={service} value={service}>
                                                    {service}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-semibold text-slate-700 mb-1">Shift Type</label>
                                        <select
                                            value={addClientShiftType}
                                            onChange={e => {
                                                const val = e.target.value;
                                                setAddClientShiftType(val);
                                                if (val.includes('24')) setAddClientShiftHours(24);
                                                else setAddClientShiftHours(10);
                                            }}
                                            className="w-full px-3.5 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1AA6A8]/30 focus:border-[#1AA6A8] bg-white text-sm font-medium text-slate-800"
                                        >
                                            <option value="10-Hour Shift">10-Hour Shift (10 hrs)</option>
                                            <option value="24-Hour Shift">24-Hour Shift (24 hrs)</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-semibold text-slate-700 mb-1">Service Start Date <span className="text-red-500">*</span></label>
                                        <input
                                            type="date"
                                            value={addClientStartDate}
                                            onChange={e => {
                                                const val = e.target.value;
                                                setAddClientStartDate(val);
                                                // Sync worker start dates if not changed individually
                                                setAddClientSelectedWorkers(prev => prev.map(w => ({ ...w, startDate: w.startDate || val })));
                                            }}
                                            className="w-full px-3.5 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1AA6A8]/30 focus:border-[#1AA6A8] bg-white text-sm"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs font-semibold text-slate-700 mb-1">Duration Format</label>
                                        <div className="flex items-center gap-2 mt-1">
                                            <button
                                                type="button"
                                                onClick={() => setAddClientIsOpenEnded(true)}
                                                className={`flex-1 py-1.5 px-3 rounded-lg text-xs font-bold border transition-all ${addClientIsOpenEnded ? 'bg-[#E6F7F7] text-[#1AA6A8] border-[#1AA6A8]' : 'bg-slate-50 text-slate-600 border-slate-200'}`}
                                            >
                                                Open-ended
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setAddClientIsOpenEnded(false)}
                                                className={`flex-1 py-1.5 px-3 rounded-lg text-xs font-bold border transition-all ${!addClientIsOpenEnded ? 'bg-[#E6F7F7] text-[#1AA6A8] border-[#1AA6A8]' : 'bg-slate-50 text-slate-600 border-slate-200'}`}
                                            >
                                                Fixed End Date
                                            </button>
                                        </div>
                                    </div>

                                    {!addClientIsOpenEnded && (
                                        <div className="sm:col-span-2">
                                            <label className="block text-xs font-semibold text-slate-700 mb-1">Service End Date</label>
                                            <input
                                                type="date"
                                                value={addClientEndDate}
                                                onChange={e => setAddClientEndDate(e.target.value)}
                                                className="w-full px-3.5 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1AA6A8]/30 focus:border-[#1AA6A8] bg-white text-sm"
                                            />
                                        </div>
                                    )}

                                    <div>
                                        <label className="block text-xs font-semibold text-slate-700 mb-1">Full Month Rate (₹/day) <span className="text-red-500">*</span></label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">₹</span>
                                            <input
                                                type="number"
                                                value={addClientCompleteMonthRate}
                                                onChange={e => setAddClientCompleteMonthRate(e.target.value === '' ? '' : Number(e.target.value))}
                                                placeholder="e.g. 500 or 600"
                                                className="w-full pl-7 pr-3.5 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1AA6A8]/30 focus:border-[#1AA6A8] text-sm font-semibold"
                                            />
                                        </div>
                                        <p className="text-[10px] text-slate-400 mt-0.5">Applied for monthly billing cycles</p>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-semibold text-slate-700 mb-1">Partial Month Rate (₹/day) <span className="text-red-500">*</span></label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">₹</span>
                                            <input
                                                type="number"
                                                value={addClientIncompleteMonthRate}
                                                onChange={e => setAddClientIncompleteMonthRate(e.target.value === '' ? '' : Number(e.target.value))}
                                                placeholder="e.g. 1000"
                                                className="w-full pl-7 pr-3.5 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1AA6A8]/30 focus:border-[#1AA6A8] text-sm font-semibold"
                                            />
                                        </div>
                                        <p className="text-[10px] text-slate-400 mt-0.5">Applied if service closes in &lt;30 days</p>
                                    </div>
                                </div>
                            </div>

                            {/* Section 3: Patient Care & Work Form Scope */}
                            <div className="bg-teal-50/40 p-4 rounded-2xl border border-teal-200/80 space-y-4">
                                <div className="flex items-center justify-between pb-2 border-b border-teal-200/60">
                                    <div className="flex items-center gap-2">
                                        <HeartPulse className="w-4 h-4 text-teal-600" />
                                        <h3 className="font-bold text-slate-800 text-xs uppercase tracking-wider">
                                            3. Patient Care &amp; Work Form Scope
                                        </h3>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="flex items-center gap-1 bg-white p-0.5 rounded-lg border border-teal-200 shadow-2xs">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setAddClientFormType('patient_care_form');
                                                    if (addClientAgeUnit === 'Months' || addClientAgeUnit === 'Days') setAddClientAgeUnit('Years');
                                                }}
                                                className={`px-2 py-1 text-[11px] font-bold rounded-md transition-all ${
                                                    addClientFormType === 'patient_care_form'
                                                        ? 'bg-teal-600 text-white shadow-2xs'
                                                        : 'text-slate-600 hover:text-slate-900'
                                                }`}
                                            >
                                                Patient Care
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setAddClientFormType('baby_care_form');
                                                    if (addClientAgeUnit === 'Years') setAddClientAgeUnit('Months');
                                                }}
                                                className={`px-2 py-1 text-[11px] font-bold rounded-md transition-all ${
                                                    addClientFormType === 'baby_care_form'
                                                        ? 'bg-pink-600 text-white shadow-2xs'
                                                        : 'text-slate-600 hover:text-slate-900'
                                                }`}
                                            >
                                                Baby Care
                                            </button>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setIsAddClientCareExpanded(!isAddClientCareExpanded)}
                                            className="text-xs text-teal-700 hover:text-teal-900 font-semibold flex items-center gap-1"
                                        >
                                            {isAddClientCareExpanded ? (
                                                <>
                                                    <ChevronUp className="w-3.5 h-3.5" />
                                                    Collapse
                                                </>
                                            ) : (
                                                <>
                                                    <ChevronDown className="w-3.5 h-3.5" />
                                                    Expand
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>

                                {!isAddClientCareExpanded && (
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                                        <div className="bg-white p-2 rounded-lg border border-teal-100">
                                            <span className="text-[10px] text-slate-400 font-semibold block">Patient / Baby</span>
                                            <span className="font-bold text-slate-800 truncate block">{addClientPatientName || addClientName || 'Not specified'}</span>
                                        </div>
                                        <div className="bg-white p-2 rounded-lg border border-teal-100">
                                            <span className="text-[10px] text-slate-400 font-semibold block">Age &amp; Weight</span>
                                            <span className="font-bold text-slate-800 truncate block">
                                                {addClientAge ? `${addClientAge} ${addClientAgeUnit}` : 'N/A'}
                                                {addClientWeight ? ` • ${addClientWeight}kg` : ''}
                                            </span>
                                        </div>
                                        <div className="bg-white p-2 rounded-lg border border-teal-100">
                                            <span className="text-[10px] text-slate-400 font-semibold block">Selected Duties</span>
                                            <span className="font-bold text-slate-800 truncate block">{addClientDuties.length} duties</span>
                                        </div>
                                        <div className="bg-white p-2 rounded-lg border border-teal-100">
                                            <span className="text-[10px] text-slate-400 font-semibold block">Condition</span>
                                            <span className="font-bold text-slate-800 truncate block">{addClientMedicalCondition || 'None'}</span>
                                        </div>
                                    </div>
                                )}

                                {isAddClientCareExpanded && (
                                    <div className="space-y-4">
                                        {/* Patient & Health Information */}
                                        <div className="space-y-2.5">
                                            <div className="flex items-center justify-between">
                                                <h4 className="text-[11px] font-bold uppercase tracking-wider text-teal-800">
                                                    A. Patient &amp; Health Information
                                                </h4>
                                                {addClientName && !addClientPatientName && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setAddClientPatientName(addClientName)}
                                                        className="text-[11px] text-teal-700 hover:underline font-semibold"
                                                    >
                                                        Copy Client Name as Patient
                                                    </button>
                                                )}
                                            </div>

                                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                                                <div>
                                                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                                                        {addClientFormType === 'baby_care_form' ? "Baby's Name" : "Patient's Name"}
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={addClientPatientName}
                                                        onChange={e => setAddClientPatientName(e.target.value)}
                                                        placeholder={addClientName ? `Default: ${addClientName}` : 'e.g. Ramesh Patel / Baby of Tina'}
                                                        className="w-full px-3 py-1.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500/30 bg-white text-xs sm:text-sm font-medium"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-semibold text-slate-700 mb-1">Age</label>
                                                    <div className="flex gap-1">
                                                        <input
                                                            type="text"
                                                            value={addClientAge}
                                                            onChange={e => {
                                                                const val = e.target.value;
                                                                if (/(?:kgs?|kg|kilos?|years?|months?|days?)/i.test(val)) {
                                                                    const parsed = parseAgeAndWeight(val, addClientAgeUnit, addClientWeight, addClientFormType === 'baby_care_form');
                                                                    setAddClientAge(parsed.age);
                                                                    if (parsed.weight && !addClientWeight) setAddClientWeight(parsed.weight);
                                                                    setAddClientAgeUnit(parsed.ageUnit);
                                                                } else {
                                                                    setAddClientAge(val);
                                                                }
                                                            }}
                                                            placeholder={addClientFormType === 'baby_care_form' ? "e.g. 15 or 3" : "e.g. 68"}
                                                            className="w-full px-3 py-1.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500/30 bg-white text-xs sm:text-sm font-medium"
                                                        />
                                                        <select
                                                            value={addClientAgeUnit}
                                                            onChange={e => setAddClientAgeUnit(e.target.value as 'Days' | 'Months' | 'Years')}
                                                            className="px-2 py-1.5 border border-slate-200 rounded-xl text-xs font-bold bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                                                        >
                                                            <option value="Days">Days</option>
                                                            <option value="Months">Months</option>
                                                            <option value="Years">Years</option>
                                                        </select>
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-semibold text-slate-700 mb-1">Weight (kg)</label>
                                                    <input
                                                        type="text"
                                                        value={addClientWeight}
                                                        onChange={e => setAddClientWeight(e.target.value.replace(/\s*(?:kgs?|kg|kilos?)\b/gi, '').trim())}
                                                        placeholder="e.g. 55 or 4.5"
                                                        className="w-full px-3 py-1.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500/30 bg-white text-xs sm:text-sm font-medium"
                                                    />
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                                                <div>
                                                    <label className="block text-xs font-semibold text-slate-700 mb-1">Guardian / Relative Name</label>
                                                    <input
                                                        type="text"
                                                        value={addClientRelativeName}
                                                        onChange={e => setAddClientRelativeName(e.target.value)}
                                                        placeholder={addClientName ? `Default: ${addClientName}` : 'e.g. Son, Daughter, Spouse'}
                                                        className="w-full px-3 py-1.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500/30 bg-white text-xs sm:text-sm font-medium"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-semibold text-slate-700 mb-1">Alternate Contact Phone (Optional)</label>
                                                    <input
                                                        type="tel"
                                                        value={addClientAltPhone}
                                                        onChange={e => setAddClientAltPhone(e.target.value)}
                                                        placeholder="e.g. 9123456789"
                                                        className="w-full px-3 py-1.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500/30 bg-white text-xs sm:text-sm font-medium"
                                                    />
                                                </div>
                                            </div>

                                            <div>
                                                <label className="block text-xs font-semibold text-slate-700 mb-1">Medical Condition &amp; Health Details</label>
                                                <textarea
                                                    rows={2}
                                                    value={addClientMedicalCondition}
                                                    onChange={e => setAddClientMedicalCondition(e.target.value)}
                                                    placeholder="Bedridden, stroke recovery, diabetic, post-surgery precautions..."
                                                    className="w-full px-3 py-1.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500/30 bg-white text-xs sm:text-sm font-medium resize-none"
                                                />
                                            </div>
                                        </div>

                                        {/* Caregiver Work Form Checklist */}
                                        <div className="space-y-2 pt-2 border-t border-teal-200/60">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <h4 className="text-[11px] font-bold uppercase tracking-wider text-teal-800">
                                                        B. Caregiver Work Form Checklist ({addClientFormType === 'baby_care_form' ? 'Baby Care' : 'Patient Care'})
                                                    </h4>
                                                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-teal-100 text-teal-800">
                                                        {addClientDuties.length} selected
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-2 text-xs">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const dict = addClientFormType === 'baby_care_form' ? BABY_CARE_DUTY_LABELS : PATIENT_CARE_DUTY_LABELS;
                                                            setAddClientDuties(Object.keys(dict));
                                                        }}
                                                        className="font-bold text-teal-700 hover:text-teal-900"
                                                    >
                                                        Select All
                                                    </button>
                                                    <span className="text-slate-300">•</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => setAddClientDuties([])}
                                                        className="font-bold text-slate-500 hover:text-red-600"
                                                    >
                                                        Clear
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 max-h-[220px] overflow-y-auto pr-1">
                                                {Object.entries(addClientFormType === 'baby_care_form' ? BABY_CARE_DUTY_LABELS : PATIENT_CARE_DUTY_LABELS).map(([dKey, dLabel]) => {
                                                    const isChecked = addClientDuties.includes(dKey);
                                                    return (
                                                        <button
                                                            key={dKey}
                                                            type="button"
                                                            onClick={() => {
                                                                setAddClientDuties(prev =>
                                                                    prev.includes(dKey) ? prev.filter(k => k !== dKey) : [...prev, dKey]
                                                                );
                                                            }}
                                                            className={`p-2 rounded-lg border text-left text-xs font-medium flex items-center gap-2 transition-all ${
                                                                isChecked
                                                                    ? 'bg-teal-50 border-teal-500 text-teal-900 font-bold shadow-2xs'
                                                                    : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                                                            }`}
                                                        >
                                                            <div className={`w-3.5 h-3.5 rounded flex items-center justify-center shrink-0 text-[10px] ${
                                                                isChecked ? 'bg-teal-600 text-white' : 'border border-slate-300'
                                                            }`}>
                                                                {isChecked && <Check className="w-2.5 h-2.5 stroke-[3]" />}
                                                            </div>
                                                            <span className="truncate">{dLabel}</span>
                                                        </button>
                                                    );
                                                })}
                                            </div>

                                            <div className="pt-1">
                                                <label className="block text-xs font-semibold text-slate-700 mb-1">
                                                    Other Work / Special Instructions for Caregiver
                                                </label>
                                                <input
                                                    type="text"
                                                    value={addClientCareInstructions}
                                                    onChange={e => setAddClientCareInstructions(e.target.value)}
                                                    placeholder="e.g. Assistance with walker, medicines at 8 PM, night feeds every 3 hours..."
                                                    className="w-full px-3 py-1.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500/30 bg-white text-xs sm:text-sm font-medium"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Section 4: Security Deposit */}
                            <div>
                                <div className="flex items-center gap-2 pb-2 mb-3 border-b border-slate-100">
                                    <RupeeIcon className="w-4 h-4 text-emerald-600" />
                                    <h3 className="font-bold text-slate-800 text-xs uppercase tracking-wider">4. Security Deposit</h3>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-700 mb-1">Deposit Collected (₹)</label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">₹</span>
                                            <input
                                                type="number"
                                                value={addClientDepositAmount}
                                                onChange={e => setAddClientDepositAmount(e.target.value === '' ? '' : Number(e.target.value))}
                                                placeholder="e.g. 5000"
                                                className="w-full pl-7 pr-3.5 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1AA6A8]/30 focus:border-[#1AA6A8] text-sm font-semibold text-emerald-700"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-semibold text-slate-700 mb-1">Payment Method</label>
                                        <select
                                            value={addClientDepositMethod}
                                            onChange={e => setAddClientDepositMethod(e.target.value)}
                                            className="w-full px-3.5 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1AA6A8]/30 focus:border-[#1AA6A8] bg-white text-sm"
                                        >
                                            <option value="UPI">UPI / GPay / PhonePe</option>
                                            <option value="Cash">Cash</option>
                                            <option value="Bank Transfer">Bank Transfer / NEFT</option>
                                            <option value="Cheque">Cheque</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-semibold text-slate-700 mb-1">Deposit Payment Date</label>
                                        <input
                                            type="date"
                                            value={addClientDepositDate}
                                            onChange={e => setAddClientDepositDate(e.target.value)}
                                            className="w-full px-3.5 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1AA6A8]/30 focus:border-[#1AA6A8] bg-white text-sm"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Section 5: Assigned Worker(s) */}
                            <div>
                                <div className="flex items-center justify-between pb-2 mb-3 border-b border-slate-100">
                                    <div className="flex items-center gap-2">
                                        <Users className="w-4 h-4 text-purple-600" />
                                        <h3 className="font-bold text-slate-800 text-xs uppercase tracking-wider">5. Assigned Worker(s) <span className="text-red-500">*</span></h3>
                                    </div>
                                    <span className="text-[11px] text-slate-400 font-medium">{addClientSelectedWorkers.length} assigned</span>
                                </div>

                                {/* Worker Search & Selector */}
                                <div className="space-y-2 mb-3">
                                    <div className="flex items-center justify-between">
                                        <label className="block text-xs font-semibold text-slate-700">Select &amp; Add Staff Member</label>
                                        <span className="text-[10px] font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-100">
                                            {allWorkers.filter(w => !addClientSelectedWorkers.some(sw => sw.workerId === w.id)).length} available in directory
                                        </span>
                                    </div>
                                    
                                    {/* Search Box */}
                                    <div className="relative">
                                        <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                        <input
                                            type="text"
                                            value={addClientWorkerSearch}
                                            onChange={e => setAddClientWorkerSearch(e.target.value)}
                                            placeholder="Search worker by name or role..."
                                            className="w-full pl-8 pr-3 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1AA6A8]/30 focus:border-[#1AA6A8] bg-slate-50/50"
                                        />
                                    </div>

                                    {/* Dropdown with filtered workers */}
                                    <select
                                        onChange={(e) => {
                                            const selectedId = e.target.value;
                                            if (!selectedId) return;
                                            const emp = allWorkers.find(w => w.id === selectedId);
                                            if (emp && !addClientSelectedWorkers.some(w => w.workerId === selectedId)) {
                                                setAddClientSelectedWorkers(prev => [
                                                    ...prev,
                                                    { workerId: emp.id, workerName: emp.name || emp.full_name || 'Worker', startDate: addClientStartDate }
                                                ]);
                                            }
                                            e.target.value = '';
                                            setAddClientWorkerSearch('');
                                        }}
                                        className="w-full px-3.5 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1AA6A8]/30 focus:border-[#1AA6A8] bg-white text-sm font-medium"
                                    >
                                        <option value="">-- Click to choose from available directory staff --</option>
                                        {allWorkers
                                            .filter(w => !addClientSelectedWorkers.some(sw => sw.workerId === w.id))
                                            .filter(w => {
                                                if (!addClientWorkerSearch.trim()) return true;
                                                const q = addClientWorkerSearch.toLowerCase();
                                                return (w.name || '').toLowerCase().includes(q) || (w.role || '').toLowerCase().includes(q);
                                            })
                                            .map(w => (
                                                <option key={w.id} value={w.id}>
                                                    {w.name || w.full_name} — {w.role || w.job_title || 'Care Taker'}
                                                </option>
                                            ))}
                                    </select>
                                </div>

                                {/* Selected Workers Table / List */}
                                {addClientSelectedWorkers.length > 0 ? (
                                    <div className="space-y-2 bg-purple-50/40 p-3 rounded-xl border border-purple-100">
                                        {addClientSelectedWorkers.map((w, idx) => (
                                            <div key={w.workerId} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-white p-2.5 rounded-lg border border-purple-200/70 shadow-sm">
                                                <div className="flex items-center gap-2.5">
                                                    <div className="w-7 h-7 rounded-full bg-purple-100 text-purple-700 font-bold text-xs flex items-center justify-center">
                                                        {w.workerName.charAt(0).toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <span className="font-bold text-slate-800 text-xs sm:text-sm">{w.workerName}</span>
                                                        <span className="text-[10px] text-purple-600 bg-purple-100/70 px-1.5 py-0.5 rounded ml-2 font-medium">Worker #{idx + 1}</span>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <label className="text-[11px] text-slate-500 font-semibold whitespace-nowrap">Assigned Since:</label>
                                                    <input
                                                        type="date"
                                                        value={w.startDate}
                                                        onChange={e => {
                                                            const newDate = e.target.value;
                                                            setAddClientSelectedWorkers(prev => prev.map((item, i) => i === idx ? { ...item, startDate: newDate } : item));
                                                        }}
                                                        className="px-2 py-1 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-purple-400 bg-white"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => setAddClientSelectedWorkers(prev => prev.filter((_, i) => i !== idx))}
                                                        className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors ml-1"
                                                        title="Remove worker"
                                                    >
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="p-3 text-center border border-dashed border-slate-200 rounded-xl text-slate-400 text-xs">
                                        No workers selected yet. Please assign at least one staff member above.
                                    </div>
                                )}
                            </div>

                            {/* Section 6: Past Attendance Sync */}
                            <div className="bg-amber-50/50 p-4 rounded-xl border border-amber-200/70 space-y-3">
                                <div className="flex items-start gap-2.5">
                                    <input
                                        type="checkbox"
                                        id="autoMarkAttendance"
                                        checked={addClientAutoMarkAttendance}
                                        onChange={e => setAddClientAutoMarkAttendance(e.target.checked)}
                                        className="mt-0.5 rounded border-amber-300 text-[#1AA6A8] focus:ring-[#1AA6A8]"
                                    />
                                    <label htmlFor="autoMarkAttendance" className="text-xs font-bold text-slate-800 cursor-pointer">
                                        Auto-mark assigned worker(s) as "Present" for all past days from Start Date up to Today
                                        <p className="font-normal text-slate-500 mt-0.5 text-[11px]">
                                            Populates daily duty attendance records so monthly billing on 31st August and HR attendance reports are accurate right away.
                                        </p>
                                    </label>
                                </div>

                                {addClientAutoMarkAttendance && (
                                    <div className="pl-6 pt-1 flex items-center gap-3">
                                        <label className="text-xs font-semibold text-slate-700 whitespace-nowrap">Past Absent Days (if any):</label>
                                        <input
                                            type="number"
                                            min="0"
                                            value={addClientAbsentDays}
                                            onChange={e => setAddClientAbsentDays(e.target.value === '' ? '' : Number(e.target.value))}
                                            placeholder="0"
                                            className="w-20 px-3 py-1.5 text-xs font-semibold border border-amber-300 bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400"
                                        />
                                        <span className="text-[11px] text-slate-400">days</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-end gap-3">
                            <button
                                type="button"
                                disabled={isSubmittingAddClient}
                                onClick={() => setIsAddClientModalOpen(false)}
                                className="px-4 py-2 text-xs sm:text-sm font-bold text-slate-600 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                disabled={isSubmittingAddClient || !addClientName.trim() || !addClientPhone.trim() || addClientSelectedWorkers.length === 0}
                                onClick={handleAddManualClient}
                                className="px-5 py-2 text-xs sm:text-sm font-bold text-white bg-[#1AA6A8] hover:bg-[#1AA6A8]/90 rounded-xl shadow-md transition-all flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                {isSubmittingAddClient ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Saving Client...
                                    </>
                                ) : (
                                    <>
                                        <Check className="w-4 h-4" />
                                        Save &amp; Create Client
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Client Invoice Generator Modal */}
            {isClientInvoiceOpen && clientInvoiceLead && (() => {
                const total = ciDays * ciRate;
                const net = Math.max(0, total - ciDeposit);
                return (
                    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]">
                        <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden border border-slate-200 animate-in zoom-in-95 duration-200 max-h-[92vh] flex flex-col">
                            <div className="p-5 border-b border-slate-100 bg-slate-900 flex justify-between items-center shrink-0">
                                <div className="flex items-center gap-3">
                                    <div className="w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center">
                                        <FileText className="w-5 h-5 text-white" />
                                    </div>
                                    <div>
                                        <h2 className="text-base font-bold text-white">Client Invoice Generator</h2>
                                        <p className="text-xs text-slate-400">{clientInvoiceLead.name}</p>
                                    </div>
                                </div>
                                <button onClick={() => setIsClientInvoiceOpen(false)} className="text-slate-400 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-colors">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                            <div className="p-5 space-y-4 overflow-y-auto flex-1">
                                {!ciAttendanceVerified && (
                                    <div className="bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2.5 rounded-lg text-xs font-medium flex items-start gap-2">
                                        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
                                        <p>Attendance is not yet marked or verified by HR for this period. Days of service may be inaccurate.</p>
                                    </div>
                                )}
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Start Date</label>
                                        <input
                                            type="date"
                                            value={ciStartDate}
                                            onChange={e => {
                                                const val = e.target.value;
                                                setCiStartDate(val);
                                                if (val && ciEndDate) {
                                                    fetchClientInvoiceAttendanceCRM(val, ciEndDate);
                                                }
                                            }}
                                            className="w-full px-3 py-2 rounded-lg border-2 border-slate-200 bg-white text-sm font-semibold outline-none focus:ring-2 focus:ring-[#1AA6A8] text-slate-800"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">End Date</label>
                                        <input
                                            type="date"
                                            value={ciEndDate}
                                            onChange={e => {
                                                const val = e.target.value;
                                                setCiEndDate(val);
                                                if (ciStartDate && val) {
                                                    fetchClientInvoiceAttendanceCRM(ciStartDate, val);
                                                }
                                            }}
                                            className="w-full px-3 py-2 rounded-lg border-2 border-slate-200 bg-white text-sm font-semibold outline-none focus:ring-2 focus:ring-[#1AA6A8] text-slate-800"
                                        />
                                    </div>
                                </div>

                                {/* Attendance Summary */}
                                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                                    <div className="flex items-center justify-between mb-3">
                                        <h3 className="font-semibold text-slate-900 text-sm">Attendance Summary</h3>
                                        <button
                                            type="button"
                                            onClick={() => fetchClientInvoiceAttendanceCRM(ciStartDate, ciEndDate)}
                                            disabled={isCiLoadingAttendance}
                                            className="text-xs text-[#1AA6A8] font-semibold hover:underline flex items-center gap-1 disabled:opacity-50"
                                        >
                                            {isCiLoadingAttendance ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                                            Refresh
                                        </button>
                                    </div>
                                    {isCiLoadingAttendance ? (
                                        <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-[#1AA6A8]" /></div>
                                    ) : ciAttendanceSummary ? (
                                        <div className="space-y-3">
                                            <div className="grid grid-cols-4 gap-2 text-center">
                                                <div className="bg-white rounded-lg p-2.5 border border-slate-200/70 shadow-xs">
                                                    <p className="text-xl sm:text-2xl font-black text-emerald-600">{ciAttendanceSummary.fullDays}</p>
                                                    <p className="text-[10px] sm:text-[11px] text-slate-500 mt-0.5 font-medium leading-tight">Full Days Present</p>
                                                </div>
                                                <div className="bg-white rounded-lg p-2.5 border border-slate-200/70 shadow-xs">
                                                    <p className="text-xl sm:text-2xl font-black text-amber-600">{ciAttendanceSummary.halfDays}</p>
                                                    <p className="text-[10px] sm:text-[11px] text-slate-500 mt-0.5 font-medium leading-tight">Half Days (0.5d)</p>
                                                </div>
                                                <div className="bg-white rounded-lg p-2.5 border border-slate-200/70 shadow-xs">
                                                    <p className="text-xl sm:text-2xl font-black text-red-500">{ciAttendanceSummary.absentDays}</p>
                                                    <p className="text-[10px] sm:text-[11px] text-slate-500 mt-0.5 font-medium leading-tight">Days Absent</p>
                                                </div>
                                                <div className="bg-white rounded-lg p-2.5 border border-slate-200/70 shadow-xs">
                                                    <p className="text-xl sm:text-2xl font-black text-[#1AA6A8]">{ciAttendanceSummary.effectiveDays}</p>
                                                    <p className="text-[10px] sm:text-[11px] text-slate-500 mt-0.5 font-medium leading-tight">Effective Days</p>
                                                </div>
                                            </div>

                                            {/* Date Details for Half Days & Absent Days */}
                                            {((ciAttendanceSummary.halfDayDates && ciAttendanceSummary.halfDayDates.length > 0) || (ciAttendanceSummary.absentDates && ciAttendanceSummary.absentDates.length > 0)) && (
                                                <div className="bg-white rounded-lg p-2.5 border border-slate-200/70 text-xs space-y-1.5">
                                                    {ciAttendanceSummary.halfDayDates && ciAttendanceSummary.halfDayDates.length > 0 && (
                                                        <div className="flex items-center gap-1.5 flex-wrap text-slate-600">
                                                            <span className="font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded text-[11px]">
                                                                Half Days ({ciAttendanceSummary.halfDayDates.length}):
                                                            </span>
                                                            <span className="font-medium text-slate-700">
                                                                {ciAttendanceSummary.halfDayDates.map(d => format(new Date(`${d}T00:00:00`), 'dd MMM')).join(', ')}
                                                            </span>
                                                        </div>
                                                    )}
                                                    {ciAttendanceSummary.absentDates && ciAttendanceSummary.absentDates.length > 0 && (
                                                        <div className="flex items-center gap-1.5 flex-wrap text-slate-600">
                                                            <span className="font-semibold text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded text-[11px]">
                                                                Absent Days ({ciAttendanceSummary.absentDates.length}):
                                                            </span>
                                                            <span className="font-medium text-slate-700">
                                                                {ciAttendanceSummary.absentDates.map(d => format(new Date(`${d}T00:00:00`), 'dd MMM')).join(', ')}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-slate-400 text-center py-3">No attendance data found</p>
                                    )}
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Days of Service</label>
                                        <input type="number" min="0" step="0.5" value={ciDays} onChange={e => setCiDays(parseFloat(e.target.value) || 0)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm font-semibold outline-none focus:ring-2 focus:ring-[#1AA6A8]" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Client Rate / Day (₹)</label>
                                        <input type="number" min="0" value={ciRate} onChange={e => setCiRate(parseFloat(e.target.value) || 0)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm font-semibold outline-none focus:ring-2 focus:ring-[#1AA6A8]" />
                                    </div>
                                </div>
                                <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-slate-500">{ciDays} day{ciDays !== 1 ? 's' : ''} × ₹{ciRate.toLocaleString('en-IN')}/day</span>
                                        <span className="font-semibold text-slate-800">₹{total.toLocaleString('en-IN')}</span>
                                    </div>
                                    <div className="flex justify-between text-base font-bold border-t border-slate-200 pt-2 mt-1">
                                        <span className="text-slate-800">Monthly Bill Amount</span>
                                        <span className="text-[#1AA6A8] font-black">₹{total.toLocaleString('en-IN')}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="p-5 border-t border-slate-100 bg-slate-50 flex flex-col-reverse sm:flex-row justify-end gap-3 rounded-b-2xl">
                                <button onClick={() => setIsClientInvoiceOpen(false)} className="px-5 py-2.5 rounded-xl font-semibold text-slate-600 hover:bg-slate-200 transition-colors w-full sm:w-auto text-center">Cancel</button>
                                <div className="flex gap-3 flex-1 sm:flex-none w-full sm:w-auto">
                                    <button
                                        onClick={async () => {
                                            const total = ciDays * ciRate;
                                            const lead = clientInvoiceLead;
                                            setIsClientInvoiceOpen(false);
                                            const toastId = toast.loading(`Generating invoice for ${lead.name}...`);
                                            try {
                                                const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
                                                const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;
                                                const formatDateStr = (ds: string) => {
                                                    if (!ds) return '';
                                                    const [y, m, d] = ds.split('-');
                                                    return `${d}/${m}/${y}`;
                                                };
                                                const formattedPeriod = (ciStartDate && ciEndDate)
                                                    ? `${formatDateStr(ciStartDate)} To ${formatDateStr(ciEndDate)}`
                                                    : 'As agreed';
                                                const generatedInvNo = `INV-C${Math.floor(Math.random() * 9000) + 1000}`;
                                                // 1. Generate PDF
                                                const invResp = await fetch(`${SUPABASE_URL}/functions/v1/generate-invoice`, {
                                                    method: 'POST',
                                                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${SUPABASE_ANON_KEY}` },
                                                    body: JSON.stringify({
                                                        lead_id: ciLeadId || lead.id,
                                                        manual_invoice: true,
                                                        rate_per_day: ciRate,
                                                        start_date: ciStartDate,
                                                        end_date: ciEndDate,
                                                        deposit_collected: 0,
                                                        service_period: formattedPeriod,
                                                        invoice_number: generatedInvNo,
                                                        is_deposit: false,
                                                        client_name: lead.name,
                                                        client_phone: lead.phone || lead.whatsapp_number,
                                                        service_name: lead.service_interest,
                                                        service_hours: lead.shift_duration,
                                                    })
                                                });
                                                if (!invResp.ok) throw new Error(await invResp.text());
                                                const invData = await invResp.json();
                                                if (invData.error) throw new Error(invData.error);
                                                const invoicePdfUrl = invData.public_url;
                                                toast.loading('Sending invoice via WhatsApp...', { id: toastId });
                                                // 2. Send template message with QR header + PDF follow-up
                                                let phoneDigits = lead.whatsapp_number || lead.phone || '';
                                                phoneDigits = phoneDigits.replace(/\D/g, '');
                                                if (phoneDigits.length === 10) phoneDigits = `91${phoneDigits}`;
                                                const waResp = await fetch(`${SUPABASE_URL}/functions/v1/meta-whatsapp-outbound`, {
                                                    method: 'POST',
                                                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${SUPABASE_ANON_KEY}`, 'apikey': SUPABASE_ANON_KEY },
                                                    body: JSON.stringify({
                                                        phone: phoneDigits,
                                                        leadId: lead.id,
                                                        useTemplate: true,
                                                        templateName: 'client_monthly_invoice',
                                                        templateParams: [lead.name || 'there', String(total)],
                                                        sendInvoicePdf: true,
                                                        invoicePdfUrl: invoicePdfUrl,
                                                    })
                                                });
                                                const waData = await waResp.json();
                                                if (!waData.success) throw new Error(waData.error || 'WhatsApp dispatch failed');

                                                // Record in unified service_bills ledger
                                                const { data: activeSvc } = await supabase
                                                    .from('services')
                                                    .select('id')
                                                    .or(`client_id.eq.${lead.id},lead_id.eq.${lead.id}`)
                                                    .eq('status', 'active')
                                                    .maybeSingle();

                                                if (activeSvc?.id) {
                                                    await recordServiceInvoice({
                                                        serviceId: activeSvc.id,
                                                        clientId: lead.id,
                                                        periodStart: ciStartDate,
                                                        periodEnd: ciEndDate,
                                                        totalDays: ciDays,
                                                        dailyRateUsed: ciRate,
                                                        amount: total,
                                                        invoiceNumber: `INV-C${Math.floor(Math.random() * 9000) + 1000}`,
                                                        invoicePdfUrl: invoicePdfUrl,
                                                        type: 'recurring',
                                                    });
                                                }

                                                // Move lead to Monthly Billing stage
                                                await supabase
                                                    .from('crm_leads')
                                                    .update({ pipeline_stage: 'Monthly Billing' })
                                                    .eq('id', ciLeadId || lead.id);
                                                toast.success(`Invoice sent to ${lead.name} on WhatsApp! ✅ Recorded in Monthly Billing ledger.`, { id: toastId, duration: 4000 });
                                            } catch (err: any) {
                                                toast.error(err.message || 'Failed to send invoice', { id: toastId });
                                            }
                                        }}
                                        className="flex-[1.5] sm:flex-none px-5 py-2.5 rounded-xl font-bold text-white bg-[#25D366] hover:bg-[#1ebd5a] transition-all shadow-md flex items-center justify-center gap-2 whitespace-nowrap"
                                    >
                                        <Send className="w-4 h-4" /> Send Invoice on WhatsApp
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Payslip Generator when releasing from CRM */}
            {releasedAssignment && (
                <PayslipGenerator
                    assignment={releasedAssignment}
                    autoCloseAssignmentOnGenerate={false}
                    onClose={() => setReleasedAssignment(null)}
                    onGenerated={() => { setReleasedAssignment(null); fetchLeads(); }}
                />
            )}

            {/* Client Details Modal */}
            {detailsModalClient && (
                <ClientDetailsModal
                    client={detailsModalClient}
                    onClose={() => setDetailsModalClient(null)}
                    onServiceUpdated={() => fetchLeads()}
                    onStartNewService={(c) => {
                        setDetailsModalClient(null);
                        navigate(`/admin/clients?restart=${c?.id || detailsModalClient.id}`);
                    }}
                />
            )}

            {/* ── Direct Database Update Confirmation Modal ────────────── */}
            {pendingDbConfirmation &&
                createPortal(
                    <div className="fixed inset-0 z-[400] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-amber-200 overflow-hidden animate-in zoom-in-95 duration-200">
                            {/* Warning Header */}
                            <div className="p-5 border-b border-amber-100 bg-amber-50 flex items-center gap-3">
                                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center shrink-0 text-amber-600">
                                    <AlertTriangle className="w-5 h-5" />
                                </div>
                                <div>
                                    <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                                        Database Update Confirmation
                                    </h3>
                                    <p className="text-xs text-amber-800 font-medium mt-0.5">
                                        ⚠️ This will make changes from database as well.
                                    </p>
                                </div>
                            </div>

                            {/* Body Details */}
                            <div className="p-5 space-y-4">
                                <div>
                                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">
                                        Field to Modify
                                    </p>
                                    <p className="text-sm font-semibold text-slate-800">
                                        {pendingDbConfirmation.fieldName || pendingDbConfirmation.title}
                                    </p>
                                </div>

                                {/* Comparison Box */}
                                <div className="grid grid-cols-2 gap-3 p-3.5 bg-slate-50 border border-slate-200 rounded-xl text-xs">
                                    <div>
                                        <span className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Current (Database)</span>
                                        <div className="p-2 bg-white rounded-lg border border-slate-200 text-slate-600 font-medium break-words">
                                            {String(pendingDbConfirmation.oldValue || 'None / Not set')}
                                        </div>
                                    </div>
                                    <div>
                                        <span className="text-[10px] font-bold text-emerald-600 uppercase block mb-1">New Value</span>
                                        <div className="p-2 bg-emerald-50 rounded-lg border border-emerald-200 text-emerald-800 font-bold break-words">
                                            {String(pendingDbConfirmation.newValue || 'None / Not set')}
                                        </div>
                                    </div>
                                </div>

                                <div className="p-3 bg-amber-50/70 rounded-xl border border-amber-200/60 text-xs text-amber-900 leading-relaxed flex items-start gap-2">
                                    <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                                    <span>
                                        Confirming will permanently update the database records in Supabase (including linked lead, client profile, and active services).
                                    </span>
                                </div>

                                {/* Buttons */}
                                <div className="flex items-center gap-3 pt-2">
                                    <button
                                        type="button"
                                        disabled={pendingDbConfirmation.loading}
                                        onClick={() => setPendingDbConfirmation(null)}
                                        className="flex-1 py-2.5 px-4 bg-white border border-slate-200 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition-colors text-sm"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        disabled={pendingDbConfirmation.loading}
                                        onClick={async () => {
                                            try {
                                                setPendingDbConfirmation(prev => prev ? { ...prev, loading: true } : null);
                                                await pendingDbConfirmation.onConfirm();
                                                setPendingDbConfirmation(null);
                                            } catch (err: any) {
                                                console.error('Confirmation error:', err);
                                                toast.error(err.message || 'Failed to update database');
                                                setPendingDbConfirmation(prev => prev ? { ...prev, loading: false } : null);
                                            }
                                        }}
                                        className="flex-1 py-2.5 px-4 bg-amber-600 hover:bg-amber-700 text-white font-bold rounded-xl shadow-md transition-all text-sm flex items-center justify-center gap-2"
                                    >
                                        {pendingDbConfirmation.loading ? (
                                            <>
                                                <Loader2 className="w-4 h-4 animate-spin" /> Updating...
                                            </>
                                        ) : (
                                            <>
                                                <Check className="w-4 h-4" /> Confirm & Save
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>,
                    document.body
                )}

            {/* ── Quick Edit Lead & Rates Modal ───────────────────────── */}
            {quickEditLead &&
                createPortal(
                    <div className="fixed inset-0 z-[350] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 overflow-y-auto animate-in fade-in duration-200">
                        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg border border-slate-200 overflow-hidden my-auto animate-in zoom-in-95 duration-200 max-h-[90vh] flex flex-col">
                            {/* Header */}
                            <div className="p-5 border-b border-slate-100 bg-slate-50 flex justify-between items-center shrink-0">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center text-primary shrink-0">
                                        <Edit3 className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-slate-900 text-base">
                                            Quick Edit Lead & Rates
                                        </h3>
                                        <p className="text-xs text-slate-500 mt-0.5">
                                            {quickEditLead.name} • {quickEditLead.whatsapp_number || quickEditLead.phone || 'No phone'}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setQuickEditLead(null)}
                                    className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            {/* Body Form */}
                            <form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    handleSaveQuickEdit();
                                }}
                                className="p-5 overflow-y-auto space-y-4 flex-1"
                            >
                                {/* Database notice badge */}
                                <div className="p-3 bg-amber-50/80 border border-amber-200/70 rounded-xl flex items-start gap-2.5 text-xs text-amber-900 leading-relaxed">
                                    <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                                    <span>
                                        <strong>Direct Database Sync:</strong> Changes saved here will be verified with a confirmation prompt before updating Supabase records.
                                    </span>
                                </div>

                                {/* Daily Rates Grid */}
                                <div className="bg-emerald-50/50 border border-emerald-200/80 rounded-xl p-4 space-y-3">
                                    <p className="text-xs font-bold text-emerald-800 uppercase tracking-wider flex items-center gap-1.5">
                                        <TrendingUp className="w-3.5 h-3.5 text-emerald-600" /> Daily Rates & Lead Value
                                    </p>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-xs font-semibold text-slate-700 mb-1">
                                                Full Month Rate (₹/day)
                                            </label>
                                            <div className="relative">
                                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500 font-semibold">₹</span>
                                                <input
                                                    type="number"
                                                    value={quickEditForm.completeMonthDailyRate}
                                                    onChange={e => setQuickEditForm(prev => ({ ...prev, completeMonthDailyRate: e.target.value }))}
                                                    placeholder="e.g. 1000"
                                                    className="w-full pl-7 pr-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 bg-white text-sm font-semibold text-slate-800"
                                                />
                                            </div>
                                            {quickEditForm.completeMonthDailyRate && !isNaN(Number(quickEditForm.completeMonthDailyRate)) && (
                                                <p className="text-[11px] text-emerald-700 font-medium mt-1">
                                                    ≈ ₹{(Number(quickEditForm.completeMonthDailyRate) * 30).toLocaleString('en-IN')}/month
                                                </p>
                                            )}
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-slate-700 mb-1">
                                                Partial Month Rate (₹/day)
                                            </label>
                                            <div className="relative">
                                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500 font-semibold">₹</span>
                                                <input
                                                    type="number"
                                                    value={quickEditForm.incompleteMonthDailyRate}
                                                    onChange={e => setQuickEditForm(prev => ({ ...prev, incompleteMonthDailyRate: e.target.value }))}
                                                    placeholder="e.g. 1200"
                                                    className="w-full pl-7 pr-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 bg-white text-sm font-semibold text-slate-800"
                                                />
                                            </div>
                                            <p className="text-[11px] text-slate-500 mt-1">For shorter duration stays</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Service & Shift Selection */}
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-700 mb-1">
                                            Care Service
                                        </label>
                                        <select
                                            value={quickEditForm.serviceCategory}
                                            onChange={e => setQuickEditForm(prev => ({ ...prev, serviceCategory: e.target.value }))}
                                            className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-white text-sm font-medium text-slate-800"
                                        >
                                            <option value="">Select Service...</option>
                                            {CARE_SERVICES.map(svc => (
                                                <option key={svc} value={svc}>{svc}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-700 mb-1">
                                            Shift / Offered Time
                                        </label>
                                        <select
                                            value={
                                                quickEditForm.shiftType.includes('24') || quickEditForm.shiftType.toLowerCase().includes('live')
                                                    ? '24 Hours (Live-in)'
                                                    : quickEditForm.shiftType.includes('10')
                                                    ? '10 Hours'
                                                    : quickEditForm.shiftType
                                            }
                                            onChange={e => setQuickEditForm(prev => ({ ...prev, shiftType: e.target.value }))}
                                            className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-white text-sm font-medium text-slate-800"
                                        >
                                            <option value="">Select Shift...</option>
                                            <option value="10 Hours">10 Hours</option>
                                            <option value="24 Hours (Live-in)">24 Hours (Live-in)</option>
                                        </select>
                                    </div>
                                </div>

                                {/* Patient & Relative Names */}
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-700 mb-1">
                                            Patient Name
                                        </label>
                                        <input
                                            type="text"
                                            value={quickEditForm.patientName}
                                            onChange={e => setQuickEditForm(prev => ({ ...prev, patientName: e.target.value }))}
                                            placeholder="Patient full name"
                                            className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-white text-sm text-slate-800"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-700 mb-1">
                                            Relative / Contact Person
                                        </label>
                                        <input
                                            type="text"
                                            value={quickEditForm.relativeName}
                                            onChange={e => setQuickEditForm(prev => ({ ...prev, relativeName: e.target.value }))}
                                            placeholder="Relative name"
                                            className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-white text-sm text-slate-800"
                                        />
                                    </div>
                                </div>

                                {/* Phone & Planned Start Date */}
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-700 mb-1">
                                            Patient / Direct Phone
                                        </label>
                                        <input
                                            type="text"
                                            value={quickEditForm.patientPhone}
                                            onChange={e => setQuickEditForm(prev => ({ ...prev, patientPhone: e.target.value }))}
                                            placeholder="10-digit phone"
                                            className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-white text-sm text-slate-800"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-700 mb-1">
                                            Planned Start Date
                                        </label>
                                        <input
                                            type="date"
                                            value={quickEditForm.startDate}
                                            onChange={e => setQuickEditForm(prev => ({ ...prev, startDate: e.target.value }))}
                                            className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-white text-sm text-slate-800"
                                        />
                                    </div>
                                </div>

                                {/* Location / Address */}
                                <div>
                                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                                        Address / Location
                                    </label>
                                    <textarea
                                        rows={2}
                                        value={quickEditForm.address}
                                        onChange={e => setQuickEditForm(prev => ({ ...prev, address: e.target.value }))}
                                        placeholder="Flat/House no., Area, City..."
                                        className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-white text-sm text-slate-800 resize-none"
                                    />
                                </div>

                                {/* Footer Actions */}
                                <div className="flex items-center gap-3 pt-3 border-t border-slate-100">
                                    <button
                                        type="button"
                                        onClick={() => setQuickEditLead(null)}
                                        className="flex-1 py-2.5 px-4 bg-white border border-slate-200 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition-colors text-sm"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="flex-1 py-2.5 px-4 bg-[#1AA6A8] hover:bg-[#158587] text-white font-bold rounded-xl shadow-md transition-all text-sm flex items-center justify-center gap-2"
                                    >
                                        <Check className="w-4 h-4" /> Save to Database
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>,
                    document.body
                )}
        </div>
    );
}
